<template>
    <div class="template-view has-header" v-bind:class="{'group-drag' : draggingGroup}">
        <div class="header main">

            <div class="align-left" style="margin-right: 0;">
                <dateselectionheader style="width: 500px;"
                                     :label="dateDisplay"
                                     @PrevDate="SelectPreviousDate"
                                     @NextDate="SelectNextDate"
                                     @CalendarOpen="toggleDatePicker"
                                     @SetToday="SelectToday"/>
            </div>

            <div class="align-middle">
                <headerbuttonstack :state="headerButtons"/>
            </div>

            <div class="align-right">
                <headerbuttonstack :state="sideButtons"/>
            </div>

        </div>
        <grid-view v-if="!loading" class="template-grid" first-column="250px"
                   :cols="gridColumns" :rows="gridRows" id="template-grid"
                   :vertical-separators="gridVerticalSeparators"
                   :horizontal-separators="gridHorizontalSeparators">
            <grid-cell v-for="headerCell of gridHeaderCells" :key="headerCell.key"
                       class="header-cell" :class="templateDaySelection === 'W' ? '' : 'header-cell--day-mode'"
                       @click="headerCell.onClick"
                       :col-from="headerCell.colFrom" :col-to="headerCell.colTo" :row="1"
                       :sticky="headerCell.sticky" :style="headerCell.style">
                {{ headerCell.label }}
                <i style="margin-left: 5px; color: orange;"
                   :style="templateDaySelection === 'W' ? { fontSize: '14px' } : { fontSize: '11px', position: 'absolute', left: 'calc(100% - 20px)' }"
                   v-tooltip="getGroupInfoWrapper(dayTemplateGroupActiveMap[headerCell.markKey])"
                   @click="selectTemplateGroup(dayTemplateGroupActiveMap[headerCell.markKey])"
                   v-if="selectedGroup != null && headerCell.markKey && dayTemplateGroupActiveMap[headerCell.markKey] !== selectedGroup.id"
                   class="fas fa-exclamation-triangle">
                </i>
            </grid-cell>
            <grid-cell v-for="headerCell of gridHeaderDateCells" :key="headerCell.key" class="header-cell"
                       :col-from="headerCell.colFrom" :col-to="headerCell.colTo" :row="1"
                       :sticky="headerCell.sticky" :style="headerCell.style">
                {{ headerCell.label }}
            </grid-cell>
            <grid-cell v-for="headerCell of gridHeaderInfoCells" :key="headerCell.key"
                       class="header-cell header-cell--info"
                       :col-from="headerCell.colFrom" :col-to="headerCell.colTo" :row="2"
                       :sticky="headerCell.sticky" :style="headerCell.style">
                <div class="total-unassigned-hours" v-if="templateDaySelection === 'W' && headerCell.unassignedHours"
                     v-tooltip="headerCell.unassignedHoursTooltip">
                    {{ showProductivity ? $t('roster.view.prodShort') + ': '  + headerCell.productiveHours : headerCell.unassignedHours }}
                </div>
                <div class="indicator" v-if="headerCell.indicatorStyle"
                     :style="headerCell.indicatorStyle"
                     v-tooltip="headerCell.indicatorTooltip"
                     >
                    <div class="bar" :style="headerCell.indicatorBarStyle"></div>
                </div>
                <div class="quarter-wrapper" v-if="headerCell.quarters">
                    <div class="quarter-point" v-for="quarter of headerCell.quarters" :key="quarter.key"
                         v-tooltip="quarter.tooltip"></div>
                </div>
                <div class="prod-bar" v-if="headerCell.quarters && showProductivity" v-texttooltip="$t('roster.view.prodShort') + ': ' + headerCell.actual + ' / ' + headerCell.planned"
                     v-bind:style="getProdBarStyle(headerCell)">
                </div>
            </grid-cell>
            <grid-cell v-for="cell of gridEventAddButtonCells" :key="cell.key"
                       class="event-add-button-container"
                       :col-from="cell.colFrom" :col-to="cell.colTo" :row="3">
                <button class="add-event-button" @click="newEventPopup($event, cell.dayIndex, cell.directSave)">
                    <i class="fas fa-plus"></i>
                </button>
            </grid-cell>
            <grid-cell class="header-cell header-cell--first-col" :col-from="1" :col-to="2" :row="1" :row-to="3"
                       :sticky="{ top: '55px', left: 0 }" style="position: absolute!important; z-index: 100; width: 250px; height: 64px">
                <div class="pretitle">
                    <div class="label">{{ $t('shift.main.group') }}:</div>
                    <dropdownsearch :state="templateGroupDropDownState" class="viewfilter-dropdown"/>
                </div>
            </grid-cell>
            <grid-cell v-for="cell of gridGroupHeaderCells" :key="cell.key"
                       class="group-header-cell"
                       :class="{'group-header-cell--subgroup': cell.isSubgroup}"
                       :col-from="cell.colFrom" :col-to="cell.colTo" :row="cell.row"
                       :sticky="{ left: 0 }" :style="cell.style">
                <span class="group-header-cell-label">{{ cell.label }}</span>
                <div class="action-button-container">
                    <button class="cell-button cell-button--small" v-if="canEdit && cell.spotId !== 'events'"
                            @click="openNewShiftTemplatePopup($event, cell.templateGroup, cell.directSave)">
                        <i class="fas fa-plus"></i>
                    </button>
                    <button class="cell-button cell-button--small" v-if="canEdit && cell.spotId === 'events'"
                            @click="newEventPopup($event, 0, cell.directSave)">
                        <i class="fas fa-plus"></i>
                    </button>
                    <button class="cell-button cell-button--small"
                            :class="{'group-button--collapsed': cell.collapsed}"
                            @click="toggleCollapseState(cell.spotId)">
                        <i class="fas fa-chevron-up"></i>
                    </button>
                </div>
            </grid-cell>
            <grid-cell v-for="cell of gridGroupRows" :key="cell.key"
                       class="group-row-cell" :style="cell.noSpotLabel ? { marginTop: '-1px' } : {}"
                       :col-from="cell.colFrom" :col-to="cell.colTo" :row="cell.row">
                <span v-if="cell.noSpotLabel" class="group-header-cell-label">
                    {{ $t('shift.template.nonePresent', [cell.noSpotLabel]) }}
                </span>
                <span v-else-if="cell.noSpotFilterLabel" class="group-header-cell-label">
                    {{ $t('shift.template.filtered', [cell.noSpotFilterLabel]) }}
                </span>
            </grid-cell>
            <grid-cell v-for="cell of gridEventDisplayCells" :key="cell.key"
                       class="template-display-cell template-display-cell--single-row"
                       :col-from="cell.colFrom" :col-to="cell.colTo" :row="cell.row"
                       :style="cell.style" :draggable="false">
                <div class="display-cell-label display-cell-label--large">
                    <i class="fas fa-circle display-cell-new-indicator event-cell-new-indicator" v-if="cell.isNew"></i>
                    <span :style="{ marginLeft: cell.isNew ? 0 : '0.5rem' }">{{ cell.label }}</span>
                </div>

                <div class="display-cell-actions" v-if="canEdit">
                    <button class="display-cell-action"
                            @click="editEventPopup($event, cell.event, cell.directSave)">
                        <i class="fas fa-cog"></i>
                    </button>
                </div>
            </grid-cell>
            <grid-cell v-for="cell of gridTemplateDisplayCells" :key="cell.key"
                       class="template-display-cell" :collapsed="cell.collapsed"
                       :class="{'group-header-cell--subgroup': cell.isSubgroup}"
                       :col-from="cell.colFrom" :col-to="cell.colTo" :row="cell.row"
                       :style="cell.style" :draggable="cell.draggable">
                <div class="display-cell-image" :style="{ background: cell.picture }">
                    <!--                    <template v-if="!cell.picture">{{ parseInt(cell.duration) }}</template>-->
                </div>
                <div class="display-cell-label display-cell-label--large">
                    <span>{{ cell.employeeName ? cell.duration+" "+cell.employeeName : cell.duration }}</span>
                    <i class="fas fa-circle display-cell-new-indicator" v-if="cell.template.isNew"></i>
                </div>
                <span class="display-cell-label">{{ cell.period }}</span>
                <div class="display-cell-actions" v-if="canEdit && !cell.template.isRecommendation && !(cell.template.isNew && cell.template.eventId)">
                    <button v-if="!templateWillBeDeleted(cell.template)" class="display-cell-action"
                            @click="openTemplateButtonMenu($event, cell.templateGroup, cell.template, cell.directSave)">
                        <i class="fas fa-cog"></i>
                    </button>
                    <button v-else class="display-cell-action"
                            @click="restoreTemplate(cell.template)"
                            :title="$t('shift.template.restore')">
                        <i class="fas fa-undo"></i>
                    </button>
                </div>
            </grid-cell>
            <grid-cell v-for="cell of gridEventCells" :key="cell.key"
                       class="event-cell" :class="{'event-cell--new': cell.isNew, 'event-cell--delete': cell.isDeleted}"
                       :col-from="cell.colFrom" :col-to="cell.colTo" :row="cell.row"
                       @dblclick="editEventPopup($event, cell.event, cell.directSave)">
                <div class="event-color-indicator" :style="{ backgroundColor: cell.color }"></div>
                <span class="event-label" :style="{ marginRight: canEdit ? 0 : '0.5rem' }">{{ cell.label }} </span>
                <button class="event-delete-button" v-if="canEdit"
                        @click="removeEvent({ id: cell.eventId }, cell.directSave)">
                    <i class="fas fa-times"></i>
                </button>
            </grid-cell>
            <grid-cell v-for="cell of gridRecommendationCells" :key="cell.key"
                       class="template-cell recommendation-cell"
                       :class="cell.classList"
                       :col-from="cell.colFrom" :col-to="cell.colTo" :row="cell.row"
                       :style="cell.style" :resizable="cell.resizable"
                       v-tooltip="cell.tooltip"
                       @click="acceptRecommendation(cell.templateId, cell.recommendation, cell.day, cell.directSave)">
                <span class="cell-label cell-label--left">{{ cell.labelLeft }}</span>
                <span class="cell-label cell-label--right">{{ cell.labelRight }}</span>
                <i class="fas fa-check recommendation-check"></i>
            </grid-cell>
            <grid-cell v-for="cell of gridTemplateCells" :key="cell.key"
                       class="template-cell" :collapsed="cell.collapsed"
                       :class="cell.classList"
                       :col-from="cell.colFrom" :col-to="cell.colTo" :row="cell.row"
                       :style="cell.style" :resizable="cell.resizable"
                       v-texttooltip="cell.texttooltip"
                       :ref="'templateCell-' + cell.key"
                       @resizeStart="templateCellResizeStart(cell, $event)"
                       @resizeMove="templateCellResizeMove(cell, $event)"
                       @resizeEnd="templateCellResizeEnd(cell, $event, cell.directSave)">
                <span class="cell-label cell-label--left">
                    {{ cell.labelLeft }}
                </span>
                <span class="cell-label cell-label--right">
                    {{ cell.labelRight }}
                </span>
                <button class="delete-weekday-button" v-if="cell.showDeleteButton"
                        @click="deleteWeekdayFromTemplate(cell.template, cell.day, cell.directSave)">
                    <i class="fas fa-times"></i>
                </button>
            </grid-cell>
            <grid-cell v-for="cell of gridTemplateAddWeekdayCells" :key="cell.key"
                       class="template-cell template-cell--add-weekday"
                       :col-from="cell.colFrom" :col-to="cell.colTo" :row="cell.row"
                       :resizable="false" :collapsed="cell.collapsed"
                       @click="addWeekdayToTemplate(cell.template, cell.weekday, cell.directSave)">
                <span class="cell-label cell-label--left">{{ cell.labelLeft }}</span>
                <span class="cell-label cell-label--right">{{ cell.labelRight }}</span>
            </grid-cell>
            <grid-cell v-for="cell of gridTemplateBreakCells" :key="cell.key"
                       class="template-break-cell" :class="cell.classList" :collapsed="cell.collapsed"
                       :col-from="cell.colFrom" :col-to="cell.colTo" :row="cell.row"
                       :style="cell.style"
                       :resizable="false"
                       v-texttooltip="cell.texttooltip"
                       >
            </grid-cell>
            <!-- MEO Cells -->
            <template v-if="planningType !== 2">
                <grid-cell v-for="cell of gridEmployeeCountHeaderCells" :key="cell.key"
                           class="group-header-cell"
                           :class="{'group-header-cell--subgroup': cell.isSubgroup}"
                           :col-from="cell.colFrom" :col-to="cell.colTo" :row="cell.row"
                           :sticky="{ left: 0 }" :style="cell.style">
                    <span class="group-header-cell-label">{{ cell.label }}</span>
                </grid-cell>
                <grid-cell v-for="cell of gridEmployeeBulletPointCells" :key="cell.key"
                           class="group-employee-quarter-cell"
                           :col-from="cell.colFrom" :col-to="cell.colTo" :row="cell.row"
                           :style="cell.style">
                    <div class="employeequarter" v-for="quarter of cell.quarters" :key="quarter.key"
                         :style="quarter.style"
                         v-tooltip="quarter.tooltip"></div>
                </grid-cell>
                <grid-cell v-for="cell of gridEmployeeCountCells" :key="cell.key"
                           class="group-employee-count-cell"
                           :col-from="cell.colFrom" :col-to="cell.colTo" :row="cell.row">
                    <div class="label" v-tooltip="cell.tooltip" :style="cell.labelStyle">{{ cell.value }}</div>
                </grid-cell>
            </template>
        </grid-view>

        <div v-if="infoVideoOpened" class="info-video-container" @click="infoVideoOpened = false">
            <div class="info-video-sheet">
                <button class="close-video-button" @click="infoVideoOpened = false">
                    <i class="fas fa-times"></i>
                </button>
                <iframe src="https://player.vimeo.com/video/827163413?h=7a1add82af&autoplay=1&color=6d8d23&title=0&byline=0&portrait=0"
                        style="width:100%;height:100%;"
                        frameborder="0" allow="autoplay; fullscreen; picture-in-picture" allowfullscreen>
                </iframe>
            </div>
        </div>

        <div class="pd-container" :class="{'pd-collapsed': !displayPdChart}" v-if="renderPdChart"
             style="width: 100%;">
            <personnel-deployment-detail class="pd-detail"
                                         :title="$t('roster.view.personnel') + ':' + $t('roster.view.usage')"
                                         @titleBarClick="togglePDSection"
                                         @openInfoVideo="infoVideoOpened = true"
                                         v-if="!loading">
                <template v-slot:before-title>
                    <i class="fa fa-chevron-up pd-section-toggle" :class="{'pd-toggle-up': displayPdChart}"/>
                </template>
            </personnel-deployment-detail>

            <personnel-deployment-chart ref="pdChart"
                                        class="pd-chart"
                                        :week="templateDaySelection === 'W' ? currentWeekStart : null"
                                        :day="templateDaySelection !== 'W' ? timeFrameStart : null"
                                        :day-mode-hours="dayModeHours"
                                        :current-week-shift-template-map="pdLoading ? {} : finalWeekTemplates"
                                        :chart-padding-right="20"
                                        :force-loading="pdLoading"
                                        @onDayDblClick="onChartDayDblClick"
                                        style="width: calc(100% - 265px);"
                                        v-if="!loading && displayPdChart"/>
        </div>


        <div class="event-info" v-if="eventOnHover">
            <div class="header">{{ eventOnHover.header }}</div>
            <div class="description">{{ eventOnHover.description }}</div>
        </div>
        <div class="drag-info" v-if="dragging">
            <div class="description">{{ draggingTime.format('YYYY-MM-DD HHa') }}</div>
        </div>
        <div class="drag-group group" v-if="draggingGroup">
            <div class="header">
                <div class="fa-stack drag">
                    <i class="fas fa-stack-2x fa-arrows-alt"></i>
                </div>
                <div class="label">{{ draggingGroup.header }}</div>
                <div class="fa-stack add">
                    <i class="fas fa-circle fa-stack-2x"></i>
                    <i class="fas fa-plus fa-stack-2x"></i>
                </div>
                <div class="fa-stack">
                    <i class="fas fa-circle fa-stack-2x"></i>
                    <i class="fas fa-stack-2x fa-angle-down"></i>
                </div>
            </div>
        </div>

        <div v-if="loading || weeklyLoading" class="loading-md bg">
            <i class="fas fa-spinner"></i>
            <div v-if="!$helpers.isEmpty(loadingText)" class="loading-text">
                {{ loadingText }}
            </div>
        </div>
        <dropdowncontainer ref="dropdowncontainer"/>

        <datepickerarray :state="datepickerarrayState" ref="datepickerarray"
                         v-on:datepickerarray-dates-picked="datePickArrayCallback"></datepickerarray>

        <shift-design-modal v-if="showShiftDesignModal" :planningState="planningState"
                            :showShiftDesignDetails="showShiftDesignDetails" :selectedShiftDesign="selectedShiftDesign"
                            @close-shift-design-modal="closeShiftDesignModal" @delete-shift-design="deleteShiftDesign"
                            @open-shift-design-details="updatePlaningTable">
        </shift-design-modal>
        <shift-design-details v-if="showShiftDesignDetails"
                              :shiftGroupState="shiftGroupState"
                              :shiftDesigns="shiftDesigns" :selected="selectedShiftDesign"
                              :spotMap="spotMap" @close-shift-design-details="closeShiftDesignDetails"
                              @accept-shift-design="transformShiftDesignIntoChanges">
        </shift-design-details>
    </div>
</template>

<script>

import dropdowncontainer from '../uicomponents/dropdowncontainer.vue'
import dropdownsearch from '../uicomponents/dropdownsearch.vue'
import datepickerarray from '../uicomponents/datepickerarray.vue'
import PersonnelDeploymentDetail from '../uicomponents/charts/PersonnelDeploymentDetail.vue';
import PersonnelDeploymentChart from '../uicomponents/charts/PersonnelDeploymentChart.vue';
import { mapGetters } from 'vuex';
import moment from 'moment';
import GridView from '@/components/uicomponents/gridview/GridView.vue';
import GridCell from '@/components/uicomponents/gridview/GridCell.vue';
import { dateToHHmm, dateToYYYYMMDD } from '@/date-helper';
import { addDays, addHours, addMinutes, differenceInMinutes, format, set } from "date-fns";
import ShiftDesignModal from "@/components/shiftcontext/ShiftDesignModal.vue";
import ShiftDesignDetails from "@/components/shiftcontext/ShiftDesignDetails.vue";

const X_OFFSET = 2;
const Y_OFFSET = 3;
const WEEKMODE_MIN_INTERVAL = 5;
const WEEKMODE_HOUR_INTERVAL = 60 / WEEKMODE_MIN_INTERVAL;
const WEEKMODE_DAY_INTERVAL = 24 * WEEKMODE_HOUR_INTERVAL;
const DAYMODE_MIN_INTERVAL = 1;
const DAYMODE_HOUR_INTERVAL = 60 / DAYMODE_MIN_INTERVAL;
const DAYMODE_DAY_INTERVAL = 24 * DAYMODE_HOUR_INTERVAL;

const hexToRGBArray = color => {
    if (color.charAt(0) === '#')
        color = color.substring(1); // remove #

    if (color.length === 3)
        color = color.charAt(0) + color.charAt(0) + color.charAt(1) + color.charAt(1) + color.charAt(2) + color.charAt(2);
    else if (color.length !== 6)
        throw ( 'Invalid hex color: ' + color );

    const rgb = [];
    for (let i = 0; i <= 2; i++)
        rgb[i] = parseInt(color.substring(i * 2, i * 2 + 2), 16);
    return rgb;
};

const luma = color => {
    const rgb = ( typeof color === 'string' ) ? hexToRGBArray(color) : color;
    return ( 0.2126 * rgb[0] ) + ( 0.7152 * rgb[1] ) + ( 0.0722 * rgb[2] ); // SMPTE C, Rec. 709 weightings
};

export default {
    name: 'templateview',
    components: {
        ShiftDesignDetails,
        ShiftDesignModal,
        GridCell,
        GridView,
        dropdowncontainer,
        dropdownsearch,
        datepickerarray,
        PersonnelDeploymentDetail,
        PersonnelDeploymentChart,
    },
    data() {
        return {
            optionsWeeksExpanded: false,
            currentYear: this.$helpers.getMomentTZ().year(),
            currentSelectedWeek: this.$helpers.getMomentTZ().isoWeek(),
            templateDaySelection: 'W',
            displayedTimeframes: [
                { label: '6', length: 6 * 60 * 60 * 1000, divisions: 6 },
                { label: '12', length: 12 * 60 * 60 * 1000, divisions: 12 },
                { label: '24', length: 24 * 60 * 60 * 1000, divisions: 24 },
                { label: '36', length: 36 * 60 * 60 * 1000, divisions: 36 },
                { label: '48', length: 48 * 60 * 60 * 1000, divisions: 48 }
            ],
            selectedViewFilter: { id: 'full-overlap', label: this.$t('shift.template.overlapAll') },
            displayedTimeframeSelected: { label: '24', length: 24 * 60 * 60 * 1000, divisions: 24 },
            eventOnHover: null,
            embedded: this.$route.name === 'planning',
            mouseX: 0,
            mouseY: 0,
            storeEventMap: {},
            selectedEventId: null,
            mainSpotsForUnit: {},
            uiData: [],
            selectedTimeFrame: null,
            timeFramePopupOffsetY: 0,
            timeFramePopupOffsetX: 0,
            loading: false,
            refreshing: true,
            loadingPercent: null,
            pdLoading: false,
            weeklyLoading: false,
            loadingText: this.$t('shift.template.loadShiftPlanning'),
            dragging: false,
            draggingTimeFrame: null,
            draggingStart: false,
            draggingStartPos: { x: 0, y: 0 },
            draggingHandle: null,
            draggingGroup: null,
            spotVisibilityList: [],

            hourlySelection: 24,

            currentWeekShiftTemplateMap: {},
            shiftTemplates: [],
            shiftTemplateSkills: [],
            shiftTemplateSkillMap: {},
            datepickerarrayState: {
                toggled: false,
                monthView: false,
                posx: 0,
                posy: 0,
                date: this.$helpers.getMomentTZ().month(0).date(1),
                selectedDate: this.$helpers.getMomentTZ().month(0).date(1),
                dateArray: [],
                yearView: true,
                noEdit: true,
            },

            yearDayFilter: [],
            groupCollapsedStates: {},
            datePickArrayCallback: function (dateArray) {
            },
            todayDate: this.$helpers.getMomentTZ(),
            planningIndicatorDailyMap: {},
            planningIndicatorMap: {},
            planningIndicatorTypeMap: {},
            displayIndicatorType: null,
            planningType: 2,
            displayColoredBars: true,
            displayOnlyActiveTemplates: true,
            countSpotsWithoutThreshold: true,
            showProductivity: false,
            countWorkTimeToShiftStart: false,
            selectedGroup: null,
            viewMode: 'full-overlap',

            editMode: false,
            directSaveMode: false,
            optimizationMode: false,
            optimizationGroup: null,

            // these properties store the changed templates to apply them on save
            addedTemplateMap: {},
            removedTemplateMap: {},
            changedTemplateMap: {},

            addedEventMap: {},
            removedEventMap: {},
            changedEventMap: {},

            infoVideoOpened: false,
            useVlhForForecast: false,
            vlhActualCurve: [],
            vlhPlannedCurve: [],
            loadedVlh: false,
            loadingVlh: false,
            vlhDate: null,
            vlhRequirementMap: {},

            shiftGroupState: null,
            shiftDesigns: null,
            selectedShiftDesign: null,
            showShiftDesignModal: false,
            showShiftDesignDetails: false,
        }
    },
    props: {},
    watch: {

        storeId(newVal, oldVal) {
            //console.log("Changed storeId template view: "+newVal);
            this.refresh();
        },
        loginStatus(newVal, oldVal) {
            //console.log("Changed login templateview");
            this.refresh();

        },
        currentYear(newVal, oldVal) {
            // console.log('Changed year');
            this.refresh();

        },
        viewFilterDropDownState(newVal, oldVal) {
            if (newVal != oldVal) {
                if (newVal.items.length > 0) {
                    this.selectedViewFilter = newVal.items[0];
                } else {
                    this.selectedViewFilter = { id: 'full-overlap', label: this.$t('shift.template.overlapAll') };
                }
            }
        },
        currentWeekStart: {
            immediate: true,
            handler(newVal, oldVal) {
                if (newVal != oldVal && !this.refreshing) {
                    let view = this;
                    view.weeklyLoading = true;
                    view.RefreshPlanningIndicator()
                    .then(result => {
                        return view.RefreshVlhThresholdCurve();

                    })
                    .then(res => {
                        console.log("currentWeekStart")
                        return view.RefreshShiftTemplates(true);
                    }).then(res => {
                        view.weeklyLoading = false;
                        view.$store.commit('setAllowContentSwitch', { allowContentSwitch: true });
                    }).catch(error => {
                        console.log(error);
                        view.weeklyLoading = false;
                        view.$store.commit('setAllowContentSwitch', { allowContentSwitch: true });
                        view.$helpers.error(view.$t('global.notifications.error'), view.$t('global.notifications.dataLoadingError'));
                    })

                }

            }

        }


    },
    computed: {
        ...mapGetters([
            'rosterFromTo',
            'userUnitSortMap',
            'employeeMap',
            'employeeSkillMap',
            'employeeTypeMap',
            'businessUnitMap',
            'spotMap',
            'skillMap',
            'storeSettings',
            'planningEmployeeThresholdMap',
            'currentMonthEmployeeContractMap',
            'shiftTemplateMap',
            'shiftTemplateGroupMap',
            'shiftTemplateGroupFrameMap',
            'allowContentSwitch',
            'allowedContractMap',
            'projectMap',
            'locationMap',
            'isSuperUser',
            'storeSettings',
            'displayPdChart',
            'renderPdChart',
            'actualShiftMap',
            'planShiftMap',
            'mlCostCenterMap',
            'userInfo',
        ]),

        daySelected() {
            return this.templateDaySelection === 'W'
                ? null
                : moment(this.currentWeekStart).isoWeekday(this.templateDaySelection);
        },

        dayFrom() {
            return this.templateDaySelection === 'W' ? null : moment(this.daySelected);
        },

        dayTo() {
            return this.templateDaySelection === 'W'
                ? null
                : moment(this.daySelected).add(this.dayModeHours, 'hours');
        },

        dayModeHours() {
            return this.templateDaySelection === 'W' ? 0 : ( this.hourlySelection || 24 );
        },

        canEdit() {
            return this.editMode || this.directSaveMode;
        },

        maxShiftCreatedCount() {
            let view = this;
            let countArray = [ 0, 0, 0, 0, 0, 0, 0 ];
            let max = 0;

            this.shiftTemplates.forEach(t => {
                if (t.eventId == null && this.selectedGroup != null && t.groupId == this.selectedGroup.id) {
                    t.weekDays.forEach(day => {
                        let index = day - 1;
                        countArray[index] += t.count;
                    })

                }
            })
            let validFrom = this.currentWeekStart;
            let validTo = this.currentWeekStart.clone().add(7, 'days');

            Object.values(this.storeEventMap).forEach(event => {
                let eventFrom = view.$helpers.getMomentFromStringTZ(event.from);
                let eventTo = view.$helpers.getMomentFromStringTZ(event.to);
                if (eventFrom <= validTo && eventTo >= validFrom) {
                    let eventCountArray = [ 0, 0, 0, 0, 0, 0, 0 ];
                    this.shiftTemplates.forEach(t => {
                        if (t.eventId == event.id && this.selectedGroup != null && t.groupId == this.selectedGroup.id) {
                            t.weekDays.forEach(day => {
                                let index = day - 1;
                                eventCountArray[index] += t.count;
                            })
                        }
                    })
                    for (let i = 0; i < eventCountArray.length; i++) {
                        if (eventCountArray[i] > countArray[i]) {
                            countArray[i] = eventCountArray[i];
                        }
                    }

                }

            });
            countArray.forEach(i => {
                if (i > max) {
                    max = i;
                }
            })
            return max;
        },
        activeEmployeeCount() {
            let count = Object.values(this.employeeMap).filter(e => e.active).length;
            return count;
        },
        enableVlh() {
            return this.storeSettings.enableVlh;
        },
        headerButtons() {

            let view = this;

            let state = {
                buttons: []
            };
            if (this.templateDaySelection != 'W') {
                state.buttons.push({
                    type: 'block border-r',
                    icon: 'fa-chevron-left',
                    click: () => {
                        view.templateDaySelection = 'W';
                        view.RefreshDisplayedTimeframes();
                        view.planningType = 2;
                    },
                    tooltip: this.$t('shift.template.returnToWeeklyView')
                });
            }
            if(!this.editMode){
                state.buttons.push({
                    type: 'block',
                    icon: 'fa-sync-alt',
                    click: () => {
                        this.refresh();
                    },
                    tooltip: this.$t('global.general.refreshData')
                });
                if (Object.values(view.shiftTemplateGroupMap).length < 1) {
                    state.buttons.push({
                        type: 'block green',
                        icon: 'fa-layer-plus',
                        click: ($event) => {
                            view.OpenNewShiftTemplateGroupPopup($event);
                        },
                        tooltip: this.$t('shift.template.addShiftGroup'),
                        helperText: this.$t('shift.template.addShiftGroupHelp'),
                        externalUrl: 'https://meindienstplan.atlassian.net/servicedesk/customer/portal/1/topic/b826791f-6f4d-4473-a65e-87eba3c3a7c5/article/2219606024'
                    });
                }
                if (Object.values(view.shiftTemplateGroupMap).filter(g => g.immutable == false).length > 0) {
                    state.buttons.push({
                        type: 'block red',
                        icon: 'fa-layer-minus',
                        click: ($event) => {
                            view.OpenDeleteShiftTemplateGroupPopup($event);
                        },
                        tooltip: this.$t('shift.template.deleteShiftGroup'),
                        helperText: this.$t('shift.template.deleteShiftGroupHelp'),
                        externalUrl: 'https://meindienstplan.atlassian.net/servicedesk/customer/portal/1/topic/b826791f-6f4d-4473-a65e-87eba3c3a7c5/article/2219606024'
                    });
                }
            }



            if (this.templateDaySelection != 'W') {
                let planningName = this.$t('shift.template.meo');
                if(this.enableVlh && this.useVlhForForecast){
                    planningName = this.$t('shift.template.vlh');
                }
                if(this.enableVlh || this.storeSettings.enableMeo){
                    if (this.planningType == 2) {
                        state.buttons.push({
                            type: 'block green',
                            icon: 'fa-ruler-horizontal',
                            click: () => {
                                view.setPlanningType(0)
                            },
                            tooltip: () => {return (this.enableVlh && this.useVlhForForecast ? this.$t('shift.main.vlh') : this.$t('shift.main.meo'))+' Planung mit Hilfe von Soll-Kennzahlen'},
                            title: this.$t('shift.template.planWithTargetNumbers'),
                            helperText: this.$t('shift.template.planWithTargetNumbersHelp', [planningName]),
                            externalUrl: 'https://meindienstplan.atlassian.net/servicedesk/customer/portal/1/topic/b826791f-6f4d-4473-a65e-87eba3c3a7c5/article/2219606024'
                        });
                    } else if (this.planningType == 0) {
                        state.buttons.push({
                            type: 'block orange',
                            icon: 'fa-ruler-horizontal',
                            click: () => {
                                view.setPlanningType(1);
                            },
                            tooltip: ()=> {return (this.enableVlh && this.useVlhForForecast ? this.$t('shift.main.vlh') : this.$t('shift.main.meo'))+' Planung mit Hilfe von Ist-Kennzahlen'},
                            title: this.$t('shift.template.planWithActualNumbers'),
                            helperText: this.$t('shift.template.planWithActualNumbersHelp', [planningName]),
                            externalUrl: 'https://meindienstplan.atlassian.net/servicedesk/customer/portal/1/topic/b826791f-6f4d-4473-a65e-87eba3c3a7c5/article/2219606024'
                        });
                    } else if (this.planningType == 1) {
                        state.buttons.push({
                            type: 'block red',
                            icon: 'fa-ruler-horizontal',
                            click: () => {
                                view.setPlanningType(2);
                            },
                            tooltip: this.$t('shift.template.hidePlanningHelp'),
                            helperText: this.$t('shift.template.hidePlanningHelpOn', [planningName]),
                            externalUrl: 'https://meindienstplan.atlassian.net/servicedesk/customer/portal/1/topic/b826791f-6f4d-4473-a65e-87eba3c3a7c5/article/2219606024'
                        });
                    }
                }
            }

            if (this.isSuperUser && !this.editMode ) {
                state.buttons.push({
                    type: 'block red label-center',
                    label: 'A', icon: 'fa-undo-alt',
                    click: () => {
                        view.shiftDesignerModal();
                    },
                    tooltip: this.$t('shift.template.createShiftPlanCapacity'),
                    helperText: this.$t('shift.template.createShiftPlanCapacityHelp'),
                    externalUrl: 'https://meindienstplan.atlassian.net/servicedesk/customer/portal/1/topic/b826791f-6f4d-4473-a65e-87eba3c3a7c5/article/2219606024'
                });
            }

            state.buttons.push({
                type: 'block yellow label-center',
                icon: 'fa-info',
                click: () => {
                    view.openPlannedBudgetPopup();
                },
                tooltip: this.$t('shift.template.hourlyBudgetInfo'),
                helperText: this.$t('shift.template.hourlyBudgetInfoHelp'),
            });

            if (this.canEdit && this.isSuperUser && !this.editMode) {
                if (this.templateDaySelection === 'W' && this.storeId === 65) {
                    state.buttons.push({
                        type: 'block blue label-center',
                        icon: this.optimizationMode ? 'fad fa-object-ungroup' : 'fa-object-ungroup',
                        style: '--fa-secondary-opacity: 1;',
                        click: () => this.toggleOptimizationMode(),
                        tooltip: this.optimizationMode ? this.$t('shift.template.optModeOff') : this.$t('shift.template.optModeOn'),
                        helperText: this.$t('shift.template.optModeHelp'),
                    });
                }
            }

            if (this.editMode) {

                state.buttons.push({
                    type: 'block red label-center',
                    icon: 'fa-times',
                    click: () => this.discardChanges(),
                    tooltip: this.$t('global.ui.cancel'),
                    title: this.$t('global.ui.cancel'),
                    helperText: this.$t('shift.template.cancelAndReturn'),
                });
                state.buttons.push({
                    type: 'block label-center' + (this.hasChanges ? ' green' : ''),
                    icon: 'fa-save',
                    click: () => this.saveChanges(),
                    tooltip: this.$t('global.ui.save'),
                    title: this.$t('global.ui.save'),
                    helperText: this.$t('shift.template.saveAndReturn'),
                    disabled: !this.hasChanges,
                });

            } else if (!this.directSaveMode) {
                state.buttons.push({
                    type: 'block border-r label-center',
                    icon: 'fa-edit',
                    click: () => this.activateEditMode(),
                    tooltip: this.$t('global.ui.edit'),
                    title: this.$t('global.ui.edit'),
                    helperText: this.$t('shift.template.allShiftPlanEdit'),
                });
            }

            /*
                if (this.isSuperUser && Object.keys(this.shiftTemplateGroupMap).length > 1) {
                    state.buttons.push({
                        type: "block blue label-center",
                        icon: "fa-object-ungroup",
                        click: () => {view.toggleOptimizationMode()},
                        tooltip: 'Optimierungsmodus',
                        title: 'Optimierungsmodus',
                        helperText: 'Überlagert die optimierte Schichtgruppe und ermöglicht die Übernahme der optimierten Schichten',
                    });
                }*/


            state.helperLinks = [
                {
                    icon: 'fa-video',
                    title: this.$t('store.structure.watchTrainingVideo'),
                    helperText: this.$t('store.structure.watchTrainingVideoHelp'),
                    externalUrl: 'https://www.youtube.com/watch_popup?v=95nkTvYOE7s',
                    externalLabel: this.$t('store.structure.watchTrainingVideoLabel'),
                    cancelLabel: this.$t('global.ui.close'),
                },
                {
                    title:  this.$t('store.structure.getSupport'),
                    helperText: this.$t('store.structure.getSupportHelp'),
                    externalUrl: 'https://meindienstplan.atlassian.net/servicedesk/customer/portal/1/group/2/create/12',
                    externalLabel:  this.$t('store.structure.getSupportLabel'),
                    cancelLabel: this.$t('global.ui.close'),
                }
            ];

            return state;
        },
        sideButtons() {
            let view = this;

            let state = {
                buttons: [
                    {
                        type: 'block border-r',
                        icon: 'fa-eye',
                        tooltip: this.$t('store.templates.viewOptions'),
                        click: ($event) => {
                            view.OpenMenuDetails($event, this.displayMenuState);
                        },

                    },
                    {
                        type: 'block',
                        icon: 'fa-bars',
                        tooltip: this.$t('global.ui.additionalOptions'),
                        click: ($event) => {
                            view.OpenMenuDetails($event, this.burgerMenuState)
                        }
                    }
                ]
            };


            return state;
        },
        displayMenuState() {
            const displayItems = [
                {
                    closeOnClick: true,
                    icon: !this.displayColoredBars ? 'fa-toggle-off' : 'fa-toggle-on',
                    text: this.$t('shift.template.showShifts'),
                    click: () => this.$set(this, 'displayColoredBars', !this.displayColoredBars),
                },
                {
                    closeOnClick: true,
                    icon: !this.displayOnlyActiveTemplates ? 'fa-toggle-off' : 'fa-toggle-on',
                    text: this.$t('shift.template.showActiveShiftPlans'),
                    click: () => this.$set(this, 'displayOnlyActiveTemplates', !this.displayOnlyActiveTemplates),
                },

                {
                    closeOnClick: true,
                    icon: !this.countSpotsWithoutThreshold ? 'fa-toggle-off' : 'fa-toggle-on',
                    text: this.$t('shift.template.countPositionsWithout'),
                    show: (this.planningType == 0 || this.planningType == 1) && this.templateDaySelection != 'W',
                    click: () => this.$set(this, 'countSpotsWithoutThreshold', !this.countSpotsWithoutThreshold),
                },
                {
                    closeOnClick: true,
                    icon: !this.showProductivity ? 'fa-toggle-off' : 'fa-toggle-on',
                    text: this.$t('roster.view.showProductivity'),
                    show: true,
                    click: () => this.$set(this, 'showProductivity', !this.showProductivity),
                },
                {
                    closeOnClick: true,
                    icon: !this.countWorkTimeToShiftStart ? 'fa-toggle-off' : 'fa-toggle-on',
                    text: this.$t('roster.view.countWorkTimeToShiftStart'),
                    show: true,
                    click: () => this.$set(this, 'countWorkTimeToShiftStart', !this.countWorkTimeToShiftStart),
                },
            ];
            if(this.enableVlh && this.storeSettings.enableMeo){
                displayItems.push({
                    closeOnClick: true,
                    icon: !this.useVlhForForecast ? 'fa-toggle-off' : 'fa-toggle-on',
                    text: this.$t('shift.template.vlhNotMeo'),
                    show: this.templateDaySelection != 'W',
                    click: () => {
                        this.toggleVlh();
                    },
                },)
            }

            let viewItems = [
                {
                    closeOnClick: true,
                    icon: this.viewMode === 'full-split' ? 'fa-toggle-off' : 'fa-toggle-on',
                    text: this.$t('shift.template.overlappingView'),
                    click: () => this.$set(this, 'viewMode', this.viewMode === 'full-split' ? 'full-overlap' : 'full-split'),
                },
                {
                    closeOnClick: false,
                    icon: 'fa-table',
                    text: this.$t('shift.template.onlyDefault'),
                    key: 'standard',
                    click: () => this.$set(this, 'viewMode', 'standard'),
                },
                {
                    closeOnClick: false,
                    icon: 'fa-chalkboard-teacher',
                    text: this.$t('shift.template.onlyProjects'),
                    key: 'project',
                    click: () => this.$set(this, 'viewMode', 'project'),
                },
            ];

            const events = [
                ...Object.values(this.currentWeekEventMap),
                ...Object.values(this.addedEventMap),
            ];
            for (const event of events) {
                viewItems.push({
                    closeOnClick: false,
                    icon: 'fa-circle',
                    color: event.color,
                    text: event.name,
                    key: 'event-' + event.id,
                    click: () => this.$set(this, 'viewMode', 'event-' + event.id),
                });
            }

            viewItems = viewItems.map((item) => {
                const active = this.viewMode === item.key;
                return { ...item, active };
            });

            return {
                componentref: 'burgermenustate',
                groups: [
                    {
                        label: this.$t('shift.template.display'),
                        items: displayItems
                    },
                    {
                        label: this.$t('shift.template.view'),
                        items: viewItems,
                    },
                ],
                items: [],
            };
        },

        burgerMenuState() {
            let view = this;
            let state = {
                componentref: 'burgermenustate',
                groups: [
                    {
                        label: this.$t('shift.main.groups'),
                        items: [
                            {
                                closeOnClick: true,
                                icon: 'fa-copy',
                                disabled: false,
                                text: this.$t('shift.template.copyShiftGroup'),
                                click: () => {
                                    view.OpenCopyShiftTemplateGroupPopup();
                                }
                            }
                        ]
                    },

                    {
                        label: this.$t('shift.template.usage'),
                        items: [
                            {
                                closeOnClick: true,
                                icon: 'fa-edit',
                                disabled: false,
                                text: view.directSaveMode ? this.$t('shift.template.leaveEditMode') : this.$t('shift.template.enterEditMode'),
                                click: () => {
                                    view.discardChanges();
                                    view.toggleDirectSaveMode();
                                }
                            }
                        ]
                    },

                ],
                items: []
            };

            if(this.isSuperUser && this.storeSettings.id == 500){
                state.groups.push({
                        label: this.$t('shift.template.validity'),
                        items: [
                            {
                                closeOnClick: true,
                                icon: 'fa-clock',
                                disabled: false,
                                text: this.$t('shift.template.continueIndefinitely'),
                                click: () => {
                                    view.updateAllNoValidTo();
                                }
                            }
                        ]
                    },);
            }

            if((this.storeSettings.enableVlh || this.storeSettings.enableMeo) && (this.isSuperUser || this.storeSettings.id == 440 || this.storeSettings.id == 441)){
                state.groups.push({
                    label: this.$t('shift.template.planing'),
                    items: [
                        {
                            closeOnClick: true,
                            icon: 'fa-ruler-horizontal',
                            text: this.$t('shift.template.autoShiftPlan'),
                            click: () => {
                                view.OpenCreateShiftDesignPopup();
                            }
                        },
                        {
                            closeOnClick: true,
                            icon: 'fa-ruler-horizontal',
                            text: this.$t('shift.template.shiftPlanResults'),
                            click: () => {
                                view.OpenLoadShiftDesignPopup();
                            }
                        }
                    ]
                });
            }


            return state;
        },
        validGroups() {
            let view = this;
            let res = [];
            let validFrom = this.currentWeekStart;
            let validTo = this.currentWeekStart.clone().add(7, 'days');
            Object.values(this.shiftTemplateGroupFrameMap).forEach(f => {
                let from = f.from != null ? view.$helpers.getMomentFromStringTZ(f.from) : null;
                let to = f.to != null ? view.$helpers.getMomentFromStringTZ(f.to) : null;

                if (( from == null || from < validTo ) && ( to == null || to > validFrom ) && !res.includes(f.groupId)) {
                    res.push(f.groupId)
                }
            })

            return res;
        },
        dayTemplateGroupActiveMap() {
            const map = {};
            let start = this.currentWeekStart.clone();
            while (start < this.currentWeekEnd) {
                const activeGroup = Object.values(this.shiftTemplateGroupFrameMap).find(f => {
                    const from = f.from != null ? this.$helpers.getMomentFromStringTZ(f.from) : null;
                    const to = f.to != null ? this.$helpers.getMomentFromStringTZ(f.to) : null;
                    return ( from <= start || from == null ) && ( to > start || to == null );
                });

                // if (activeGroup)
                //     console.log('activeGroup:', activeGroup.id, 'selectedGroup:', this.selectedGroup.id, 'match:', activeGroup.id === this.selectedGroup.id);

                map[start.format("YYYY-MM-DD")] = activeGroup != null ? activeGroup.groupId : null;
                start = start.add(1, 'days');
            }

            // console.log('dayTemplateGroupActiveMap:', map);

            return map;
        },
        currentWeeksCollapsed() {
            var curWeek = this.currentSelectedWeek;
            var weeks = [];

            var post = 3;
            var pre = 3;

            var prePost = pre + post;
            var collapsedTotal = prePost + 1;

            var preEllipsis = true;
            var postEllipsis = true;
            if (curWeek < pre + 1) {
                preEllipsis = false;
                post = collapsedTotal - curWeek;
                pre = prePost - post;
            } else if (curWeek > 52 - post) {
                postEllipsis = false;
                post = 52 - curWeek;
                pre = prePost - post;
            }
            if (preEllipsis) {
                weeks.push('...');
            }
            for (let i = pre; i > 0; i--) {
                weeks.push(curWeek - i);
            }
            weeks.push(curWeek);
            for (let i = 1; i <= post; i++) {
                weeks.push(curWeek + i);
            }
            if (postEllipsis) {
                weeks.push('...');
            }
            return weeks;
        },
        todayDateLabel() {
            return this.GetWeeklyLabelFormat(this.todayDate.isoWeekday(), this.todayDate);
        },
        timeMarks() {
            let marks = [];

            for (let x = 0; x < this.dayModeHours; x++) {
                let timemark = ( x > 9 ? x : '0' + x );

                if (this.displayedTimeframeSelected.length <= 48 * 60 * 60 * 1000) timemark = ( ( x % 24 ) > 9 ? ( x % 24 ) : '0' + ( x % 24 ) );
                if (this.displayedTimeframeSelected.label === 'W') {
                    let moment = this.$helpers.getMomentTZ().year(this.currentYear).isoWeekYear(this.currentYear).isoWeek(this.currentSelectedWeek);
                    moment.isoWeekday(x + 1);

                    timemark = {
                        label: this.GetWeeklyLabelFormat(moment.isoWeekday(), moment),
                        key: moment.format("YYYY-MM-DD")
                    };
                    marks.push(timemark)
                } else {
                    marks.push(timemark.toString());
                }


            }

            return marks;
        },
        specialWeekMap() {
            let weekMap = Array(this.$helpers.getMomentTZ().year(this.currentYear).isoWeeksInYear()).fill(false);
            Object.values(this.storeEventMap).filter(e => e.fromMoment.year() === this.currentYear).forEach(e => {
                for (let x in weekMap) {
                    if (e.fromMoment.isoWeek() <= x && e.toMoment.isoWeek() >= x) weekMap[x] = true;
                }
            });
            return weekMap;
        },
        loginStatus() {
            return this.$store.state.loggedIn;
        },
        storeId() {
            return this.$store.state.storeId;
        },
        dateDisplay() {
            if (this.templateDaySelection === 'W') {
                return this.currentWeekStart.isoWeekYear() + ' KW ' + this.currentSelectedWeek;
            } else {
                let date = this.$helpers.getMomentTZ().year(this.currentYear).date(0).isoWeekYear(this.currentYear).isoWeek(this.currentSelectedWeek);
                date.isoWeekday(this.templateDaySelection);
                return date.format('DD.MM.YYYY');
            }
        },
        currentWeekStart() {
            let currentWeekStart = this.$helpers.getMomentTZ().year(this.currentYear).isoWeekYear(this.currentYear).isoWeek(this.currentSelectedWeek).startOf('isoWeek').hours(0).minutes(0);
            return currentWeekStart;
        },
        currentWeekEnd() {
            return this.currentWeekStart.clone().add(1, 'weeks');
        },
        currentYearWeeks() {
            return this.$helpers.getMomentTZ().year(this.currentYear).isoWeeksInYear();
        },
        timeFrameStart() {
            let timeFrameStart;
            if (this.templateDaySelection === 'W') {
                timeFrameStart = this.currentWeekStart.clone().isoWeekday(1).hours(0).minutes(0);
            } else {
                timeFrameStart = this.currentWeekStart.clone().isoWeekday(1).hours(0).minutes(0).add(this.templateDaySelection - 1, 'days');
            }


            return timeFrameStart;
        },
        timeFrameEnd() {
            let timeFrameEnd;
            if (this.templateDaySelection === 'W') {
                timeFrameEnd = this.timeFrameStart.clone().add(7, 'days');
            } else {
                timeFrameEnd = this.timeFrameStart.clone().add(this.displayedTimeframeSelected.length, 'milliseconds');
            }

            return timeFrameEnd;
        },
        titleDisplay() {
            let selectedWeek = this.currentSelectedWeek;

            if (this.templateDaySelection === 'W') {
                return this.$t('shift.main.calendarWeekShort') + this.currentSelectedWeek;
            } else {
                if (this.timeFrameStart.year() !== this.currentYear) return this.timeFrameStart.format('dddd DD.MM');
                return this.timeFrameStart.format('dddd DD.MM');
            }
        },
        titleDisplayNextDay() {
            let selectedWeek = this.currentSelectedWeek;

            if (this.templateDaySelection === 'W') {
                if (this.currentSelectedWeek === this.timeFrameStart.isoWeeksInYear()) {
                    selectedWeek = 1;
                }
                return this.$t('shift.main.calendarWeekShort') + selectedWeek;
            } else {
                if (this.timeFrameStart.clone().add(1, 'day').day() === 1) selectedWeek++;
                if (this.timeFrameStart.clone().add(1, 'day').year() !== this.currentYear) {
                    selectedWeek = 1;
                }
                return this.timeFrameStart.clone().add(1, 'day').format('dddd DD.MMMM');
            }
        },
        yearNextDay() {
            if (this.templateDaySelection === 'W') {
                let year = this.currentYear;
                if (this.currentSelectedWeek === this.timeFrameStart.isoWeeksInYear()) {
                    year++;
                }
                return year;
            } else {
                if (this.timeFrameStart.clone().add(1, 'day').year() !== this.currentYear) {
                    return this.currentYear + 1;
                } else {
                    return this.currentYear;
                }
            }
        },
        weekStartTimes() {
            //precompute the dates of current selected Week for each weekday
            let weekStartTimes = [];
            for (let i = 0; i < 7; i++) {
                weekStartTimes.push(this.currentWeekStart.clone().add(i, 'days'));
            }
            return weekStartTimes;
        },

        templateCreationEventDropDownItems() {
            let items = [ { value: null, label: this.$t('shift.template.noEvent') } ];
            Object.entries(this.storeEventMap).map(entry => entry[1]).filter(entry => entry.allowTemplateAssignment && entry.toMoment > this.currentWeekStart && entry.fromMoment < this.currentWeekEnd).forEach(entry => {
                let item = {
                    value: entry.id,
                    label: entry.name + ' ' + entry.fromMoment.format('DD.MM.YYYY') + ' - ' + entry.toMoment.format('DD.MM.YYYY')
                }
                items.push(item);
            })
            return items;

        },
        projectDropDownItems() {
            let items = [ { value: null, label: this.$t('shift.template.noProject') } ];
            let view = this;
            Object.values(this.projectMap).forEach(entry => {
                let item = {
                    value: entry.id,
                    label: entry.name + ' ' + view.$helpers.getMomentFromStringTZ(entry.from).format('DD.MM.YYYY') + ' - ' + view.$helpers.getMomentFromStringTZ(entry.to).format('DD.MM.YYYY')
                }
                items.push(item);
            })
            return items;

        },
        locationDropDownItems() {
            let items = [ { value: null, label: this.$t('shift.template.ownStore') } ];
            let view = this;
            Object.values(this.locationMap).forEach(entry => {
                let item = {
                    value: entry.id,
                    label: entry.name
                }
                items.push(item);
            })
            return items;

        },
        costCenterDropDownItems() {
            let items = [ { value: null, label: this.$t('shift.template.ownStore') } ];
            let view = this;
            Object.values(this.mlCostCenterMap).forEach(entry => {
                let item = {
                    value: entry.id,
                    label: entry.name
                }
                if(entry.name != this.storeSettings.name){
                    items.push(item);
                }
                
            })
            return items;

        },

        viewFilterDropDownState() {
            let items = [];
            items.push({ id: 'full-overlap', label: this.$t('shift.template.overlappingView') });
            items.push({ id: 'full-split', label: this.$t('shift.template.splitView') });
            items.push({ id: 'standard', label: this.$t('shift.template.onlyDefault') })
            items.push({ id: 'project', label: this.$t('shift.template.onlyProjects') })
            Object.keys(this.eventDisplayMap).forEach(key => {
                let item = {
                    id: key,
                    label: this.eventDisplayMap[key].name,
                    color: this.eventDisplayMap[key].timeFrame.color
                };
                items.push(item);
            });
            let state = {
                toggled: false,
                displayedItems: [],
                input: '',
                items: items,
                selectMultiple: false,
                selectedItems: [],
                selectFirst: true,
                styleLight: true,
                boldInput: true,
            }
            return state;
        },

        templateGroupDropDownState() {
            let items = [];
            Object.values(this.shiftTemplateGroupMap).sort((a, b) => {
                if (a.immutable == true) {
                    return -1;
                } else {
                    return a.name.localeCompare(b.name);
                }
            }).forEach(group => {
                //this.shiftTemplateGroupFrameMapitems.push({
                items.push({
                    label: group.name,
                    value: group,
                    selected: this.selectedGroup != null ? this.selectedGroup.id == group.id : false
                })
            });
            let selectedItem = items.find(i => i.selected == true);

            let state = {
                toggled: false,
                displayedItems: [],
                input: selectedItem != null ? selectedItem.label : '',
                selectedItem: selectedItem,
                items: items,
                selectFirst: false,
                styleLight: true,
                boldInput: true,
                onceSelected: ($event, item) => {
                    // eslint-disable-next-line vue/no-side-effects-in-computed-properties
                    this.selectTemplateGroup(item.value.id);
                },
                noBorder: true
            }
            return state;
        },
        spots() {
            return Object.values(this.spotMap);
        },
        skills() {
            return Object.values(this.skillMap);
        },
        employeeEmployeeSkillMap() {
            let map = {};
            Object.values(this.employeeSkillMap).forEach(eSkill => {
                if (!map[eSkill.employeeId]) {
                    map[eSkill.employeeId] = [];
                }
                map[eSkill.employeeId].push(eSkill.skillId);
            });
            return map;
        },
        templateValidMomentMap() {
            let map = {};
            let view = this;
            const templates = [
                ...this.shiftTemplates,
                ...Object.values(this.addedTemplateMap),
                ...Object.values(this.newEventTemplateMap),
            ];
            templates.forEach(template => {
                if (view.changedTemplateMap[template.id]) {
                    template = view.changedTemplateMap[template.id];
                }
                let from = template.validFrom ? view.$helpers.getMomentFromStringTZ(template.validFrom) : null;
                let to = template.validTo ? view.$helpers.getMomentFromStringTZ(template.validTo) : null;
                map[template.id] = { from: from, to: to };
            });
            return map;
        },
        weekStartDateItems() {
            let entry = { past: [], future: [] }
            let currentWeekIterator = this.currentWeekStart.clone();
            let currentIsoYear = currentWeekIterator.isoWeekYear();
            while (currentWeekIterator.isoWeekYear() == currentIsoYear) {
                currentWeekIterator = currentWeekIterator.add(1, 'weeks').startOf('week');
                let item = {
                    value: currentWeekIterator.clone(),
                    label: currentWeekIterator.format('DD.MM.YYYY') + ' KW ' + currentWeekIterator.isoWeek()
                }
                entry.future.push(item);
            }
            let item = {
                value: null,
                label: this.$t('shift.template.unlimited')
            }
            entry.future.push(item);
            currentWeekIterator = this.currentWeekStart.clone();
            item = {
                value: currentWeekIterator.clone(),
                label: currentWeekIterator.format('DD.MM.YYYY') + ' KW ' + currentWeekIterator.isoWeek()
            }
            entry.past.push(item);
            while (currentWeekIterator.isoWeekYear() == currentIsoYear) {
                currentWeekIterator = currentWeekIterator.add(-1, 'weeks').startOf('week');
                let item = {
                    value: currentWeekIterator.clone(),
                    label: currentWeekIterator.format('DD.MM.YYYY') + ' KW ' + currentWeekIterator.isoWeek()
                }
                entry.past.push(item);
            }
            item = {
                value: null,
                label: this.$t('shift.template.unlimited')
            }
            entry.past.push(item);
            return entry;

        },

        // TODO: Refactor these two methods
        eventDisplayMap() {
            let eventDisplayMap = {};
            let view = this;
            Object.keys(this.storeEventMap).map(key => this.storeEventMap[key]).filter(event => event.fromMoment < this.timeFrameEnd && event.toMoment > this.timeFrameStart)
                .forEach(event => {
                    let item = {
                        id: event.id,
                        name: event.name,
                        description: event.description,
                        shiftCreationMode: event.shiftCreationMode,
                        timeFrame: {
                            eventId: event.id,
                            fromDate: event.fromMoment,
                            toDate: event.toMoment,
                            color: event.color,
                        }
                    }
                    eventDisplayMap[item.id] = item;
                });
            return eventDisplayMap;

        },
        templateDisplayMap() {

            //this map depends in the weekStartTimes which change when currentWeekStart changes and on the selectedEventId
            let templateDisplayMap = {}

            let view = this;
            let templates = [];


            //create the templatedisplays and for each template group create array of weekdayindexes where event timeframes actually exist / are allowed to exist
            let templateGroupsEventMarker = {};

            let noTimeFrameAllowedEvents = Object.keys(this.eventDisplayMap).filter(key => this.eventDisplayMap[key].shiftCreationMode == 2).map(key => this.eventDisplayMap[key])

            let eventTemplates = [];

            const shiftTemplates = Object.values({
                ...this.templateMap,
                ...this.changedTemplateMap,
                ...this.addedTemplateMap,
            });

            shiftTemplates.filter(s => this.selectedViewFilter.id != 'standard' && this.selectedViewFilter.id != 'project' && ( ( ( this.selectedViewFilter.id == 'full-overlap' || this.selectedViewFilter.id == 'full-split' ) && s.eventId != null ) || ( s.eventId == this.selectedViewFilter.id ) ) && this.eventDisplayMap[s.eventId]).forEach(shiftTemplate => {
                let compoundId = shiftTemplate.groupId + '-' + shiftTemplate.spotId;
                if (!templateDisplayMap[compoundId]) {
                    templateDisplayMap[compoundId] = [];
                }
                let event = this.eventDisplayMap[shiftTemplate.eventId];

                let day = this.currentWeekStart;
                let fromDate = this.$helpers.getMomentFromStringUTC(shiftTemplate.from, 'HH:mm');
                fromDate = day.clone().hours(fromDate.hours()).minutes(fromDate.minutes());
                let toDate = this.$helpers.getMomentFromStringUTC(shiftTemplate.to, 'HH:mm');
                toDate = day.clone().hours(toDate.hours()).minutes(toDate.minutes());

                if (toDate <= fromDate) {
                    toDate = toDate.add(24, 'hours');
                }
                let hourValue = ( ( toDate.diff(fromDate, 'minutes') - shiftTemplate.breakValue ) / 60 ).toFixed(2);

                let shiftTemplateTemp = {};
                shiftTemplateTemp.id = shiftTemplate.id;
                shiftTemplateTemp.from = shiftTemplate.from;
                shiftTemplateTemp.timebarFrom = shiftTemplate.from;
                shiftTemplateTemp.to = shiftTemplate.to;
                shiftTemplateTemp.breakFrom = shiftTemplate.breakFrom;
                shiftTemplateTemp.breakValue = shiftTemplate.breakValue;
                shiftTemplateTemp.groupId = shiftTemplate.groupId;
                shiftTemplateTemp.spotId = shiftTemplate.spotId;
                shiftTemplateTemp.projectId = shiftTemplate.projectId;
                shiftTemplateTemp.fromLocationId = shiftTemplate.fromLocationId;
                shiftTemplateTemp.toLocationId = shiftTemplate.toLocationId;
                shiftTemplateTemp.weekDays = shiftTemplate.weekDays;
                shiftTemplateTemp.eventId = shiftTemplate.eventId;
                shiftTemplateTemp.replacing = event.shiftCreationMode == 0;
                shiftTemplateTemp.merged = false;
                shiftTemplateTemp.hourValue = hourValue;
                shiftTemplateTemp.name = ( shiftTemplate.count > 1 ? ( shiftTemplate.count + 'x ' ) : '' ) + hourValue + ' h' + ( shiftTemplate.projectId != null ? ' (P)' : '' ) + ( shiftTemplate.fromLocationId != null ? ' (E)' : '' );
                shiftTemplateTemp.validLabel = view.getTemplateValidLabel(shiftTemplate.id);
                shiftTemplateTemp.picture = this.getAvatarForTemplate(shiftTemplate);
                shiftTemplateTemp.timeFrames = [];
                shiftTemplateTemp.timeFrameMap = {};
                shiftTemplateTemp.allowedTimeFrames = [];


                shiftTemplate.weekDays.forEach(w => {
                    let day = this.weekStartTimes[w - 1];
                    let fromDate = this.$helpers.getMomentFromStringUTC(shiftTemplate.from, 'HH:mm');
                    fromDate = day.clone().hours(fromDate.hours()).minutes(fromDate.minutes());
                    let toDate = this.$helpers.getMomentFromStringUTC(shiftTemplate.to, 'HH:mm');
                    toDate = day.clone().hours(toDate.hours()).minutes(toDate.minutes());

                    let dayStartDate = fromDate.clone().startOf('day');
                    let dayEndDate = dayStartDate.clone().add(1, 'days');

                    if (toDate.diff(fromDate) <= 0) {
                        toDate.add(24, 'hours')
                    }
                    let isValidFrom = shiftTemplate.validFrom == null || this.$helpers.getMomentFromStringTZ(shiftTemplate.validFrom) <= fromDate;
                    let isValidTo = shiftTemplate.validTo == null || fromDate < this.$helpers.getMomentFromStringTZ(shiftTemplate.validTo);

                    let timeFrameAllowed = noTimeFrameAllowedEvents.filter(e => e.timeFrame.fromDate < toDate && e.timeFrame.toDate > fromDate && fromDate >= e.timeFrame.fromDate).length == 0;
                    let timeFrame = {
                        templateId: shiftTemplate.id,
                        fromDate: fromDate,
                        toDate: toDate,
                        weekDay: w,
                        background: event.timeFrame.color,
                        eventframe: true,
                        isFake: false,
                        tooltip: this.getShiftTemplateTooltip(shiftTemplate.id, null)
                    };
                    if (shiftTemplate.breakFrom != null && shiftTemplate.breakValue != -1) {
                        let breakFrom = this.$helpers.getMomentFromStringTZ(shiftTemplate.breakFrom, 'HH:mmZ');
                        breakFrom = breakFrom.date(fromDate.date()).month(fromDate.month()).year(fromDate.year());
                        if (breakFrom < fromDate) {
                            breakFrom = breakFrom.add(1, 'days');
                        }
                        let breakTo = breakFrom.clone().add(shiftTemplate.breakValue, 'minutes');

                        timeFrame.breakFrom = breakFrom;
                        timeFrame.breakTo = breakTo;
                    }

                    if (shiftTemplateTemp.eventId != null && timeFrameAllowed && isValidFrom && isValidTo) {
                        // crate event time frame regardless of weekday actually overlapping with event

                        shiftTemplateTemp.timeFrames.push(timeFrame);
                        shiftTemplateTemp.timeFrameMap[w] = timeFrame;

                        if (shiftTemplateTemp.replacing && event.timeFrame.toDate > fromDate && event.timeFrame.fromDate < toDate && fromDate >= event.timeFrame.fromDate) {//event.timeFrame.fromDate < dayEndDate && event.timeFrame.toDate > dayStartDate && event.timeFrame.fromDate >= dayStartDate){
                            if (templateGroupsEventMarker[shiftTemplateTemp.groupId] == null) {
                                templateGroupsEventMarker[shiftTemplateTemp.groupId] = [];
                            }
                            //add weekday index only if it doenst exist already
                            let index = templateGroupsEventMarker[shiftTemplateTemp.groupId].findIndex(x => x == w)
                            if (index === -1) {
                                templateGroupsEventMarker[shiftTemplateTemp.groupId].push(w);
                            }
                        }

                    }
                    if (shiftTemplateTemp.eventId != null && timeFrameAllowed) {
                        shiftTemplateTemp.allowedTimeFrames.push(timeFrame);
                    }

                });
                if (shiftTemplateTemp.allowedTimeFrames.filter(t => ( t.fromDate < this.timeFrameEnd && t.toDate > this.timeFrameStart ) && !t.isFake).length > 0) {

                    templateDisplayMap[compoundId].push(shiftTemplateTemp);
                    templateDisplayMap[shiftTemplateTemp.id] = shiftTemplateTemp;
                    eventTemplates.push(shiftTemplateTemp);
                }


            });

            //then work through all non event templates but only create timeframes on weekdays that weren't marked by EVENT by same group
            let standardTemplates = [];
            let weekDays = [ 1, 2, 3, 4, 5, 6, 7 ];
            shiftTemplates.filter(s => ( ( this.selectedViewFilter.id == 'full-overlap' || this.selectedViewFilter.id == 'full-split' || this.selectedViewFilter.id == 'standard' ) && s.eventId == null ) || ( this.selectedViewFilter.id == 'project' && s.projectId != null )).forEach(shiftTemplate => {
                let compoundId = shiftTemplate.groupId + '-' + shiftTemplate.spotId;
                if (!templateDisplayMap[compoundId]) {
                    templateDisplayMap[compoundId] = [];
                }

                let day = this.currentWeekStart;
                let fromDate = this.$helpers.getMomentFromStringUTC(shiftTemplate.from, 'HH:mm');
                fromDate = day.clone().hours(fromDate.hours()).minutes(fromDate.minutes());
                let toDate = this.$helpers.getMomentFromStringUTC(shiftTemplate.to, 'HH:mm');
                toDate = day.clone().hours(toDate.hours()).minutes(toDate.minutes());


                if (toDate <= fromDate) {
                    toDate = toDate.add(24, 'hours');

                }

                let hourValue = ( ( toDate.diff(fromDate, 'minutes') - shiftTemplate.breakValue ) / 60 ).toFixed(2);

                let shiftTemplateTemp = {};
                shiftTemplateTemp.id = shiftTemplate.id;
                shiftTemplateTemp.from = shiftTemplate.from;
                shiftTemplateTemp.timebarFrom = shiftTemplate.from;
                shiftTemplateTemp.to = shiftTemplate.to;
                shiftTemplateTemp.breakFrom = shiftTemplate.breakFrom;
                shiftTemplateTemp.breakValue = shiftTemplate.breakValue;
                shiftTemplateTemp.groupId = shiftTemplate.groupId;
                shiftTemplateTemp.spotId = shiftTemplate.spotId;
                shiftTemplateTemp.projectId = shiftTemplate.projectId;
                shiftTemplateTemp.fromLocationId = shiftTemplate.fromLocationId;
                shiftTemplateTemp.toLocationId = shiftTemplate.toLocationId;
                shiftTemplateTemp.weekDays = shiftTemplate.weekDays;
                shiftTemplateTemp.eventId = shiftTemplate.eventId;
                shiftTemplateTemp.hourValue = hourValue;
                shiftTemplateTemp.name = ( shiftTemplate.count > 1 ? ( shiftTemplate.count + 'x ' ) : '' ) + hourValue + ' h' + ( shiftTemplate.projectId != null ? ' (P)' : '' ) + ( shiftTemplate.fromLocationId != null ? ' (E)' : '' );
                shiftTemplateTemp.validLabel = view.getTemplateValidLabel(shiftTemplate.id);
                shiftTemplateTemp.picture = this.getAvatarForTemplate(shiftTemplate);
                shiftTemplateTemp.timeFrames = [];
                shiftTemplateTemp.timeFrameMap = {};
                shiftTemplateTemp.allowedTimeFrames = [];
                if (shiftTemplate.projectId && view.projectMap[shiftTemplate.projectId]) {
                    shiftTemplateTemp.color = view.projectMap[shiftTemplate.projectId].color;
                }

                weekDays.filter(w => templateGroupsEventMarker[shiftTemplateTemp.groupId] == null || !templateGroupsEventMarker[shiftTemplateTemp.groupId].includes(w) || this.selectedViewFilter.id == 'full-split').forEach(w => {
                    let fake = !shiftTemplate.weekDays.includes(w);
                    let day = this.weekStartTimes[w - 1];

                    let fromDate = this.$helpers.getMomentFromStringUTC(shiftTemplate.from, 'HH:mm');
                    fromDate = day.clone().hours(fromDate.hours()).minutes(fromDate.minutes());
                    let toDate = this.$helpers.getMomentFromStringUTC(shiftTemplate.to, 'HH:mm');
                    toDate = day.clone().hours(toDate.hours()).minutes(toDate.minutes());

                    if (toDate.diff(fromDate) <= 0) {
                        toDate.add(24, 'hours')
                    }
                    let isValidFrom = shiftTemplate.validFrom == null || this.$helpers.getMomentFromStringTZ(shiftTemplate.validFrom) <= fromDate;
                    let isValidTo = shiftTemplate.validTo == null || fromDate < this.$helpers.getMomentFromStringTZ(shiftTemplate.validTo);

                    let timeFrameAllowed = noTimeFrameAllowedEvents.filter(e => e.timeFrame.fromDate < toDate && e.timeFrame.toDate > fromDate && fromDate >= e.timeFrame.fromDate).length == 0;
                    let timeFrame = {
                        templateId: shiftTemplate.id,
                        fromDate: fromDate,
                        toDate: toDate,
                        weekDay: w,
                        eventframe: false,
                        isFake: fake,
                        tooltip: false
                    };
                    if (shiftTemplate.breakFrom != null && shiftTemplate.breakValue != -1 && !shiftTemplate.autoBreak) {
                        let breakFrom = this.$helpers.getMomentFromStringTZ(shiftTemplate.breakFrom, 'HH:mmZ');
                        breakFrom = breakFrom.date(fromDate.date()).month(fromDate.month()).year(fromDate.year());
                        if (breakFrom < fromDate) {
                            breakFrom = breakFrom.add(1, 'days');
                        }
                        let breakTo = breakFrom.clone().add(shiftTemplate.breakValue, 'minutes');

                        timeFrame.breakFrom = breakFrom;
                        timeFrame.breakTo = breakTo;
                    }
                    if (shiftTemplate.projectId && view.projectMap[shiftTemplate.projectId]) {
                        timeFrame.color = view.projectMap[shiftTemplate.projectId].color;
                    }
                    // Sets the background color to orange/green/gray, if actual/planned shifts exists for the template on that day
                    if (view.displayColoredBars) {
                        let frameInfo = this.GetTemplateTimeFrameShiftInfo(shiftTemplateTemp, w - 1);
                        timeFrame.background = frameInfo.background;
                        timeFrame.tooltip = this.getShiftTemplateTooltip(shiftTemplate.id, frameInfo.employeeName);
                    } else {
                        timeFrame.tooltip = this.getShiftTemplateTooltip(shiftTemplate.id, null)
                    }


                    if (timeFrameAllowed && isValidFrom && isValidTo) {
                        shiftTemplateTemp.timeFrames.push(timeFrame);
                        shiftTemplateTemp.timeFrameMap[w] = timeFrame;
                    }
                    if (timeFrameAllowed) {
                        shiftTemplateTemp.allowedTimeFrames.push(timeFrame);
                    }

                });

                if (shiftTemplateTemp.allowedTimeFrames.filter(t => ( t.fromDate < this.timeFrameEnd && t.toDate > this.timeFrameStart ) && !t.isFake).length > 0) {
                    templateDisplayMap[compoundId].push(shiftTemplateTemp);
                    templateDisplayMap[shiftTemplateTemp.id] = shiftTemplateTemp;
                    eventTemplates.push(shiftTemplateTemp);
                }


            });

            if (this.selectedViewFilter.id == 'full-overlap') {
                standardTemplates.sort((a, b) => {
                    let timeA = this.$helpers.getMomentFromStringTZ(a.from, 'HH:mmZ');
                    let timeB = this.$helpers.getMomentFromStringTZ(b.from, 'HH:mmZ');
                    return timeA.unix() - timeB.unix();
                });
                eventTemplates.sort((a, b) => {
                    let timeA = this.$helpers.getMomentFromStringTZ(a.from, 'HH:mmZ');
                    let timeB = this.$helpers.getMomentFromStringTZ(b.from, 'HH:mmZ');
                    return timeA.unix() - timeB.unix();
                });
                standardTemplates.forEach(template => {
                    //find a template that replaces standard shifts and hasnt been merged yet and is in same group
                    let mergeTemplate = eventTemplates.find(eventtemplate => eventtemplate.replacing &&
                        !eventtemplate.merged &&
                        eventtemplate.groupId == template.groupId &&
                        eventtemplate.timeFrames.filter(tf => {
                            let overlapping = false;
                            template.timeFrames.forEach(tf2 => {
                                if (tf.fromDate < tf2.toDate && tf.toDate > tf2.fromDate) {
                                    overlapping = true;
                                }
                            });
                            return overlapping;
                        }).length == 0);
                    if (mergeTemplate) {
                        mergeTemplate.merged = true;
                        template.timeFrames.forEach(tf => {
                            mergeTemplate.timeFrames.push(tf);
                        });
                        if (this.$helpers.getMomentFromStringTZ(template.from, 'HH:mmZ') < this.$helpers.getMomentFromStringTZ(mergeTemplate.from, 'HH:mmZ')) {
                            mergeTemplate.timebarFrom = template.timebarFrom;
                        }
                        //after merging timeframes, delete the standard event from the map
                        let compoundId = template.groupId + '-' + template.spotId;
                        delete templateDisplayMap[template.id];
                        const index = templateDisplayMap[compoundId].findIndex(temp => temp.id === template.id)
                        if (~index) {
                            templateDisplayMap[compoundId].splice(index, 1);
                        }
                    }
                })
            }

            return templateDisplayMap;

        },

        templateMap() {
            const templateMap = {};
            for (const template of this.shiftTemplates) {
                templateMap[template.id] = template;
            }
            return templateMap;
        },

        templatesByEventId() {
            const templatesByEvent = {};
            const shiftTemplates = Object.values({
                ...this.shiftTemplates,
                ...this.changedTemplateMap,
                ...this.addedTemplateMap,
                ...this.newEventTemplateMap,
            });
            for (const template of shiftTemplates) {
                if (!templatesByEvent[template.eventId]) {
                    templatesByEvent[template.eventId] = [];
                }
                templatesByEvent[template.eventId].push(template);
            }
            return templatesByEvent;
        },

        userUnitSortMapByUnitId() {
            let map = {};
            Object.values(this.userUnitSortMap).forEach(sort => {
                map[sort.unitId] = sort;
            })
            return map;
        },

        // TODO: Refactor this?
        templateDisplayGroups() {
            let view = this;
            let templateDisplayGroups = [];
            const userUnitSortMap = this.userUnitSortMapByUnitId;
            let group = view.selectedGroup;

            if (group != null) {
                //first only add main groups, already sorted by spot

                Object.values(view.businessUnitMap).forEach(bunit => {
                    view.spots.forEach(spot => {
                        if (spot.unitId == bunit.id && spot.isMain) {

                            let header = spot.name;

                            let displaygroup = {
                                id: group.id,
                                header: header,
                                collapsed: this.groupCollapsedStates[spot.id],
                                spot: spot,
                                draggable: false,
                                isMain: spot.isMain
                            };
                            displaygroup.templateGroup = group;
                            if (this.spotVisibilityList.find(s => s.id === displaygroup.spot.id)) {

                                templateDisplayGroups.push(displaygroup);
                            }

                        }
                    })
                });


                //SORT MAIN GROUPS BY PRIORITY
                templateDisplayGroups.sort((a, b) => {
                    let userunitsortA = userUnitSortMap[a.spot.id];
                    let userunitsortB = userUnitSortMap[b.spot.id];


                    let prioA = userunitsortA != null ? userunitsortA.priority : a.spot.priority;
                    let prioB = userunitsortB != null ? userunitsortB.priority : b.spot.priority;
                    if (prioA == null) return 1;
                    if (prioB == null) return -1;
                    if (prioA != prioB) return prioA - prioB;

                    return a.header.toUpperCase().localeCompare(b.header.toUpperCase());
                });
                //CREATE SUB GROUPS
                let subGroups = [];

                Object.values(view.businessUnitMap).forEach(bunit => {
                    view.spots.forEach(spot => {
                        if (spot.unitId == bunit.id && !spot.isMain) {

                            let header = spot.name;
                            if (spot.isMain) {
                                header = bunit.name + ': ' + spot.name;
                            }
                            let displaygroup = {
                                id: group.id,
                                header: header,
                                collapsed: this.groupCollapsedStates[spot.id],
                                spot: spot,
                                draggable: false,
                                isMain: spot.isMain
                            };

                            displaygroup.templateGroup = group;
                            if (this.spotVisibilityList.find(s => s.id === displaygroup.spot.id)) {
                                subGroups.push(displaygroup);
                            }

                        }
                    })
                });


                //SORT SUB GROUPS BY PRIORITY
                subGroups.sort((a, b) => {
                    let userunitsortA = userUnitSortMap[a.spot.id];
                    let userunitsortB = userUnitSortMap[b.spot.id];
                    let prioA = userunitsortA != null ? userunitsortA.priority : a.spot.priority;
                    let prioB = userunitsortB != null ? userunitsortB.priority : b.spot.priority;

                    if (prioA == null) return 1;
                    if (prioB == null) return -1;
                    if (prioA != prioB) return prioA - prioB;

                    return a.header.toUpperCase().localeCompare(b.header.toUpperCase());
                });
                //SPLICE BY PARENT
                if (subGroups.length > 0) {
                    subGroups = subGroups.reverse();
                    subGroups.forEach(g => {
                        let parent = templateDisplayGroups.find(gm => gm.spot.isMain && gm.spot.unitId === g.spot.unitId && gm.id === g.id);
                        let parentIndex = templateDisplayGroups.indexOf(parent) + 1;
                        templateDisplayGroups.splice(parentIndex, 0, g);
                    });
                }
            }

            templateDisplayGroups = [
                {
                    id: 'events',
                    header: this.$t('shift.template.events'),
                    collapsed: this.groupCollapsedStates['events'],
                    spot: null,
                    draggable: false,
                    isMain: true
                },
                ...templateDisplayGroups,
            ];

            return templateDisplayGroups;
        },
        timeFrameLength() {
            return this.displayedTimeframeSelected.length;
        },
        planningEmployeeMap() {
            let map = {};

            let start = this.currentWeekStart.toDate();
            let end = addDays(this.currentWeekEnd.toDate(), 1);
            let timeFrameBySpotMap = {};
            this.templateDisplayGroups.forEach(g => {
                if (g.id === 'events') return;
                let compoundId = g.id + '-' + g.spot.id;
                if (!timeFrameBySpotMap[g.spot.id]) {
                    timeFrameBySpotMap[g.spot.id] = [];
                }
                if (this.templateDisplayMap[compoundId]) {
                    this.templateDisplayMap[compoundId].forEach(item => {
                        item.timeFrames.forEach(frame => {
                            if (!frame.isFake) {
                                timeFrameBySpotMap[g.spot.id].push(frame);
                            }
                        })
                    })
                }
            })

            while (start < end) {
                let dayKey = dateToYYYYMMDD(start);

                let dayEntry = {
                    hourMap: {},
                    spotPresent: {},
                    spotCount: {},
                }
                let startDate = start;
                let endDate = addDays(startDate, 1);
                Object.values(this.spotMap).forEach(s => {
                    dayEntry.spotPresent[s.id] = 0;
                    dayEntry.spotCount[s.id] = 0;
                    if (timeFrameBySpotMap[s.id]) {
                        let minutes = 0;
                        let frames = timeFrameBySpotMap[s.id].filter(tf => tf.fromDate < endDate && tf.toDate > startDate)
                        frames.forEach(frame => {
                            let from = startDate > frame.fromDate ? startDate : frame.fromDate;
                            let to = endDate < frame.toDate ? endDate : frame.toDate;
                            let wv = differenceInMinutes(to, from);
                            if (frame.breakFrom != null && frame.breakFrom < endDate && frame.breakTo > startDate) {
                                let bFrom = startDate > frame.breakFrom ? startDate : frame.breakFrom;
                                let bTo = endDate < frame.breakTo ? endDate : frame.breakTo;
                                wv = Math.max(0, wv - differenceInMinutes(bTo, bFrom));
                            }

                            minutes += wv;
                        });
                        dayEntry.spotPresent[s.id] = minutes;
                        dayEntry.spotCount[s.id] = frames.length;
                    }
                });
                map[dayKey] = dayEntry;

                let hourStart = start;
                let hourEnd = addHours(hourStart, 24);

                while (hourStart < hourEnd) {
                    let hourKey = format(hourStart, 'HH');
                    let hourEntry = {
                        quarterMap: {},
                        spotPresent: {},
                        spotCount: {},
                    }
                    let startDate = hourStart;
                    let endDate = addHours(startDate, 1);
                    Object.values(this.spotMap).forEach(s => {
                        hourEntry.spotPresent[s.id] = 0;
                        hourEntry.spotCount[s.id] = 0;
                        if (timeFrameBySpotMap[s.id]) {
                            let minutes = 0;
                            let frames = timeFrameBySpotMap[s.id].filter(tf => tf.fromDate < endDate && tf.toDate > startDate);
                            frames.forEach(frame => {
                                let from = startDate > frame.fromDate ? startDate : frame.fromDate.toDate();
                                let to = endDate < frame.toDate ? endDate : frame.toDate.toDate();
                                let wv = differenceInMinutes(to, from);
                                if (frame.breakFrom != null && frame.breakFrom < endDate && frame.breakTo > startDate) {
                                    let bFrom = startDate > frame.breakFrom ? startDate : frame.breakFrom.toDate();
                                    let bTo = endDate < frame.breakTo ? endDate : frame.breakTo.toDate();
                                    wv = Math.max(0, wv - differenceInMinutes(bTo, bFrom));
                                }

                                minutes += wv;
                            });
                            hourEntry.spotPresent[s.id] = minutes;
                            hourEntry.spotCount[s.id] = frames.length;
                        }
                    });

                    dayEntry.hourMap[hourKey] = hourEntry;

                    let quarterStart = hourStart;
                    let quarterEnd = addHours(quarterStart, 1);
                    while (quarterStart < quarterEnd) {
                        let quarterKey = format(quarterStart, 'mm');
                        let quarterEntry = {
                            spotPresent: {},
                            spotCount: {},
                        }
                        let startDate = quarterStart;
                        let endDate = addMinutes(startDate, 15);
                        Object.values(this.spotMap).forEach(s => {
                            quarterEntry.spotPresent[s.id] = 0;
                            quarterEntry.spotCount[s.id] = 0;
                            if (timeFrameBySpotMap[s.id]) {
                                let minutes = 0;
                                let frames = timeFrameBySpotMap[s.id].filter(tf => tf.fromDate < endDate && tf.toDate > startDate)
                                frames.forEach(frame => {
                                    let from = startDate > frame.fromDate ? startDate : frame.fromDate.toDate();
                                    let to = endDate < frame.toDate ? endDate : frame.toDate.toDate();
                                    let wv = differenceInMinutes(to, from);
                                    if (frame.breakFrom != null && frame.breakFrom < endDate && frame.breakTo > startDate) {
                                        let bFrom = startDate > frame.breakFrom ? startDate : frame.breakFrom.toDate();
                                        let bTo = endDate < frame.breakTo ? endDate : frame.breakTo.toDate();
                                        // console.log(bTo, bFrom, wv);
                                        wv = Math.max(0, wv - differenceInMinutes(bTo, bFrom));
                                    }

                                    minutes += wv;
                                });
                                quarterEntry.spotPresent[s.id] = minutes;
                                quarterEntry.spotCount[s.id] = frames.length;
                            }
                        });

                        hourEntry.quarterMap[quarterKey] = quarterEntry;
                        quarterStart = addMinutes(quarterStart, 15);
                    }
                    hourStart = addHours(hourStart, 1);
                }
                start = addDays(start, 1);
                // console.log(start);
            }
            return map;
        },
        planningEmployeeThresholdSpotMap() {
            let map = {}
            Object.values(this.planningEmployeeThresholdMap).forEach(t => {
                if (t.typeId == this.displayIndicatorType) {
                    if (!map[t.spotId]) {
                        map[t.spotId] = [];
                    }
                    map[t.spotId].push(t);
                }
            })
            return map;

        },
        planningValuesMap() {
            // TODO: Replace moment with date
            let map = {};
            let hourly = this.templateDaySelection !== 'W';
            const planningEmployeeMap = this.planningEmployeeMap;
            const planningIndicatorDailyMap = this.planningIndicatorDailyMap;
            const divisor = this.planningIndicatorTypeMap[this.displayIndicatorType] ? Math.pow(10, this.planningIndicatorTypeMap[this.displayIndicatorType].fractional) : 1;
            const vlhRequirementMap = this.vlhRequirementMap;
            const useVlhForForecast = this.useVlhForForecast;
            const planningType = this.planningType;

            let weekEnd = this.currentWeekEnd.clone().add(1, 'days');
            Object.values(this.spotMap).forEach(spot => {
                map[spot.id] = {};
                for (let x = 0; x < this.dayModeHours; x++) {
                    let entry = {
                        present: 0,
                        presentMinutes: 0,
                        required: 0,
                        hasReq: false,
                        requiredMinutes: 0,
                        color: 'black',
                        avg: 0,
                        quarterEntries: [],
                    }
                    if (hourly) {
                        let format = this.timeFrameStart.clone().add(x, 'hour');
                        if (format < weekEnd) {
                            let dayKey = format.format('YYYY-MM-DD');
                            let hourKey = format.format('HH');
                            if (!planningEmployeeMap[dayKey] || !planningIndicatorDailyMap[dayKey]) continue;
                            let qEs = Object.entries(planningEmployeeMap[dayKey].hourMap[hourKey].quarterMap);
                            qEs.forEach(e => {
                                let key = e[0];
                                let value = e[1];

                                let requiredMinutes = 0;
                                let hasReq = false;
                                if(useVlhForForecast){
                                    requiredMinutes = vlhRequirementMap[dayKey] ? vlhRequirementMap[dayKey].hourMap[hourKey].quarterMap[key].spotRequirements[spot.id].req : 0;
                                    hasReq = vlhRequirementMap[dayKey] ? vlhRequirementMap[dayKey].hourMap[hourKey].quarterMap[key].spotRequirements[spot.id].hasReq : false
                                } else {
                                    requiredMinutes = planningIndicatorDailyMap[dayKey] ? planningIndicatorDailyMap[dayKey].hourMap[hourKey].quarterMap[key].spotRequirements[spot.id].req * 15 : 0;
                                    hasReq =  planningIndicatorDailyMap[dayKey] ? planningIndicatorDailyMap[dayKey].hourMap[hourKey].quarterMap[key].spotRequirements[spot.id].hasReq : false
                                }

                                let presentMinutes = planningEmployeeMap[dayKey].hourMap[hourKey].quarterMap[key].spotPresent[spot.id];


                                let qE = {
                                    id: key,

                                    present: ( presentMinutes / 15 ).toFixed(2),
                                    required: ( requiredMinutes / 15 ).toFixed(2),
                                    hasReq: hasReq,
                                }

                                if(this.countSpotsWithoutThreshold || qE.hasReq){
                                    let color = presentMinutes < requiredMinutes ? 'red' : presentMinutes > requiredMinutes ? 'red' : '#0b0';
                                    qE.color = color;

                                } else {

                                    qE.required = '-'
                                }
                                entry.quarterEntries.push(qE);
                            })
                            entry.presentMinutes = planningEmployeeMap[dayKey].hourMap[hourKey].spotPresent[spot.id];
                            entry.present = ( entry.presentMinutes / 60 ).toFixed(2);

                            let requiredMinutes = 0;
                            let hasReq = false;
                            if(useVlhForForecast){
                                requiredMinutes = vlhRequirementMap[dayKey] ? vlhRequirementMap[dayKey].hourMap[hourKey].spotRequirements[spot.id].req : 0;
                                hasReq = vlhRequirementMap[dayKey] ? vlhRequirementMap[dayKey].hourMap[hourKey].spotRequirements[spot.id].hasReq : false
                            } else {
                                requiredMinutes = planningIndicatorDailyMap[dayKey] ? planningIndicatorDailyMap[dayKey].hourMap[hourKey].spotRequirements[spot.id].req * 60 : 0;
                                hasReq =  planningIndicatorDailyMap[dayKey] ? planningIndicatorDailyMap[dayKey].hourMap[hourKey].spotRequirements[spot.id].hasReq : false
                            }
                            entry.requiredMinutes = requiredMinutes;
                            entry.hasReq = hasReq;

                            entry.required = ( entry.requiredMinutes / 60 ).toFixed(2);
                            entry.avg = ( planningIndicatorDailyMap[dayKey].hourMap[hourKey].plannedAvg / divisor ).toFixed(2);
                            entry.quarterEntries.sort((a, b) => a.id - b.id);
                        }

                    } else {
                        let format = this.timeFrameStart.clone().add(x, 'days');
                        if (format < weekEnd) {
                            let dayKey = format.format('YYYY-MM-DD');
                            entry.presentMinutes = planningEmployeeMap[dayKey].spotPresent[spot.id];
                            entry.present = ( entry.presentMinutes / ( 60 * 24 ) ).toFixed(2);

                            let requiredMinutes = 0;
                            let hasReq = false;
                            if(useVlhForForecast){
                                requiredMinutes = vlhRequirementMap[dayKey] ? vlhRequirementMap[dayKey].spotRequirements[spot.id].req : 0;
                                hasReq = vlhRequirementMap[dayKey] ? vlhRequirementMap[dayKey].spotRequirements[spot.id].hasReq : false
                            } else {
                                requiredMinutes = planningIndicatorDailyMap[dayKey] ? planningIndicatorDailyMap[dayKey].spotRequirements[spot.id].req * 24 * 60 : 0;
                                hasReq =  planningIndicatorDailyMap[dayKey] ? planningIndicatorDailyMap[dayKey].spotRequirements[spot.id].hasReq : false
                            }
                            entry.requiredMinutes = requiredMinutes;
                            entry.hasReq = hasReq;
                            entry.required = ( entry.requiredMinutes / ( 60 * 24 ) ).toFixed(2);
                            entry.avg = ( planningIndicatorDailyMap[dayKey].plannedAvg / divisor ).toFixed(2);
                        }


                    }
                    if(this.countSpotsWithoutThreshold || entry.hasReq){
                        entry.color = entry.presentMinutes < entry.requiredMinutes ? 'red' : entry.presentMinutes > entry.requiredMinutes ? 'red' : '#0b0';

                    } else {
                        entry.required = '-'
                    }

                    map[spot.id][x] = entry;
                }
            })
            let sum = {};
            for (let x = 0; x < this.dayModeHours; x++) {
                let sumEntry = {
                    present: 0,
                    presentMinutes: 0,
                    required: 0,
                    requiredMinutes: 0,
                    color: 'black',
                    avg: 0
                }
                Object.values(map).forEach(e => {
                    if (!e[x]) return;
                    if(this.countSpotsWithoutThreshold || e[x].hasReq){
                        sumEntry.presentMinutes += e[x].presentMinutes;
                        sumEntry.requiredMinutes += e[x].requiredMinutes;
                        if (sumEntry.avg == 0) {
                            sumEntry.avg = e[x].avg;
                        }
                    }
                })
                if (hourly) {
                    sumEntry.present = ( sumEntry.presentMinutes / 60 ).toFixed(2);
                    sumEntry.required = ( sumEntry.requiredMinutes / 60 ).toFixed(2);
                } else {
                    sumEntry.present = ( sumEntry.presentMinutes / ( 60 * 24 ) ).toFixed(2);
                    sumEntry.required = ( sumEntry.requiredMinutes / ( 60 * 24 ) ).toFixed(2);

                }
                sumEntry.color = sumEntry.presentMinutes < sumEntry.requiredMinutes ? 'red' : sumEntry.presentMinutes > sumEntry.requiredMinutes ? 'red' : '#0b0';

                sum[x] = sumEntry;
            }
            map.sum = sum;
            return map;
        },
        containerHeight() {
            let subtractHeight = 90;
            if (this.embedded) subtractHeight = 45;
            if (this.renderPdChart) subtractHeight += this.displayPdChart ? 320 : 40;
            return `calc(100% - ${subtractHeight}px) !important`;
        },

        selectedGroupTemplateMap() {
            const templates = Object.values(this.currentWeekShiftTemplateMap);
            const map = {};
            if (!this.selectedGroup) return map;
            for (const template of templates) {
                if (template.groupId === this.selectedGroup.id) {
                    map[template.id] = template;
                }
            }
            return map;
        },

        optimizationGroupTemplateMap() {
            const otherGroupId = this.optimizationGroup;

            if (!otherGroupId) return {};

            // Calculate matches by looping over the spotIds and computing the distance to otherGroup (from, to, weekdays)

            // const groupedScoreMap = {};

            /**
             * {
             *  group.id + '-' + group.spot.id: {
             *    templateId (from selected group): overlayTemplateMatch
             *  }
             * }
             * @type {{[groupSpotId: string]: {[templateId]: string[]}}}
             */
            const optimizationTemplateMap = {};

            const otherGroupTemplateMap = {};
            for (const template of this.shiftTemplates) {
                // console.log(template.groupId);
                if (template.groupId === otherGroupId) {
                    const spotTemplates = otherGroupTemplateMap[template.spotId] || [];
                    spotTemplates.push(template);
                    otherGroupTemplateMap[template.spotId] = spotTemplates;
                }
            }

            for (const group of this.templateDisplayGroups) {
                if (group.id === 'events') continue;

                // TODO: Optimize for performance (aka replace moment with raw dates)
                const groupTemplates = this.templateDisplayMap[group.id + '-' + group.spot.id] || [];
                const otherGroupTemplates = otherGroupTemplateMap[group.spot.id];

                if (!otherGroupTemplates || otherGroupTemplates.length === 0) continue;

                let scoreList = [];

                for (let template of groupTemplates) {
                    const origTemplate = this.currentWeekShiftTemplateMap[template.id];
                    if (origTemplate)
                        template = origTemplate;
                    // TODO: Check validFrom and validTo and show only templates that are valid for the current week

                    const { from: templateFrom, to: templateTo } = this.getTemplateFromToDates(template, 1);
                    const templateWeekdays = template.weekDays;
                    const templateWDCount = templateWeekdays.length;

                    for (const otherTemplate of otherGroupTemplates) {
                        const {
                            from: otherTemplateFrom,
                            to: otherTemplateTo
                        } = this.getTemplateFromToDates(otherTemplate, 1);
                        const otherTemplateWeekdays = otherTemplate.weekDays;
                        const otherTemplateWDCount = otherTemplateWeekdays.length;

                        const identicalWeekdays = templateWeekdays.filter(wd => otherTemplateWeekdays.includes(wd));
                        const identicalWDCount = identicalWeekdays.length;
                        const averageWDCount = ( templateWDCount + otherTemplateWDCount ) / 2 + 1; // add 1 because when subtracting identical WDCount it can never be 0
                        const maxWDCount = Math.max(templateWDCount, otherTemplateWDCount);
                        const minWDCount = Math.min(templateWDCount, otherTemplateWDCount);

                        const templateFromNum = Math.floor(templateFrom.getTime() / 1000 / 60);
                        const templateToNum = Math.floor(templateTo.getTime() / 1000 / 60);
                        const otherTemplateFromNum = Math.floor(otherTemplateFrom.getTime() / 1000 / 60);
                        const otherTemplateToNum = Math.floor(otherTemplateTo.getTime() / 1000 / 60);
                        const score = (
                            Math.abs(templateFromNum - otherTemplateFromNum)
                            + Math.abs(templateToNum - otherTemplateToNum)
                            + 3600 * ( maxWDCount / minWDCount )
                        ) * ( averageWDCount - identicalWDCount ) / averageWDCount;

                        scoreList.push({ score, template, otherTemplate });
                    }
                }

                // console.table(Object.fromEntries(scoreList.map(({ score, template, otherTemplate }) => ([ template.id + ' - ' + otherTemplate.id, score ]))));

                // sort the scores from lowest (best match) to highest
                scoreList = scoreList.sort(({ score: a }, { score: b }) => a - b);

                // increase the score for templates that occur multiple times after each other
                scoreList = scoreList.map(({ score, template, otherTemplate }, index, origArray) => {
                    let i = index;
                    let scoreIncrease = 0;
                    while (origArray[index + i] && origArray[index + i].template.id === template.id) {
                        scoreIncrease += 1 / ( index + 1 );
                        i++;
                    }
                    return { score: score + scoreIncrease, template, otherTemplate };
                });

                // sort the scores from lowest (best match) to highest
                scoreList = scoreList.sort(({ score: a }, { score: b }) => a - b);

                // console.table(Object.fromEntries(scoreList.map(({ score, template, otherTemplate }) => ([ template.id + ' - ' + otherTemplate.id, score ]))));
                // console.log(scoreList.map(({ score, template, otherTemplate }) => ([ template.id + ' - ' + otherTemplate.id, score ])));

                const alreadyMatchedIds = new Set();
                const alreadyMatchedOtherIds = new Set();

                const matchedTemplates = {};

                for (let { template, otherTemplate } of scoreList) {
                    // mark otherTemplates to not assign them to multiple templates
                    if (template.isNew || template.id.endsWith('-rec') ||
                        alreadyMatchedIds.has(template.id) || alreadyMatchedOtherIds.has(otherTemplate.id))
                        continue;

                    // matchedTemplates[template.id].push(otherTemplate);
                    const acceptedRecommendation = this.addedTemplateMap[template.id + '-rec'];
                    if (acceptedRecommendation) {
                        for (const weekDay of acceptedRecommendation.weekDays) {
                            otherTemplate.weekDays = otherTemplate.weekDays.filter(wd => wd !== weekDay);
                        }
                    }
                    matchedTemplates[template.id] = otherTemplate;
                    alreadyMatchedIds.add(template.id);
                    alreadyMatchedOtherIds.add(otherTemplate.id);
                }

                optimizationTemplateMap[group.id + '-' + group.spot.id] = matchedTemplates;

                // groupedScoreMap[group.id + '-' + group.spot.id] = scoreList;
            }

            //console.log('optimizationTemplateMap:', optimizationTemplateMap);

            // console.log('groupedScoreMap:', groupedScoreMap);

            return optimizationTemplateMap;
        },

        /**
         * Combines planShifts and actualShifts from the current week into a map
         * @returns {{ [key: string]: { planShifts: {}, actualShifts: {} } }}
         */
        weeklyShiftsByDay() {
            const startDate = this.currentWeekStart;
            const endDate = this.currentWeekEnd;

            const planShiftMap = this.planShiftMap || {};
            const actualShiftMap = this.actualShiftMap || {};

            const planShiftsByDay = {};
            for (const planShift of Object.values(planShiftMap)) {
                const shiftFrom = moment(planShift.from);
                const key = shiftFrom.format('YYYY-MM-DD');
                if (!planShiftsByDay[key]) planShiftsByDay[key] = [];
                planShiftsByDay[key].push(planShift);
            }
            const actualShiftByPlanId = {};
            for (const actualShift of Object.values(actualShiftMap)) {
                actualShiftByPlanId[actualShift.planshiftId] = actualShift;
            }

            const shiftMap = {};
            for (const day = moment(startDate); day.isBefore(endDate); day.add(1, 'day')) {
                const key = day.format('YYYY-MM-DD');

                const entry = {
                    planShifts: {},
                    actualShifts: {},
                };

                const planShifts = planShiftsByDay[key] || [];
                for (const planShift of planShifts) {
                    entry.planShifts[planShift.id] = planShift;
                    const actualShift = actualShiftByPlanId[planShift.id];
                    if (actualShift)
                        entry.actualShifts[actualShift.id] = actualShift;
                }

                shiftMap[key] = entry;
            }

            return shiftMap;
        },

        currentWeekEventMap() {
            const startDate = this.currentWeekStart;
            const endDate = this.currentWeekEnd;

            const eventMap = this.storeEventMap || {};

            const currentWeekEventMap = {};
            for (const event of Object.values(eventMap)) {
                const eventFrom = moment(event.fromMoment);
                const eventTo = moment(event.toMoment);
                if (eventFrom.isBefore(endDate) && eventTo.isAfter(startDate)) {
                    const weekDays = [];
                    for (let day = moment(eventFrom); day.isBefore(eventTo); day.add(1, 'day')) {
                        weekDays.push(day.day() + 1);
                    }
                    currentWeekEventMap[event.id] = {
                        ...event,
                        spotId: 'events',
                        weekDays,
                        isEvent: true,
                    };
                }
            }

            return currentWeekEventMap;
        },

        templateSpotMap() {
            // [ spotId => Template[] ]
            const map = {
                'events': Object.values(this.currentWeekEventMap),
            };

            // loop over business units and create empty arrays for each spot
            for (const spot of this.spots) {
                map[spot.id] = [];
            }

            const templates = Object.values(this.selectedGroupTemplateMap).sort((a, b) => a.eventId ? -1 : b.eventId ? 1 : 0);
            for (const template of templates) {
                // TODO: Merge matched templates from overlayGroupTemplateMap if optimization mode is enabled
                // trim the from and to times to 5 characters, as one response contains also seconds
                let from = template.from;
                if (from.length > 5) from = from.substring(0, 5);
                let to = template.to;
                if (to.length > 5) to = to.substring(0, 5);

                // if (this.templateDaySelection !== 'W' &&
                //     !template.weekDays.includes(this.templateDaySelection) &&
                //     this.dayModeHours > 24 && !template.weekDays.includes(( this.templateDaySelection % 7 ) + 1))
                //     continue;

                if (!map[template.spotId])
                    map[template.spotId] = [];

                // if (this.optimizationMode) {
                //     const optimizationTemplates = this.optimizationGroupTemplateMap[template.groupId + '-' + template.spotId];
                //     if (optimizationTemplates && optimizationTemplates[template.id]) {
                //         const recommendations = optimizationTemplates[template.id];
                //         for (const recommendation of recommendations) {
                //             map[template.spotId].push({ ...recommendation, isRecommendation: true });
                //         }
                //     }
                // }

                map[template.spotId].push({ ...template, from, to });
            }

            const newTemplates = [
                ...Object.values(this.addedTemplateMap),
                ...Object.values(this.newEventTemplateMap),
            ];
            for (const template of newTemplates) {
                if (this.templateDaySelection !== 'W' &&
                    !template.weekDays.includes(this.templateDaySelection) &&
                    this.dayModeHours > 24 && !template.weekDays.includes(( this.templateDaySelection % 7 ) + 1))
                    continue;
                if (!map[template.spotId])
                    map[template.spotId] = [];
                map[template.spotId].push(template);
            }

            const events = {
                ...this.addedEventMap,
                ...this.currentWeekEventMap,
                ...this.changedEventMap,
            };

            for (const spotId of Object.keys(map)) {
                map[spotId].sort((a, b) => {
                    let aFrom = a.from;
                    let bFrom = b.from;
                    if (moment.isMoment(aFrom)) aFrom = aFrom.format('HH:mm');
                    else if (moment.isDate(aFrom)) aFrom = moment(aFrom).format('HH:mm');
                    if (moment.isMoment(bFrom)) bFrom = bFrom.format('HH:mm');
                    else if (moment.isDate(bFrom)) bFrom = moment(bFrom).format('HH:mm');
                    const [ aFromHr, aFromMin ] = aFrom.split(':');
                    const [ bFromHr, bFromMin ] = bFrom.split(':');
                    let timeA = aFromHr * 60 + aFromMin
                    let timeB = bFromHr * 60 + bFromMin;

                    const aExists = !!a.eventId && !!events[a.eventId];
                    const bExists = !!b.eventId && !!events[b.eventId];
                    const eventFromA = aExists ? new Date(events[a.eventId].from) : 0;
                    const eventFromB = bExists ? new Date(events[b.eventId].from) : 0;
                    const eventNameA = aExists ? events[a.eventId].name : '';
                    const eventNameB = bExists ? events[b.eventId].name : '';
                    // const isRecommendationA = a.isRecommendation ? 1 : 0;
                    // const isRecommendationB = b.isRecommendation ? 1 : 0;
                    return timeA - timeB // sort by time
                        // || isRecommendationB - isRecommendationA // sort recommendations to the bottom // doesn't work here
                        || ( !a.eventId ) - ( !b.eventId ) // sort event templates to their templates they are based of (if still the same time)
                        || eventFromA - eventFromB  // sort by event start time
                        || eventNameA.localeCompare(eventNameB); // sort by event name
                });
            }

            return map;
        },

        actualShiftsByTemplateId() {
            // [ templateId => ActualShift[] ]
            const map = {};

            for (const template of Object.values(this.selectedGroupTemplateMap)) {
                map[template.id] = [];
            }

            for (const actualShift of Object.values(this.actualShiftMap)) {
                const planShift = this.planShiftMap[actualShift.planshiftId];
                if (!planShift) continue;
                const template = this.selectedGroupTemplateMap[planShift.templateId];
                if (!template) continue;
                // Filter out actual shifts that are not in the current week
                const shiftFrom = moment(actualShift.from);
                if (!shiftFrom.isBetween(this.currentWeekStart, this.currentWeekEnd)) continue;
                map[template.id].push(actualShift);
            }

            return map;
        },

        planShiftsByTemplateId() {
            // [ templateId => PlanShift[] ]
            const map = {};

            for (const template of Object.values(this.selectedGroupTemplateMap)) {
                map[template.id] = [];
            }

            for (const planShift of Object.values(this.planShiftMap)) {
                const template = this.selectedGroupTemplateMap[planShift.templateId];
                if (!template) continue;
                // Filter out plan shifts that are not in the current week
                const shiftFrom = moment(planShift.from);
                if (!shiftFrom.isBetween(this.currentWeekStart, this.currentWeekEnd)) continue;
                map[template.id].push(planShift);
            }

            return map;
        },

        realUnassignedShiftTemplateTimesByDay() {
            const templates = Object.values({
                ...this.selectedGroupTemplateMap,
                ...this.addedTemplateMap,
                ...this.changedTemplateMap,
                ...this.newEventTemplateMap,
            }).filter(t => this.removedTemplateMap[t.id] == null);

            const events = Object.values({
                ...this.currentWeekEventMap,
                ...this.addedEventMap,
                ...this.changedEventMap,
            }).filter(e => this.removedEventMap[e.id] == null);

            const realShiftTemplateTimesMap = {};

            const currentWeekStart = this.currentWeekStart.toDate();

            let carryOverDays = [];
            for (let i = 1; i <= 7; i++) {
                const dayStart = addDays(currentWeekStart, i - 1);
                const shiftTemplates = templates.filter(t => t.weekDays.includes(i));

                if (!this.countWorkTimeToShiftStart) {
                    shiftTemplates.push(...carryOverDays);
                    carryOverDays = [];
                }

                shiftTemplates.filter(t => t.from >= t.to && !t.carryOver).forEach(t => {
                    t.carryOver = true;
                    t.carryOverTo = i + 1;
                    carryOverDays.push({...t});
                    t.carryOver = false;
                    t.carryOverTo = null;
                })

                const shiftTemplateSpotTimeMap = { total: 0, productiveTime: 0, eventTime: 0, totalEventTime: 0, carryOver: 0 };

                for (const template of shiftTemplates) {
                    const { from: templateFrom, to: templateTo } = this.getTemplateFromToDates(template, i);

                    if (this.skipTemplate(events, template, templateFrom, templateTo, i)) {
                        continue;
                    }

                    // calculate breakTime and shiftTime depending on day-break over
                    let breakValue = template.breakValue * 1000 * 60 || 0;
                    const spotId = template.spotId;
                    let diff = templateTo.getTime() - templateFrom.getTime();

                    if (!this.countWorkTimeToShiftStart && diff / 1000 / 3600 <= 24 && template.from >= template.to) { // assuming shifts less or equal to 24 hours, if we go over midnight, we need to consider break and work times partially
                        let aliquot = diff;
                        if (template.carryOver && i === template.carryOverTo) {
                            diff = templateTo.getTime() - new Date(templateTo.getFullYear(), templateTo.getMonth(), templateTo.getDate(), 0, 0, 0, 0).getTime();
                        } else {
                            diff = new Date(templateFrom.getFullYear(), templateFrom.getMonth(), templateFrom.getDate(), 24, 0, 0, 0).getTime() - templateFrom.getTime();
                        }

                        if (breakValue !== 0) {
                            aliquot = aliquot / diff;
                            breakValue = breakValue / aliquot;
                        }
                    }
                    const shiftTime = Math.round((diff - breakValue) / 1000 / 60);

                    if (!shiftTemplateSpotTimeMap[spotId]) shiftTemplateSpotTimeMap[spotId] = { count: 0, time: 0 };
                    shiftTemplateSpotTimeMap[spotId] = {
                        count: shiftTemplateSpotTimeMap[spotId].count + 1,
                        time: shiftTemplateSpotTimeMap[spotId].time + shiftTime,
                    };

                    if (template.carryOver) {
                        if (template.isRealCarryOver) {
                            shiftTemplateSpotTimeMap.carryOver += shiftTime;
                        }
                    } else {
                        if (this.spotMap[spotId].productive) {
                            shiftTemplateSpotTimeMap.productiveTime += shiftTime;
                            if (template.eventId) {
                                shiftTemplateSpotTimeMap.eventTime += shiftTime
                            }
                        }
                        shiftTemplateSpotTimeMap.total += shiftTime;
                        if (template.eventId) {
                            shiftTemplateSpotTimeMap.totalEventTime += shiftTime
                        }
                    }
                }

                const dayKey = format(dayStart, 'yyyy-MM-dd');

                realShiftTemplateTimesMap[dayKey] = shiftTemplateSpotTimeMap;
            }

            return realShiftTemplateTimesMap;
        },

        newEventTemplateMap() {
            const newTemplates = {};

            for (const template of Object.values(this.currentWeekShiftTemplateMap)) {
                let newTemplate = null;

                for (const day of template.weekDays) {
                    const { from: templateFrom } = this.getTemplateFromToDates(template, day);
                    for (const event of Object.values(this.addedEventMap)) {
                        if (event.shiftCreationMode === 2) continue;

                        let { from: eventFrom, to: eventTo } = this.getEventFromToDates(event);

                        if (eventTo <= eventFrom) {
                            const newTo = new Date(eventFrom);
                            newTo.setDate(eventFrom.getDate() + 1);
                            newTo.setHours(eventTo.getHours(), eventTo.getMinutes());
                            eventTo = newTo;
                        }

                        if (templateFrom >= eventFrom && templateFrom < eventTo) {
                            if (!newTemplate) {
                                newTemplate = { ...template };
                                newTemplate.id = `${template.id}-${event.id}`;
                                newTemplate.eventId = event.id;
                                newTemplate.weekDays = [];
                                newTemplate.isNew = true;
                            }
                            newTemplate.weekDays.push(day);
                        }
                    }
                }
                if (newTemplate)
                    newTemplates[newTemplate.id] = newTemplate;
            }

            return newTemplates;
        },

        finalWeekTemplates() {
            const templates = Object.values({
                ...this.selectedGroupTemplateMap,
                ...this.addedTemplateMap,
                ...this.changedTemplateMap,
                ...this.newEventTemplateMap,
            }).filter(t => this.removedTemplateMap[t.id] == null);

            const events = Object.values({
                ...this.currentWeekEventMap,
                ...this.addedEventMap,
                ...this.changedEventMap,
            }).filter(e => this.removedEventMap[e.id] == null);

            const finalWeekTemplatesMap = {};

            for (let template of templates) {
                template = { ...template };

                for (const day of template.weekDays) {
                    const { from: templateFrom, to: templateTo } = this.getTemplateFromToDates(template, day);

                    let skipDay = false;

                    for (const event of events) {
                        if (event.id === template.eventId && event.shiftCreationMode !== 2) continue;

                        let { from: eventFrom, to: eventTo } = this.getEventFromToDates(event);

                        if (eventTo <= eventFrom) {
                            const newTo = new Date(eventFrom);
                            newTo.setDate(eventFrom.getDate() + 1);
                            newTo.setHours(eventTo.getHours(), eventTo.getMinutes());
                            eventTo = newTo;
                        }

                        const eventFromDay = set(eventFrom, { hours: 0, minutes: 0, seconds: 0, milliseconds: 0 });
                        const eventToDay = set(eventFrom, { hours: 24, minutes: 0, seconds: 0, milliseconds: 0 });
                        if (templateFrom >= eventFromDay && templateFrom < eventToDay) {
                            const eventWillBeRemoved = this.removedEventMap[event.id];
                            if (eventWillBeRemoved) continue;

                            if (event.shiftCreationMode === 2 && templateFrom >= eventFrom && templateFrom < eventTo) {
                                skipDay = true;
                                break;
                            }
                            if (
                                event.shiftCreationMode === 0 && (
                                    this.viewMode === 'full-overlap' && !template.eventId &&
                                    this.viewMode !== 'full-split'
                                )
                            ) {
                                const templatesForEvent = this.templatesByEventId[event.id];
                                const hasEventTemplates = templatesForEvent && templatesForEvent.length > 0;
                                const hasEventTemplatesForDay = hasEventTemplates && templatesForEvent.find(t => t.weekDays.includes(day) && !this.removedTemplateMap[t.id] && this.getTemplateFromToDates(t, day).from <= eventTo);

                                if (hasEventTemplatesForDay) {
                                    skipDay = true;
                                    break;
                                }
                            }
                        }
                    }

                    if (skipDay) template.weekDays = template.weekDays.filter(wd => wd !== day);
                }

                if (template.weekDays.length > 0)
                    finalWeekTemplatesMap[template.id] = template;
            }

            return finalWeekTemplatesMap;
        },

        hasChanges() {
            return Object.values({
                ...this.addedTemplateMap,
                ...this.changedTemplateMap,
                ...this.removedTemplateMap,
                ...this.addedEventMap,
                ...this.changedEventMap,
                ...this.removedEventMap,
            }).length > 0;
        },

        rowIndexMap() {
            // [ 'events' | spotId | templateId => rowIndex ]
            const map = {};
            let index = Y_OFFSET; // The header row spans from 1 to 3

            map['events'] = index++; // Event header row from 2 to 3

            const events = [
                ...Object.values(this.addedEventMap),
                ...Object.values(this.currentWeekEventMap),
            ].sort((a, b) => {
                const aFrom = moment(a.fromMoment);
                const bFrom = moment(b.fromMoment);
                const aName = a.name || '';
                const bName = b.name || '';
                return aFrom.unix() - bFrom.unix() || aName.localeCompare(bName);
            });

            if (!this.groupCollapsedStates['events']) {
                for (const event of events) {
                    const { from: eventFrom, to: eventTo } = this.getEventFromToDates(event);
                    if (!eventFrom || !eventTo) continue;
                    if (this.dayFrom && eventTo <= this.dayFrom) continue;
                    if (this.dayTo && eventFrom >= this.dayTo) continue;
                    map['event-' + event.id] = index++;
                }
            }

            if (this.planningType !== 2 && this.templateDaySelection !== 'W') {
                map['sum-ec-row-1'] = index++;
                map['sum-ec-row-2'] = index++;
            }

            for (const { spot, collapsed, id } of this.templateDisplayGroups) {
                if (id === 'events') continue;
                map[spot.id] = index++;
                if (this.planningType !== 2 && this.templateDaySelection !== 'W') {
                    map[spot.id + '-ec-row-1'] = index++;
                    map[spot.id + '-ec-row-2'] = index++;
                }

                const templatesForSpot = this.templateSpotMap[spot.id] || [];

                if (templatesForSpot.length === 0)
                    map[spot.id + '-notemplates'] = index++;

                for (const template of templatesForSpot) {
                    if (template.isRecommendation) continue;

                    if (template.id.endsWith('-rec')) continue;

                    // if (this.optimizationMode) {
                    //     const groupRecommendations = this.optimizationGroupTemplateMap[template.groupId + '-' + template.spotId];
                    //     if (!template.eventId && groupRecommendations) {
                    //         const recommendations = groupRecommendations[template.id] || [];
                    //         for (const recommendation of recommendations) {
                    //             map[recommendation.id] = index++;
                    //         }
                    //     }
                    // }

                    if (this.templateDaySelection !== 'W') {
                        const currentWeekStart = this.currentWeekStart.toDate();
                        const currentDayStart = addDays(currentWeekStart, this.templateDaySelection - 1);
                        const currentDayEnd = addHours(currentDayStart, this.hourlySelection);

                        const days = this.hourlySelection > 24 ? [ this.templateDaySelection, ( this.templateDaySelection % 7 ) + 1 ] : [ this.templateDaySelection ];
                        const skipTemplates = [];
                        for (const day of days) {
                            const { from: templateFrom } = this.getTemplateFromToDates(template, day);

                            if (templateFrom >= currentDayEnd) {
                                skipTemplates.push(true);
                                continue;
                            }

                            for (const event of events) {
                                if (event.id === template.eventId) continue;

                                let { from: eventFrom, to: eventTo } = this.getEventFromToDates(event);

                                if (eventTo <= eventFrom) {
                                    const newTo = new Date(eventFrom);
                                    newTo.setDate(eventFrom.getDate() + 1);
                                    newTo.setHours(eventTo.getHours(), eventTo.getMinutes());
                                    eventTo = newTo;
                                }

                                if (templateFrom >= eventFrom && templateFrom < eventTo) {
                                    if (
                                        event.shiftCreationMode === 2 ||
                                        event.shiftCreationMode === 0 && (
                                            this.viewMode === 'full-overlap' && !template.eventId &&
                                            this.viewMode !== 'full-split'
                                        )
                                    ) {
                                        const eventWillBeRemoved = this.removedEventMap[event.id];
                                        if (!eventWillBeRemoved) {
                                            const templatesForEvent = this.templatesByEventId[event.id];
                                            const hasEventTemplates = templatesForEvent && templatesForEvent.length > 0;
                                            if (hasEventTemplates && templatesForEvent.find(t => t.weekDays.includes(day) && this.getTemplateFromToDates(t, day).from <= eventTo)) {
                                                skipTemplates.push(true);
                                            }
                                        }
                                    }
                                }
                            }
                        }

                        let noDisplay = true;
                        for (const day of template.weekDays) {
                            const { from: templateFrom, to: templateTo } = this.getTemplateFromToDates(template, day);

                            if (templateFrom >= currentDayStart && templateFrom < currentDayEnd ||
                                templateTo > currentDayStart && templateTo <= currentDayEnd) {
                                noDisplay = false;
                                break;
                            }
                        }
                        if (skipTemplates.length === days.length || noDisplay) continue;
                    }

                    if (this.shouldDisplayGridRow(template.id)) {
                        map[template.id] = index++;

                        if (this.addedTemplateMap[template.id + '-rec']) {
                            map[template.id + '-rec'] = index++;
                        }
                    }

                }
            }
            return map;
        },

        /* Displayable grid values */

        gridRows() {
            // Count rows using rowIndexMap, sort descending
            return Object.values(this.rowIndexMap).sort((a, b) => b - a)[0] + 2;
        },

        gridColumns() {
            if (this.templateDaySelection === 'W')
                return 7 * WEEKMODE_DAY_INTERVAL + 2 * WEEKMODE_HOUR_INTERVAL; // X_OFFSET not needed as the first column will be added by the grid
            else
                return this.dayModeHours * DAYMODE_HOUR_INTERVAL;
        },

        gridVerticalSeparators() {
            const separators = new Set([ 2 ]);
            if (this.templateDaySelection === 'W') {
                for (let i = 1; i <= 7; i++) {
                    separators.add(i * WEEKMODE_DAY_INTERVAL + X_OFFSET);
                }
            } else {
                for (let i = 1; i <= this.dayModeHours; i++) {
                    separators.add(i * DAYMODE_HOUR_INTERVAL + X_OFFSET);
                }
            }
            return Array.from(separators);
        },

        gridHorizontalSeparators() {
            const separators = new Set();
            if (this.planningType !== 2) {
                let lastEventIndex = 4;
                if (!this.groupCollapsedStates['events']) {
                    const events = [
                        ...Object.values(this.addedEventMap),
                        ...Object.values(this.currentWeekEventMap),
                    ].filter((event) => {
                        const { from: eventFrom, to: eventTo } = this.getEventFromToDates(event);
                        if (this.dayFrom && eventTo <= this.dayFrom) return false;
                        if (this.dayTo && eventFrom >= this.dayTo) return false;
                        return true;
                    });
                    lastEventIndex += events.length;
                }
                separators.add(lastEventIndex);
            }
            for (const { spot, id } of this.templateDisplayGroups) {
                if (id === 'events') continue;
                let rowIndex = this.rowIndexMap[spot.id];
                if (rowIndex != null) separators.add(rowIndex);

                for (const template of this.templateSpotMap[spot.id]) {
                    // TODO: Check if template is not overlay
                    rowIndex = this.rowIndexMap[template.id];
                    if (rowIndex != null) separators.add(rowIndex);
                }
            }
            return Array.from(separators);
        },

        /* Cell types */

        gridHeaderCells() {
            const cells = [];
            if (this.templateDaySelection === 'W') {
                for (let i = 0; i < 7; i++) {
                    const sticky = { top: 0 };
                    const style = {
                        backgroundColor: i < 5 ? 'var(--contrast-0)' : 'var(--contrast-1)',
                        cursor: 'pointer',
                    };
                    const date = moment(this.currentWeekStart).weekday(i);
                    const label = this.$helpers.translateDayShort(date.format('dddd').substring(0,2)) + date.format('. DD.MM.');
                    const markKey = date.format("YYYY-MM-DD");
                    const colFrom = i * WEEKMODE_DAY_INTERVAL + X_OFFSET;
                    const colTo = ( i + 1 ) * WEEKMODE_DAY_INTERVAL + X_OFFSET;
                    const onClick = () => {
                        this.$set(this, 'optimizationMode', false);
                        this.$set(this, 'templateDaySelection', i + 1);
                    };
                    cells.push({
                        key: 'headercell-' + ( i + 1 ),
                        markKey,
                        colFrom,
                        colTo,
                        label,
                        style,
                        sticky,
                        onClick,
                    });
                }
            } else {
                const daySelected = this.daySelected.toDate();
                for (let i = 0; i < this.dayModeHours; i++) {
                    const sticky = { top: 32 };
                    const style = { backgroundColor: i < 24 ? 'var(--contrast-0)' : 'var(--contrast-1)' };
                    const date = addHours(daySelected, i); // moment(this.daySelected).hour(0).add(i, "hours");
                    const label = ( i % 24 ).toString().padStart(2, '0'); // format(date, 'HH');
                    const markKey = ( i === 0 || i === 24 ) ? format(date, "yyyy-MM-dd") : null;
                    const colFrom = i * DAYMODE_HOUR_INTERVAL + X_OFFSET;
                    const colTo = ( i + 1 ) * DAYMODE_HOUR_INTERVAL + X_OFFSET;
                    const onClick = () => {
                    };
                    cells.push({
                        key: 'headercell-' + ( i + 1 ),
                        markKey,
                        colFrom,
                        colTo,
                        label,
                        style,
                        sticky,
                        onClick,
                    });
                }
            }
            const colFromLastCell = this.templateDaySelection === 'W'
                ? 7 * WEEKMODE_DAY_INTERVAL + X_OFFSET
                : this.dayModeHours * DAYMODE_HOUR_INTERVAL + X_OFFSET;
            cells.push({
                key: 'headercell-last',
                colFrom: colFromLastCell,
                colTo: -1,
                label: '',
                style: { backgroundColor: 'var(--contrast-0)' },
                sticky: { top: this.templateDaySelection === 'W' ? 0 : 32 },
                onClick: () => {
                },
            });
            return cells;
        },

        gridHeaderInfoCells() {
            const cells = [];
            if (this.templateDaySelection === 'W') {
                for (let i = 0; i < 7; i++) {
                    const sticky = { top: 32 };
                    const style = { backgroundColor: i < 5 ? 'var(--contrast-0)' : 'var(--contrast-1)' };
                    const colFrom = i * WEEKMODE_DAY_INTERVAL + X_OFFSET;
                    const colTo = ( i + 1 ) * WEEKMODE_DAY_INTERVAL + X_OFFSET;
                    const date = moment(this.currentWeekStart).add(i, 'days');

                    let totalUnassigned = this.realUnassignedShiftTemplateTimesByDay[date.format('YYYY-MM-DD')] || {
                        total: 0,
                        productiveTime: 0,
                        eventTime: 0,
                        totalEventTime: 0,
                        carryOver: 0,
                    };
                    const totalUnassignedMinutes = (totalUnassigned.totalEventTime !== 0 ? totalUnassigned.totalEventTime : totalUnassigned.total) + totalUnassigned.carryOver;
                    const unassignedHours = Math.floor(totalUnassignedMinutes / 60);
                    const unassignedMinutes = totalUnassignedMinutes % 60;
                    const unassignedHoursText = ( unassignedHours || 0 ) + 'h:' + unassignedMinutes.toString().padStart(2, '0');

                    const unassignedHoursTooltip = this.getUnassignedShiftTimesTooltip(i + 1); // this.assignedShiftTooltipWrapper(date.format('YYYY-MM-DD'));

                    const indicatorStyle = this.getIndicatorBarBackgroundStyle(date);
                    const indicatorTooltip = this.getIndicatorAmountWrapper(date);
                    const indicatorBarStyle = this.getIndicatorBarStyle(date);
                    const productivity = this.getProductivity(date, totalUnassigned);

                    cells.push({
                        key: 'headercell-info-' + ( i + 1 ),
                        colFrom: colFrom,
                        colTo,
                        style,
                        sticky,
                        date,
                        unassignedHoursTooltip,
                        unassignedHours: unassignedHoursText,
                        productiveHours: productivity.label,
                        indicatorStyle,
                        indicatorTooltip,
                        indicatorBarStyle,
                    });
                }
            } else {
                for (let i = 0; i < this.dayModeHours; i++) {
                    const sticky = { top: 64 };
                    const style = { backgroundColor: i < 24 ? 'var(--contrast-0)' : 'var(--contrast-1)' };
                    const label = moment(this.daySelected).hour(i).format('HH');
                    const colFrom = i * DAYMODE_HOUR_INTERVAL + X_OFFSET;
                    const colTo = ( i + 1 ) * DAYMODE_HOUR_INTERVAL + X_OFFSET;
                    const quarters = [];
                    let productiveHours = 0;

                    let actual = 0;
                    let planned = 0;

                    for (let qi = 0; qi < 4; qi++) {
                        const quarterAmountTooltip = this.getQuarterAmountWrapper(i, qi + 1);
                        quarters.push({ key: `quarter-${i}-${qi}`, tooltip: quarterAmountTooltip });
                        productiveHours += this.getProductivityHour(i, qi +1);

                        let format = this.timeFrameStart.clone().add(i, 'hour').minutes(( qi - 1 ) * 15);
                        let dayKey = format.format('YYYY-MM-DD');
                        let hourKey = format.format('HH');
                        let quarterKey = format.format('mm');

                        if (this.planningIndicatorDailyMap[dayKey]) {
                            let indicator = this.planningIndicatorDailyMap[dayKey].hourMap[hourKey].quarterMap[quarterKey];

                            if (indicator) {
                                let divisor = this.planningIndicatorTypeMap[this.displayIndicatorType] ? Math.pow(10, this.planningIndicatorTypeMap[this.displayIndicatorType].fractional) : 1;

                                if (indicator.actual) actual += indicator.actual / divisor;
                                if (indicator.planned) planned += indicator.planned / divisor;
                            }
                        }
                    }

                    actual = actual !== 0 && productiveHours !== 0 ? (actual / (productiveHours / 60)).toFixed(2) : ' - ';
                    planned = planned !== 0 && productiveHours !== 0 ? (planned / (productiveHours / 60)).toFixed(2) : ' - ';

                    cells.push({
                        key: 'headercell-info-' + ( i + 1 ),
                        colFrom: colFrom,
                        colTo,
                        label,
                        style,
                        sticky,
                        quarters,
                        actual,
                        planned,
                    });
                }
            }
            const colFromLastCell = this.templateDaySelection === 'W'
                ? 7 * WEEKMODE_DAY_INTERVAL + X_OFFSET
                : this.dayModeHours * DAYMODE_HOUR_INTERVAL + X_OFFSET;
            cells.push({
                key: 'headercell-info-last',
                colFrom: colFromLastCell,
                colTo: -1,
                style: { backgroundColor: 'var(--contrast-0)' },
                sticky: { top: this.templateDaySelection === 'W' ? 32 : 64 },
            });
            return cells;
        },

        gridHeaderDateCells() {
            if (this.templateDaySelection === 'W') return [];
            const cells = [];

            const dayFrom = this.dayFrom;

            const colFromLastCell = this.dayModeHours * DAYMODE_HOUR_INTERVAL + X_OFFSET;

            for (let i = 0; i < this.dayModeHours; i += 24) {
                const sticky = { top: 0 };
                const style = { backgroundColor: i % 2 === 0 ? 'var(--contrast-0)' : 'var(--contrast-1)' };
                const colFrom = i * DAYMODE_HOUR_INTERVAL + X_OFFSET;
                let colTo = ( i + 24 ) * DAYMODE_HOUR_INTERVAL + X_OFFSET;
                if (colTo > colFromLastCell) colTo = colFromLastCell;

                const date = moment(dayFrom).add(i, 'hours');
                const label = date.format('ddd DD.MM.');

                cells.push({
                    key: 'headercell-date-' + ( i + 1 ),
                    colFrom,
                    colTo,
                    label,
                    style,
                    sticky,
                });
            }

            cells.push({
                key: 'headercell-date-last',
                colFrom: colFromLastCell,
                colTo: -1,
                style: { backgroundColor: 'var(--contrast-0)' },
                sticky: { top: 0 },
            });

            return cells;
        },

        gridEventAddButtonCells() {
            if (this.templateDaySelection !== 'W') return [];
            const cells = [];
            for (let i = 0; i < 7; i++) {
                const colFrom = i * WEEKMODE_DAY_INTERVAL + X_OFFSET;
                const colTo = ( i + 1 ) * WEEKMODE_DAY_INTERVAL + X_OFFSET;

                cells.push({
                    key: 'event-add-button-' + ( i + 1 ),
                    colFrom,
                    colTo,
                    dayIndex: i,
                    directSave: !this.editMode,
                });
            }

            return cells;
        },

        gridGroupHeaderCells() {
            const cells = [];
            for (const templateGroup of this.templateDisplayGroups) {
                const { spot, collapsed, id } = templateGroup;
                const spotId = id === 'events' ? 'events' : spot.id;
                const rowIndex = this.rowIndexMap[spotId];
                if (rowIndex == null) continue;

                const sticky = { top: 0, left: 0 };
                const style = {
                    backgroundColor: id === 'events' ? 'var(--md)' : spot.color,
                    color: id === 'events' ? null : ( luma(spot.color) >= 165 ) ? '#404040' : null,
                    marginTop: ( this.planningType === 2 || id === 'events' ) ? 0 : '2px',
                    marginBottom: ( this.planningType !== 2 && id === 'events' ) ? '3px' : this.planningType !== 2 ? 0 : '1px',
                };
                const label = id === 'events' ? this.$t('shift.template.events') : spot.name;
                const colFrom = 1;
                const colTo = 2;
                cells.push({
                    key: 'groupheader-' + spotId,
                    spotId,
                    row: rowIndex, colFrom, colTo, label, style, sticky, collapsed,
                    isSubgroup: spot && !spot.isMain,
                    templateGroup,
                    directSave: this.directSaveMode,
                });
            }
            return cells;
        },

        gridGroupRows() {
            if (this.planningType !== 2 && this.templateDaySelection !== 'W') return [];

            const cells = [];

            const colFrom = X_OFFSET;
            const colTo = this.templateDaySelection === 'W'
                ? 7 * WEEKMODE_DAY_INTERVAL + X_OFFSET
                : this.dayModeHours * DAYMODE_HOUR_INTERVAL + X_OFFSET;
            for (const { spot, id } of this.templateDisplayGroups) {
                if (id === 'events') continue;

                cells.push({ key: 'grouprow-' + spot.id, row: this.rowIndexMap[spot.id], colFrom, colTo });
                const noTemplateIndex = this.rowIndexMap[spot.id + '-notemplates'];
                const templatesForSpot = Object.values(this.templateSpotMap[spot.id]).filter(template => this.shouldDisplayGridRow(template.id));
                if (noTemplateIndex)
                    cells.push({
                        key: 'grouprow-notemplates-' + spot.id,
                        row: noTemplateIndex,
                        colFrom,
                        colTo,
                        noSpotLabel: spot.name,
                    });
                else if (templatesForSpot.length === 0 && !this.groupCollapsedStates[spot.id])
                    cells.push({
                        key: 'grouprow-notemplates-' + spot.id,
                        row: this.rowIndexMap[spot.id],
                        colFrom,
                        colTo,
                        noSpotFilterLabel: spot.name,
                    });
            }
            return cells;
        },

        gridEmployeeCountHeaderCells() {
            const cells = [];
            for (const templateGroup of this.templateDisplayGroups) {
                const { spot, collapsed, id } = templateGroup;
                // Reuse event index as sum index
                const spotId = id === 'events' ? 'sum' : spot.id;

                for (let i = 1; i <= 2; i++) {
                    const rowIndex = this.rowIndexMap[spotId + '-ec-row-' + i];
                    if (rowIndex == null) continue;

                    const sticky = { top: 0, left: 0 };
                    const style = {
                        backgroundColor: spotId === 'sum' ? 'var(--md)' : spot.color,
                        color: spotId === 'sum' ? null : ( luma(spot.color) >= 165 ) ? '#404040' : null,
                        marginTop: ( spotId === 'sum' && i === 1 ) ? '2px' : 0,
                        marginBottom: i === 2 ? '3px' : 0,
                    };
                    const label = i === 1 ? this.$t('shift.template.targetAverageEmp') : this.$t('shift.template.actualAverageEmp');
                    const colFrom = 1;
                    const colTo = 2;
                    cells.push({
                        key: 'groupheader-' + spotId + '-ec-' + i,
                        spotId,
                        row: rowIndex, colFrom, colTo, label, style, sticky, collapsed,
                        isSubgroup: spot && !spot.isMain,
                        templateGroup,
                    });
                }
            }
            return cells;
        },

        gridEmployeeBulletPointCells() {
            const cells = [];
            for (const templateGroup of this.templateDisplayGroups) {
                const { spot, id } = templateGroup;
                if (id === 'events') continue;
                const spotId = spot.id;

                const rowIndex = this.rowIndexMap[spotId];

                const planningValuesHours = this.planningValuesMap[spotId];
                if (planningValuesHours == null) continue;

                for (let i = 0; i < this.dayModeHours; i++) {
                    if (!planningValuesHours[i] || !planningValuesHours[i].quarterEntries) continue;

                    const colFrom = i * DAYMODE_HOUR_INTERVAL + X_OFFSET;
                    const colTo = ( i + 1 ) * DAYMODE_HOUR_INTERVAL + X_OFFSET;

                    const quarters = [];

                    planningValuesHours[i].quarterEntries.forEach((quarter, quarterIndex) => {
                        quarters.push({
                            key: 'bulletpoint-' + spotId + '-' + i + '-' + quarterIndex,
                            quarterIndex,
                            tooltip: this.getEmployeeCountWrapper(spotId, i, quarterIndex),
                            style: {
                                background: quarter.color,
                            },
                        });
                    });

                    cells.push({
                        key: 'bulletpoint-' + spotId + '-' + i,
                        row: rowIndex,
                        colFrom,
                        colTo,
                        spotId,
                        quarters,
                    });
                }
            }
            return cells;
        },

        gridEmployeeCountCells() {
            const cells = [];
            for (const templateGroup of this.templateDisplayGroups) {
                const { spot, id } = templateGroup;
                // Reuse event index as sum index
                const spotId = id === 'events' ? 'sum' : spot.id;

                const row1 = this.rowIndexMap[spotId + '-ec-row-1'];
                const row2 = this.rowIndexMap[spotId + '-ec-row-2'];
                if (row1 == null || row2 == null) continue;

                const planningValuesHours = this.planningValuesMap[spotId];
                if (planningValuesHours == null) continue;

                for (let i = 0; i < this.dayModeHours; i++) {
                    if (!planningValuesHours[i] || !planningValuesHours[i].required || !planningValuesHours[i].present)
                        continue;
                    const colFrom = i * DAYMODE_HOUR_INTERVAL + X_OFFSET;
                    const colTo = ( i + 1 ) * DAYMODE_HOUR_INTERVAL + X_OFFSET;

                    const tooltip = this.getIndicatorAvgWrapper(spotId, i);
                    const required = planningValuesHours[i].required;
                    const present = planningValuesHours[i].present;
                    const color = planningValuesHours[i].color;

                    cells.push({
                        key: 'employee-count-' + spotId + '-' + i + '-req',
                        row: row1,
                        colFrom,
                        colTo,
                        spotId,
                        tooltip,
                        value: required,
                    });

                    cells.push({
                        key: 'employee-count-' + spotId + '-' + i + '-pres',
                        row: row2,
                        colFrom,
                        colTo,
                        spotId,
                        tooltip,
                        labelStyle: { color },
                        value: present,
                    });
                }
            }
            return cells;
        },

        gridEventDisplayCells() {
            if (this.groupCollapsedStates['events']) return [];

            const cells = [];

            const events = [
                ...Object.values(this.currentWeekEventMap),
                ...Object.values(this.addedEventMap),
            ];

            const eventPixelWidth = this.canEdit ? 5 : 10;

            for (let event of events) {
                event = this.changedEventMap[event.id] || event;
                const row = this.rowIndexMap['event-' + event.id];
                if (row == null) continue;

                const label = event.name;
                let backgroundImage = `linear-gradient(to left, ${event.color} ${eventPixelWidth}px, transparent ${eventPixelWidth}px)`;

                const isDeleted = !!this.removedEventMap[event.id];
                if (isDeleted)
                    backgroundImage += `, repeating-linear-gradient(45deg, transparent, transparent 5px, var(--contrast-1) 5px, var(--contrast-1) 10px)`;

                const style = { backgroundImage };
                const sticky = { left: 0 };
                cells.push({
                    key: 'event-display-' + event.id,
                    row,
                    colFrom: 1,
                    colTo: 2,
                    label,
                    style,
                    sticky,
                    event,
                    isNew: !!this.addedEventMap[event.id],
                    isDeleted,
                    directSave: this.directSaveMode,
                });
            }
            return cells;
        },

        gridTemplateDisplayCells() {
            const rowIndexes = this.rowIndexMap;

            const eventPixelWidth = this.canEdit ? 5 : 10;

            const cells = [];
            for (const templateGroup of this.templateDisplayGroups) {
                const { spot, collapsed, id } = templateGroup;
                if (id === 'events') continue;

                for (let template of this.templateSpotMap[spot.id]) {
                    const row = rowIndexes[template.id];
                    if (row == null) continue;

                    template = this.newEventTemplateMap[template.id] || this.addedTemplateMap[template.id] || this.changedTemplateMap[template.id] || template;
                    if (!template.from || !template.to) continue;
                    const { from: fromTime, to: toTime, breakFrom, breakTo } = this.getTemplateFromToDates(template, 1);

                    // const hourValue = ((toTime.diff(fromTime, 'minutes') - template.breakValue) / 60).toFixed(2);
                    const breakValue = (template.breakValue && template.breakValue > 0) ? template.breakValue / 60 : 0;
                    const hourValue = ( ( toTime.getTime() - fromTime.getTime() ) / ( 1000 * 60 * 60 ) - breakValue ).toFixed(2);

                    // const templateDisplay = this.templateDisplayMap[template.id] || {};
                    const duration = ( template.count > 1 ? ( template.count + 'x ' ) : '' ) + hourValue + ' h' +
                        ( template.projectId != null ? ' (P)' : '' ) + ( template.fromLocationId != null ? ' (E)' : '' );
                    const period = this.getTemplateValidLabel(template.id);
                    const employee = template.employeeId ? this.employeeMap[template.employeeId] : null;
                    let employeeName = this.templateDaySelection === 'W' && employee != null ? employee.lastName+" "+employee.firstName : '';
                    if(employeeName.length > 12){
                        employeeName = employeeName.substring(0, 12) + '...';
                    }
                    const picture = this.templateDaySelection === 'W' && template.employeeId == null
                        // ? 'radial-gradient(circle, var(--contrast-3) 0%, var(--contrast-3) 35%, var(--contrast-2) 35%, var(--bg) 100%)'
                        ? null
                        : this.getAvatarForTemplate(template);

                    let backgroundImage;
                    if (template.eventId) {
                        const event = this.addedEventMap[template.eventId] || this.changedEventMap[template.eventId] || this.currentWeekEventMap[template.eventId];
                        if (event) {
                            backgroundImage = `linear-gradient(to left, ${event.color} ${eventPixelWidth}px, transparent ${eventPixelWidth}px)`;
                        }
                    }
                    const style = { backgroundImage };

                    cells.push({
                        key: `templatedisplay-${template.id}`,
                        colFrom: 1,
                        colTo: 2,
                        row,
                        employeeName,
                        duration,
                        period,
                        picture,
                        isSubgroup: spot && !spot.isMain,
                        templateGroup,
                        template,
                        style,
                        directSave: this.directSaveMode,
                        collapsed: collapsed,
                    });
                }
            }

            return cells;
        },

        gridEventCells() {
            if (this.groupCollapsedStates['events']) return [];

            const rowIndexes = this.rowIndexMap;

            const cells = [];

            const events = [
                ...Object.values(this.currentWeekEventMap),
                ...Object.values(this.addedEventMap),
            ];
            for (let event of events) {
                event = this.changedEventMap[event.id] || event;
                const { from: eventFrom, to: eventTo } = this.getEventFromToDates(event);
                if (!eventFrom || !eventTo) continue;
                if (this.dayFrom && eventTo <= this.dayFrom) continue;
                if (this.dayTo && eventFrom >= this.dayTo) continue;
                const row = rowIndexes['event-' + event.id];

                const { from: colFrom, to: colTo } = this.getGridCellSpan(eventFrom, eventTo);

                /*
                 console.log(
                     'Event:', event.name, '\n',
                     'from:', eventFrom.toISOString(), '\n',
                     'to:', eventTo.toISOString(), '\n',
                     'cols:', colFrom, ' - ', colTo,
                 ); */
                cells.push({
                    key: `event-${event.id}`,
                    eventId: event.id,
                    event,
                    colFrom,
                    colTo,
                    row,
                    label: event.name,
                    color: event.color,
                    isNew: !!this.addedEventMap[event.id],
                    isDeleted: !!this.removedEventMap[event.id],
                    directSave: this.directSaveMode,
                });
            }

            return cells;
        },

        gridRecommendationCells() {
            if (!this.optimizationMode) return [];

            const cells = [];

            const recommendations = Object.values(this.optimizationGroupTemplateMap)
                .flatMap((groupRecs) => Object.entries(groupRecs)) || [];

            for (const [ templateId, recommendation ] of recommendations) {
                const row = this.rowIndexMap[templateId];
                if (!row) continue;

                const origTemplate = this.templateMap[templateId];
                const template = { ...origTemplate, ...( this.changedTemplateMap[templateId] || {} ) };
                const { from: templateFrom, to: templateTo } = this.getTemplateFromToDates(template, 1);

                const equalTemplate = this.findEqualTemplate(recommendation, template.groupId, template.spotId);

                for (const day of recommendation.weekDays) {

                    const { from: recFrom, to: recTo } = this.getTemplateFromToDates(recommendation, day);
                    if (!recFrom || !recTo) continue;
                    const { from: colFrom, to: colTo } = this.getGridCellSpan(recFrom, recTo);

                    if (dateToHHmm(recFrom) === dateToHHmm(templateFrom) && dateToHHmm(recTo) === dateToHHmm(templateTo) && template.weekDays.includes(day))
                        continue;
                    if (equalTemplate && equalTemplate.weekDays.includes(day))
                        continue;

                    // Full height if no template is underneath

                    const fullSize = !template.weekDays.includes(day) && !origTemplate.weekDays.includes(day);

                    cells.push({
                        key: `recommendation-${recommendation.id}-day-${day}`,
                        colFrom,
                        colTo,
                        row,
                        labelLeft: dateToHHmm(recFrom),
                        labelRight: dateToHHmm(recTo),
                        style: {
                            // background,
                            // color,
                        },
                        classList: fullSize ? [] : [ 'template-cell--half' ],
                        resizable: false,
                        templateId: templateId,
                        recommendation,
                        tooltip: this.$t('shift.template.adopt'),
                        day,
                        directSave: this.directSaveMode,
                    });
                }
            }

            return cells;
        },

        gridTemplateCells() {
            const rowIndexes = this.rowIndexMap;

            const events = Object.values({
                ...this.currentWeekEventMap,
                ...this.addedEventMap,
                ...this.changedEventMap,
            });

            const cells = [];
            for (const { spot, collapsed, id } of this.templateDisplayGroups) {
                if (id === 'events') continue;

                for (let origTemplate of this.templateSpotMap[spot.id]) {
                    // if (origTemplate.isRecommendation) continue; // Skip recommendations as they will be rendered in their own computed property

                    let template = origTemplate;

                    const row = rowIndexes[template.id];

                    if (row == null) continue;

                    let newEventTemplate = false;
                    if (this.newEventTemplateMap[origTemplate.id]) {
                        template = this.newEventTemplateMap[origTemplate.id];
                        newEventTemplate = true;
                    }
                    if (this.addedTemplateMap[origTemplate.id]) {
                        template = this.addedTemplateMap[origTemplate.id];
                    }
                    if (this.changedTemplateMap[origTemplate.id]) {
                        template = this.changedTemplateMap[origTemplate.id];
                    }

                    const event = template.eventId ? ( this.addedEventMap[template.eventId] || this.changedEventMap[template.eventId] || this.currentWeekEventMap[template.eventId] ) : null;

                    const isDeleted = !!(this.removedTemplateMap[origTemplate.id] || this.removedEventMap[template.eventId]);

                    const hasRecommendation = this.optimizationMode && this.optimizationGroupTemplateMap[template.groupId + '-' + template.spotId] && this.optimizationGroupTemplateMap[template.groupId + '-' + template.spotId][template.id];

                    // const templateDisplay = this.templateDisplayMap[template.id] || { timeFrameMap: {} };

                    const weekDays = new Set([ ...template.weekDays, ...origTemplate.weekDays ]);

                    const visibleDays = new Set();

                    for (const day of weekDays) {
                        // const timeframe = templateDisplay.timeFrameMap[day] || {};
                        const { from: templateFrom, to: templateTo } = this.getTemplateFromToDates(template, day);
                        const { from: origFrom, to: origTo } = this.getTemplateFromToDates(origTemplate, day);

                        const activeGroup = this.dayTemplateGroupActiveMap[dateToYYYYMMDD(templateFrom)];
                        const sGroup = this.selectedGroup;

                        if (sGroup && sGroup.id !== template.groupId) continue;
                        if (activeGroup && activeGroup !== template.groupId && this.displayOnlyActiveTemplates) continue;
                        if (this.dayFrom && origTo <= this.dayFrom) continue;
                        if (this.dayTo && origFrom >= this.dayTo) continue;

                        if (template.isRecommendation) continue;

                        // TODO: Create computed map for this
                        let skipTemplate = false;
                        let overriddenByEvent = false;
                        let eventWillBeRemoved = false;

                        for (const event of events) {
                            if (event.id === template.eventId && event.shiftCreationMode !== 2) continue;

                            let { from: eventFrom, to: eventTo } = this.getEventFromToDates(event);

                            if (eventTo <= eventFrom) {
                                const newTo = new Date(eventFrom);
                                newTo.setDate(eventFrom.getDate() + 1);
                                newTo.setHours(eventTo.getHours(), eventTo.getMinutes());
                                eventTo = newTo;
                            }

                            const eventFromDay = set(eventFrom, { hours: 0, minutes: 0, seconds: 0, milliseconds: 0 });
                            const eventToDay = set(eventTo, { hours: 24, minutes: 0, seconds: 0, milliseconds: 0 });
                            if (templateFrom >= eventFromDay && templateFrom < eventToDay) {
                                if (event.shiftCreationMode === 2 && templateFrom >= eventFrom && templateFrom < eventTo) {
                                    // if (this.currentWeekEventMap[event.id] && this.currentWeekEventMap[event.id].shiftCreationMode === 2 && !this.editMode)
                                    //     skipTemplate = true;
                                    // else {
                                    //     overriddenByEvent = true;
                                    // }
                                    skipTemplate = true;
                                    break;
                                }
                                if (
                                    event.shiftCreationMode === 0 && (
                                        this.viewMode === 'full-overlap' && !template.eventId &&
                                        this.viewMode !== 'full-split'
                                    )
                                ) {
                                    eventWillBeRemoved = this.removedEventMap[event.id];
                                    const templatesForEvent = this.templatesByEventId[event.id];
                                    const hasEventTemplates = templatesForEvent && templatesForEvent.length > 0;
                                    const hasEventTemplatesForDay = hasEventTemplates && templatesForEvent.find(t => t.weekDays.includes(day) && !this.removedTemplateMap[t.id] && this.getTemplateFromToDates(t, day).from <= eventTo);

                                    if (this.addedEventMap[event.id] || this.changedEventMap[event.id]) {
                                        overriddenByEvent = hasEventTemplatesForDay;
                                    } else if (!eventWillBeRemoved && hasEventTemplatesForDay) {
                                        skipTemplate = true;
                                        break;
                                    }
                                }
                            }
                        }

                        if (skipTemplate) continue;

                        const { from: colFrom, to: colTo } = this.getGridCellSpan(templateFrom, templateTo);
                        const { from: origColFrom, to: origColTo } = this.getGridCellSpan(origFrom, origTo);

                        // TODO: Check if template is valid for this or next day
                        const isValid = this.timeFrameIsValid(template, day) || (
                            this.dayModeHours > 24 && this.timeFrameIsValid(template, day + 1)
                        );
                        const isNewDay = !origTemplate.weekDays.includes(day);
                        // TODO: Improve day deletion in template
                        const isDayDeleted = origTemplate.weekDays.includes(day) && !template.weekDays.includes(day);
                        const isNewEntry = template.isNew || isNewDay;
                        let background = isNewEntry ? 'rgba(109, 141, 33, 0.5)' : null; // timeframe.background || null;
                        let color = undefined;
                        /* if (origTemplate.isRecommendation) {
                            background = '#0066cc';
                            color = '#efefef';
                        } else */
                        if (event) {
                            background = event.color;
                            color = '#404040';
                        }
                        if (!isValid && !this.editMode) continue;
                        if (!this.optimizationMode && isDayDeleted) continue;

                        if (isDeleted || !isValid || overriddenByEvent || isDayDeleted)
                            background = 'repeating-linear-gradient(45deg, #FF00008A, #FF00008A, 10px, #FF00006A 10px, #FF00006A 20px)';
                        // const color = '#404040';

                        const wasResized = origColFrom !== colFrom || origColTo !== colTo;

                        if (wasResized && !isNewDay) {
                            // Push the original cell
                            let texttooltip = this.currentWeekShiftTemplateMap[template.id] && this.currentWeekShiftTemplateMap[template.id].description != null && !this.canEdit ?  this.currentWeekShiftTemplateMap[template.id].description : null;

                            cells.push({
                                key: `template-${template.id}-orig-day-${day}`,
                                colFrom: origColFrom,
                                colTo: origColTo,
                                row,
                                labelLeft: '',
                                labelRight: '',
                                texttooltip: texttooltip,
                                tooltip:  () => this.currentWeekShiftTemplateMap[template.id].description != null && !this.canEdit ?  this.currentWeekShiftTemplateMap[template.id].description : null,
                                style: {
                                    background,
                                    color,
                                    borderLeft: '2px dashed var(--contrast-3)',
                                    borderRight: '2px dashed var(--contrast-3)',
                                },
                                classList: hasRecommendation ? [ 'template-cell--half' ] : [],
                                resizable: false,
                                template,
                                day,
                                collapsed: collapsed,
                            });
                        }

                        let texttooltip = this.currentWeekShiftTemplateMap[template.id] && this.currentWeekShiftTemplateMap[template.id].description != null && !this.canEdit ?  this.currentWeekShiftTemplateMap[template.id].description : null;

                        cells.push({
                            key: `template-${template.id}-day-${day}`,
                            colFrom: colFrom,
                            colTo: colTo,
                            row,
                            labelLeft: dateToHHmm(templateFrom),
                            labelRight: dateToHHmm(templateTo),
                            tooltip:  () => this.currentWeekShiftTemplateMap[template.id].description != null && !this.canEdit ?  this.currentWeekShiftTemplateMap[template.id].description : null,
                            texttooltip: texttooltip,
                            style: {
                                background: ( wasResized && !isNewDay && !newEventTemplate )
                                    ? 'rgba(204,204,204,0.5)'
                                    : background,
                                color,
                                opacity: eventWillBeRemoved ? 0.5 : 1,
                            },
                            classList: hasRecommendation ? [ 'template-cell--half' ] : [],
                            resizable: !newEventTemplate && this.canEdit && !this.optimizationMode && !isDeleted && isValid && !eventWillBeRemoved,
                            showDeleteButton: this.canEdit && !eventWillBeRemoved && ( !event || !template.isNew ) && !isDeleted && isValid && !isDayDeleted && ( template.weekDays.length > 1 || template.eventId ),
                            template,
                            day,
                            directSave: this.directSaveMode,
                            collapsed: collapsed,
                        });



                        visibleDays.add(day);
                    }

                    if (this.displayColoredBars && !this.optimizationMode) {
                        const planShifts = this.planShiftsByTemplateId[template.id] || [];
                        for (const planShift of planShifts) {
                            if (planShift.employeeId == null) continue;

                            const psFrom = moment(planShift.from);
                            const psTo = moment(planShift.to);

                            if (!visibleDays.has(psFrom.weekday() + 1)) continue;

                            if (this.dayFrom && ( psFrom.isSameOrAfter(this.dayTo) || psTo.isSameOrBefore(this.dayFrom) ))
                                continue;

                            const { from: psColFrom, to: psColTo } = this.getGridCellSpan(psFrom, psTo);

                            const style = {};
                            const classList = [ 'template-cell--planned' ];

                            const employee = this.employeeMap[planShift.employeeId];
                            const employeeName = employee ? `${employee.firstName} ${employee.lastName}` : null;
                            const tooltip = planShift.templateId ? this.calcShiftTemplateTooltip(planShift.templateId, employeeName) : 'Mitarbeiter: ' + employeeName;

                            const day = psFrom.weekday() + 1;
                            const isDayDeleted = origTemplate.weekDays.includes(day) && !template.weekDays.includes(day);
                            if (isDayDeleted) continue;

                            /*
                                const templateFrom = moment(psFrom).hour(fromHr).minutes(fromMin);
                                const templateTo = moment(psFrom).hour(toHr).minutes(toMin);
                                if (templateTo.diff(templateFrom, 'minutes') < 0) templateTo.add(1, 'day');

                                const origTemplateFrom = moment(psFrom).hour(origFromHr).minutes(origFromMin);
                                const origTemplateTo = moment(psFrom).hour(origToHr).minutes(origToMin);
                                if (origTemplateTo.diff(origTemplateFrom, 'minutes') < 0) origTemplateTo.add(1, 'day');

                                let labelLeft = psFrom.diff(origTemplateFrom, 'minutes', false);
                                let labelRight = psTo.diff(origTemplateTo, 'minutes', false);
                                if (labelLeft === 0) labelLeft = '';
                                if (labelRight === 0) labelRight = '';
                                if (labelLeft > 0) labelLeft = `+${labelLeft}`;
                                if (labelRight > 0) labelRight = `+${labelRight}`;
                                */

                            cells.push({
                                key: `templatedisplay-${template.id}-ps-${planShift.id}`,
                                colFrom: psColFrom,
                                colTo: psColTo,
                                row,
                                labelLeft: '',
                                labelRight: '',
                                style,
                                classList,
                                resizable: false,
                                tooltip,
                                texttooltip: tooltip,
                                collapsed: collapsed,
                            });
                        }

                        const actualShifts = this.actualShiftsByTemplateId[template.id] || [];
                        for (const actualShift of actualShifts) {
                            const asFrom = moment(actualShift.from);
                            const asTo = moment(actualShift.to);

                            if (!visibleDays.has(asFrom.weekday() + 1)) continue;

                            if (this.dayFrom && ( asFrom.isSameOrAfter(this.dayTo) || asTo.isSameOrBefore(this.dayFrom) ))
                                continue;

                            const { from: asColFrom, to: asColTo } = this.getGridCellSpan(asFrom, asTo);

                            const style = {};
                            const classList = [ 'template-cell--actual' ];

                            let differenceBegin = null;
                            let differenceEnd = null;

                            const planShift = this.planShiftMap[actualShift.planshiftId];
                            const templateId = planShift ? planShift.templateId : null;

                            if (planShift) {
                                const psFrom = moment(planShift.from);
                                const psTo = moment(planShift.to);

                                differenceBegin = asFrom.diff(psFrom, 'minutes', false);
                                differenceEnd = asTo.diff(psTo, 'minutes', false);
                            }

                            const employee = this.employeeMap[actualShift.employeeId];
                            const employeeName = employee ? `${employee.firstName} ${employee.lastName}` : null;
                            const tooltip = this.calcShiftTemplateTooltip(templateId, employeeName, differenceBegin, differenceEnd); // : 'Mitarbeiter: ' + employeeName;
                            // if (labelLeft !== null || labelRight !== null) tooltip += ` // Abweichung`;
                            // if (labelLeft !== null) tooltip += ` Beginn: ${labelLeft}min`;
                            // if (labelLeft !== null && labelRight !== null) tooltip += `, `;
                            // if (labelRight !== null) tooltip += ` Ende: ${labelRight}min`;

                            const day = asFrom.weekday() + 1;
                            const isDayDeleted = origTemplate.weekDays.includes(day) && !template.weekDays.includes(day);
                            if (isDayDeleted) continue;

                            cells.push({
                                key: `templatedisplay-${template.id}-as-${actualShift.id}`,
                                colFrom: asColFrom,
                                colTo: asColTo,
                                row,
                                // labelLeft,
                                // labelRight,
                                style,
                                classList,
                                resizable: false,
                                tooltip,
                                texttooltip: tooltip,
                                collapsed: collapsed,
                            });
                        }
                    }
                }
            }

            return cells;
        },

        gridTemplateBreakCells() {
            const cells = [];
            let currentWeekEndDate = this.currentWeekEnd.toDate();
            let gridTemplateCellsMap = new Map();
            this.gridTemplateCells.forEach(({ template, day }) => {
                if (template && day) {
                    const key = `${template.id}-${day}`;
                    gridTemplateCellsMap.set(key, { template, day });
                }
            });

            for (const { spot, collapsed, id } of this.templateDisplayGroups) {
                if (id === 'events') continue;

                for (const origTemplate of this.templateSpotMap[spot.id]) {
                    let template = origTemplate;

                    const row = this.rowIndexMap[template.id];
                    if (row == null) continue;

                    if (this.addedTemplateMap[origTemplate.id]) {
                        template = this.addedTemplateMap[origTemplate.id];
                    }
                    if (this.changedTemplateMap[origTemplate.id]) {
                        template = this.changedTemplateMap[origTemplate.id];
                    }

                    const breakValue = template.breakValue;
                    if (template.autoBreak || breakValue <= 0) continue;

                    const hasRecommendation = this.optimizationMode && this.optimizationGroupTemplateMap[template.groupId + '-' + template.spotId] && this.optimizationGroupTemplateMap[template.groupId + '-' + template.spotId][template.id];

                    for (const day of template.weekDays) {
                        const { breakFrom, breakTo } = this.getTemplateFromToDates(template, day);

                        if (
                            breakFrom === null || breakFrom.getTime() > currentWeekEndDate.getTime() ||
                            ( !this.timeFrameIsValid(template, day) && !this.editMode ) ||
                            ( this.templateDaySelection !== 'W' && breakTo < this.dayFrom ) ||
                            ( this.templateDaySelection !== 'W' && breakFrom >= this.dayTo )
                        )
                            continue;

                        const templateCell = gridTemplateCellsMap.get(`${template.id}-${day}`);

                        if (!templateCell) continue;

                        const tooltip = breakValue > 60
                            ? `${Math.floor(breakValue / 60)}h ${( breakValue % 60 ).toString().padStart(2, '0')}min Pause`
                            : breakValue + 'min Pause';

                        const { from, to } = this.getGridCellSpan(breakFrom, breakTo);
                        let style="overflow: visible !important;";

                        cells.push({
                            key: `templatebreak-${template.id}-${breakValue}-day-${day}`,
                            colFrom: from,
                            colTo: to,
                            row,
                            classList: hasRecommendation ? [ 'template-cell--half' ] : [],
                            tooltip,
                            style,
                            texttooltip: tooltip,
                            collapsed: collapsed,
                        });
                    }

                }
            }
            return cells;
        },

        gridTemplateAddWeekdayCells() {
            if (!this.canEdit || this.optimizationMode) return [];

            const rowIndexes = this.rowIndexMap;

            const cells = [];
            for (const { spot, collapsed, id } of this.templateDisplayGroups) {
                if (id === 'events') continue;

                for (let template of this.templateSpotMap[spot.id]) {

                    if (template.isRecommendation) continue;

                    const row = rowIndexes[template.id];
                    if (row == null) continue;

                    if (this.addedTemplateMap[template.id]) {
                        template = this.addedTemplateMap[template.id];
                    } else if (this.changedTemplateMap[template.id]) {
                        template = this.changedTemplateMap[template.id];
                    }

                    if (template.eventId) continue;

                    let weekdays = [];
                    if (this.templateDaySelection === 'W') {
                        weekdays = new Array(7).fill(0).map((_, i) => i + 1).filter(i => !template.weekDays.includes(i));
                    } else {
                        if (!template.weekDays.includes(this.templateDaySelection))
                            weekdays.push(this.templateDaySelection);
                        if (this.hourlySelection > 24 && !template.weekDays.includes(this.templateDaySelection + 1))
                            weekdays.push(this.templateDaySelection + 1);
                        if (!template.weekDays.includes(this.templateDaySelection - 1))
                            weekdays.push(this.templateDaySelection - 1);
                    }

                    const events = Object.values({
                        ...this.currentWeekEventMap,
                        ...this.addedEventMap,
                        ...this.changedEventMap,
                    });

                    for (let day of weekdays) {
                        const { from: templateFrom, to: templateTo } = this.getTemplateFromToDates(template, day);
                        let { from: colFrom, to: colTo } = this.getGridCellSpan(templateFrom, templateTo);

                        let skipTemplate = false;
                        for (const event of events) {
                            const { from: eventFrom, to: eventTo } = this.getEventFromToDates(event);
                            if (templateFrom >= eventFrom && templateFrom < eventTo) {
                                if (
                                    event.shiftCreationMode === 2 ||
                                    event.shiftCreationMode === 0 && (
                                        this.viewMode === 'full-overlap' && !template.eventId &&
                                        this.viewMode !== 'full-split'
                                    )
                                ) {
                                    const eventWillBeRemoved = this.removedEventMap[event.id];
                                    if (!eventWillBeRemoved) {
                                        const templatesForEvent = this.templatesByEventId[event.id];
                                        const hasEventTemplates = templatesForEvent && templatesForEvent.length > 0;
                                        if (hasEventTemplates && templatesForEvent.find(t => t.weekDays.includes(day) && this.getTemplateFromToDates(t, day).from <= eventTo)) {
                                            skipTemplate = true;
                                            break;
                                        }
                                    }
                                }
                            }
                        }

                        // if (this.templateDaySelection !== 'W' && day === 0 && templateTo > this.dayFrom) {
                        //     colFrom = X_OFFSET;
                        //     colTo = Math.round(templateTo.getHours() * DAYMODE_HOUR_INTERVAL + templateTo.getMinutes() / DAYMODE_MIN_INTERVAL + X_OFFSET);
                        //     console.log('templateFrom', templateFrom, 'templateTo', templateTo, 'colFrom', colFrom, 'colTo', colTo);
                        // }

                        if (skipTemplate || !colFrom || !colTo || colFrom < X_OFFSET || colTo < X_OFFSET || ( this.templateDaySelection !== 'W' && colFrom >= this.gridColumns )) continue;

                        cells.push({
                            key: `template-${template.id}-add-weekday-${day}`,
                            colFrom,
                            colTo,
                            row,
                            labelLeft: dateToHHmm(templateFrom),
                            labelRight: dateToHHmm(templateTo),
                            weekday: day,
                            template,
                            directSave: this.directSaveMode,
                            collapsed: collapsed,
                        });
                    }
                }
            }

            return cells;
        },
        planningState() {
            let view = this;

            return view.shiftDesigns.map(d => ({
                id: d.id,
                creationDate: view.$helpers.getMomentFromStringTZ(d.creationDate)
                    .format('DD.MM.YYYY HH:mm'),
                from: view.$helpers.getMomentFromStringTZ(d.from)
                    .format('DD.MM.YYYY HH:mm'),
                to: view.$helpers.getMomentFromStringTZ(d.to)
                    .format('DD.MM.YYYY HH:mm'),
                status: d.state > 2 ? this.$t('global.notifications.error') : (d.state > 1 ? this.$t('workBalance.payroll.finished') : 'In Bearbeitung'),
                state: d.state
            }));
        }
    },
    created() {
        window.templateview = this;
        // let view = this;
        // window.addEventListener('resize', function(){requestAnimationFrame(function(){view.$forceUpdate()})});
        // if (this.daySelected) {
        //     this.templateDaySelection = this.daySelected.weekday() + 1;
        // }
    },

    mounted() {
        this.embedded = this.$route.name != 'planning';

        let currentDate = this.$helpers.getMomentTZ();
        if (currentDate.year() == this.rosterFromTo.year && currentDate.month() == this.rosterFromTo.month) {
            this.currentYear = currentDate.isoWeekYear();
            this.currentSelectedWeek = currentDate.isoWeek();
        } else {
            let t = this.$helpers.getMomentFromStringTZ(this.rosterFromTo.from).date(1).month(this.rosterFromTo.month).year(this.rosterFromTo.year);

            this.currentYear = t.isoWeekYear();
            this.currentSelectedWeek = t.isoWeek();

        }
        this.useVlhForForecast = this.enableVlh && !this.storeSettings.enableMeo;
        console.log("useVLH: "+this.useVlhForForecast)
        this.refresh();
        window.addEventListener('beforeunload', this.befeoreUnload);
    },
    updated: function () {
        this.$nextTick(function () {
            if (!this.refreshing) {
                console.log('updated view')
                this.loading = false;
            }
        })
    },
    beforeDestroy() {
        window.removeEventListener('beforeunload', this.befeoreUnload);

        if (this.$refs.datepickerarray && this.$refs.datepickerarray.$el) $(this.$refs.datepickerarray.$el).remove();
        if (this.$refs.dropdowncontainer && this.$refs.dropdowncontainer.$el) $(this.$refs.dropdowncontainer.$el).remove();
    },
    methods: {
        skipTemplate(events, template, templateFrom, templateTo, i) {
            let skipTemplate = false;
            template.isRealCarryOver = true;

            for (const event of events) {
                let { from: eventFrom, to: eventTo } = this.getEventFromToDates(event);

                if (template.carryOver) {
                    if (templateFrom < eventFrom) {
                        template.isRealCarryOver = true;
                        continue;
                    }
                    // creating events duplicates shifts, we want to ignore carry-over from those
                    template.isRealCarryOver = event.id !== template.eventId;
                    break;
                }

                if (event.id === template.eventId && event.shiftCreationMode !== 2) continue;

                if (this.startsDuringEvent(templateFrom, eventFrom, eventTo) && !this.removedEventMap[event.id]) {
                    if (event.shiftCreationMode === 2) {
                        skipTemplate = true;
                        break;
                    }
                    else if (event.shiftCreationMode === 0 && this.viewMode === 'full-overlap' && !template.eventId) {
                        const templatesForEvent = this.templatesByEventId[event.id];
                        if (templatesForEvent && templatesForEvent.length > 0 && templatesForEvent.find(t => t.weekDays.includes(i) && !this.removedTemplateMap[t.id] && this.getTemplateFromToDates(t, i).from <= eventTo)) {
                            skipTemplate = true;
                            break;
                        }
                    }
                }
            }

            return skipTemplate;
        },
        startsDuringEvent(templateFrom, eventFrom, eventTo) {
            return templateFrom >= set(eventFrom, { hours: 0, minutes: 0, seconds: 0, milliseconds: 0 }) && templateFrom < set(eventTo, { hours: 24, minutes: 0, seconds: 0, milliseconds: 0 });
        },
        getProdBarStyle(headerCell) {
            let background = '';

            if (headerCell.actual !== ' - ') {
                if (headerCell.planned !== ' - ') {
                    let percentage = Math.round((Number(headerCell.actual) / (Number(headerCell.actual) + Number(headerCell.planned))) * 100);

                    background = 'linear-gradient(to right, var(--md) ' + percentage + '%, #e51c50 ' + percentage + '% 100%)';
                } else {
                    background = 'var(--md)';
                }
             } else if (headerCell.planned !== ' - ') {
                background = '#e51c50'
            }

            return {background: background}
        },
        translateDayShort(val) {
            return this.daysShort[val] || "In";
        },
        async updateAllNoValidTo(){
            const promiseQueue = [];
            let totalCount = 0;
            let updatedCount = 0;
            let group = this.shiftTemplateGroupMap[this.selectedGroup.id];
            let templates = this.selectedGroupTemplateMap;
            for(const template of Object.values(templates)){
                if(template.validTo != null){
                    template.validTo = null;
                    promiseQueue.push(() => this.updateShiftTemplate(template, false))
                    updatedCount++;
                }
                totalCount++;
            }

            try {
                this.loading = true;
                this.loadingPercent = '0%';
                this.loadingPercent = 0;
                let processedCount = 0;
                for await (const result of this.$helpers.workPromiseQueue(promiseQueue)) {
                    if (result.error) {
                        const message = typeof result.error === 'string' ? result.error : result.error.message;
                        this.$helpers.error(this.$t('global.notifications.error'), message);
                        continue;
                    }
                    if (result.finished) {
                        this.loadingPercent = 99;
                        continue;
                    }
                    this.loadingPercent = Math.round(result.progress * 99);
                    this.loadingText = this.loadingPercent + '%';
                }

                this.loadingPercent = 100;
                this.loadingText = this.loadingPercent + '%';
                console.log("updateAllNoValidTo")
                await this.RefreshShiftTemplates();
                // this.loadingPercent = null;
                // this.loadingText = 'Fertig!';

                this.optimizationMode = false;
                this.optimizationGroup = null;
                this.editMode = false;
                this.$store.commit("setAllowContentSwitch", {allowContentSwitch: true});

                this.addedTemplateMap = {};
                this.changedTemplateMap = {};
                this.removedTemplateMap = {};

                this.addedEventMap = {};
                this.changedEventMap = {};
                this.removedEventMap = {};
            } catch (e) {
                console.error(e);
            } finally {
                this.loading = false;
            }
        },
        refresh(groupId = null, force = false) {

            if (this.$store.state.jwt != undefined && this.$store.state.storeId != undefined && (!this.loading || force)) {
                console.log("refresh")
                this.directSaveMode = localStorage.getItem('templateView.directSaveMode') === 'true';
                this.loading = true;
                this.refreshing = true;
                this.loadingText = this.$t('shift.template.loadShiftPlan');
                let view = this;
                view.$store.commit('setAllowContentSwitch', { allowContentSwitch: false });
                let employeePayload = { ids: [] };
                Promise.all([ view.$helpers.GetUserUnitSort(true), view.$helpers.GetStoreSettings(), view.$helpers.GetEmployeeTypeMap(),
                    view.$helpers.GetEmployeeMap(), view.$helpers.GetSkillMap(), view.$helpers.GetBusinessUnitMap(),
                    view.$helpers.GetSpotMap(), view.$helpers.GetPlanningEmployeeThresholdMap(), view.$helpers.GetProjectMap(),
                    view.$helpers.GetLocationMap(), view.$helpers.GetShiftTemplateGroupMap(), view.$helpers.GetShiftTemplateGroupFrameMap(false, false, this.currentYear)
                     ])
                .then(results => {
                        Object.values(this.employeeMap).filter(e => e.active).forEach(e => {
                            employeePayload.ids.push(e.id);
                        })
                        return Promise.all([ view.$helpers.GetEmployeeAvailabilityMapForEmployees(employeePayload), view.$helpers.GetEmployeeSkillMapForEmployees(employeePayload), view.$helpers.GetMlCostCenterMap() ]);
                }).then(response => {
                    let groups = Object.values(view.shiftTemplateGroupMap);
                    let mainGroup = null;
                    if (groupId != null) {
                        mainGroup = groups.find(group => group.id == groupId);
                    } else {

                        mainGroup = groups.find(group => view.validGroups.includes(group.id));
                    }
                    if (mainGroup == null) {
                        mainGroup = groups.find(group => group.immutable == true);
                    }
                    if (mainGroup == null && groups.length > 0) {
                        mainGroup = groups[0];
                    }
                    view.selectedGroup = mainGroup;

                    return view.loadStoreEvents();
                }).then(response => {
                    view.updateStoreEvents(response.data);
                    return view.loadPlanningIndicatorTypes();
                }).then(response => {
                    view.planningIndicatorTypeMap = {};
                    let typeId = null;
                    response.data.forEach(t => {
                        view.planningIndicatorTypeMap[t.id] = t;
                        if (t.isDefault) {
                            typeId = t.id;
                        }
                    });
                    if (typeId == null) {
                        Object.values(view.planningIndicatorTypeMap).forEach(it => {
                            if ([ 'umsatz', 'sales', 'umsätze' ].includes(it.name.toLowerCase()) || [ 'eur, euro, €' ].includes(it.unit.toLowerCase)) {
                                typeId = it.id;
                            }
                        });
                    }
                    if (Object.values(view.planningIndicatorTypeMap).length > 0 && typeId == null) {
                        typeId = Object.values(view.planningIndicatorTypeMap)[0].id;
                    }
                    view.displayIndicatorType = typeId;

                    return view.RefreshPlanningIndicator()

                }).then(result => {
                    return this.RefreshVlhThresholdCurve();

                }).then(result => {

                    return view.RefreshShiftTemplates();
                }).then(result => {

                    view.groupCollapsedStates = {};

                    view.mainSpotsForUnit = {};
                    view.spotVisibilityList = [];
                    Object.values(view.spotMap).forEach(s => {
                        if (s.isMain) {
                            view.mainSpotsForUnit[s.unitId] = s.id;
                        }
                        view.spotVisibilityList.push(s);
                    });

                    const _ = view.templateDisplayMap;

                    view.RefreshDisplayedTimeframes();
                    view.loading = false;
                    view.refreshing = false;
                    view.$store.commit('setAllowContentSwitch', { allowContentSwitch: true });
                    requestAnimationFrame(function () {
                        view.$forceUpdate();
                    });
                }).catch(error => {
                    console.log(error);
                    view.loading = false;
                    view.refreshing = false;
                    view.$store.commit('setAllowContentSwitch', { allowContentSwitch: true });
                    view.$helpers.error(view.$t('global.notifications.error'), view.$t('global.notifications.dataLoadingError'));
                });

            }
        },

        closeShiftDesignModal()  {
            this.showShiftDesignModal = false;
        },
        closeShiftDesignDetails()  {
            this.showShiftDesignDetails = false;
        },
        OpenMenuDetails($event, state) {
            this.$helpers.OpenPopup({
                type: 'burgermenu',
                componentState: state,
                listStyle: true,
                noScroll: true,
                pointerDirection: 0,
                elRelative: $event.currentTarget,
                pointerOffset: -15,
            });
        },
        RefreshShiftTemplates(loading = false) {
            let view = this;
            let eventPayload = { ids: [] };
            let validFrom = this.currentWeekStart;
            let validTo = this.currentWeekStart.clone().add(7, 'days');

            Object.values(this.storeEventMap).forEach(event => {
                let eventFrom = view.$helpers.getMomentFromStringTZ(event.from);
                let eventTo = view.$helpers.getMomentFromStringTZ(event.to);
                if (eventFrom < validTo && eventTo > validFrom) {
                    eventPayload.ids.push(event.id)
                }

            });

            return view.loadShiftTemplatesPagedCompletely(eventPayload)
                .then(response => {
                    view.updateShiftTemplates(response);
                    let templatePayload = { ids: [] };
                    /*
                    for (let i = 0; i < response.length; i++) {
                        templatePayload.ids.push(response[i].id);
                    } */
                    return view.loadShiftTemplateSkills(templatePayload);
                }).then(response => {
                    view.shiftTemplateSkills = response;
                    view.shiftTemplateSkillsMap = {};
                    view.shiftTemplateSkills.forEach(ts => view.shiftTemplateSkillsMap[ts.id] = ts);

                    return this.loadShiftMaps()
                    .catch(e => console.error(e))
                    .finally(() => {
                        if (loading) {

                            view.loading = false;
                        }
                    });
                })
        },
        // loads shiftMaps to color the bars green/orange/gray and load correct data for the chart
        async loadShiftMaps() {
            await this.$helpers.GetPlanShiftMap();
            await this.$helpers.GetActualShiftMap();
            await this.$helpers.GetActualBreakMap();
        },
        toggleVlh(){
            let view = this;
            if(this.useVlhForForecast){
                this.$set(this, 'useVlhForForecast', false);
            } else {
                if(!this.loadedVlh && !this.loadingVlh){
                    this.loadingVlh = true;
                    this.loadVlhThresholdCurve()
                    .then(result => {
                        if(result){
                            view.$set(this, 'useVlhForForecast', true);
                        }

                        view.loadingVlh = false;
                    });
                } else if(this.loadedVlh){
                    this.$set(this, 'useVlhForForecast', true);
                }
            }
        },
        RefreshVlhThresholdCurve(){
            if(this.useVlhForForecast && this.displayIndicatorType != null){
                return this.loadVlhThresholdCurve();
            } else {
                return new Promise((resolve, reject) => {
                    resolve(true);
                });
            }
        },
        loadVlhThresholdCurve(){

            let view = this;
            let from = this.currentWeekStart;
            let to = this.currentWeekStart.clone().add(8, 'days');

            return new Promise((resolve, reject) => {
                if(this.vlhDate == null || this.vlhDate != from.format('YYYY-MM-DD')){
                     console.log("loadVlhThresholdCurve")
                    view.$helpers.GetVlhThresholdCurve(from, to, this.displayIndicatorType, true, 'quarterhourly')
                    .then(responseA => {

                        view.$helpers.GetVlhThresholdCurve(from, to, this.displayIndicatorType, false, 'quarterhourly')
                        .then(responseB => {

                            view.vlhPlannedCurve = responseA.data;
                            view.vlhActualCurve = responseB.data;
                            view.calculateVlhRequirementMap();
                            view.loadedVlh = true;
                            view.vlhDate = from.format('YYYY-MM-DD');
                            resolve(true);
                        }).catch(error => {

                            view.$helpers.error(view.$t('global.notifications.error'), view.$t('shift.template.loadVlhError'));
                            resolve(false);
                        });
                    }).catch(error => {

                        view.$helpers.error(view.$t('global.notifications.error'), view.$t('shift.template.loadVlhError'));
                        resolve(false);
                    });
                } else {
                    view.loadedVlh = true;
                    resolve(true);
                }

            });

        },
        calculateVlhRequirementMap(){
            let start = this.currentWeekStart.clone();
            let end = this.currentWeekEnd.clone().add(1, 'days');

            let vlhSpotPlannedReqMap = {};
            let vlhSpotPlannedDayMap = {};



            this.vlhPlannedCurve.forEach(req => {
                if(!vlhSpotPlannedReqMap[req.spotId]){
                    vlhSpotPlannedReqMap[req.spotId] = [];
                }
                if(!vlhSpotPlannedDayMap[req.spotId]){
                    vlhSpotPlannedDayMap[req.spotId] = {};
                }
                let from = this.$helpers.getMomentFromStringTZ(req.date);
                let dayKey = from.format('YYYY-MM-DD');
                let hourKey = from.format('HH');
                let quarterKey = from.format('mm');

                if(!vlhSpotPlannedDayMap[req.spotId][dayKey]){
                    vlhSpotPlannedDayMap[req.spotId][dayKey] = {daySum: 0, hourMap: {}};
                }
                if(!vlhSpotPlannedDayMap[req.spotId][dayKey].hourMap[hourKey]){
                    vlhSpotPlannedDayMap[req.spotId][dayKey].hourMap[hourKey] = {hourSum: 0, quarterMap: {}};
                }
                if(!vlhSpotPlannedDayMap[req.spotId][dayKey].hourMap[hourKey].quarterMap[quarterKey]){
                    vlhSpotPlannedDayMap[req.spotId][dayKey].hourMap[hourKey].quarterMap[quarterKey] = {quarterSum: 0};
                }
                vlhSpotPlannedDayMap[req.spotId][dayKey].daySum += req.workTime;
                vlhSpotPlannedDayMap[req.spotId][dayKey].hourMap[hourKey].hourSum += req.workTime;
                vlhSpotPlannedDayMap[req.spotId][dayKey].hourMap[hourKey].quarterMap[quarterKey].quarterSum += req.workTime;

                vlhSpotPlannedReqMap[req.spotId].push({from: from, workTime: req.workTime, spotId: req.spotId});
            });

            let vlhSpotActualReqMap = {};
            let vlhSpotActualDayMap = {};
            this.vlhActualCurve.forEach(req => {
                if(!vlhSpotActualReqMap[req.spotId]){
                    vlhSpotActualReqMap[req.spotId] = [];
                }
                if(!vlhSpotActualDayMap[req.spotId]){
                    vlhSpotActualDayMap[req.spotId] = {};
                }
                let from = this.$helpers.getMomentFromStringTZ(req.date);
                let dayKey = from.format('YYYY-MM-DD');
                let hourKey = from.format('HH');
                let quarterKey = from.format('mm');

                if(!vlhSpotActualDayMap[req.spotId][dayKey]){
                    vlhSpotActualDayMap[req.spotId][dayKey] = {daySum: 0, hourMap: {}};
                }
                if(!vlhSpotActualDayMap[req.spotId][dayKey].hourMap[hourKey]){
                    vlhSpotActualDayMap[req.spotId][dayKey].hourMap[hourKey] = {hourSum: 0, quarterMap: {}};
                }
                if(!vlhSpotActualDayMap[req.spotId][dayKey].hourMap[hourKey].quarterMap[quarterKey]){
                    vlhSpotActualDayMap[req.spotId][dayKey].hourMap[hourKey].quarterMap[quarterKey] = {quarterSum: 0};
                }
                vlhSpotActualDayMap[req.spotId][dayKey].daySum += req.workTime;
                vlhSpotActualDayMap[req.spotId][dayKey].hourMap[hourKey].hourSum += req.workTime;
                vlhSpotActualDayMap[req.spotId][dayKey].hourMap[hourKey].quarterMap[quarterKey].quarterSum += req.workTime;

                vlhSpotActualReqMap[req.spotId].push({from: from, workTime: req.workTime, spotId: req.spotId});
            });

            let vlhRequirementMap = {};
            while (start < end) {
                let dayKey = start.format('YYYY-MM-DD');
                let dayEntry = {
                    hourMap: {},
                    spotRequirements: {}
                }
                let dayEnd = start.clone().add(1, 'days');
                Object.values(this.spotMap).forEach(s => {

                    let reqPlanned = vlhSpotPlannedDayMap[s.id] != null && vlhSpotPlannedDayMap[s.id][dayKey] != null ? vlhSpotPlannedDayMap[s.id][dayKey].daySum : 0;
                    let reqActual = vlhSpotActualDayMap[s.id] != null && vlhSpotActualDayMap[s.id][dayKey] != null ? vlhSpotActualDayMap[s.id][dayKey].daySum : 0;
                    let hasReq = vlhSpotActualReqMap[s.id] != null || vlhSpotPlannedReqMap[s.id] != null;

                    dayEntry.spotRequirements[s.id] = {req: this.planningType == 0 ? reqPlanned : reqActual,  hasReq: hasReq};

                });

                vlhRequirementMap[dayKey] = dayEntry;

                let hourStart = start.clone();
                let hourFin = hourStart.clone().add(24, 'hours');
                while (hourStart < hourFin) {
                    let hourKey = hourStart.format('HH');
                    let hourEntry = {
                        quarterMap: {},
                        spotRequirements: {}
                    }
                    Object.values(this.spotMap).forEach(s => {
                        let reqPlanned = vlhSpotPlannedDayMap[s.id] != null && vlhSpotPlannedDayMap[s.id][dayKey] != null && vlhSpotPlannedDayMap[s.id][dayKey].hourMap[hourKey] != null ? vlhSpotPlannedDayMap[s.id][dayKey].hourMap[hourKey].hourSum : 0;
                        let reqActual = vlhSpotActualDayMap[s.id] != null && vlhSpotActualDayMap[s.id][dayKey] != null && vlhSpotActualDayMap[s.id][dayKey].hourMap[hourKey] != null ? vlhSpotActualDayMap[s.id][dayKey].hourMap[hourKey].hourSum : 0;
                        let hasReq = vlhSpotActualReqMap[s.id] != null || vlhSpotPlannedReqMap[s.id] != null;


                        hourEntry.spotRequirements[s.id] = {req: this.planningType == 0 ? reqPlanned : reqActual, hasReq: hasReq};
                    });
                    dayEntry.hourMap[hourKey] = hourEntry;

                    let quarterStart = hourStart.clone();
                    let quarterFin = quarterStart.clone().add(1, 'hours');
                    while (quarterStart < quarterFin) {
                        let quarterKey = quarterStart.format('mm');
                        let quarterEntry = {
                            spotRequirements: {}
                        }
                        Object.values(this.spotMap).forEach(s => {
                            let reqPlanned = vlhSpotPlannedDayMap[s.id] != null && vlhSpotPlannedDayMap[s.id][dayKey] != null && vlhSpotPlannedDayMap[s.id][dayKey].hourMap[hourKey] != null && vlhSpotPlannedDayMap[s.id][dayKey].hourMap[hourKey].quarterMap[quarterKey] != null ? vlhSpotPlannedDayMap[s.id][dayKey].hourMap[hourKey].quarterMap[quarterKey].quarterSum : 0;
                            let reqActual = vlhSpotActualDayMap[s.id] != null && vlhSpotActualDayMap[s.id][dayKey] != null && vlhSpotActualDayMap[s.id][dayKey].hourMap[hourKey] != null && vlhSpotActualDayMap[s.id][dayKey].hourMap[hourKey].quarterMap[quarterKey] != null ? vlhSpotActualDayMap[s.id][dayKey].hourMap[hourKey].quarterMap[quarterKey].quarterSum : 0;
                            let hasReq = vlhSpotActualReqMap[s.id] != null || vlhSpotPlannedReqMap[s.id] != null;

                            quarterEntry.spotRequirements[s.id] = {req: this.planningType == 0 ? reqPlanned : reqActual, hasReq: hasReq};
                        });
                        hourEntry.quarterMap[quarterKey] = quarterEntry;
                        quarterStart.add(15, 'minutes');
                    }
                    hourStart.add(1, 'hours');
                }
                start.add(1, 'days');
            }

            this.vlhRequirementMap = vlhRequirementMap;
        },
        openTemplateButtonMenu($event, group, template, directSave = false) {
            let view = this;
            view.$helpers.OpenButtonContainer([

                {
                    color: '#e5c420', icon: 'fa-edit', label: this.$t('global.ui.edit'), click: () => {
                        view.openEditShiftTemplatePopupCow($event, group, template, directSave);
                    }
                },
                {
                    color: '#eb7d34', icon: 'fa-pencil-alt', label: this.$t('shift.template.validity'), click: () => {
                        view.openEditShiftTemplatePopup($event, group, template, directSave);
                    }
                },
                {
                    color: '#6d8d23', icon: 'fa-copy', label: this.$t('shift.template.duplicate'), click: () => {
                        view.duplicateShiftTemplate(template, directSave);
                    }
                },
                {
                    color: '#e51650', icon: 'fa-trash', label: this.$t('global.ui.delete'), click: () => {
                        view.openDeleteShiftTemplatePopup($event, template.id, directSave);
                    }
                },

            ], { elRelative: $event.currentTarget, offsetx: 5, offsety: -10, closeOnSelect: true })

        },
        openPlannedBudgetPopup() {
            let view = this;
            let employeeLevelItems = [
                { label: this.$t('workBalance.payroll.crew'), value: 1, selected: true },
                { label: this.$t('shift.template.management'), value: 2 },
            ]
            let dataEntries = [];
            dataEntries.push({
                label: this.$tc('store.structure.employeeLevel'), key: 'employeeLevels', type: 'dropdownsearch', hidden: false,
                dropdownSearchState: {
                    selectMultiple: true, styleLight: true,
                    placeholder: this.$t('shift.template.selectEmployeeLevel'),
                    items: employeeLevelItems
                }

            });


            view.$helpers.OpenPopup({
                type: 'datafill',
                componentState: {
                    entries: dataEntries,
                    selectCallback: function (data) {
                        view.OpenPlannedBudgetInfoPopup(data.employeeLevels);
                    }
                },
                header: this.$t('global.ui.selectEmployee'),
                remainAfterSelect: false,
                width: "600px",
                expanded: true,
                centered: true
            });
        },
        OpenPlannedBudgetInfoPopup(employeeLevels = [ 1 ]) {
            let view = this;
            let groupId = view.selectedGroup != null ? view.selectedGroup.id : null;
            Promise.all([ view.$helpers.GetShiftTemplateMap(groupId), view.$helpers.GetCurrentMonthEmployeeContractMap(false, false, this.storeSettings.hideDeactivatedEmployees), view.$helpers.GetAllowedContractMap(), view.$helpers.GetEmployeePreferenceMap() ])
                .then(results => {
                    let templateMap = {};
                    Object.values(results[0]).forEach(t => {
                        if (t.groupId == view.selectedGroup.id && employeeLevels.includes(view.spotMap[t.spotId].employeeLevel)) {
                            templateMap[t.id] = t;
                        }
                    })
                    let contractMap = results[1];
                    let contractMomentMap = {};
                    let employeeIdContractMap = {}
                    let contracts = Object.values(contractMap).filter(c => {
                        let e = view.employeeMap[c.employeeId]
                        let el = e != null ? ( e.spotId != null ? view.spotMap[e.spotId].employeeLevel : 1 ) : 1;
                        let filterOk = employeeLevels.includes(el);
                        if (filterOk) {
                            let from = view.$helpers.getMomentFromStringTZ(c.from);
                            let to = c.to != null ? view.$helpers.getMomentFromStringTZ(c.to) : null;
                            contractMomentMap[c.id] = { fromDate: from, toDate: to };
                            if (!employeeIdContractMap[c.employeeId]) {
                                employeeIdContractMap[c.employeeId] = [];
                            }
                            employeeIdContractMap[c.employeeId].push(c);
                        }
                        return filterOk;
                    })

                    let allowedContractMap = results[2];

                    let employeePrefMap = results[3];
                    let prefMomentMap = {};
                    let prefs = Object.values(employeePrefMap).filter(p => {
                        let e = view.employeeMap[p.employeeId]
                        let el = e != null ? ( e.spotId != null ? view.spotMap[e.spotId].employeeLevel : 1 ) : 1;
                        let filterOk = employeeLevels.includes(el) && p.state == 1;
                        if (filterOk) {
                            let from = view.$helpers.getMomentFromStringTZ(p.from);
                            let to = p.to != null ? view.$helpers.getMomentFromStringTZ(p.to) : null;
                            prefMomentMap[p.id] = { fromDate: from, toDate: to };

                        }
                        return filterOk;

                    })

                    let from = view.$helpers.getMomentFromValuesTZ(view.rosterFromTo.year, view.rosterFromTo.month, 1);
                    let to = from.clone().endOf('month').add(1, 'days').startOf('day');


                    let plannedPrefTime = 0;
                    prefs.forEach(p => {
                        plannedPrefTime += view.$helpers.CalculateEmployeePreferenceWorkTime(p, prefMomentMap, employeeIdContractMap, contractMomentMap, allowedContractMap, from, to, view.storeSettings)
                    })
                    plannedPrefTime = plannedPrefTime / 60.0;


                    let month = from.format('MMMM')
                    let stats = view.GetPlannedStatsFromTemplates(templateMap, from, to)
                    let shiftCount = stats.shiftCount;
                    let plannedTime = stats.plannedTime / 60.0;
                    let avgShiftTime = shiftCount > 0 ? ( plannedTime / shiftCount ) : 0;

                    let holidays = Object.values(this.storeEventMap).filter(h => h.active && h.federalHoliday).map(h => h.fromMoment);
                    let includingContractHolidayMoments = Object.values(this.storeEventMap).filter(h => h.active && (h.federalHoliday || h.contractHoliday)).map(h => h.fromMoment);
                        
                    let workTimeRegular = this.$helpers.GetWorktimeForRangeWithinMonthFromContracts(from, from, to, contracts, allowedContractMap, view.storeSettings, view.storeSettings.workTimeMode, view.storeSettings.exitFactoring, null, holidays, includingContractHolidayMoments) / 60.0
                    let diff = plannedTime - workTimeRegular;

                    let employeeDiffCount = Math.ceil(diff / 173.0);
                    if (employeeDiffCount < -1) {
                        employeeDiffCount = employeeDiffCount * -1 + ' zu viel'
                    } else {
                        employeeDiffCount = employeeDiffCount + ' zu wenig'
                    }
                    let shiftDiffCount = Math.floor(diff / 8.0);
                    if (shiftDiffCount < -1) {
                        shiftDiffCount = shiftDiffCount * -1 + ' zu wenig'
                    } else {
                        shiftDiffCount = shiftDiffCount + ' zu viel'
                    }
                    let percentage = 0;
                    if (workTimeRegular > 0) {
                        percentage = plannedTime / workTimeRegular;
                    }
                    let avgHourlyWage = this.$helpers.GetAvgHourlyWageFromContractsForMonth(from, to, contracts, allowedContractMap) / 100.0;

                    let dataEntries = [
                        {
                            label: this.$t('shift.template.usePlannedAbsences'),
                            key: 'usePlannedPrefs',
                            type: "bool",
                            hidden: false,
                            value: false
                        },
                        {
                            label: this.$tc('global.general.absence', 1) + ' %', key: 'unavailablePercentage', hidden: false, value: 0,
                            slider: {
                                hideMin: true,
                                hideMax: true,
                                min: 0,
                                max: 100,
                                value: 0,
                                step: 1,
                                inputControl: true,
                                sliderInput: (x) => {return x},
                            },
                        },
                        {
                            label: this.$t('shift.template.targetToContract'),
                            key: 'plannedWorkTime',
                            hidden: false,
                            type: 'output',
                            value: workTimeRegular.toFixed(2),
                            disabled: true,

                            computed: (datafill, lastVal, entryBeforeUpdate) => {
                                let unavailablePercentage = datafill.entries.find(i => i.key == 'unavailablePercentage').value;
                                let avgShiftTime = datafill.entries.find(i => i.key == 'avgShiftTime').value;
                                let usePlannedPrefs = datafill.entries.find(i => i.key == 'usePlannedPrefs').value;
                                let remainingWorkTimeRegular = workTimeRegular * ( 100 - unavailablePercentage ) / 100;
                                if (usePlannedPrefs) {
                                    remainingWorkTimeRegular = Math.max(0, remainingWorkTimeRegular - plannedPrefTime);
                                }
                                let shiftCount = avgShiftTime > 0 ? Math.ceil(remainingWorkTimeRegular / avgShiftTime) : 0

                                entryBeforeUpdate.value = remainingWorkTimeRegular.toFixed(2) + ' / ' + shiftCount + ' Ø Schichten';
                                return entryBeforeUpdate;
                            }

                        },
                        {
                            label: this.$t('shift.template.plannedAbsences'),
                            key: 'plannedUnavailableTime',
                            hidden: false,
                            type: 'output',
                            value: 0.00,
                            disabled: true,

                            computed: (datafill, lastVal, entryBeforeUpdate) => {
                                let unavailablePercentage = datafill.entries.find(i => i.key == 'unavailablePercentage').value;
                                let avgShiftTime = datafill.entries.find(i => i.key == 'avgShiftTime').value;
                                let usePlannedPrefs = datafill.entries.find(i => i.key == 'usePlannedPrefs').value;
                                let remainingWorkTimeRegular = workTimeRegular * unavailablePercentage / 100.0;
                                if (usePlannedPrefs) {
                                    remainingWorkTimeRegular = remainingWorkTimeRegular + plannedPrefTime;
                                }
                                let shiftCount = avgShiftTime > 0 ? Math.ceil(remainingWorkTimeRegular / avgShiftTime) : 0

                                entryBeforeUpdate.value = remainingWorkTimeRegular.toFixed(2) + ' / ' + shiftCount + ' Ø Schichten';
                                return entryBeforeUpdate;
                            }

                        },


                        { divider: true },
                        {
                            label: this.$t('shift.template.plannedDefaultShifts'),
                            key: 'plannedShiftCount',
                            hidden: false,
                            type: 'output',
                            value: shiftCount,
                            disabled: true
                        },
                        {
                            label: this.$t('shift.template.plannedDefaultHours'),
                            key: 'plannedTime',
                            hidden: false,
                            type: 'output',
                            value: plannedTime.toFixed(2),
                            disabled: true
                        },
                        {
                            label: this.$t('shift.template.averageShiftTime'),
                            key: 'avgShiftTime',
                            type: 'output',
                            hidden: false,
                            value: avgShiftTime.toFixed(2),
                            disabled: true
                        },
                        {
                            label: this.$t('shift.template.averageHourlyWage'),
                            key: 'avgHourlyWage',
                            hidden: false,
                            type: 'output',
                            value: avgHourlyWage.toFixed(2) + ' €',
                            disabled: true
                        },
                        {
                            label: this.$t('shift.template.plannedLaborCosts'),
                            key: 'plannedCost',
                            hidden: false,
                            type: 'output',
                            value: ( plannedTime * avgHourlyWage ).toFixed(2) + ' €',
                            disabled: true
                        },
                        {
                            label: this.$t('shift.template.differenceToEmployeeTarget'),
                            key: 'diff',
                            hidden: false,
                            type: 'output',
                            value: diff >= 0 ? '+ ' + diff.toFixed(2) : diff.toFixed(2),
                            disabled: true,
                            computed: (datafill, lastVal, entryBeforeUpdate) => {
                                let unavailablePercentage = datafill.entries.find(i => i.key == 'unavailablePercentage').value;
                                let usePlannedPrefs = datafill.entries.find(i => i.key == 'usePlannedPrefs').value;
                                let remainingWorkTimeRegular = workTimeRegular * ( 100 - unavailablePercentage ) / 100.0
                                if (usePlannedPrefs) {
                                    remainingWorkTimeRegular = Math.max(0, remainingWorkTimeRegular - plannedPrefTime);
                                }

                                let diff = plannedTime - remainingWorkTimeRegular;

                                entryBeforeUpdate.value = diff >= 0 ? '+ ' + diff.toFixed(2) : diff.toFixed(2);
                                return entryBeforeUpdate;
                            }
                        },
                        {
                            label: this.$t('shift.template.planningPercent'),
                            key: 'plannedPercentage',
                            hidden: false,
                            type: 'output',
                            value: ( percentage * 100 ).toFixed(2) + '%',
                            disabled: true,
                            computed: (datafill, lastVal, entryBeforeUpdate) => {
                                let unavailablePercentage = datafill.entries.find(i => i.key == 'unavailablePercentage').value;
                                let usePlannedPrefs = datafill.entries.find(i => i.key == 'usePlannedPrefs').value;
                                let remainingWorkTimeRegular = workTimeRegular * ( 100 - unavailablePercentage ) / 100.0
                                if (usePlannedPrefs) {
                                    remainingWorkTimeRegular = Math.max(0, remainingWorkTimeRegular - plannedPrefTime);
                                }

                                let percentage = 0;
                                if (remainingWorkTimeRegular > 0) {
                                    percentage = plannedTime / remainingWorkTimeRegular;
                                }

                                entryBeforeUpdate.value = ( percentage * 100 ).toFixed(2) + '%';
                                return entryBeforeUpdate;
                            }
                        },
                        { divider: true },
                        {
                            label: this.$t('shift.template.employeeWeeklyHours'), key: 'weekWorkTime', hidden: false, value: 40,
                            slider: {
                                hideMin: true,
                                hideMax: true,
                                min: 1,
                                max: 60,
                                value: 40,
                                step: 1,
                                inputControl: true,
                                sliderInput: (x) => {return x},
                            },
                        },
                        {
                            label: this.$t('shift.template.differenceInEmployees'),
                            key: 'employeeDiffCount',
                            hidden: false,
                            type: 'output',
                            value: employeeDiffCount,
                            disabled: true,

                            computed: (datafill, lastVal, entryBeforeUpdate) => {
                                let unavailablePercentage = datafill.entries.find(i => i.key == 'unavailablePercentage').value;
                                let usePlannedPrefs = datafill.entries.find(i => i.key == 'usePlannedPrefs').value;
                                let remainingWorkTimeRegular = workTimeRegular * ( 100 - unavailablePercentage ) / 100.0
                                if (usePlannedPrefs) {
                                    remainingWorkTimeRegular = Math.max(0, remainingWorkTimeRegular - plannedPrefTime);
                                }

                                let diff = plannedTime - remainingWorkTimeRegular;
                                let weekWorkTime = datafill.entries.find(i => i.key == 'weekWorkTime').value;
                                let monthWorkTime = weekWorkTime * 4.325
                                let employeeDiffCount = Math.ceil(diff / monthWorkTime);
                                if (employeeDiffCount <= -1) {
                                    employeeDiffCount = employeeDiffCount * -1 + ' zu viel'
                                } else {
                                    employeeDiffCount = employeeDiffCount + ' zu wenig'
                                }
                                entryBeforeUpdate.value = employeeDiffCount;
                                return entryBeforeUpdate;
                            }

                        },
                        { divider: true },
                        /*{label: 'Ø Schicht Stunden', key: 'avgShiftTime', hidden: false, value: 8,
                            slider: {
                                hideMin: true,
                                hideMax: true,
                                min: 1,
                                max: 12,
                                value: 8,
                                step: 0.5
                            },
                        }, */
                        {
                            label: this.$t('shift.template.differenceInAverageShifts'),
                            key: 'shiftDiffCount',
                            hidden: false,
                            type: 'output',
                            value: shiftDiffCount,
                            disabled: true,

                            computed: (datafill, lastVal, entryBeforeUpdate) => {
                                let unavailablePercentage = datafill.entries.find(i => i.key == 'unavailablePercentage').value;
                                let usePlannedPrefs = datafill.entries.find(i => i.key == 'usePlannedPrefs').value;
                                let remainingWorkTimeRegular = workTimeRegular * ( 100 - unavailablePercentage ) / 100.0
                                if (usePlannedPrefs) {
                                    remainingWorkTimeRegular = Math.max(0, remainingWorkTimeRegular - plannedPrefTime);
                                }

                                let diff = plannedTime - remainingWorkTimeRegular;
                                let avgShiftTime = datafill.entries.find(i => i.key == 'avgShiftTime').value;

                                let shiftDiffCount = Math.floor(diff / avgShiftTime);
                                if (shiftDiffCount <= -1) {
                                    shiftDiffCount = shiftDiffCount * -1 + ' zu wenig'
                                } else {
                                    shiftDiffCount = shiftDiffCount + ' zu viel'
                                }
                                entryBeforeUpdate.value = shiftDiffCount;
                                return entryBeforeUpdate;
                            }

                        },


                    ];

                    let title = this.$t('shift.template.shiftScheduleInformation') + ': ' + month
                    if (employeeLevels.includes(1) && employeeLevels.includes(2)) {
                        title += " " + this.$t('shift.template.total')
                    } else if (employeeLevels.includes(1)) {
                        title += " Crew"
                    } else if (employeeLevels.includes(2)) {
                        title += " Management"
                    }
                    view.$helpers.OpenPopup({
                        type: 'datafill',
                        componentState: {
                            entries: dataEntries,
                            selectCallback: null
                        },
                        header: title,
                        remainAfterSelect: false,
                        width: "800px",
                        expanded: true,
                        centered: true
                    });


                }).catch(error => {
                console.log(error);
                view.$helpers.error(view.$t('global.notifications.error'), view.$t('global.notifications.dataLoadingError'));
            });
        },
        GetPlannedStatsFromTemplates(templateMap, from, to) {
            let view = this;
            let start = from.clone();
            let weekDayMap = { '1': [], '2': [], '3': [], '4': [], '5': [], '6': [], '7': [] };
            let momentMap = {};
            Object.values(templateMap).forEach(t => {
                if (t.eventId == null) {
                    let entry = {}
                    if (t.validFrom != null) {
                        entry.validFromDate = view.$helpers.getMomentFromStringTZ(t.validFrom);
                    } else {
                        entry.validFromDate = from;
                    }
                    if (t.validTo != null) {
                        entry.validToDate = view.$helpers.getMomentFromStringTZ(t.validTo);
                    } else {
                        entry.validToDate = to;
                    }
                    momentMap[t.id] = entry;
                    for (let i = 1; i <= 7; i++) {
                        if (t.weekDays.includes(i)) {
                            weekDayMap['' + i].push(t);
                        }
                    }
                }


            })
            let time = 0;
            let count = 0;
            while (start < to) {
                let weekDay = start.isoWeekday();
                let templates = weekDayMap["" + weekDay];
                let tTime = 0;
                templates.forEach(t => {
                    if (momentMap[t.id] && ( start >= momentMap[t.id].validFromDate && start < momentMap[t.id].validToDate )) {
                        if (t.workValue == -1) {
                            let fromT = view.$helpers.getMomentFromStringUTC(t.from, "HH:mm");
                            fromT = start.clone().hours(fromT.hours()).minutes(fromT.minutes());
                            let toT = view.$helpers.getMomentFromStringUTC(t.to, "HH:mm");
                            toT = start.clone().hours(toT.hours()).minutes(toT.minutes());
                            if (toT <= fromT) {
                                toT = toT.add(1, 'days');
                            }
                            if (toT > to) {
                                toT = to;
                            }
                            let diff = toT.diff(fromT, 'minutes');
                            let breakValue = t.breakValue ? t.breakValue : view.$helpers.CalculateAutoBreakValueForPlanShift(false, fromT, toT, view.storeSettings);
                            tTime += ( diff - breakValue );
                            count++;
                        } else {
                            tTime += t.workValue;
                            count++;
                        }
                    }
                })
                time += tTime;
                start = start.add(1, 'days');
            }
            return { plannedTime: time, shiftCount: count };
        },
        shiftDesignerModal($event, data) {
            let view = this;
            view.$helpers.OpenAlert({
                header: this.$t('shift.template.scheduleToCapacity'),
                description: this.$t('shift.template.scheduleToCapacityQuery'),
                callbackAccept: (alert) => {
                    alert.hidden = true;
                    view.AutoGenerateTemplates();
                }
            });
        },
        GetWeeklyOrDailyTimeFrames(timeFrames) {
            let temp = timeFrames;
            if (this.templateDaySelection !== 'W') {
                temp = [];
                timeFrames.forEach(t => {
                    if (!temp.find(t2 => t2.templateId === t.templateId)) temp.push(t);
                });
            }
            return temp;
        },
        RefreshPlanningIndicator() {
            let view = this;
            view.planningIndicatorDailyMap = {};
            view.planningIndicatorMap = {};
            if (this.$store.state.jwt != undefined && this.$store.state.storeId != undefined) {

                let result = [];
                let from = this.currentWeekStart.format();
                let to = this.currentWeekStart.clone().add(8, 'days').format();

                return view.loadPlanningIndicatorsPagedCompletely(this.displayIndicatorType, from, to, 1, result)
                    .then(res => {

                        view.calculatePlanningIndicatorMaps(res);
                        return new Promise((accept, reject) => {
                            accept(true);
                        })
                    })

            } else {
                return new Promise((accept, reject) => {
                    accept(true);
                })
            }

        },
        loadPlanningIndicatorsPagedCompletely(type, from, to, page = 1, data = []) {

            const url = '/api/sec/planningindicator';
            const params = {
                storeId: this.$store.state.storeId,
                typeId: type,
                from,
                to,
                page,
            }

            return this.axios({
                method: 'get',
                params: params,
                headers: {
                    'AUTHORIZATION': 'bearer ' + this.$store.state.jwt
                },
                url,
            }).then(response => {
                if (response.data.length < 1) {
                    return data;
                } else {
                    data.push(...response.data);
                    return this.loadPlanningIndicatorsPagedCompletely(type, from, to, page + 1, data);
                }
            });
        },
        setPlanningType(type) {
            this.planningType = type;
            this.calculatePlanningIndicatorMaps(Object.values(this.planningIndicatorMap));
            if(this.useVlhForForecast){
                this.calculateVlhRequirementMap();
            }
            this.$forceUpdate();
        },
        calculatePlanningIndicatorMaps(indicators) {
            let view = this;
            let dailyIndicatorMap = {};

            let start = this.currentWeekStart.clone();
            let end = this.currentWeekEnd.clone().add(1, 'days');

            while (start < end) {
                let dayKey = start.format('YYYY-MM-DD');
                let dayEntry = {
                    hourMap: {},
                    planned: 0,
                    actual: 0,
                    spotRequirements: {}
                }

                Object.values(this.spotMap).forEach(s => {
                    dayEntry.spotRequirements[s.id] = {req: 0, hasReq: false};
                });
                dailyIndicatorMap[dayKey] = dayEntry;

                let hourStart = start.clone();
                let hourEnd = hourStart.clone().add(24, 'hours');
                while (hourStart < hourEnd) {
                    let hourKey = hourStart.format('HH');
                    let hourEntry = {
                        quarterMap: {},
                        planned: 0,
                        actual: 0,
                        spotRequirements: {}
                    }
                    Object.values(this.spotMap).forEach(s => {
                        hourEntry.spotRequirements[s.id] = {req: 0, hasReq: false};
                    });
                    dayEntry.hourMap[hourKey] = hourEntry;

                    let quarterStart = hourStart.clone();
                    let quarterEnd = quarterStart.clone().add(1, 'hours');
                    while (quarterStart < quarterEnd) {
                        let quarterKey = quarterStart.format('mm');
                        let quarterEntry = {
                            planned: 0,
                            actual: 0,
                            spotRequirements: {}
                        }
                        Object.values(this.spotMap).forEach(s => {
                            quarterEntry.spotRequirements[s.id] = {req: 0, hasReq: false};
                        });
                        hourEntry.quarterMap[quarterKey] = quarterEntry;
                        quarterStart.add(15, 'minutes');
                    }
                    hourStart.add(1, 'hours');
                }
                start.add(1, 'days');
            }
            //fill with indicator values;
            let indicatorMap = {};
            indicators.forEach(i => {
                if (i.typeId == this.displayIndicatorType) {
                    i.dateMoment = view.$helpers.getMomentFromStringTZ(i.from);
                    let dayKey = i.dateMoment.clone().startOf('day').format('YYYY-MM-DD');
                    let hourKey = i.dateMoment.clone().startOf('hour').format('HH')
                    let quarterKey = '00';
                    if (i.dateMoment.minutes() >= 15) {
                        quarterKey = '15'
                    }
                    if (i.dateMoment.minutes() >= 30) {
                        quarterKey = '30'
                    }
                    if (i.dateMoment.minutes() >= 45) {
                        quarterKey = '45'
                    }
                    if (dailyIndicatorMap[dayKey]) {
                        if (i.planned) {
                            dailyIndicatorMap[dayKey].planned += i.amount;
                            if (dailyIndicatorMap[dayKey].hourMap[hourKey]) {
                                dailyIndicatorMap[dayKey].hourMap[hourKey].planned += i.amount;
                                if (dailyIndicatorMap[dayKey].hourMap[hourKey].quarterMap[quarterKey]) {
                                    dailyIndicatorMap[dayKey].hourMap[hourKey].quarterMap[quarterKey].planned += i.amount;
                                }
                            }
                        }
                        if (!i.planned) {
                            dailyIndicatorMap[dayKey].actual += i.amount;
                            if (dailyIndicatorMap[dayKey].hourMap[hourKey]) {
                                dailyIndicatorMap[dayKey].hourMap[hourKey].actual += i.amount;
                                if (dailyIndicatorMap[dayKey].hourMap[hourKey].quarterMap[quarterKey]) {
                                    dailyIndicatorMap[dayKey].hourMap[hourKey].quarterMap[quarterKey].actual += i.amount;
                                }
                            }


                        }
                        indicatorMap[i.id] = i;
                    }

                }

            });
            //calculate requirements
            start = this.currentWeekStart.clone();
            end = this.currentWeekEnd.clone().add(1, 'days');

            while (start < end) {
                let dayKey = start.format('YYYY-MM-DD');
                let dayEntry = dailyIndicatorMap[dayKey];
                dayEntry.plannedAvg = this.planningType == 0 ? Math.round(dayEntry.planned / 96) : Math.round(dayEntry.actual / 96);
                let plannedAvg = dayEntry.plannedAvg;

                Object.values(this.spotMap).forEach(s => {
                    if (this.planningEmployeeThresholdSpotMap[s.id]) {
                        let minThreshold = 0;
                        let minEmployeeCount = 0;
                        let hasReq = this.planningEmployeeThresholdSpotMap[s.id] != null && this.planningEmployeeThresholdSpotMap[s.id].length > 0;
                        this.planningEmployeeThresholdSpotMap[s.id].forEach(t => {
                            if (t.threshold <= plannedAvg && t.threshold >= minThreshold) {
                                minThreshold = t.threshold;
                                minEmployeeCount = t.employeeCount;
                            }
                        })
                        //dayEntry.spotRequirements[s.id] = minEmployeeCount;
                        dayEntry.spotRequirements[s.id] = {req: minEmployeeCount, hasReq: hasReq};
                    }

                });


                let hourStart = start.clone();
                let hourEnd = hourStart.clone().add(24, 'hours');
                while (hourStart < hourEnd) {
                    let hourKey = hourStart.format('HH');
                    let hourEntry = dayEntry.hourMap[hourKey];
                    hourEntry.plannedAvg = this.planningType == 0 ? Math.round(hourEntry.planned / 4) : Math.round(hourEntry.actual / 4);





                    let quarterStart = hourStart.clone();
                    let quarterEnd = quarterStart.clone().add(1, 'hours');

                    let hourSpotRequirements = hourEntry.spotRequirements;
                    while (quarterStart < quarterEnd) {
                        let quarterKey = quarterStart.format('mm');
                        let quarterEntry = hourEntry.quarterMap[quarterKey];
                        quarterEntry.plannedAvg = this.planningType == 0 ? quarterEntry.planned : quarterEntry.actual;
                        let plannedAvg = quarterEntry.plannedAvg


                        Object.values(this.spotMap).forEach(s => {
                            if (this.planningEmployeeThresholdSpotMap[s.id]) {
                                let minThreshold = 0;
                                let minEmployeeCount = 0;
                                let hasReq = this.planningEmployeeThresholdSpotMap[s.id] != null && this.planningEmployeeThresholdSpotMap[s.id].length > 0;

                                this.planningEmployeeThresholdSpotMap[s.id].forEach(t => {
                                    if (t.threshold <= plannedAvg && t.threshold >= minThreshold) {
                                        minThreshold = t.threshold;
                                        minEmployeeCount = t.employeeCount;
                                    }
                                })

                                quarterEntry.spotRequirements[s.id] = {req: minEmployeeCount, hasReq: hasReq};

                                if(!hourSpotRequirements[s.id]){
                                    hourSpotRequirements[s.id] = {req: minEmployeeCount, hasReq: hasReq};
                                } else {
                                    hourSpotRequirements[s.id].req += minEmployeeCount *0.25;
                                    hourSpotRequirements[s.id].hasReq = hourSpotRequirements[s.id].hasReq || hasReq;
                                }
                            }

                        });
                        quarterStart.add(15, 'minutes');
                    }



                    hourStart.add(1, 'hours');
                }
                start.add(1, 'days');
            }
            this.planningIndicatorDailyMap = dailyIndicatorMap;
            //console.log(this.planningIndicatorDailyMap);
            this.planningIndicatorMap = indicatorMap;
        },

        getTemplateFromToDates(template, dayFromOne) {
            const day = dayFromOne - 1;

            const currentWeek = this.currentWeekStart;

            let from;
            if (moment.isMoment(template.from)) {
                // from = moment(template.from).year(currentWeek.year()).month(currentWeek.month()).date(currentWeek.date() + day);
                from = template.from.toDate();
                from.setFullYear(currentWeek.year(), currentWeek.month(), currentWeek.date() + day);
            } else {
                let [ fromHr, fromMin ] = template.from.substr(0, 5).split(':');
                fromHr = parseInt(fromHr);
                fromMin = parseInt(fromMin);
                // from = moment(currentWeek).add(day, 'days').hours(fromHr).minutes(fromMin);

                from = currentWeek.toDate();
                from.setDate(currentWeek.date() + day);
                from.setHours(fromHr, fromMin);
            }

            let to;
            if (moment.isMoment(template.to)) {
                // to = moment(template.to).year(currentWeek.year()).month(currentWeek.month()).date(currentWeek.date() + day);
                to = template.to.toDate();
                to.setFullYear(currentWeek.year(), currentWeek.month(), currentWeek.date() + day);
            } else {
                let [ toHr, toMin ] = template.to.substr(0, 5).split(':');
                toHr = parseInt(toHr);
                toMin = parseInt(toMin);
                // to = moment(currentWeek).add(day, 'days').hours(toHr).minutes(toMin);

                to = currentWeek.toDate();
                to.setDate(currentWeek.date() + day);
                to.setHours(toHr, toMin);
            }

            if (to.getTime() <= from.getTime()) {
                to.setDate(to.getDate() + 1);
            }

            let breakFrom;
            let breakTo;
            if (!template.breakFrom) {
                breakFrom = null;
            } else if (moment.isMoment(template.breakFrom)) {
                // breakFrom = moment(template.breakFrom).year(currentWeek.year()).month(currentWeek.month()).date(currentWeek.date() + day);
                breakFrom = template.breakFrom.toDate();
                breakFrom.setFullYear(currentWeek.year(), currentWeek.month(), currentWeek.date() + day);
                breakTo = new Date(breakFrom);
                breakTo.setMinutes(breakFrom.getMinutes() + template.breakValue);

                if(breakFrom.getTime() < from.getTime()){
                    breakFrom.setDate(breakFrom.getDate() +1);
                    breakTo.setDate(breakTo.getDate() +1);
                }
            } else {
                let [ breakFromHr, breakFromMin ] = template.breakFrom.split(':');
                breakFromHr = parseInt(breakFromHr);
                breakFromMin = parseInt(breakFromMin);
                // breakFrom = moment(currentWeek).add(day, 'days').hours(breakFromHr).minutes(breakFromMin);
                breakFrom = currentWeek.toDate();
                breakFrom.setDate(currentWeek.date() + day);
                breakFrom.setHours(breakFromHr, breakFromMin);
                breakTo = new Date(breakFrom);
                breakTo.setMinutes(breakFrom.getMinutes() + template.breakValue);

                if(breakFrom.getTime() < from.getTime()){
                    breakFrom.setDate(breakFrom.getDate() +1);
                    breakTo.setDate(breakTo.getDate() +1);
                }
            }

            // const breakTo = breakFrom ? moment(breakFrom).add(template.breakValue, 'minutes') : null;

            return { from, to, breakFrom, breakTo };
        },

        getEventFromToDates(event) {
            let eventFrom = event.fromMoment || event.from || event.fromDate;
            let eventTo = event.toMoment || event.to || event.toDate;

            if (moment.isMoment(eventFrom)) eventFrom = eventFrom.toDate();
            else if (typeof eventFrom === 'string') eventFrom = new Date(eventFrom);

            if (moment.isMoment(eventTo)) eventTo = eventTo.toDate();
            else if (typeof eventTo === 'string') eventTo = new Date(eventTo);

            return { from: eventFrom, to: eventTo };
        },

        timeFrameIsValid(template, day) {
            const { from: fromDate } = this.getTemplateFromToDates(template, day);

            const validDates = this.templateValidMomentMap[template.id];
            if (!validDates) return true;

            const { from: validFrom, to: validTo } = validDates;
            // if (validFrom && fromDate.isBefore(validFrom)) return false;
            if (validFrom && fromDate.getTime() < validFrom.toDate().getTime()) return false;
            // if (validTo && fromDate.isAfter(validTo)) return false;
            if (validTo && fromDate.getTime() > validTo.toDate().getTime()) return false;
            return true;
        },

        getUnassignedShiftTimesTooltip(day) {
            return () => {
                const labelValues = [];

                for (const spot of Object.values(this.spotMap)) {
                    const spotName = spot.label;
                    const spotColor = spot.color;
                    const spotId = spot.id;
                    let spotShiftCount = '0';
                    let spotShiftTimes = '--:--';
                    const dateKey = this.currentWeekStart.clone().add(day - 1, 'days').format('YYYY-MM-DD');
                    const shiftTimesByDay = this.realUnassignedShiftTemplateTimesByDay[dateKey];
                    if (shiftTimesByDay && shiftTimesByDay[spotId]) {
                        spotShiftCount = shiftTimesByDay[spotId].count;
                        const totalSpotMinutes = shiftTimesByDay[spotId].time;
                        const spotHours = Math.floor(totalSpotMinutes / 60);
                        const spotMinutes = ( totalSpotMinutes % 60 ).toString().padStart(2, '0');
                        spotShiftTimes = `${spotHours}h:${spotMinutes}`;
                    }
                    labelValues.push({
                        spotName,
                        spotColor,
                        spotId,
                        spotShiftCount,
                        spotShiftTimes,
                    });
                }

                const labels = labelValues.sort((a, b) => {
                    let nameA = a.spotName;
                    let nameB = b.spotName;
                    return ( nameA < nameB ) ? -1 : ( nameA > nameB ) ? 1 : 0;
                }).map((v) => ( {
                    text: v.spotName,
                    type: 'space-content',
                    iconLeft: {
                        type: 'label-center smaller round',
                        label: v.spotShiftCount,
                        styleButton: {
                            background: v.spotColor,
                            color: luma(v.spotColor) > 165 ? '#000' : '#fff',
                            'margin-right': '5px',
                        }
                    },
                    iconRight: { type: '', label: v.spotShiftTimes },
                } ));

                const tooltip = {
                    components: [
                        {
                            type: 'listlabel',
                            state: {
                                labels,
                            },
                            pinTopRight: true,
                        },
                    ],
                };
                return tooltip;
            };
        },

        /* Grid calculation methods */
        getGridCellSpan(dayFromZeroOrFrom, fromHrOrTo, fromMin = null, toHr = null, toMin = null) {
            let dayFromZero = dayFromZeroOrFrom;
            let fromHr = fromHrOrTo;
            let toDay = null;
            if (fromMin === null && toHr === null && toMin === null) {
                const from = moment(dayFromZeroOrFrom);
                const to = moment(fromHrOrTo);
                dayFromZero = from.isoWeekday() - 1;
                fromHr = from.hour();
                fromMin = from.minutes();
                toHr = to.hour();
                toMin = to.minutes();

                let fStartOfDay = from.clone().startOf('day');
                let tStartOfDay = to.clone().startOf('day');


                toDay = dayFromZero + ( tStartOfDay.diff(fStartOfDay,'days')%8 );
            }

            const spanningFrom = Math.max(
                X_OFFSET,
                this.templateDaySelection === 'W'
                    ? Math.round(
                        dayFromZero * WEEKMODE_DAY_INTERVAL
                        + fromHr * WEEKMODE_HOUR_INTERVAL
                        + fromMin / WEEKMODE_MIN_INTERVAL
                        + X_OFFSET
                    )
                    : Math.min(
                        this.dayModeHours * DAYMODE_HOUR_INTERVAL + X_OFFSET,
                        Math.round(
                            ( dayFromZero - this.templateDaySelection + 1 ) * DAYMODE_DAY_INTERVAL
                            + fromHr * DAYMODE_HOUR_INTERVAL
                            + fromMin / DAYMODE_MIN_INTERVAL
                            + X_OFFSET
                        )
                    ),
            );

            if (toDay === null)
                toDay = toHr * 60 + toMin <= fromHr * 60 + fromMin ? dayFromZero + 1 : dayFromZero;

            if (toDay === -1) toDay = 6;

            if (toDay === dayFromZero && toHr === fromHr && toMin === fromMin) toDay += 1;


            const spanningTo = this.templateDaySelection === 'W'
                ? Math.round(
                    toDay * WEEKMODE_DAY_INTERVAL
                    + toHr * WEEKMODE_HOUR_INTERVAL
                    + toMin / WEEKMODE_MIN_INTERVAL
                    + X_OFFSET
                )
                : Math.min(
                    Math.round(
                        ( toDay - this.templateDaySelection + 1 ) * DAYMODE_DAY_INTERVAL
                        + toHr * DAYMODE_HOUR_INTERVAL
                        + toMin / DAYMODE_MIN_INTERVAL
                        + X_OFFSET
                    ),
                    this.dayModeHours * DAYMODE_HOUR_INTERVAL + X_OFFSET,
                );

            return { from: spanningFrom, to: spanningTo };
        },

        findEqualTemplate(template, groupId = null, spotId = null, multiple = false) {
            if (groupId === null) groupId = template.groupId;
            if (spotId === null) spotId = template.spotId;

            const templates = Object.values({
                ...this.currentWeekShiftTemplateMap,
                ...this.changedTemplateMap,
            });

            const {
                from: templateFrom,
                to: templateTo,
                breakFrom: templateBreakFrom,
                breakTo: templateBreakTo
            } = this.getTemplateFromToDates(template, 1);

            const predicate = t => {
                if (t.groupId !== groupId || t.spotId !== spotId) return false;
                const {
                    from: tFrom,
                    to: tTo,
                    breakFrom: tBreakFrom,
                    breakTo: tBreakTo
                } = this.getTemplateFromToDates(t, 1);
                if (tBreakFrom && ( !templateBreakFrom || templateBreakFrom.getTime() !== tBreakFrom.getTime() )) return false;
                if (tBreakTo && ( !templateBreakTo || templateBreakTo.getTime() !== tBreakTo.getTime() )) return false;
                if (t.breakValue !== template.breakValue) return false;

                return tFrom.getTime() === templateFrom.getTime() && tTo.getTime() === templateTo.getTime()
            };
            if (multiple) return templates.filter(predicate);
            else return templates.find(predicate);
        },

        shouldDisplayGridRow(templateId) {
            const template = this.currentWeekShiftTemplateMap[templateId];

            if (this.viewMode === 'full-overlap') return true;
            else if (this.viewMode === 'full-split') return true;
            else if (this.viewMode === 'standard' && !template.eventId) return true;
            else if (this.viewMode === 'project' && template.projectId) return true;
            else return this.viewMode === `event-${template.eventId}`;
        },

        getDailyIndicatorBarWidth(date) {
            let format = date.format('YYYY-MM-DD');
            if (!this.planningIndicatorDailyMap[format] || !this.planningIndicatorDailyMap[format].planned || !this.planningIndicatorDailyMap[format].actual) return 'calc(0% - 10px)';
            let length = ( this.planningIndicatorDailyMap[format].actual / this.planningIndicatorDailyMap[format].planned ) * 100;
            if (length > 100) length = 100;
            let ratio = ( this.planningIndicatorDailyMap[format].actual / this.planningIndicatorDailyMap[format].planned );
            let margin = 10 * ratio;
            return 'calc(' + length + '%)';
        },
        getIndicatorBarBackgroundStyle(date) {
            let color = 'var(--contrast-3)';
            let format = date.format('YYYY-MM-DD');
            let indicator = this.planningIndicatorDailyMap[format];
            if (indicator && indicator.actual && indicator.planned) color = '#e51c50';
            return { background: color }
        },
        getIndicatorAvgWrapper(spotId, index) {
            return () => this.getIndicatorAvg(spotId, index);
        },
        getIndicatorAvg(spotId, index) {
            const entry = this.planningValuesMap[spotId][index];
            return this.$t('shift.template.based15minAverage') + ': ' + entry.avg;
        },
        getEmployeeCountWrapper(spotId, index, quarterIndex) {
            return () => {
                let quarter = this.planningValuesMap[spotId][index].quarterEntries[quarterIndex];

                return quarter.present + ' / ' + quarter.required
            };
        },
        getIndicatorAmountWrapper(date) {
            return () => this.getIndicatorAmount(date);
        },
        getIndicatorAmount(date) {
            let format = date.format('YYYY-MM-DD');
            let indicator = this.planningIndicatorDailyMap[format];
            let amount = '-/-';
            if (indicator) {
                let divisor = this.planningIndicatorTypeMap[this.displayIndicatorType] ? Math.pow(10, this.planningIndicatorTypeMap[this.displayIndicatorType].fractional) : 1;

                if (indicator.actual && indicator.planned) amount = ( indicator.actual / divisor ).toFixed(2) + '/' + ( indicator.planned / divisor ).toFixed(2);
                if (!indicator.actual && indicator.planned) amount = '-/' + ( indicator.planned / divisor ).toFixed(2);
                if (indicator.actual && !indicator.planned) amount = ( indicator.actual / divisor ).toFixed(2) + '/-';
                if (this.planningIndicatorTypeMap[this.displayIndicatorType]) {
                    amount += ' ' + this.planningIndicatorTypeMap[this.displayIndicatorType].name;
                }

            }
            return amount;
        },
        getProductivity(date, totalUnassigned) {
            const indicator = this.planningIndicatorDailyMap[date.format('YYYY-MM-DD')];

            if (!indicator) return ' - / - '

            const productiveHours = ((totalUnassigned.eventTime !== 0 ? totalUnassigned.eventTime : totalUnassigned.productiveTime) + totalUnassigned.carryOver) / 60;

            const divisor = this.planningIndicatorTypeMap[this.displayIndicatorType] ? Math.pow(10, this.planningIndicatorTypeMap[this.displayIndicatorType].fractional) : 1;

            let productivity = {};
            productivity.planned = indicator.planned !== 0 && productiveHours !== 0 ? (indicator.planned / divisor / productiveHours).toFixed(2) : ' - ',
            productivity.actual = indicator.actual !== 0 && productiveHours !== 0 ? productivity.actual = (indicator.actual / divisor / productiveHours).toFixed(2) : ' - ',
            productivity.label = productivity.actual + '/' + productivity.planned;
            return productivity;
        },
        getIndicatorBarStyle(date) {
            let color = '#6d8d23';
            let format = date.format('YYYY-MM-DD');
            let indicator = this.planningIndicatorDailyMap[format];
            let width = this.getDailyIndicatorBarWidth(date);
            if (indicator) {
                if (!indicator.actual) color = '#e51c50';
                if (!indicator.planned) color = '#6d8d23';
                if (!indicator.planned || !indicator.actual) width = '100%';
            }
            return { background: color, width: width }
        },
        getTemplateValidLabel(templateId) {
            let result = '';
            if (!this.templateValidMomentMap[templateId]) return result;
            let from = this.templateValidMomentMap[templateId].from;
            let to = this.templateValidMomentMap[templateId].to;
            if (from != null && to != null) {
                result = from.format('DD.MM.YYYY') + ' - ' + to.clone().add(-1,'days').format('DD.MM.YYYY');
            } else if (from != null) {
                result = from.format('DD.MM.YYYY') + ' -  x';
            } else if (to != null) {
                result = 'x - ' + to.clone().add(-1,'days').format('DD.MM.YYYY');
            }
            return result;
        },


        getShiftTemplateTooltip(templateId = null, employeeName = null, differenceBegin = null, differenceEnd = null) {
            return () => {
                const template = templateId ? this.currentWeekShiftTemplateMap[templateId] : null;

                const tooltip = [];
                if (employeeName != null) {
                    tooltip.push(this.$tc('global.general.employee') + ': ' + employeeName);
                }

                if (template) {
                    if (template.projectId && this.projectMap[template.projectId]) {
                        tooltip.push(this.$t('shift.plan.project') + ': ' + this.projectMap[template.projectId].name);
                    }
                    if (template.fromLocationId && this.locationMap[template.fromLocationId]) {
                        tooltip.push(this.$t('store.settings.location') + ': ' + this.locationMap[template.fromLocationId].name);
                    }
                }

                if (differenceBegin || differenceEnd) {
                    const formatHHmm = (minutes) => {
                        const hours = minutes > 0 ? Math.floor(minutes / 60) : Math.ceil(minutes / 60);
                        const mins = Math.abs(minutes % 60).toString().padStart(2, '0');
                        return hours === 0
                            ? `${minutes > 0 ? '+' : ''}${minutes}min`
                            : `${minutes > 0 ? '+' : ''}${hours}h ${mins}min`;
                    };
                    let difference = this.$t('shift.template.deviation');
                    if (differenceBegin) {
                        if (typeof differenceBegin === 'number') differenceBegin = formatHHmm(differenceBegin);
                        difference += this.$t('shift.template.beginFormatted') + differenceBegin;
                    }
                    if (differenceBegin && differenceEnd) difference += ', ';
                    if (differenceEnd) {
                        if (typeof differenceEnd === 'number') differenceEnd = formatHHmm(differenceEnd);
                        difference += this.$t('shift.template.endFormatted') + differenceEnd;
                    }
                    tooltip.push(difference);
                }

                return tooltip.length > 0 ? tooltip.join(' // ') : false;
            };
        },
        calcShiftTemplateTooltip(templateId = null, employeeName = null, differenceBegin = null, differenceEnd = null) {

                const template = templateId ? this.currentWeekShiftTemplateMap[templateId] : null;

                const tooltip = [];
                if (employeeName != null) {
                    tooltip.push(this.$tc('global.general.employee') + ': ' + employeeName);
                }

                if (template) {
                    if (template.projectId && this.projectMap[template.projectId]) {
                        tooltip.push(this.$t('shift.plan.project') + ': ' + this.projectMap[template.projectId].name);
                    }
                    if (template.fromLocationId && this.locationMap[template.fromLocationId]) {
                        tooltip.push(this.$t('store.settings.location') + ': ' + this.locationMap[template.fromLocationId].name);
                    }
                }

                if (differenceBegin || differenceEnd) {
                    const formatHHmm = (minutes) => {
                        const hours = minutes > 0 ? Math.floor(minutes / 60) : Math.ceil(minutes / 60);
                        const mins = Math.abs(minutes % 60).toString().padStart(2, '0');
                        return hours === 0
                            ? `${minutes > 0 ? '+' : ''}${minutes}min`
                            : `${minutes > 0 ? '+' : ''}${hours}h ${mins}min`;
                    };
                    let difference = this.$t('shift.template.deviation');
                    if (differenceBegin) {
                        if (typeof differenceBegin === 'number') differenceBegin = formatHHmm(differenceBegin);
                        difference += this.$t('shift.template.beginFormatted') + differenceBegin;
                    }
                    if (differenceBegin && differenceEnd) difference += ', ';
                    if (differenceEnd) {
                        if (typeof differenceEnd === 'number') differenceEnd = formatHHmm(differenceEnd);
                        difference += this.$t('shift.template.endFormatted') + differenceEnd;
                    }
                    tooltip.push(difference);
                }

                return tooltip.length > 0 ? tooltip.join(' // ') : false;

        },
        getQuarterAmountWrapper(hour, quarter) {
            return () => ( {
                components: [ {
                    type: 'listlabel',
                    state: { labels: this.getQuarterLabels(hour, quarter) },
                    pinTopRight: true,
                } ],
            } );
        },
        getGroupInfoWrapper(groupId) {
            return () => {
                if (groupId == null) {
                    return this.$t('shift.template.noShiftGroupActive')
                } else {
                    return this.$t('shift.template.shiftGroupActive', [this.shiftTemplateGroupMap[groupId].name])
                }

            };
        },
        selectTemplateGroup(groupId) {
            if (groupId != null) {
                console.log("selectTemplateGroup")
                let view = this;
                this.loading = true;
                this.loadingText = this.$t('shift.template.loadShiftPlan')
                this.selectedGroup = this.shiftTemplateGroupMap[groupId];
                // TODO: Display warning if there are unsaved changes
                this.discardChanges();

                this.RefreshShiftTemplates(true)
                    .catch(error => {
                        view.loading = false;

                        view.$helpers.error(view.$t('global.notifications.error'), view.$t('global.notifications.dataLoadingError'));
                    })
            }

        },

        getProductivityHour(hour, quarter) {
            let productiveMinutes = 0;
            let format = this.timeFrameStart.clone().add(hour, 'hour').minutes(( quarter - 1 ) * 15);
            let dayKey = format.format('YYYY-MM-DD');
            let hourKey = format.format('HH');
            let quarterKey = format.format('mm');
            const present =  this.planningEmployeeMap[dayKey].hourMap[hourKey].quarterMap[quarterKey];
            for (const spot of Object.values(this.spotMap).filter(spot => spot.productive)) {
                const minutes = present.spotPresent[spot.id];
                if (minutes > 0) {
                    productiveMinutes += minutes;
                }
            }
            return productiveMinutes;
        },
        getQuarterLabels(hour, quarter) {
            let format = this.timeFrameStart.clone().add(hour, 'hour').minutes(( quarter - 1 ) * 15);
            let dayKey = format.format('YYYY-MM-DD');
            let hourKey = format.format('HH');
            let quarterKey = format.format('mm');

            let reqMap;

            if(this.useVlhForForecast){
                if (!this.vlhRequirementMap[dayKey] || !this.planningEmployeeMap[dayKey]) return [];
                reqMap = this.vlhRequirementMap;
            } else {
                if (!this.planningIndicatorDailyMap[dayKey] || !this.planningEmployeeMap[dayKey]) return [];
                reqMap = this.planningIndicatorDailyMap;
            }


            let amount = this.GetQuarterAmount(dayKey, hourKey, quarterKey);
            let reqIndicator = reqMap[dayKey].hourMap[hourKey].quarterMap[quarterKey];
            let presentIndicator = this.planningEmployeeMap[dayKey].hourMap[hourKey].quarterMap[quarterKey];
            let labels = [];

            Object.values(this.spotMap).forEach(spot => {
                let req = this.countSpotsWithoutThreshold ||  reqIndicator.spotRequirements[spot.id].hasReq ? reqIndicator.spotRequirements[spot.id].req : '-';

                let spotLabel = {
                    text: spot.name + ' ' + ( presentIndicator.spotPresent[spot.id] / 15 ).toFixed(2) + '/' + (this.useVlhForForecast ? (req/15).toFixed(2) : req),
                    type: 'space-content',
                    iconLeft: {
                        type: 'label-center smaller round',
                        label: 0,
                        styleButton: {
                            background: spot.color,
                            color: this.$helpers.GetContrastingColor(spot.color),
                            'margin-right': '5px'
                        }
                    },
                    spot: spot,
                }
                labels.push(spotLabel);

            })
            labels.sort((a, b) => {
                let nameA = a.text;
                let nameB = b.text;
                return ( nameA < nameB ) ? -1 : ( nameA > nameB ) ? 1 : 0;
            });
            labels.unshift({ text: amount, type: 'space-content' })
            return labels;
        },
        GetQuarterAmount(dayKey, hourKey, quarterKey) {
            let amount = '-/-';
            if (this.planningIndicatorDailyMap[dayKey]) {
                let indicator = this.planningIndicatorDailyMap[dayKey].hourMap[hourKey].quarterMap[quarterKey];

                if (indicator) {
                    let divisor = this.planningIndicatorTypeMap[this.displayIndicatorType] ? Math.pow(10, this.planningIndicatorTypeMap[this.displayIndicatorType].fractional) : 1;

                    if (indicator.actual && indicator.planned) amount = ( indicator.actual / divisor ).toFixed(2) + '/' + ( indicator.planned / divisor ).toFixed(2);
                    if (!indicator.actual && indicator.planned) amount = '-/' + ( indicator.planned / divisor ).toFixed(2);
                    if (indicator.actual && !indicator.planned) amount = ( indicator.actual / divisor ).toFixed(2) + '/-';
                    if (this.planningIndicatorTypeMap[this.displayIndicatorType]) {
                        amount += ' ' + this.planningIndicatorTypeMap[this.displayIndicatorType].name;
                    }
                }
            }


            return amount;
        },
        GetWeeklyLabelFormat(dayOfWeek, moment) {
            if (!moment) {
                moment = this.$helpers.getMomentTZ().year(this.currentYear).isoWeekYear(this.currentYear).isoWeek(this.currentSelectedWeek);
                moment.isoWeekday(dayOfWeek);
            }
            return moment.format('dddd') + ' ' + moment.format('DD.MM.')
        },
        toggleCollapseState(spotId) {
            this.$set(this.groupCollapsedStates, spotId, !this.groupCollapsedStates[spotId]);
        },
        RefreshButton() {
            this.refresh();
        },

        getTemplatesForDisplayGroup(group) {
            const otherGroupId = Object.keys(this.shiftTemplateGroupMap).filter(key => key !== group.id)[0] || null;
            if (this.templateDisplayMap[group.id + '-' + group.spot.id]) {
                let result = this.templateDisplayMap[group.id + '-' + group.spot.id].sort((a, b) => {
                    let timeA = this.$helpers.getMomentFromStringTZ(a.timebarFrom, 'HH:mmZ');
                    let timeB = this.$helpers.getMomentFromStringTZ(b.timebarFrom, 'HH:mmZ');

                    return timeA.unix() - timeB.unix();
                });
                if (this.optimizationMode && otherGroupId) {
                    const overlayTemplateMap = this.optimizationGroupTemplateMap[group.id + '-' + group.spot.id] || [];

                    result = result.flatMap((template) => {
                        let overlayTemplates = overlayTemplateMap[template.id];
                        if (overlayTemplates && overlayTemplates.length > 0) {
                            overlayTemplates = overlayTemplates.map(overlayTemplate => ( {
                                ...overlayTemplate,
                                isOverlay: true,
                            } ))
                            return [ ...overlayTemplates, template ];
                        } else {
                            return [ template ];
                        }
                    });
                }
                // console.log(result);
                /*let result = this.templateDisplayMap[group.id+'-'+group.spot.id].filter(t => this.templateDaySelection === 'W' || t.weekDays.includes(this.templateDaySelection)).sort((a,b) => {
                        let timeA = this.$helpers.getMomentFromStringTZ(a.timebarFrom, "HH:mmZ");
                        let timeB = this.$helpers.getMomentFromStringTZ(b.timebarFrom, "HH:mmZ");

                        return timeA.unix() - timeB.unix();
                    }); */

                return result;
            } else {
                return [];
            }

        },
        openNewShiftTemplatePopup($event, shiftTemplateGroup, directSave = false) {
            let view = this;
            let maxTemplateCount = Math.max(Math.round(this.activeEmployeeCount * 1.10), 80)

            if (this.maxShiftCreatedCount >= maxTemplateCount) {
                let description = this.$t('shift.template.shiftCreationWarning', [maxTemplateCount, this.maxShiftCreatedCount]);

                description += this.$t('shift.template.shiftCreationHelp', [( Math.max(1, this.maxShiftCreatedCount - maxTemplateCount + 1) )]);
                let dataEntries = [];


                this.$helpers.OpenPopup({
                    elRelative: null,
                    type: 'datafill',
                    componentState: {
                        entries: dataEntries,
                        positiveButtonLabel: this.$t('global.ui.approve'),
                        description: description,

                        selectCallback: function (data) {

                        }
                    },
                    header: this.$t('shift.template.maxShiftWarning'),
                    expanded: true,
                    centered: true
                });
            } else {
                let day = this.daySelected || this.currentWeekStart;
                let from = moment(day).hours(8).minutes(0);

                let to = moment(day).hours(16).minutes(0);

                let eventitems = this.templateCreationEventDropDownItems;
                let projectitems = this.projectDropDownItems;
                let locationitems = this.locationDropDownItems;
                let costCenterItems = this.costCenterDropDownItems;
                let selectedValidFromItems = [ this.weekStartDateItems.past[0] ];

                const currentWeekday = this.daySelected ? this.daySelected.weekday() + 1 : null;
                let employeeItems = Object.values(this.employeeMap)
                .filter(e => e.active)
                .sort((a, b) => {
                    return a.lastName.localeCompare(b.lastName);
                }).map(employee => {
                    return {
                        label: employee.lastName + ' ' + employee.firstName,
                        value: employee.id
                    }
                });

                employeeItems.push({ label: 'Kein Mitarbeiter', value: null, selected: true });
                let selectedEmployeeItem = employeeItems.find(e => e.selected);


                let dataEntries = [
                    { label: 'Group Id', key: 'groupId', hidden: true, type: '', value: shiftTemplateGroup.id },
                    { label: 'Spot Id', key: 'spotId', hidden: true, type: '', value: shiftTemplateGroup.spot.id },
                    {
                        label: this.$t('global.general.from').toLowerCase(),
                        key: 'from',
                        hidden: false,
                        type: 'timeframe',
                        value: from,
                        flexGroup: true,
                        timeBig: true,
                        startTime: true,
                        focusProceedKey: 'to'
                    },
                    {
                        label: this.$t('global.general.to').toLowerCase(),
                        key: 'to',
                        hidden: false,
                        type: 'timeframe',
                        value: to,
                        flexGroup: true,
                        timeBig: true,
                        endTime: true,
                        focusProceedRef: 'validFrom'
                    },
                    { divider: true },
                    { label: this.$t('shift.template.thisWeekOnly'), key: 'weekOnly', hidden: false, type: 'bool', value: false },

                    {
                        label: this.$t('workBalance.bonus.validFrom'),
                        key: 'validFrom',
                        hidden: false,
                        type: 'date',
                        value: this.weekStartDateItems.past[0].value,
                        flexGroup: false
                    },
                    { label: this.$t('workBalance.bonus.ongoingIndefinitely'), key: 'noValidTo', hidden: false, type: 'bool', value: true },
                    {
                        label: this.$t('pages.employeeContract.validUntil'),
                        key: 'validTo',
                        hidden: this.$helpers.CheckDatafillKeysForHiddenInverted('noValidTo'),
                        type: 'date',
                        value: this.weekStartDateItems.future[0].value.clone().add(-1,'days').startOf('day'),
                        flexGroup: false
                    },
                    {
                        key: 'weekDays', hidden: false, flexGroup: false, type: 'togglelist', toggleEntries: [
                            { label: this.$t('store.openingHours.weekDay1Short'), value: 1, selected: currentWeekday === 1 },
                            { label: this.$t('store.openingHours.weekDay2Short'), value: 2, selected: currentWeekday === 2 },
                            { label: this.$t('store.openingHours.weekDay3Short'), value: 3, selected: currentWeekday === 3 },
                            { label: this.$t('store.openingHours.weekDay4Short'), value: 4, selected: currentWeekday === 4 },
                            { label: this.$t('store.openingHours.weekDay5Short'), value: 5, selected: currentWeekday === 5 },
                            { label: this.$t('store.openingHours.weekDay6Short'), value: 6, selected: currentWeekday === 6 },
                            { label: this.$t('store.openingHours.weekDay7Short'), value: 7, selected: currentWeekday === 7 },
                        ], styles: { width: '325px' }
                    },
                    { divider: true },
                    {
                        label: this.$t('shift.template.amount'),
                        key: 'count',
                        hidden: true,
                        type: 'slider',
                        value: 1,
                        slider: { value: 1, min: 1, max: 1}
                    },
                    {
                        label: this.$t('shift.template.automaticWorkTime'),
                        key: 'autoWorkValue',
                        hidden: false,
                        type: 'bool',
                        value: true
                    },
                    {
                        label: this.$t('shift.template.workTime'), key: 'workValue', hidden: true, value: -1,
                        slider: {
                            hideMin: false,
                            hideMax: false,
                            min: 0,
                            max: 24 * 60,
                            value: -1,
                            step: 1,
                            DisplayLabel: (val) => {
                                let hours = this.$helpers.pad2(Math.floor(val / 60));
                                let minutes = this.$helpers.pad2(val % 60);
                                return hours + ':' + minutes;
                            },
                            inputControl: true,
                            sliderInput: (x) => {return x},
                            labelToInt: (x) => {
                                let split = x.split(':');
                                return Number(split[0] * 60) + Number((split[1] ? split[1] : 0));
                            },
                        },
                        computed: (datafill, lastVal, entryBeforeUpdate) => {
                            let autoWorkValue = datafill.entries.find(i => i.key == 'autoWorkValue').value;

                            if (autoWorkValue && entryBeforeUpdate.value != -1) {
                                entryBeforeUpdate.value = -1;
                                entryBeforeUpdate.slider.value = -1;
                                entryBeforeUpdate.slider = Object.assign({}, entryBeforeUpdate.slider);
                            }
                            if (!autoWorkValue && entryBeforeUpdate.value <= -1) {
                                entryBeforeUpdate.value = 0;
                                entryBeforeUpdate.slider.value = 0;
                                entryBeforeUpdate.slider = Object.assign({}, entryBeforeUpdate.slider);
                            }

                            if (autoWorkValue) {
                                entryBeforeUpdate.hidden = true;
                            } else {
                                entryBeforeUpdate.hidden = false;
                            }
                            return entryBeforeUpdate;
                        }

                    },
                    { label: this.$t('shift.template.automaticBreak'), key: 'autoBreak', hidden: false, type: 'bool', value: true },
                    {
                        label: this.$t('store.settings.breakTime'),
                        key: 'breakValue',
                        hidden: this.$helpers.CheckDatafillKeysForHiddenInverted('autoBreak'),
                        type: 'slider',
                        value: 30,
                        min: 0,
                        max: 240,
                        slider: { hideMin: false, hideMax: false, min: 0, max: 240, value: 30, step: 1, inputControl: true,
                            sliderInput: (x) => {return x}}
                    },
                    {
                        label: this.$t('shift.template.breakFrom'),
                        key: 'breakFrom',
                        hidden: this.$helpers.CheckDatafillKeysForHiddenInverted('autoBreak'),
                        type: 'timeframe',
                        value: from.clone()
                    },
                    { label: this.$t('shift.template.skillMultiplier'), key: 'skillMultiplier', hidden: true, type: '', value: 1 },
                    { label: this.$t('shift.template.workValueMultiplier'), key: 'workValueMultiplier', hidden: true, type: '', value: 1 },


                    {
                        label: this.$t('store.events.event'),
                        key: 'eventId',
                        hidden: false,
                        type: 'dropdownsearch',
                        value: null,
                        selectMultiple: false,
                        flexGroup: false,
                        dropdownSearchState: {
                            toggled: false,
                            displayedItems: [],
                            input: '',
                            items: eventitems,
                            selectMultiple: false,
                            selectedItems: [],
                            selectedItem: {},
                            onceSelected: function ($event, item, searchbar) {
                            },
                            styleLight: true,
                            placeholder: this.$t('shift.template.selectEvent'),
                        },
                        styles: { width: '442px' }
                    },
                    {
                        label: this.$t('shift.plan.project'),
                        key: 'projectId',
                        hidden: false,
                        type: 'dropdownsearch',
                        value: null,
                        selectMultiple: false,
                        flexGroup: false,
                        dropdownSearchState: {
                            toggled: false,
                            displayedItems: [],
                            input: '',
                            items: projectitems,
                            selectMultiple: false,
                            selectedItems: [],
                            selectedItem: {},
                            onceSelected: function ($event, item, searchbar) {
                            },
                            styleLight: true,
                            placeholder: this.$t('shift.template.selectProject'),
                        },
                        styles: { width: '442px' }
                    },
                    {
                        label: this.$t('store.settings.location'),
                        key: 'locationId',
                        hidden: false,
                        type: 'dropdownsearch',
                        value: null,
                        selectMultiple: false,
                        flexGroup: false,
                        dropdownSearchState: {
                            toggled: false,
                            displayedItems: [],
                            input: '',
                            items: locationitems,
                            selectMultiple: false,
                            selectedItems: [],
                            selectedItem: {},
                            onceSelected: function ($event, item, searchbar) {
                            },
                            styleLight: true,
                            placeholder: this.$t('shift.template.selectLocation'),
                        },
                        styles: { width: '442px' }
                    },
                    {
                        label: this.$t('store.settings.costCenter'),
                        key: 'costCenterId',
                        hidden: false,
                        type: 'dropdownsearch',
                        value: null,
                        selectMultiple: false,
                        flexGroup: false,
                        dropdownSearchState: {
                            toggled: false,
                            displayedItems: [],
                            input: '',
                            items: costCenterItems,
                            selectMultiple: false,
                            selectFirst: true,
                            selectedItems: [],
                            selectedItem: {},
                            onceSelected: function ($event, item, searchbar) {
                            },
                            styleLight: true,
                            placeholder: this.$t('shift.template.selectCostCenter'),
                        },
                        styles: { width: '442px' }
                    },
                    {
                        label: this.$t('roster.comp.pinnedEmployee'),
                        key: 'employeeId',
                        hidden: false,

                        type: 'dropdownsearch',
                        value: selectedEmployeeItem ? selectedEmployeeItem.value : null,
                        selectMultiple: false,
                        dropdownSearchState: {
                            toggled: false,
                            displayedItems: [],
                            input: '',
                            items: employeeItems,
                            selectMultiple: false,
                            selectedItems: selectedEmployeeItem ? [selectedEmployeeItem] : [],
                            selectedItem: selectedEmployeeItem ? selectedEmployeeItem : null,
                            onceSelected: function ($event, item, searchbar) {
                            },
                            styleLight: true,
                            placeholder: 'Fixer Mitarbeiter',
                        },
                        styles: { width: '442px' }
                    },
                    { label: this.$t('store.events.description'), key: 'description', hidden: false, type: 'text', value: '', length: 20 },


                    { divider: true },
                    {
                        label: this.$t('shift.template.shiftTarget'),
                        key: 'tasks',
                        hidden: false,
                        type: 'listentry',
                        value: [],
                        flexGroup: false,
                        listEntryState: {
                            itemLabelMapping: [ { key: 'label', label: 'Ziel' }, {
                                key: 'value',
                                label: this.$t('store.events.description')
                            } ]
                        }
                    },


                ];

                let target = $event.currentTarget;
                this.$helpers.OpenPopup({
                    elRelative: target,
                    type: 'datafill',
                    componentState: {
                        entries: dataEntries,
                        errorCheck: [
                            (entries) => {

                                if (!entries['weekDays'] || entries['weekDays'].length < 1) {
                                    return {
                                        key: 'weekDays',
                                        type: 'error',
                                        label: this.$tc('store.settings.weekdays', 2),
                                        text: this.$t('shift.template.selectWeekdayError')
                                    }
                                } else {
                                    return null
                                }
                            },

                        ],
                        selectCallback: async (shiftTemplate) => {
                            // create a temporary id for the new shiftTemplate
                            shiftTemplate.id = Math.random().toString(36).substring(2, 8);
                            shiftTemplate.isNew = true;
                            if (shiftTemplate.weekOnly) {
                                shiftTemplate.validFrom = this.weekStartDateItems.past[0].value;
                                shiftTemplate.validTo = this.weekStartDateItems.future[0].value;
                            } else if (shiftTemplate.noValidTo) {
                                shiftTemplate.validTo = null;
                            } else {
                                shiftTemplate.validTo = shiftTemplate.validTo.add(1,'days').startOf('day');
                            }

                            shiftTemplate.fromLocationId = shiftTemplate.locationId;
                            shiftTemplate.toLocationId = shiftTemplate.locationId;
                            delete shiftTemplate.locationId;
                            shiftTemplate.from = shiftTemplate.from.format('HH:mm');
                            shiftTemplate.to = shiftTemplate.to.format('HH:mm');
                            
                            if (directSave) {
                                try {
                                    await this.createShiftTemplate(shiftTemplate);
                                } catch (e) {
                                    this.$helpers.error(this.$t('global.notifications.error'), e.message);
                                }
                            } else {
                                let from = shiftTemplate.from;
                                if (typeof from === 'string') from = view.$helpers.getMomentFromStringUTC(from, 'HH:mmZ');

                                let to = shiftTemplate.to;
                                if (typeof to === 'string') to = view.$helpers.getMomentFromStringUTC(to, 'HH:mmZ');
                                to = moment(from).hours(to.hours()).minutes(to.minutes());

                                if (to <= from) {
                                    to = to.add(1, 'days');
                                }
                                if (shiftTemplate.breakFrom == null || shiftTemplate.autoBreak) {
                                    if(view.storeSettings.noBreaks){
                                        shiftTemplate.breakValue = 0;
                                        shiftTemplate.breakFrom = null;
                                    } else {

                                        shiftTemplate.breakValue = view.$helpers.CalculateAutoBreakValueForPlanShift(false, from, to, view.storeSettings);
                                        shiftTemplate.breakFrom = null;
                                    }

                                }
                                this.$set(this.addedTemplateMap, shiftTemplate.id, shiftTemplate);
                            }
                        },
                    },
                    header: this.$t('shift.main.plan'),
                    remainAfterSelect: false,
                    width: "700px",
                    expanded: true,
                    centered: true
                });
            }

        },
        openEditShiftTemplatePopupCow($event, shiftTemplateGroup, shiftTemplate, directSave = false) {
            shiftTemplate = this.changedTemplateMap[shiftTemplate.id] || this.addedTemplateMap[shiftTemplate.id] || this.currentWeekShiftTemplateMap[shiftTemplate.id];
            let day = this.currentWeekStart;

            let from = this.$helpers.getMomentFromStringUTC(shiftTemplate.from, 'HH:mm');
            from = day.clone().hours(from.hours()).minutes(from.minutes());

            let to = this.$helpers.getMomentFromStringUTC(shiftTemplate.to, 'HH:mm');
            to = day.clone().hours(to.hours()).minutes(to.minutes());
            if (to <= from) {
                to = to.add(1, 'days');
            }
            let breakFrom = null;
            if (shiftTemplate.breakFrom) {
                breakFrom = this.$helpers.getMomentFromStringUTC(shiftTemplate.breakFrom, 'HH:mm');
                breakFrom = day.clone().hours(breakFrom.hours()).minutes(breakFrom.minutes());
            } else {
                breakFrom = from.clone();
            }
            let employeeItems = Object.values(this.employeeMap)
            .filter(e => e.active)
            .sort((a, b) => {
                return a.lastName.localeCompare(b.lastName);
            }).map(employee => {
                return {
                    label: employee.lastName + ' ' + employee.firstName,
                    value: employee.id,
                    selected: shiftTemplate.employeeId == employee.id
                }
            });

            employeeItems.push({ label: 'Kein Mitarbeiter', value: null, selected: shiftTemplate.employeeId == null });
            let selectedEmployeeItem = employeeItems.find(e => e.selected);
            if(shiftTemplate.employeeId != null && selectedEmployeeItem == null){
                let employee = this.employeeMap[shiftTemplate.employeeId];
                if(employee){
                    selectedEmployeeItem = { label: employee.lastName + ' ' + employee.firstName, value: employee.id, selected: true };
                    employeeItems.push(selectedEmployeeItem);
                } else {
                    selectedEmployeeItem = employeeItems[employeeItems.length - 1];
                    selectedEmployeeItem.selected = true;
                }
            }

            let dataEntries = [
                { label: 'Id', key: 'id', hidden: true, type: '', value: shiftTemplate.id },
                { label: 'Event Id', key: 'eventId', hidden: true, type: '', value: shiftTemplate.eventId },
                { label: 'Group Id', key: 'groupId', hidden: true, type: '', value: shiftTemplateGroup.id },
                { label: 'Spot Id', key: 'spotId', hidden: true, type: '', value: shiftTemplateGroup.spot.id },
                {
                    label: this.$t('global.general.from'),
                    key: 'from',
                    hidden: false,
                    flexGroup: true,
                    type: 'timeframe',
                    value: from,
                    timeBig: true
                },
                {
                    label: this.$t('global.general.to'),
                    key: 'to',
                    hidden: false,
                    flexGroup: true,
                    type: 'timeframe',
                    value: to,
                    timeBig: true
                },
                {
                    label: this.$t('store.events.event'),
                    key: 'eventName',
                    hidden: false,
                    type: 'text',
                    disabled: true,
                    value: shiftTemplate.eventId ? this.storeEventMap[shiftTemplate.eventId].name : '-'
                },
                {
                    label: this.$t('shift.plan.project'),
                    key: 'projectName',
                    hidden: false,
                    type: 'text',
                    disabled: true,
                    value: shiftTemplate.projectId ? this.projectMap[shiftTemplate.projectId].name : '-'
                },
                {
                    label: this.$t('store.settings.location'),
                    key: 'locationName',
                    hidden: false,
                    type: 'text',
                    disabled: true,
                    value: shiftTemplate.fromLocationId ? this.locationMap[shiftTemplate.fromLocationId].name : '-'
                },
                {
                    label: this.$t('store.settings.costCenter'),
                    key: 'costCenterName',
                    hidden: false,
                    type: 'text',
                    disabled: true,
                    value: shiftTemplate.costCenterId ? this.mlCostCenterMap[shiftTemplate.costCenterId].name : '-'
                },
                {
                    label: this.$t('roster.comp.pinnedEmployee'),
                    key: 'employeeId',
                    hidden: false,

                    type: 'dropdownsearch',
                    value: selectedEmployeeItem ? selectedEmployeeItem.value : null,
                    selectMultiple: false,
                    dropdownSearchState: {
                        toggled: false,
                        displayedItems: [],
                        input: '',
                        items: employeeItems,
                        selectMultiple: false,
                        selectedItems: selectedEmployeeItem ? [selectedEmployeeItem] : [],
                        selectedItem: selectedEmployeeItem ? selectedEmployeeItem : null,
                        onceSelected: function ($event, item, searchbar) {
                        },
                        styleLight: true,
                        placeholder: 'Fixer Mitarbeiter',
                    }
                },
                { divider: true },
                {
                    label: this.$t('shift.template.amount'),
                    key: 'count',
                    hidden: shiftTemplate.count == 1,
                    type: 'slider',
                    value: shiftTemplate.count,
                    slider: { value: shiftTemplate.count, min: 1, max: shiftTemplate.count, inputControl: true,
                    sliderInput: x => {return x}},
                },
                {
                    label: this.$t('shift.template.automaticWorkTime'),
                    key: 'autoWorkValue',
                    hidden: false,
                    type: 'bool',
                    value: shiftTemplate.workValue == -1
                },
                {
                    label: this.$t('shift.template.workTime'),
                    key: 'workValue',
                    hidden: shiftTemplate.workValue == -1,
                    value: shiftTemplate.workValue,
                    slider: {
                        hideMin: false, hideMax: false,
                        min: 0,
                        max: 24 * 60,
                        value: shiftTemplate.workValue,
                        step: 1,
                        DisplayLabel: (val) => {
                            let hours = this.$helpers.pad2(Math.floor(val / 60));
                            let minutes = this.$helpers.pad2(val % 60);
                            return hours + ':' + minutes;
                        },
                        inputControl: true,
                        sliderInput: (x) => {return x},
                        labelToInt: (x) => {
                            let split = x.split(':');
                            return Number(split[0] * 60) + Number((split[1] ? split[1] : 0));
                        },
                    },
                    computed: (datafill, lastVal, entryBeforeUpdate) => {
                        let autoWorkValue = datafill.entries.find(i => i.key == 'autoWorkValue').value;

                        if (autoWorkValue && entryBeforeUpdate.value != -1) {
                            entryBeforeUpdate.value = -1;
                            entryBeforeUpdate.slider.value = -1;
                            entryBeforeUpdate.slider = Object.assign({}, entryBeforeUpdate.slider);
                        }
                        if (!autoWorkValue && entryBeforeUpdate.value <= -1) {
                            entryBeforeUpdate.value = 0;
                            entryBeforeUpdate.slider.value = 0;
                            entryBeforeUpdate.slider = Object.assign({}, entryBeforeUpdate.slider);
                        }

                        if (autoWorkValue) {
                            entryBeforeUpdate.hidden = true;
                        } else {
                            entryBeforeUpdate.hidden = false;
                        }
                        return entryBeforeUpdate;
                    }

                },
                {
                    label: this.$t('shift.template.automaticBreak'),
                    key: 'autoBreak',
                    hidden: false,
                    type: 'bool',
                    value: shiftTemplate.breakFrom == null
                },
                {
                    label: this.$t('store.settings.breakTime'),
                    key: 'breakValue',
                    hidden: this.$helpers.CheckDatafillKeysForHiddenInverted('autoBreak'),
                    type: 'slider',
                    value: shiftTemplate.breakValue,
                    min: 0,
                    max: 240,
                    slider: {
                        hideMin: false,
                        hideMax: false,
                        min: 0,
                        max: 240,
                        value: shiftTemplate.breakValue,
                        step: 1,
                        inputControl: true,
                        sliderInput: (x) => {return x},
                    }
                },
                {
                    label: this.$t('shift.template.breakFrom'),
                    key: 'breakFrom',
                    hidden: this.$helpers.CheckDatafillKeysForHiddenInverted('autoBreak'),
                    type: 'timeframe',
                    value: breakFrom
                },

                {
                    key: 'weekDays', hidden: false, type: 'togglelist', toggleEntries: [
                        { label: 'Mo', value: 1, selected: shiftTemplate.weekDays.includes(1) },
                        { label: 'Di', value: 2, selected: shiftTemplate.weekDays.includes(2) },
                        { label: 'Mi', value: 3, selected: shiftTemplate.weekDays.includes(3) },
                        { label: 'Do', value: 4, selected: shiftTemplate.weekDays.includes(4) },
                        { label: 'Fr', value: 5, selected: shiftTemplate.weekDays.includes(5) },
                        { label: 'Sa', value: 6, selected: shiftTemplate.weekDays.includes(6) },
                        { label: 'So', value: 7, selected: shiftTemplate.weekDays.includes(7) },
                    ], styles: { width: '325px' }
                },
                { divider: true },
                { label: this.$t('store.events.description'), key: 'description', hidden: false, type: 'text', value: shiftTemplate.description ? shiftTemplate.description : '', length: 20 },
                {
                    label: this.$t('shift.template.shiftTarget'),
                    key: 'tasks',
                    hidden: false,
                    type: 'listentry',
                    value: [],
                    flexGroup: false,
                    listEntryState: {
                        itemLabelMapping: [ { key: 'label', label: 'Ziel' }, { key: 'value', label: this.$t('store.events.description') } ],
                        items: shiftTemplate.tasks ? shiftTemplate.tasks : []
                    }
                },
            ];
            if (shiftTemplate) {
                for (let x in shiftTemplate) {
                    let entry = dataEntries.find(e => e.key === x);
                    if (entry) {
                        if (x === 'from' || x === 'to' || x === 'breakFrom') {
                            //datafill is a good boy that would never pass wrong date values
                        } else entry.value = shiftTemplate[x];
                    }
                }
            }

            let target = $event.currentTarget;

            this.$helpers.OpenPopup({
                elRelative: target,
                type: 'datafill',
                componentState: {
                    entries: dataEntries,
                    errorCheck: [
                        (entries) => {

                            if (!entries['weekDays'] || entries['weekDays'].length < 1) {
                                return {
                                    key: 'weekDays',
                                    type: 'error',
                                    label: this.$tc('store.settings.weekdays', 2),
                                    text: this.$t('shift.template.selectWeekdayError')
                                }
                            } else {
                                return null
                            }
                        },
                    ],
                    selectCallback: async (template) => {
                        template = { ...template };
                        if (template.breakFrom && ( template.breakFrom < template.from || template.breakFrom + template.breakValue > template.to )) {
                            template.breakFrom = template.from;
                        }
                        const newTemplate = {
                            ...shiftTemplate,
                            ...template,
                            from: template.from.format('HH:mm'),
                            to: template.to.format('HH:mm'),
                            breakFrom: ( template.breakFrom && !template.autoBreak ) ? template.breakFrom.format('HH:mm') : null,
                        };

                        if (directSave) {
                            this.loading = true;
                            this.loadingText = this.$t('shift.template.savingShiftSchedule');
                            try {
                                await this.updateShiftTemplateCow({
                                    ...newTemplate,
                                    from: this.$helpers.getMomentFromStringTZ(template.from, 'HH:mm'),
                                    to: this.$helpers.getMomentFromStringTZ(template.to, 'HH:mm'),
                                    breakFrom: newTemplate.breakFrom ? this.$helpers.getMomentFromStringTZ(template.breakFrom, 'HH:mm') : null,
                                });
                            } catch (e) {
                                this.$helpers.error(this.$t('global.notifications.error'), e.message);
                            } finally {
                                this.loading = false;
                            }
                            return;
                        }
                        if (this.addedTemplateMap[shiftTemplate.id]) {
                            this.$set(this.addedTemplateMap, shiftTemplate.id, newTemplate);
                        } else {
                            if (newTemplate.updateMode) {
                                newTemplate.updateMode = Array.from(new Set([ ...newTemplate.updateMode, 'update-cow' ]));
                            } else {
                                newTemplate.updateMode = [ 'update-cow' ];
                            }
                            this.$set(this.changedTemplateMap, shiftTemplate.id, newTemplate);
                        }
                        if (this.removedTemplateMap[shiftTemplate.id]) {
                            this.$delete(this.removedTemplateMap, shiftTemplate.id);
                        }
                        // view.UpdateShiftTemplateCow(shiftTemplate);
                    }
                },
                header: this.$t('shift.template.editShiftSchedule'),
                width: '700px',
                centered: true,
                expanded: true,
            });
        },
        openEditShiftTemplatePopup($event, shiftTemplateGroup, shiftTemplate, directSave = false) {
            shiftTemplate = this.changedTemplateMap[shiftTemplate.id] || this.addedTemplateMap[shiftTemplate.id] || this.currentWeekShiftTemplateMap[shiftTemplate.id];
            let description = this.$t('shift.template.changesPastWarning')
            let eventtitems = [];
            Object.entries(this.storeEventMap).forEach(entry => {
                let event = entry[1];
                eventtitems.push({
                    label: event.name + ' ' + event.fromMoment.format('DD.MM.YYYY') + ' - ' + event.toMoment.format('DD.MM.YYYY'),
                    value: event.id
                });
            });

            let tValidFrom = shiftTemplate.validFrom ? this.$helpers.getMomentFromStringTZ(shiftTemplate.validFrom) : this.weekStartDateItems.past[0].value;
            let tValidTo = shiftTemplate.validTo ? this.$helpers.getMomentFromStringTZ(shiftTemplate.validTo).add(-1,'days').startOf('day') : this.weekStartDateItems.future[0].value.clone().add(-1,'days').startOf('day');

            let dataEntries = [
                { label: 'Id', key: 'id', hidden: true, type: '', value: shiftTemplate.id },
                { label: 'Event Id', key: 'eventId', hidden: true, type: '', value: shiftTemplate.eventId },
                { label: 'Group Id', key: 'groupId', hidden: true, type: '', value: shiftTemplateGroup.id },
                { label: 'Spot Id', key: 'spotId', hidden: true, type: '', value: shiftTemplateGroup.spot.id },
                {
                    label: this.$t('store.events.event'),
                    key: 'eventName',
                    hidden: false,
                    type: 'text',
                    disabled: true,
                    value: shiftTemplate.eventId ? this.storeEventMap[shiftTemplate.eventId].name : '-'
                },
                {
                    label: this.$t('shift.plan.project'),
                    key: 'projectName',
                    hidden: false,
                    type: 'text',
                    disabled: true,
                    value: shiftTemplate.projectId ? this.projectMap[shiftTemplate.projectId].name : '-'
                },
                {
                    label: this.$t('store.settings.location'),
                    key: 'locationName',
                    hidden: false,
                    type: 'text',
                    disabled: true,
                    value: shiftTemplate.fromLocationId ? this.locationMap[shiftTemplate.fromLocationId].name : '-'
                },

                { divider: true },
                {
                    label: this.$t('workBalance.bonus.recursIndefinitely'),
                    key: 'noValidFrom',
                    hidden: false,
                    disabled: shiftTemplate.eventId,
                    type: 'bool',
                    value: shiftTemplate.validFrom == null
                },
                {
                    label: this.$t('workBalance.bonus.validFrom'),
                    key: 'validFrom',
                    hidden: this.$helpers.CheckDatafillKeysForHiddenInverted('noValidFrom'),
                    type: 'date',
                    value: tValidFrom,
                    flexGroup: false
                },
                {
                    label: this.$t('workBalance.bonus.ongoingIndefinitely'),
                    key: 'noValidTo',
                    hidden: false,
                    disabled: shiftTemplate.eventId,
                    type: 'bool',
                    value: shiftTemplate.validTo == null
                },
                {
                    label: this.$t('pages.employeeContract.validUntil'),
                    key: 'validTo',
                    hidden: this.$helpers.CheckDatafillKeysForHiddenInverted('noValidTo'),
                    type: 'date',
                    value: tValidTo,
                    flexGroup: false
                },


                { divider: true },
                { label: this.$t('shift.template.skillMultiplier'), key: 'skillMultiplier', hidden: true, type: '', value: 1 },
                { label: this.$t('shift.template.workValueMultiplier'), key: 'workValueMultiplier', hidden: true, type: '', value: 1 },

                { label: this.$t('store.events.description'), key: 'description', hidden: false, type: 'text', value: shiftTemplate.description ? shiftTemplate.description : '', length: 20 },

            ];


            let target = $event.currentTarget;

            this.$helpers.OpenPopup({
                elRelative: target,
                type: 'datafill',
                componentState: {
                    entries: dataEntries,
                    description: description,
                    selectCallback: async (newTemplateData) => {
                        newTemplateData = { ...newTemplateData };
                        if (newTemplateData.noValidFrom) {
                            newTemplateData.validFrom = null;
                        }
                        if (newTemplateData.noValidTo) {
                            newTemplateData.validTo = null;
                        } else {
                            newTemplateData.validTo = newTemplateData.validTo.add(1,'days').startOf('day');
                        }
                        const origTemplate = this.addedTemplateMap[shiftTemplate.id] || this.changedTemplateMap[shiftTemplate.id] || shiftTemplate;
                        const newTemplate = { ...origTemplate, ...newTemplateData };

                        if (directSave) {
                            this.loading = true;
                            this.loadingText = this.$t('shift.template.shiftScheduleUpdating');
                            try {
                                await this.updateShiftTemplate(newTemplate);
                            } catch (e) {
                                this.$helpers.error(this.$t('global.notifications.error'), e.message);
                            } finally {
                                this.loading = false;
                            }
                            return;
                        }
                        if (this.addedTemplateMap[shiftTemplate.id]) {
                            this.$set(this.addedTemplateMap, shiftTemplate.id, newTemplate);
                        } else {
                            if (newTemplate.updateMode) {
                                newTemplate.updateMode = Array.from(new Set([ ...newTemplate.updateMode, 'update' ]));
                            } else {
                                newTemplate.updateMode = [ 'update' ];
                            }
                            this.$set(this.changedTemplateMap, shiftTemplate.id, newTemplate);
                        }
                        if (this.removedTemplateMap[shiftTemplate.id]) {
                            this.$delete(this.removedTemplateMap, shiftTemplate.id);
                        }
                        // view.UpdateShiftTemplate(shiftTemplate);
                    }
                },
                header: this.$t('shift.template.shiftScheduleValidity'),
                centered: true,
                expanded: true,
            });
        },
        openDeleteShiftTemplatePopup($event, templateId, directSave = false) {
            let view = this;
            let description = this.$t('shift.template.deleteShiftScheduleQuery');
            let template = view.currentWeekShiftTemplateMap[templateId];

            if (!template) {
                this.$delete(this.addedTemplateMap, templateId);
                return;
            }

            let vfts = template.validFrom ? this.$helpers.getMomentFromStringTZ(template.validFrom).startOf('isoWeek').unix() : null;
            let forceDelete = template.eventId || vfts == this.currentWeekStart.clone().unix();

            let dataEntries = [
                { label: 'Id', key: 'id', hidden: true, type: '', value: templateId },
                {
                    label: this.$t('shift.template.deletePastToo'),
                    key: 'actuallyDelete',
                    hidden: false,
                    type: 'bool',
                    value: forceDelete,
                    disabled: forceDelete
                }

            ];
            let target = $event.currentTarget;

            this.$helpers.OpenPopup({
                elRelative: target,
                type: 'datafill',
                componentState: {
                    entries: dataEntries,
                    description: description,
                    async selectCallback(item) {
                        if (item.actuallyDelete) {
                            if (directSave) {
                                view.loading = true;
                                view.loadingText = view.$t('shift.template.shiftScheduleDeleting');
                                try {
                                    await view.deleteShiftTemplate(templateId);
                                } catch (e) {
                                    view.$helpers.error(view.$t('global.notifications.error'), e.message);
                                } finally {
                                    view.loading = false;
                                }
                                return;
                            }
                            // view.DeleteShiftTemplate(item.id);
                            if (view.addedTemplateMap[template.id]) {
                                view.$delete(view.addedTemplateMap, template.id);
                            } else {
                                view.$set(view.removedTemplateMap, template.id, template);
                                view.$delete(view.changedTemplateMap, template.id);
                            }
                            return;
                        }
                        const newTemplate = {
                            ...template,
                            validTo: moment(view.currentWeekStart).format(),
                        };
                        if (directSave) {
                            view.loading = true;
                            view.loadingText = this.$t('shift.template.shiftScheduleDeleting');
                            try {
                                const updatePayload = {
                                    id: item.id,
                                    spotId: newTemplate.spotId,
                                    description: newTemplate.description,
                                    validFrom: newTemplate.validFrom,
                                    validTo: newTemplate.validTo,
                                };
                                await view.updateShiftTemplate(updatePayload);
                            } catch (e) {
                                view.$helpers.error(view.$t('global.notifications.error'), e.message);
                            } finally {
                                view.loading = false;
                            }
                        } else {
                            if (newTemplate.updateMode) {
                                newTemplate.updateMode = Array.from(new Set([ ...newTemplate.updateMode, 'update' ]));
                            } else {
                                newTemplate.updateMode = [ 'update' ];
                            }
                            view.$set(view.changedTemplateMap, template.id, newTemplate);
                            view.$delete(view.removedTemplateMap, template.id);
                        }

                        // let updatePayload = {id: item.id, spotId: template.spotId, description: template.description, validFrom: template.validFrom ? view.$helpers.getMomentFromStringTZ(template.validFrom) : null, validTo: view.currentWeekStart.clone()};
                        // view.UpdateShiftTemplate(updatePayload);
                    }
                },
                header: this.$t('shift.template.deleteShiftSchedule'),
                centered: true,
                expanded: true,
            });


        },
        async createShiftTemplate(shiftTemplate) {
            let view = this;
            let url = '/api/sec/shifttemplate';
            url = this.$helpers.appendParam('storeId', this.$store.state.storeId, url, '/api/sec/shifttemplate');
            let day = this.currentWeekStart;

            let from = shiftTemplate.from;
            if (typeof from === 'string') from = this.$helpers.getMomentFromStringUTC(from, 'HH:mmZ');

            let to = shiftTemplate.to;
            if (typeof to === 'string') to = this.$helpers.getMomentFromStringUTC(to, 'HH:mmZ');
            to = moment(from).hours(to.hours()).minutes(to.minutes());

            if (to <= from) {
                to = to.add(1, 'days');
            }


            let breakValue = shiftTemplate.breakValue;
            let breakFrom = shiftTemplate.breakFrom;
            let invalidBreak = false;
            let invalidSpot = shiftTemplate.spotId ? false : true;
            let invalidValidity = false;
            if (shiftTemplate.breakFrom == null || shiftTemplate.autoBreak) {
                if(this.storeSettings.noBreaks){
                    breakValue = 0;
                    breakFrom = null;
                } else {

                    breakValue = view.$helpers.CalculateAutoBreakValueForPlanShift(false, from, to, view.storeSettings);
                    breakFrom = null;
                }

            } else {

                breakFrom = moment(from).hours(breakFrom.hours()).minutes(breakFrom.minutes());

                if (breakFrom < from) {
                    breakFrom.add(1, 'days');
                }

                if (breakFrom < from || breakFrom > to) {
                    invalidBreak = true;
                }
            }
            if (shiftTemplate.validFrom != null && shiftTemplate.validTo != null) {
                invalidValidity = shiftTemplate.validFrom >= shiftTemplate.validTo;
            }
            let error = null;
            if (invalidBreak) {
                error = this.$t('shift.template.invalidBreak');
                // view.$helpers.error('Fehler', 'Der Beginn der Pause muss zwischen Schicht Start und Schicht Ende liegen!')
            } else if (invalidSpot) {
                error = this.$t('shift.template.invalidSpot');
                // view.$helpers.error('Fehler', 'Die Schicht muss einer Position zugewiesen sein!');
            } else if (invalidValidity) {
                error = this.$t('shift.template.invalidValidity');
                // view.$helpers.error('Fehler', 'Gültig Von muss vor Gültig Bis liegen!');
            } else {
                let payload = {
                    groupId: shiftTemplate.groupId,
                    from: from.format('HH:mm'),
                    to: to.format('HH:mm'),
                    breakFrom: breakFrom == null ? null : breakFrom.format('HH:mm'),
                    spotId: shiftTemplate.spotId,
                    eventId: shiftTemplate.eventId ? shiftTemplate.eventId : null,
                    validFrom: shiftTemplate.eventId ? null : ( shiftTemplate.validFrom ? ( ( typeof shiftTemplate.validFrom === 'string' || shiftTemplate.validFrom instanceof String ) ? shiftTemplate.validFrom : shiftTemplate.validFrom.format() ) : null ),
                    validTo: shiftTemplate.eventId ? null : ( shiftTemplate.validTo ? ( ( typeof shiftTemplate.validTo === 'string' || shiftTemplate.validTo instanceof String ) ? shiftTemplate.validTo : shiftTemplate.validTo.format() ) : null ),
                    count: shiftTemplate.count,
                    description: shiftTemplate.description,
                    skillMultiplier: 1,
                    workValueMultiplier: 1,
                    workValue: shiftTemplate.workValue,
                    breakValue: breakValue,
                    holidayWorkValue: -1,
                    weekDays: shiftTemplate.weekDays ? shiftTemplate.weekDays : [],
                    tasks: shiftTemplate.tasks,
                    projectId: shiftTemplate.projectId,
                    fromLocationId: shiftTemplate.fromLocationId,
                    toLocationId: shiftTemplate.toLocationId,
                    employeeId: shiftTemplate.employeeId,
                    costCenterId: shiftTemplate.costCenterId
                }

                try {
                    const response = await this.axios({
                        method: 'post',
                        headers: {
                            'AUTHORIZATION': 'bearer ' + this.$store.state.jwt
                        },
                        url: url,
                        data: payload
                    });
                    view.$helpers.success(view.$t('store.events.creationSuccessful'), view.$t('shift.template.shiftScheduleCreated'));
                    view.$set(view.currentWeekShiftTemplateMap, response.data.data.id, response.data.data);
                    view.shiftTemplates.push(response.data.data);
                    view.$store.commit('invalidateState', { states: [ 'planShiftMapState', 'shiftTemplateMapState' ] });
                    return response;
                } catch (e) {
                    error = this.$t('shift.template.createShiftScheduleError');
                    // view.$helpers.error(this.$t('global.ui.createFailed'), 'Fehler beim Erstellen des Schichtplans');
                }
            }

            return Promise.reject(new Error(error));
        },
        async createShiftTemplateMulti(shiftTemplates) {
            //console.log(JSON.stringify(shiftTemplate));
            let view = this;
            let url = '/api/sec/shifttemplate/multi';

            let params = {
                storeId: this.$store.state.storeId
            }
            let payload = {
                requests: []
            }
            shiftTemplates.forEach(shiftTemplate => {
                let from = shiftTemplate.from;

                let to = from.clone().hours(shiftTemplate.to.hours()).minutes(shiftTemplate.to.minutes());

                if (to <= from) {
                    to = to.add(1, 'days');
                }

                let breakValue = shiftTemplate.breakValue;
                let breakFrom = shiftTemplate.breakFrom;
                let invalidBreak = false;
                let invalidSpot = shiftTemplate.spotId ? false : true;
                if (shiftTemplate.breakFrom == null || shiftTemplate.autoBreak) {
                    if(this.storeSettings.noBreaks){
                        breakValue = 0;
                        breakFrom = null;
                    } else {

                        breakValue = view.$helpers.CalculateAutoBreakValueForPlanShift(false, from, to, view.storeSettings);
                        breakFrom = null;
                    }
                } else {
                    if (typeof breakFrom === 'string' || breakFrom instanceof String)
                        breakFrom = moment(breakFrom, 'HH:mm');
                    breakFrom = from.clone().hours(breakFrom.hours()).minutes(breakFrom.minutes());

                    if (breakFrom < from) {
                        breakFrom.add(1, 'days');
                    }
                    if (breakFrom < from || breakFrom > to) {
                        invalidBreak = true;
                    }
                }
                if (!invalidBreak && !invalidSpot) {
                    let item = {
                        groupId: shiftTemplate.groupId,
                        from: from.format('HH:mm'),
                        to: to.format('HH:mm'),
                        breakFrom: breakFrom == null ? null : breakFrom.format('HH:mm'),
                        spotId: shiftTemplate.spotId,
                        eventId: shiftTemplate.eventId ? shiftTemplate.eventId : null,
                        count: shiftTemplate.count,
                        description: shiftTemplate.description,
                        validFrom: shiftTemplate.eventId ? null : ( shiftTemplate.validFrom ? shiftTemplate.validFrom : null ),
                        validTo: shiftTemplate.eventId ? null : ( shiftTemplate.validTo ? shiftTemplate.validTo : null ),
                        skillMultiplier: 1,
                        workValueMultiplier: 1,
                        breakValue: breakValue,
                        workValue: shiftTemplate.workValue,
                        holidayWorkValue: -1,
                        weekDays: shiftTemplate.weekDays ? shiftTemplate.weekDays : [],
                        tasks: shiftTemplate.tasks,
                        projectId: shiftTemplate.projectId,
                        fromLocationId: shiftTemplate.fromLocationId,
                        toLocationId: shiftTemplate.toLocationId,
                        employeeId: shiftTemplate.employeeId,
                        costCenterId: shiftTemplate.costCenterId
                    }
                    payload.requests.push(item);
                }

            })

            if (payload.requests.length > 0) {
                try {
                    const response = await this.axios({
                        method: 'post',
                        params: params,
                        headers: {
                            'AUTHORIZATION': 'bearer ' + this.$store.state.jwt
                        },
                        url: url,
                        data: payload
                    });
                    view.$helpers.success(view.$t('store.events.creationSuccessful'), view.$t('shift.template.shiftScheduleCreated'))
                    response.data.data.forEach(shiftTemplate => {
                        view.$set(view.currentWeekShiftTemplateMap, shiftTemplate.id, shiftTemplate);
                        view.shiftTemplates.push(shiftTemplate);
                    })
                    view.$store.commit('invalidateState', { states: [ 'planShiftMapState', 'shiftTemplateMapState' ] });
                } catch (error) {
                    // view.$helpers.error(this.$t('global.ui.createFailed'), 'Fehler beim Erstellen des Schichtplans');
                    return Promise.reject(new Error(view.$t('shift.template.createShiftScheduleError')));
                }
            }
            return Promise.resolve();
        },
        async updateShiftTemplate(shiftTemplate, refresh = true) {
            //console.log(JSON.stringify(shiftTemplate));
            let view = this;
            let url = '/api/sec/shifttemplate';
            url = this.$helpers.appendParam('storeId', this.$store.state.storeId, url, '/api/sec/shifttemplate');

            let from = view.$helpers.getMomentFromStringUTC(shiftTemplate.from, 'HH:mmZ')
            let to = view.$helpers.getMomentFromStringUTC(shiftTemplate.to, 'HH:mmZ')
            to.date(from.date());
            to.month(from.month());
            to.year(from.year());

            if (to <= from) {
                to = to.add(1, 'days');
            }

            let invalidValidity = false;
            let invalidSpot = shiftTemplate.spotId ? false : true;
            if (shiftTemplate.validFrom != null && shiftTemplate.validTo != null) {
                invalidValidity = shiftTemplate.validFrom >= shiftTemplate.validTo;
            }

            let error = null;
            if (invalidSpot) {
                error = new Error(this.$t('shift.template.invalidSpot'));
                // view.$helpers.error('Fehler', 'Die Schicht muss einer Position zugewiesen sein!');
            } else if (invalidValidity) {
                error = new Error(this.$t('shift.template.invalidValidity'));
                // view.$helpers.error('Fehler', 'Gültig Von muss vor Gültig Bis liegen!');
            } else {
                let payload = {

                    id: shiftTemplate.id,
                    spotId: shiftTemplate.spotId,
                    description: shiftTemplate.description,
                    workValue: -1,
                    holidayWorkValue: -1,
                    validFrom: shiftTemplate.validFrom ? moment(shiftTemplate.validFrom).format() : null,
                    validTo: shiftTemplate.validTo ? moment(shiftTemplate.validTo).format() : null,
                    updateFields: [ 'spotId', 'description', 'validFrom', 'validTo' ],
                    updateShifts: true
                };

                try {
                    const response = this.axios({
                        method: 'put',
                        headers: {
                            'AUTHORIZATION': 'bearer ' + this.$store.state.jwt
                        },
                        url: url,
                        data: payload,
                    });

                    view.$store.commit('invalidateState', { states: [ 'planShiftMapState', 'shiftTemplateMapState' ] });
                    view.$helpers.success(view.$t('global.notifications.updateMessage'), view.$t('shift.template.shiftScheduleEdited'));
                    if (refresh){
                        console.log("updateShiftTemplate")
                        await view.RefreshShiftTemplates();
                    }


                    return response;
                } catch (e) {
                    error = new Error(this.$t('shift.template.editShiftScheduleError') + '');
                    // console.log(error);
                    // view.$helpers.error(this.$t('global.notifications.updateFailed'), 'Fehler beim Ändern des Schichtplans');
                }
            }
            return Promise.reject(error);
        },
        async updateShiftTemplateCow(shiftTemplate, refresh = true) {
            let view = this;
            let url = '/api/sec/shifttemplate/cow';
            url = view.$helpers.appendParam('storeId', view.$store.state.storeId, url, '/api/sec/shifttemplate/cow');

            let from = shiftTemplate.from;
            let to = shiftTemplate.to;
            let breakFrom = shiftTemplate.breakFrom;
            let breakValue = shiftTemplate.breakValue;
            let autoBreak = shiftTemplate.autoBreak;
            let toNew = from.clone().hours(shiftTemplate.to.hours()).minutes(shiftTemplate.to.minutes());


            if (toNew <= from) {
                toNew = toNew.add(1, 'days');
            }

            let breakFromNew = breakFrom;
            let breakValueNew = breakValue;
            let invalidBreak = false;
            if (autoBreak) {

                breakValueNew = view.$helpers.CalculateAutoBreakValueForPlanShift(false, from, toNew, view.storeSettings);
                breakFromNew = null;
            } else {
                breakFromNew = breakFromNew.clone().date(from.date()).month(from.month()).year(from.year());

                if (breakFromNew < from) {
                    breakFromNew.add(1, 'days');
                }

                if (breakFromNew < from || breakFromNew > toNew) {
                    invalidBreak = true;
                }
            }

            let error = null;
            if (invalidBreak) {
                error = new Error(this.$t('shift.template.invalidBreak'));
                // view.$helpers.error('Fehler', 'Der Beginn der Pause muss zwischen Schicht Start und Schicht Ende liegen!')
            } else {
                let payload = {
                    id: shiftTemplate.id,
                    from: from.format('HH:mm'),
                    to: toNew.format('HH:mm'),
                    count: shiftTemplate.count,
                    weekDays: shiftTemplate.weekDays,
                    breakFrom: breakFromNew == null ? null : breakFromNew.format('HH:mm'),
                    breakValue: breakValueNew,
                    workValue: shiftTemplate.workValue,
                    updateDate: this.currentWeekStart.format(),
                    timeZone: this.storeSettings.timeZone,
                    tasks: shiftTemplate.tasks,
                    description: shiftTemplate.description,
                    employeeId: shiftTemplate.employeeId,
                    costCenterId: shiftTemplate.costCenterId,
                };

                try {
                    const response = await this.axios({
                        method: 'put',
                        headers: {
                            'AUTHORIZATION': 'bearer ' + this.$store.state.jwt
                        },
                        url: url,
                        data: payload
                    });
                    view.$helpers.success(view.$t('global.notifications.updateMessage'), 'Schichtplan wurde erfolgreich upgedatet')
                    view.$store.commit('invalidateState', { states: [ 'planShiftMapState', 'shiftTemplateMapState' ] });
                    if (refresh){
                        console.log("updateShiftTemplateCow")
                        await view.RefreshShiftTemplates();
                    }

                    return response;
                } catch (e) {
                    error = new Error(this.$t('shift.template.editShiftScheduleError') + '');
                    // view.$helpers.error(this.$t('global.notifications.updateFailed'), this.$t('shift.template.editShiftScheduleError'));
                }
            }
            return Promise.reject(error);
        },
        deleteShiftTemplate(templateId) {
            let url = '/api/sec/shifttemplate';
            let params = {
                storeId: this.$store.state.storeId,
                id: templateId
            }

            try {
                const response = this.axios({
                    method: 'delete',
                    params: params,
                    headers: {
                        'AUTHORIZATION': 'bearer ' + this.$store.state.jwt
                    },
                    url: url,
                });
                const index = this.shiftTemplates.findIndex(stemplate => stemplate.id === templateId)
                if (~index) {
                    this.shiftTemplates.splice(index, 1);
                }
                this.$delete(this.currentWeekShiftTemplateMap, templateId);
                this.$store.commit('invalidateState', { states: [ 'planShiftMapState', 'shiftTemplateMapState' ] });
                this.$helpers.success(this.$t('shift.template.deleted'), this.$t('shift.template.shiftScheduleDeleted'));
                return response;
            } catch (error) {
                return Promise.reject(new Error(this.$t('shift.template.deleteShiftScheduleError') + ''));
                // view.$helpers.error('Löschen fehlgeschlagen', 'Fehler beim Löschen des Schichtplans');
            }
        },

        newEventPopup($event, weekdayIndex, directSave = false) {
            let date = this.currentWeekStart.clone().hours(0).add(weekdayIndex + 0, 'day');
            let eventitems = [];
            Object.values(this.storeEventMap).filter(e => e.allowTemplateAssignment).sort((a, b) => b.fromMoment.unix() - a.fromMoment.unix()).forEach(event => {
                let to = event.toMoment.clone();
                if (to.hours() == 0 && to.minutes() == 0) {
                    //subtract 1 day because brainlets don't understand what an exclusive time range is
                    to.add(-1, 'days');
                }
                eventitems.push({
                    label: event.name + ' ' + event.fromMoment.format('DD.MM.YYYY') + ' - ' + to.format('DD.MM.YYYY'),
                    name: event.name,
                    value: event.id
                });
            });
            this.$helpers.PopupNewEvent($event, date, eventitems, this.currentWeekStart, this.currentWeekEnd.clone().add(-1, 'days'), async (storeEvent) => {

                storeEvent.from.year(storeEvent.fromDate.year());
                storeEvent.from.month(storeEvent.fromDate.month());
                storeEvent.from.date(storeEvent.fromDate.date());

                storeEvent.to.year(storeEvent.toDate.year());
                storeEvent.to.month(storeEvent.toDate.month());
                storeEvent.to.date(storeEvent.toDate.date());
                if (storeEvent.to.hours() == 0 && storeEvent.to.minutes() == 0) {
                    //add 1 day because brainlets don't understand what an exclusive time range is
                    storeEvent.to.add(1, 'days');
                }
                if (storeEvent.from < this.currentWeekStart) {
                    storeEvent.from = this.currentWeekStart.clone();
                }
                if (storeEvent.to > this.currentWeekEnd) {
                    storeEvent.to = this.currentWeekEnd.clone();
                }

                const events = Object.values({
                    ...this.storeEventMap,
                    ...this.addedEventMap,
                    ...this.changedEventMap,
                });
                const duplicateEvent = events.find(
                    (e) => storeEvent.name === e.name && storeEvent.from.isSame(e.from) && storeEvent.to.isSame(e.to)
                );

                if (duplicateEvent) {
                    this.$helpers.error(
                        this.$t('global.ui.createFailed'),
                        this.$t('shift.template.sameNameExists'),
                    );
                } else if (storeEvent.from >= this.currentWeekStart && storeEvent.to <= this.currentWeekEnd && storeEvent.from != storeEvent.to && storeEvent.from < storeEvent.to) {
                    storeEvent.id = 'event-' + Math.random().toString(36).substring(2, 8);
                    // console.log(storeEvent);
                    // view.createStoreEvent(storeEvent);

                    if (directSave) {
                        this.loading = true;
                        this.loadingText = this.$t('shift.template.eventCreating')
                        try {
                            await this.createStoreEvent(storeEvent);
                        } catch (e) {
                            this.$helpers.error(this.$t('global.notifications.error'), e.message);
                        } finally {
                            this.loading = false;
                        }
                    } else {
                        this.$set(this.addedEventMap, storeEvent.id, storeEvent);
                    }
                } else {
                    this.$helpers.error(this.$t('global.ui.createFailed'), this.$t('global.notifications.updateFailed'));
                }

            });
        },
        editEventPopup($event, eventT, directSave = false) {
            if (!this.canEdit) return;
            let event = this.addedEventMap[eventT.id] || this.changedEventMap[eventT.id] || this.storeEventMap[eventT.id] || eventT;
            event = Object.assign({}, event);
            let to = this.$helpers.getMomentFromStringTZ(event.to);
            if (to.hours() == 0 && to.minutes() == 0) {
                //subtract 1 day because brainlets don't understand what an exclusive time range is
                to.add(-1, 'days');
                event.to = to.format();
            }
            this.$helpers.PopupEditEvent($event, event, async (storeEvent) => {
                storeEvent.from = this.$helpers.getMomentFromStringTZ(storeEvent.from);
                storeEvent.to = this.$helpers.getMomentFromStringTZ(storeEvent.to);
                if (storeEvent.to.hours() == 0 && storeEvent.to.minutes() == 0) {
                    //add 1 day because brainlets don't understand what an exclusive time range is
                    storeEvent.to.add(1, 'days');
                }
                if (storeEvent.from < this.currentWeekStart) {
                    storeEvent.from = this.currentWeekStart.clone();
                }
                if (storeEvent.to > this.currentWeekEnd) {
                    storeEvent.to = this.currentWeekEnd.clone();
                }
                storeEvent.allowTemplateAssignment = true;
                if (storeEvent.from >= this.currentWeekStart && storeEvent.to <= this.currentWeekEnd && storeEvent.from != storeEvent.to && storeEvent.from < storeEvent.to) {
                    if (directSave) {
                        this.loading = true;
                        this.loadingText = this.$t('shift.template.eventUpdating')
                        try {
                            await this.editStoreEvent(storeEvent);
                        } catch (e) {
                            this.$helpers.error(this.$t('global.notifications.error'), e.message);
                        } finally {
                            this.loading = false;
                        }
                        return;
                    }
                    // view.editStoreEvent(storeEvent);
                    if (this.removedEventMap[storeEvent.id]) {
                        this.$delete(this.removedEventMap, storeEvent.id);
                    }

                    if (this.addedEventMap[storeEvent.id]) {
                        this.$set(this.addedEventMap, storeEvent.id, storeEvent);
                    } else {
                        this.$set(this.changedEventMap, storeEvent.id, storeEvent);
                    }
                } else {
                    this.$helpers.error(this.$t('global.ui.createFailed'), this.$t('global.notifications.updateFailed'));
                }

            });
        },
        async removeEvent(storeEvent, directSave = false) {
            if (directSave) {
                this.loading = true;
                this.loadingText = this.$t('shift.template.eventDeleting')
                try {
                    await this.deleteStoreEvent(storeEvent);
                    this.loading = false;
                    this.refresh();
                } catch (e) {
                    this.$helpers.error(this.$t('global.notifications.error'), e.message);
                    this.loading = false;
                }
            } else if (this.addedEventMap[storeEvent.id]) {
                this.$delete(this.addedEventMap, storeEvent.id);
            } else {
                this.$set(this.removedEventMap, storeEvent.id, storeEvent);
            }
        },

        createStoreEvent(storeEvent) {
            let view = this;
            let url = '/api/sec/storeevent';

            let params = {
                storeId: this.$store.state.storeId
            };
            let payload = {
                name: storeEvent.name,
                from: this.$helpers.convertDateString(storeEvent.from),
                to: this.$helpers.convertDateString(storeEvent.to),
                federalHoliday: storeEvent.federalHoliday,
                bankHoliday: storeEvent.bankHoliday,
                contractHoliday: storeEvent.contractHoliday,
                description: storeEvent.description,
                color: storeEvent.color,
                shiftCreationMode: storeEvent.shiftCreationMode,
                createWithTemplates: storeEvent.createWithTemplates,
                copyEventId: storeEvent.copyEventId,
                allowTemplateAssignment: storeEvent.allowTemplateAssignment
            };
            if (payload.shiftCreationMode == 2) {
                payload.createWithTemplates = false;
            }
            if (payload.copyEventId != null && payload.shiftCreationMode != 2) {
                let copyEventFrom = this.$helpers.getMomentFromStringTZ(this.storeEventMap[payload.copyEventId].from);
                let copyEventTo = this.$helpers.getMomentFromStringTZ(this.storeEventMap[payload.copyEventId].to)

                let copyDayIndex = copyEventTo.diff(copyEventFrom, 'minutes') > 1440 ? null : copyEventFrom.isoWeekday();
                let newDayFrom = this.$helpers.getMomentFromStringTZ(payload.from);
                let newDayTo = this.$helpers.getMomentFromStringTZ(payload.to);

                let newDayIndex = newDayTo.diff(newDayFrom, 'minutes') > 1440 ? null : newDayFrom.isoWeekday();
                payload.copyDayIndex = copyDayIndex;
                payload.newDayIndex = newDayIndex;
            }

            return this.axios({
                method: 'post',
                params: params,
                headers: {
                    'AUTHORIZATION': 'bearer ' + this.$store.state.jwt
                },
                url: url,
                data: payload
            }).then(response => {
                view.$helpers.success('Erfolg', 'Event wurde erfolgreich erstellt!');
                let event = response.data.data;
                let eventId = event.id;

                let fromMoment = view.$helpers.getMomentFromStringTZ(event.from);
                let toMoment = view.$helpers.getMomentFromStringTZ(event.to);

                view.$set(view.storeEventMap, eventId, event);
                view.$set(view.storeEventMap[eventId], 'fromMoment', fromMoment);
                view.$set(view.storeEventMap[eventId], 'toMoment', toMoment);

                view.$store.commit('invalidateState', { states: [ 'holidayMapState' ] })
                if (storeEvent.copyEventId != null) {

                    return this.refresh(null, true);
                } else if (payload.createWithTemplates && storeEvent.shiftCreationMode != 2 && payload.allowTemplateAssignment) {




                    let templates = [];
                    view.shiftTemplates.filter(s => s.eventId == null).forEach(s => {

                        let overlapsEvent = false;
                        let fromUtc = view.$helpers.getMomentFromStringTZ(s.from, 'HH:mm');
                        let toUtc = view.$helpers.getMomentFromStringTZ(s.to, 'HH:mm')
                        let templateFromMoment = fromMoment.clone().hours(fromUtc.hours()).minutes(fromUtc.minutes());
                        let templateToMoment = fromMoment.clone().hours(toUtc.hours()).minutes(toUtc.minutes());
                        let breakFromMoment = null;
                        if (s.breakFrom) {
                            let breakFromUtc = view.$helpers.getMomentFromStringTZ(s.breakFrom, 'HH:mm');
                            breakFromMoment = fromMoment.clone().hours(breakFromUtc.hours()).minutes(breakFromUtc.minutes());
                        }
                        let weekDays = [];
                        s.weekDays.forEach(w => {
                            templateFromMoment.isoWeekday(w);
                            templateToMoment.isoWeekday(w);
                            if (templateToMoment <= templateFromMoment) {
                                templateToMoment.add(1, 'days')
                            }

                            if (toMoment > templateFromMoment && fromMoment < templateToMoment && templateFromMoment >= fromMoment) {
                                weekDays.push(w);
                            }

                        })
                        if (weekDays.length > 0) {
                            let tempTemplate = JSON.parse(JSON.stringify(s));
                            tempTemplate.weekDays = weekDays;
                            tempTemplate.eventId = eventId;
                            tempTemplate.from = templateFromMoment;
                            tempTemplate.to = templateToMoment;
                            tempTemplate.breakFrom = breakFromMoment;
                            templates.push(tempTemplate);
                        }
                    });
                    if (templates.length > 0) {
                        return view.createShiftTemplateMulti(templates).catch((error) => {
                            view.$helpers.error(view.$t('global.notifications.error'), error.message);
                        });
                    }
                }
            }).catch(function (error) {
                view.$helpers.error(view.$t('global.notifications.error'), view.$t('global.ui.createFailed'));
                console.log(error)
            })

        },
        async editStoreEvent(storeEvent) {

            let view = this;
            let url = '/api/sec/storeevent';

            let params = {
                storeId: this.$store.state.storeId
            };
            let payload = {
                id: storeEvent.id,
                name: storeEvent.name,
                from: storeEvent.from.format(),
                to: storeEvent.to.format(),
                federalHoliday: storeEvent.federalHoliday,
                bankHoliday: storeEvent.bankHoliday,
                contractHoliday: storeEvent.contractHoliday,
                description: storeEvent.description,
                color: storeEvent.color,
                shiftCreationMode: storeEvent.shiftCreationMode,
                updateFields: [ 'name', 'from', 'to', 'federalHoliday', 'bankHoliday', 'contractHoliday', 'description', 'color', 'shiftCreationMode' ]
            };

            try {
                const response = await this.axios({
                    method: 'put',
                    params: params,
                    headers: {
                        'AUTHORIZATION': 'bearer ' + this.$store.state.jwt
                    },
                    url: url,
                    data: payload
                });
                view.$store.commit('invalidateState', { states: [ 'holidayMapState' ] })
                view.$set(view.storeEventMap[payload.id], 'name', payload.name);
                view.$set(view.storeEventMap[payload.id], 'color', payload.color);
                view.$set(view.storeEventMap[payload.id], 'fromMoment', view.$helpers.getMomentFromStringTZ(payload.from));
                view.$set(view.storeEventMap[payload.id], 'toMoment', view.$helpers.getMomentFromStringTZ(payload.to));
                view.$set(view.storeEventMap[payload.id], 'federalHoliday', payload.federalHoliday);
                view.$set(view.storeEventMap[payload.id], 'bankHoliday', payload.bankHoliday);
                view.$set(view.storeEventMap[payload.id], 'description', payload.description);
                view.$set(view.storeEventMap[payload.id], 'from', payload.from);
                view.$set(view.storeEventMap[payload.id], 'to', payload.to);
                view.$set(view.storeEventMap[payload.id], 'shiftCreationMode', payload.shiftCreationMode);
                view.$helpers.success(view.$t('shift.template.edited'), view.$t('shift.template.eventUpdated'));

                return response;
            } catch (error) {
                return Promise.reject(new Error(this.$t('shift.template.updateEventError') + ''));
                // view.$helpers.error('Fehler', 'Fehler beim Update des Events!');
                // console.log(error);
            }

        },
        async deleteStoreEvent(storeEvent) {

            let view = this;
            let url = '/api/sec/storeevent';

            let params = {
                id: storeEvent.id,
                storeId: this.$store.state.storeId
            }


            try {
                const response = await this.axios({
                    method: 'delete',
                    params: params,
                    headers: {
                        'AUTHORIZATION': 'bearer ' + this.$store.state.jwt
                    },
                    url: url,
                });
                view.$delete(view.storeEventMap, storeEvent.id);
                view.$store.commit('invalidateState', { states: [ 'planShiftMapState', 'shiftTemplateMapState', 'holidayMapState'] });
                view.$helpers.success(view.$t('shift.template.deleted'), view.$t('shift.template.eventDeleted'));
                return response;
            } catch (error) {
                return Promise.reject(new Error(this.$t('shift.template.deleteEventError') + ''));
                // view.$helpers.error('Fehler', 'Fehler beim Löschen des Events!');
                // console.log(error);
            }

        },

        OpenNewShiftTemplateGroupPopup($event) {
            let view = this;

            let description = '';
            let dataEntries = [

                { label: this.$t('pages.employeeData.name'), key: 'name', hidden: false, type: 'text', value: '', length: 50 },
                { label: this.$t('store.events.description'), key: 'description', hidden: false, type: 'text', value: '', length: 50 },


            ];
            let target = $event.currentTarget;

            this.$helpers.OpenPopup({
                elRelative: target,
                type: 'datafill',
                componentState: {
                    entries: dataEntries,
                    errorCheck: [
                        (entries) => {

                            if (!entries['name'] || entries['name'].length < 1) {
                                return {
                                    key: 'name',
                                    type: 'error',
                                    label: this.$t('pages.employeeData.name'),
                                    text: this.$t('shift.template.nameShiftGroupPlease')
                                }
                            } else {
                                return null
                            }
                        },
                        (entries) => {

                            if (entries['name'] && entries['name'].length > 50) {
                                return {
                                    key: 'name',
                                    type: 'error',
                                    label: this.$t('pages.employeeData.name'),
                                    text: this.$t('shift.template.nameTooLong')
                                }
                            } else {
                                return null
                            }
                        },
                        (entries) => {

                            if (entries['description'] && entries['description'].length > 3000) {
                                return {
                                    key: 'description',
                                    type: 'error',
                                    label: this.$t('store.events.description'),
                                    text: this.$t('shift.template.descriptionTooLong')
                                }
                            } else {
                                return null
                            }
                        }
                    ],
                    selectCallback: function (shiftTemplateGroup) {

                        view.CreateShiftTemplateGroup({
                            name: shiftTemplateGroup.name,
                            description: shiftTemplateGroup.description
                        });

                    },
                    description: description
                },
                header: this.$t('shift.template.addShiftGroup'),
                centered: true,
                expanded: true,
            });

        },
        OpenDeleteShiftTemplateGroupPopup($event) {
            let view = this;
            let description = this.$t('shift.template.deleteShiftQuery')
            let items = [];
            Object.values(this.shiftTemplateGroupMap).filter(group => !group.immutable).forEach(group => {
                items.push({ label: group.name, value: group.id });
            });
            let dataEntries = [
                {
                    key: 'id',
                    hidden: false,
                    type: 'dropdownsearch',
                    value: null,
                    selectMultiple: false,
                    dropdownSearchState: {
                        toggled: false,
                        displayedItems: [],
                        input: '',
                        items: items,
                        selectMultiple: false,
                        selectedItems: [],
                        onceSelected: function ($event, item, searchbar) {
                        },
                        styleLight: true,
                        placeholder: this.$t('shift.main.group'),
                    }
                },

            ];
            let target = $event.currentTarget;

            this.$helpers.OpenPopup({
                elRelative: target,
                type: 'datafill',
                componentState: {
                    entries: dataEntries,
                    description: description,
                    selectCallback: function (shiftTemplateGroup) {
                        view.DeleteShiftTemplateGroup(shiftTemplateGroup);
                    }
                },
                header: this.$t('shift.template.deleteShiftGroup'),
                centered: true,
                expanded: true,
            });

        },
        OpenCopyShiftTemplateGroupPopup() {
            let view = this;
            let description = this.$t('shift.template.copyHow')
            let items = [];
            Object.values(this.shiftTemplateGroupMap).forEach(group => {
                items.push({ label: group.name, value: group.id });
            });
            let tValidFrom = view.weekStartDateItems.past[0].value;
            let tValidTo = view.weekStartDateItems.future[0].value;

            let dataEntries = [
                {
                    label: this.$t('shift.template.startGroup'),
                    key: 'sourceGroupId',
                    hidden: false,
                    type: 'dropdownsearch',
                    value: null,
                    selectMultiple: false,
                    dropdownSearchState: {
                        toggled: false,
                        displayedItems: [],
                        input: '',
                        items: items,
                        selectMultiple: false,
                        selectedItems: [],
                        onceSelected: function ($event, item, searchbar) {
                        },
                        styleLight: true,
                        placeholder: this.$t('shift.main.group'),
                    }
                },
                {
                    label: this.$t('shift.template.targetGroup'),
                    key: 'targetGroupId',
                    hidden: false,
                    type: 'dropdownsearch',
                    value: null,
                    selectMultiple: false,
                    dropdownSearchState: {
                        toggled: false,
                        displayedItems: [],
                        input: '',
                        items: items,
                        selectMultiple: false,
                        selectedItems: [],
                        onceSelected: function ($event, item, searchbar) {
                        },
                        styleLight: true,
                        placeholder: this.$t('shift.main.group'),
                    }
                },
                {
                    label: this.$t('workBalance.bonus.recursIndefinitely'),
                    key: 'noValidFrom',
                    hidden: false,
                    disabled: false,
                    type: 'bool',
                    value: true
                },
                {
                    label: this.$t('workBalance.bonus.validFrom'),
                    key: 'validFrom',
                    hidden: this.$helpers.CheckDatafillKeysForHiddenInverted('noValidFrom'),
                    type: 'date',
                    value: tValidFrom,
                    flexGroup: false
                },
                {
                    label: this.$t('workBalance.bonus.ongoingIndefinitely'),
                    key: 'noValidTo',
                    hidden: false,
                    disabled: false,
                    type: 'bool',
                    value: true
                },
                {
                    label: this.$t('pages.employeeContract.validUntil'),
                    key: 'validTo',
                    hidden: this.$helpers.CheckDatafillKeysForHiddenInverted('noValidTo'),
                    type: 'date',
                    value: tValidTo,
                    flexGroup: false
                },
                {
                    label: this.$t('shift.template.replaceExisting'),
                    key: 'deleteExisting',
                    hidden: false,
                    disabled: false,
                    type: 'bool',
                    value: false
                },

            ];


            this.$helpers.OpenPopup({
                type: 'datafill',
                componentState: {
                    entries: dataEntries,
                    errorCheck: [
                        (entries) => {

                            if (!entries['sourceGroupId']) {
                                return {
                                    key: 'sourceGroupId',
                                    type: 'error',
                                    label: this.$t('shift.template.startGroup'),
                                    text: this.$t('shift.template.startGroupPlease')
                                }
                            } else {
                                return null
                            }
                        },
                        (entries) => {

                            if (!entries['targetGroupId']) {
                                return {
                                    key: 'targetGroupId',
                                    type: 'error',
                                    label: this.$t('shift.template.targetGroup'),
                                    text: this.$t('shift.template.targetGroupPlease')
                                }
                            } else {
                                return null
                            }
                        },
                        (entries) => {

                            if (entries['targetGroupId'] == entries['sourceGroupId']) {
                                return {
                                    key: 'targetGroupId',
                                    type: 'error',
                                    label: this.$t('shift.template.targetGroup'),
                                    text: this.$t('shift.template.sameGroupError')
                                }
                            } else {
                                return null
                            }
                        },
                    ],
                    description: description,
                    selectCallback: function (item) {
                        if (item.noValidFrom) {
                            item.validFrom = null;
                        }
                        if (item.noValidTo) {
                            item.validTo = null;
                        }
                        view.CopyShiftTemplateGroup(item);
                    }
                },
                header: this.$t('shift.template.copyShiftGroup'),
                centered: true,
                expanded: true,
            });

        },
        ToggleDataFill: function ($event, settings) {
            let box = $event.currentTarget.getBoundingClientRect();
            let target = $event.currentTarget;

            this.$refs.dropdowncontainer.Init({
                elRelative: settings.elRelative,
                data: settings.dataEntries,
                header: settings.header,
                currentComponent: 'datafill',
                remainAfterSelect: false,
                pointerOffset: ( settings.pointerOffset && settings.pointerOffset !== 0 ) ? settings.pointerOffset : 15,
                pointerDirection: ( settings.pointerDirection && settings.pointerDirection !== 0 ) ? settings.pointerDirection : 0,
                onceSelected: settings.selectCallback,
                expanded: true,
            });
        },
        async duplicateShiftTemplate(s, directSave = false) {
            const template = this.changedTemplateMap[s.id] || this.currentWeekShiftTemplateMap[s.id] || this.addedTemplateMap[s.id];
            const tempTemplate = JSON.parse(JSON.stringify(template));
            tempTemplate.id = Math.random().toString(36).substring(2, 8);
            tempTemplate.isNew = true;

            if (directSave) {
                try {
                    await this.createShiftTemplate({
                        ...tempTemplate,
                        from: this.$helpers.getMomentFromStringTZ(tempTemplate.from, 'HH:mm'),
                        to: this.$helpers.getMomentFromStringTZ(tempTemplate.to, 'HH:mm'),
                        autoBreak: tempTemplate.breakFrom == null,
                        breakFrom: tempTemplate.breakFrom == null ? null : this.$helpers.getMomentFromStringTZ(tempTemplate.breakFrom, 'HH:mm'),
                    });
                } catch (e) {
                    this.$helpers.error(this.$t('global.ui.createFailed'), this.$t('shift.template.createShiftScheduleError'));
                }
            } else {
                this.$set(this.addedTemplateMap, tempTemplate.id, tempTemplate);
            }
        },


        CreateShiftTemplateGroup: function (shiftTemplateGroup) {

            let view = this;
            let url = '/api/sec/shifttemplategroup';
            let params = {
                storeId: this.$store.state.storeId
            }

            let payload = {
                name: shiftTemplateGroup.name,
                description: shiftTemplateGroup.description,
            }


            this.axios({
                method: 'post',
                params: params,
                headers: {
                    'AUTHORIZATION': 'bearer ' + this.$store.state.jwt
                },
                url: url,
                data: payload
            }).then(response => {
                view.$store.commit('setShiftTemplateGroupMapEntry', response.data.data);
                view.$helpers.success(view.$t('store.events.creationSuccessful'), view.$t('shift.template.shiftGroupCreated'))
                view.refresh(response.data.data.id);
            }).catch(function (error) {
                view.$helpers.error(view.$t('global.ui.createFailed'), view.$t('shift.template.createShiftGroupError'));
            })

        },
        DeleteShiftTemplateGroup: function (shiftTemplateGroup) {

            let view = this;
            let url = '/api/sec/shifttemplategroup';


            let params = {
                id: shiftTemplateGroup.id,
                storeId: this.$store.state.storeId
            }


            this.axios({
                method: 'delete',
                headers: {
                    'AUTHORIZATION': 'bearer ' + this.$store.state.jwt
                },
                url: url,
                params: params,
            }).then(response => {

                view.$helpers.success(view.$t('global.notifications.success'), view.$t('shift.template.shiftGroupDeleted'));
                view.$store.commit('deleteShiftTemplateGroupMapEntry', { id: shiftTemplateGroup.id });

                view.$store.commit('invalidateState', { states: [ 'planShiftMapState', 'shiftTemplateMapState' ] });
                view.refresh();
            }).catch(function (error) {
                view.$helpers.error(view.$t('global.notifications.error'), view.$t('shift.template.deleteShiftGroupError'));
            })

        },
        CopyShiftTemplateGroup: function (item) {

            let view = this;
            let url = '/api/sec/shifttemplate/copy/group';


            let params = {
                storeId: this.$store.state.storeId
            }
            let payload = {
                sourceGroupId: item.sourceGroupId,
                targetGroupId: item.targetGroupId,
                validFrom: item.validFrom,
                validTo: item.validTo,
                deleteExisting: item.deleteExisting
            }

            this.axios({
                method: 'post',
                headers: {
                    'AUTHORIZATION': 'bearer ' + this.$store.state.jwt
                },
                url: url,
                data: payload,
                params: params,
            }).then(response => {

                view.$helpers.success(view.$t('global.notifications.success'), view.$t('shift.template.shiftGroupCopied'));


                view.$store.commit('invalidateState', { states: [ 'planShiftMapState', 'shiftTemplateMapState' ] });
                view.refresh(payload.targetGroupId);
            }).catch(function (error) {
                view.$helpers.error(view.$t('global.notifications.error'), view.$t('shift.template.deleteShiftGroupError'));
            })

        },


        AutoGenerateTemplates() {
            //First calculate how many hours are available
            let availableMinutes = 0;
            Object.values(this.employeeMap).filter(employee => employee.active && employee.typeId != null).forEach(employee => {
                if (this.employeeTypeMap[employee.typeId]) {
                    availableMinutes += this.employeeTypeMap[employee.typeId].weekTimeRegular;
                }
            });

            //calculate how many minutes are available for each spot
            let spotEmployeeCountMap = {};
            Object.values(this.spotMap).forEach(spot => {
                if (!spotEmployeeCountMap[spot.id]) {
                    spotEmployeeCountMap[spot.id] = 0;
                }
                Object.values(this.employeeMap).filter(employee => employee.active && employee.typeId != null).forEach(employee => {
                    if (this.employeeEmployeeSkillMap[employee.id] && this.employeeEmployeeSkillMap[employee.id].filter(skillId => skillId == spot.requiredSkill).length > 0) {
                        spotEmployeeCountMap[spot.id] += 1;
                    }
                });
            });

            let spotEmployeeTotal = Object.values(spotEmployeeCountMap).reduce((a, b) => a + b, 0);
            let spotTimeMap = {};
            Object.values(this.spotMap).forEach(spot => {
                let time = Math.round(( spotEmployeeCountMap[spot.id] / spotEmployeeTotal ) * availableMinutes);
                spotTimeMap[spot.id] = time;
            });
            Object.values(this.templateMap).forEach(shiftTemplate => {
                let day = this.currentWeekStart;
                let fromDate = this.$helpers.getMomentFromStringUTC(shiftTemplate.from, 'HH:mm');
                fromDate = day.clone().hours(fromDate.hours()).minutes(fromDate.minutes());
                let toDate = this.$helpers.getMomentFromStringUTC(shiftTemplate.to, 'HH:mm');
                toDate = day.clone().hours(toDate.hours()).minutes(toDate.minutes());

                if (toDate <= fromDate) {
                    toDate = toDate.add(24, 'hours');
                }
                let minutes = Math.max(0, toDate.diff(fromDate, 'minutes') -shiftTemplate.breakValue);
                shiftTemplate.weekDays.forEach(weekday => {
                    let spot = this.spotMap[shiftTemplate.spotId];
                    spotTimeMap[spot.id] = Math.max(0, spotTimeMap[spot.id] -minutes);
                });

            });

            let shiftTemplates = [];
            let dateFrom = this.$helpers.getMomentTZ();
            let dateTo = this.$helpers.getMomentTZ();
            Object.values(this.shiftTemplateGroupMap).forEach(group => {

                if(group.id == this.selectedGroup.id){
                    Object.values(this.spotMap).forEach(spot => {
                        let minutes = spotTimeMap[spot.id];

                        while (minutes > 0) {
                            //morning shift
                            dateFrom = dateFrom.clone().hours(6).minutes(0);
                            dateTo = dateTo.clone().hours(14).minutes(30);
                            let shiftTemplate = {
                                groupId: group.id,
                                spotId: spot.id,
                                from: dateFrom,
                                to: dateTo,
                                validFrom: this.currentWeekStart.format(),
                                weekDays: [],
                                count: 1,
                                workValue: -1
                            }
                            let i = 1;
                            while (i <= 7 && minutes > 0) {
                                shiftTemplate.weekDays.push(i);
                                minutes -= ( 8 * 60 );
                                i++;
                            }
                            if (shiftTemplate.weekDays.length > 0) {
                                shiftTemplates.push(shiftTemplate);
                            }

                            //midday shift
                            dateFrom = dateFrom.clone().hours(10).minutes(0);
                            dateTo = dateTo.clone().hours(18).minutes(30);
                            shiftTemplate = {
                                groupId: group.id,
                                spotId: spot.id,
                                from: dateFrom,
                                to: dateTo,
                                validFrom: this.currentWeekStart.format(),
                                weekDays: [],
                                count: 1,
                                workValue: -1
                            }
                            i = 1;
                            while (i <= 7 && minutes > 0) {
                                shiftTemplate.weekDays.push(i);
                                minutes -= ( 8 * 60 );
                                i++;
                            }
                            if (shiftTemplate.weekDays.length > 0) {
                                shiftTemplates.push(shiftTemplate);
                            }
                            //evening shift
                            dateFrom = dateFrom.clone().hours(16).minutes(0);
                            dateTo = dateTo.clone().hours(0).minutes(30);
                            shiftTemplate = {
                                groupId: group.id,
                                spotId: spot.id,
                                from: dateFrom,
                                to: dateTo,
                                validFrom: this.currentWeekStart.format(),
                                weekDays: [],
                                count: 1,
                                workValue: -1
                            }
                            i = 1;
                            while (i <= 7 && minutes > 0) {
                                shiftTemplate.weekDays.push(i);
                                minutes -= ( 8 * 60 );
                                i++;
                            }
                            if (shiftTemplate.weekDays.length > 0) {
                                shiftTemplates.push(shiftTemplate);
                            }
                        }
                    });
                }

            })


            this.createShiftTemplateMulti(shiftTemplates).catch((error) => {
                this.$helpers.error(this.$t('global.notifications.error'), error.message);
            });
        },
        GetGroupClasses(group, indexGroup) {
            return {
                'sub-spot': group.spot && !group.spot.isMain,
                'group-dragged': group === this.draggingGroup,
                'show-drag-area': this.draggingGroup && this.draggingGroup.spot && group.spot &&                                              // make sure you're dragging and the spots are set
                    ( ( this.draggingGroup.spot.isMain && group.spot.isMain ) ||                                                                 // if you're dragging a main group then the subgroups shouldn't show their drag areas
                        ( !this.draggingGroup.spot.isMain && !group.spot.isMain && group.spot.unitId == this.draggingGroup.spot.unitId && group.spot ) ), // if you're dragging a subgroup then only show the drag areas of the same parent
                'show-drag-area-last': this.draggingGroup && ( indexGroup === this.templateDisplayGroups.length - 1 ||                                     // this is for dragging in front of a group but only the last one should have it
                    // but for subgroups we have to get the last subgroup which I'll do by asking if the next one is a main group (wip)
                    ( !this.draggingGroup.spot.isMain && this.draggingGroup !== group && group.spot.unitId === this.draggingGroup.spot.unitId && indexGroup + 1 !== this.templateDisplayGroups.length && this.templateDisplayGroups[indexGroup + 1].spot.isMain ) )
            }
        },
        DragStartBusinessUnit: function ($event, templateGroup, indexGroup) {
            let view = this;
            setTimeout(function () {
                if (!window.mouse.click) return;

                view.groupCollapsedStates = {};
                view.templateDisplayGroups.forEach(g => view.groupCollapsedStates[g.header] = g.collapsed);

                for (let x in view.templateDisplayGroups) {
                    view.templateDisplayGroups[x].collapsed = true;
                    if (view.templateDisplayGroups[x].spot == null || view.templateDisplayGroups[x].spot.id === templateGroup.spot.id) {
                        view.templateDisplayGroups[x].show = false;
                    } else {
                        view.templateDisplayGroups[x].show = true;
                    }
                }

                view.draggingGroup = templateGroup;
                view.DragBusinessUnitWatch();
            }, 500);
        },
        DragBusinessUnitWatch() {
            let view = this;
            if ($(this.$el).find('.drag-group').length > 0) $(this.$el).find('.drag-group').css({
                left: ( window.mouse.x - ( $(this.$el).find('.drag-group')[0].getBoundingClientRect().width * .025 ) ) + 'px',
                top: ( window.mouse.y - ( $(this.$el).find('.drag-group')[0].getBoundingClientRect().height * .5 ) ) + 'px'
            });


            view.$forceUpdate();
            requestAnimationFrame(function () {
                if (window.mouse.click) view.DragBusinessUnitWatch();
                else {
                    requestAnimationFrame(function () {
                        view.EndDragBusinessUnit();
                    });
                }
            });
        },
        EndDragBusinessUnit() {
            let view = this;
            let elementInGroup = $(this.$el).find('.group .drag-catch-area').toArray().find(e => { //in all the current group drag areas check if the mouse is inside of it
                let box = e.getBoundingClientRect();
                return box.left < window.mouse.x && box.right > window.mouse.x && box.top < window.mouse.y && box.bottom > window.mouse.y;
            });
            if (elementInGroup) {
                let groupDroppedOverIndex = $(elementInGroup).closest('.group').attr('indexgroup');
                let groupDroppedOver = this.templateDisplayGroups[groupDroppedOverIndex];
                if (groupDroppedOver) {
                    let sortData = [];
                    this.templateDisplayGroups.forEach(g => {
                        if (!sortData.includes(g.spot.id) && g.spot.id != view.draggingGroup.spot.id) {
                            sortData.push(g.spot.id);
                        }
                    });

                    let insertIndex = sortData.indexOf(groupDroppedOver.spot.id);
                    if (insertIndex != -1) {
                        sortData.splice(insertIndex, 0, view.draggingGroup.spot.id)
                        let uniqueSortData = sortData.reduce((a, b) => {
                            if (a.indexOf(b) < 0) a.push(b);
                            return a;
                        }, []);
                        let userSort = [];
                        uniqueSortData.forEach(id => {
                            userSort.push({ unitId: id, priority: uniqueSortData.indexOf(id) })
                        })
                        this.UpdateBusinessUnitSort(userSort);
                    } else {
                        //no new position found => do nothin

                    }
                }
            }
            this.$forceUpdate();
            requestAnimationFrame(function () {
                view.draggingGroup = null;
                for (let x in view.groupCollapsedStates) {
                    let group = view.templateDisplayGroups.find(g => g.header === x);
                    if (group) group.collapsed = view.groupCollapsedStates[x];
                }
            });
        },

        EventMouseTrack() {
            if ($(window.mouse.element).hasClass('event') || $(window.mouse.element).closest('.event').length > 0) {
                this.mouseX = window.mouse.x;
                this.mouseY = window.mouse.y + 20;
                $(this.$el).find('.event-info').css({
                    left: ( this.mouseX - ( $(this.$el).find('.event-info')[0].getBoundingClientRect().width * .5 ) ) + 'px',
                    top: this.mouseY + 'px'
                });
            } else {
                this.eventOnHover = null;
            }

        },

        getAvatarForTemplate(template) {
            if (this.templateDaySelection === 'W'){
                if(template.employeeId == null || this.employeeMap[template.employeeId] == null){
                    return this.getProfilePictureUrl(null);
                } else {
                    console.log("A")
                    const employee = this.employeeMap[template.employeeId];
                    return this.getProfilePictureUrl(employee);
                }

            }

            const day = moment(this.currentWeekStart).add(this.templateDaySelection - 1, 'days').startOf('day');
            const key = day.format('YYYY-MM-DD');

            const { planShifts } = this.weeklyShiftsByDay[key] || { planShifts: {} };

            let planShift = null;
            for (const ps of Object.values(planShifts)) {
                if (ps.templateId === template.id && !!ps.employeeId) {
                    planShift = ps;
                    break;
                }
            }
            if (planShift) {
                const employee = this.employeeMap[planShift.employeeId];
                return this.getProfilePictureUrl(employee);
            }
            return this.getProfilePictureUrl(null);
        },
        // Is this function necessary?
        GetColorForType(typeId) {
            if (typeId === 1) return '#e76e13'; //vacation
            if (typeId === 2) return '#e51650'; //sickness
            if (typeId === 3) return '#e5c420'; //holiday
            if (typeId === 4) return '#7e1433'; //miscellaneous
            if (typeId === 4) return '#808080'; //no contract
        },
        GetColorForState(stateId) {
            if (stateId === 1) return '#e51650'; //unavailable
            if (stateId === 2) return '#294285'; //only available
            if (stateId === 3) return '#ff1655'; //undesired
            if (stateId === 4) return '#aace27'; //desired
        },
        GetTemplateTimeFrameShiftInfo(template, dayIndex) {
            const day = moment(this.currentWeekStart).add(dayIndex, 'days').startOf('day');
            const key = day.format('YYYY-MM-DD');

            const { actualShifts, planShifts } = this.weeklyShiftsByDay[key] || { actualShifts: {}, planShifts: {} };

            // Get hour from template

            const { from: tempFrom, to: tempTo } = this.getTemplateFromToDates(template, dayIndex);

            // Find planShift
            let planShift = null;
            for (const ps of Object.values(planShifts)) {
                if (ps.templateId === template.id && !!ps.employeeId) {
                    planShift = ps;
                    break;
                }
            }

            let level = 0; // 0 = no planShift, 1 = planShift but no actual, 2 = actualShift exists
            let colorStop = null;

            // planShift found, so level is at least 1
            let shift = planShift;
            if (planShift) {

                level = 1;

                let actualShift = null;
                for (const actShift of Object.values(actualShifts)) {
                    if (actShift.planshiftId === planShift.id) {
                        actualShift = actShift;
                        break;
                    }
                }

                if (actualShift) {
                    level = 2;
                    shift = actualShift;
                }
                const shiftFrom = moment(shift.from);
                const shiftTo = moment(shift.to);

                const fromDiff = shiftFrom.diff(tempFrom, 'minutes');
                const tempDiff = moment(tempTo).diff(tempFrom, 'minutes');

                let fromPercent = fromDiff / tempDiff;
                if (fromPercent <= 0) fromPercent = 0;

                const toDiff = shiftTo.diff(tempFrom, 'minutes');
                let toPercent = toDiff / tempDiff;
                if (toPercent >= 1) toPercent = 1;

                colorStop = [ fromPercent, toPercent ];
            }

            const colorMap = {
                0: '#e5e5e5',
                1: '#879d53',
                2: '#e86e13',
            };

            const level2 = ( shift && shift.endMissing ) ? 1 : level;
            let gradientArray = [ [ colorMap[0], 0 ] ];
            if (colorStop && ( !this.dragging || !this.draggingTimeFrame || this.draggingTimeFrame.templateId !== template.id )) {
                gradientArray.push(
                    [ colorMap[0], 0 ],
                    [ colorMap[0], colorStop[0] ],
                    [ colorMap[level], colorStop[0] ],
                    [ colorMap[level2], colorStop[1] ],
                    [ colorMap[0], colorStop[1] ],
                );
            }
            gradientArray.push([ colorMap[0], 1 ]);

            // const timeFrameWidth = ( tempToNum * 60 * 1000 - tempFromNum * 60 * 1000 ) / this.timeFrameLength;

            let gradientString = 'to right';
            for (const [ col, per ] of gradientArray) {
                gradientString += `, ${col} ${per * 100}%`;
            }
            //find employee Info
            let employeeName = null;
            if (shift != null && shift.employeeId && this.employeeMap[shift.employeeId] != null) {
                employeeName = this.employeeMap[shift.employeeId].firstName + ' ' + this.employeeMap[shift.employeeId].lastName;
            }
            return { background: `linear-gradient(${gradientString})`, employeeName: employeeName };
        },


        toggleDatePicker($event) {
            let view = this;
            let date = this.$helpers.getMomentTZ().year(this.currentYear).date(0).isoWeek(this.currentSelectedWeek);
            if (this.templateDaySelection !== 'W') date.isoWeekday(this.templateDaySelection);
            this.$helpers.OpenDatepickerPopup(
                {
                    viewsEnabled: [ 'W', 'D' ],
                    selectedTab: this.templateDaySelection === 'W' ? 'W' : 'D',
                    date: date,
                    hourlyEnabled: true,
                    hourlySelection: [ 12, 24, 36 ],
                    hourlySelected: view.hourlySelection
                },
                {
                    OnDateSelect: (date) => {

                    },
                    OnWeekSelect: (date) => {
                        view.DateSelected(date, 'W');
                    },
                    OnDaySelect: (date, hours) => {
                        view.DateSelected(date, date.isoWeekday(), hours);
                    }
                }, { elRelative: $event.currentTarget });
        },
        SelectDay($event, indexWeek) {
            if (window.mouse.element.classList.contains('fas') || window.mouse.element.classList.contains('indicator')) return;
            let date = this.$helpers.getMomentTZ().year(this.currentYear).date(0).isoWeekYear(this.currentYear).isoWeek(this.currentSelectedWeek).startOf('isoWeek');
            date.isoWeekday(indexWeek + 1);
            this.DateSelected(date, date.isoWeekday(), 24);

        },
        SelectToday() {
            let date = this.$helpers.getMomentTZ().startOf('day');
            this.DateSelected(date, date.isoWeekday());

        },
        SelectPreviousDate() {
            if (this.templateDaySelection === 'W') {
                let date = this.currentWeekStart.add(-7, 'days');
                this.DateSelected(date, this.templateDaySelection, this.dayModeHours);
            } else {
                let date = this.$helpers.getMomentTZ().year(this.currentYear).date(0).isoWeekYear(this.currentYear).isoWeek(this.currentSelectedWeek).isoWeekday(this.templateDaySelection);
                date = date.add(-1, 'days');
                this.DateSelected(date, date.isoWeekday(), this.hourlySelection);
            }


        },
        SelectNextDate() {
            if (this.templateDaySelection === 'W') {
                let date = this.currentWeekStart.add(7, 'days');
                this.DateSelected(date, this.templateDaySelection, this.dayModeHours);
            } else {
                let date = this.$helpers.getMomentTZ().year(this.currentYear).date(0).isoWeekYear(this.currentYear).isoWeek(this.currentSelectedWeek).isoWeekday(this.templateDaySelection);
                date = date.add(1, 'days');
                this.DateSelected(date, date.isoWeekday(), this.hourlySelection);
            }

        },
        DateSelected(selectedDate, templateDaySelection, displayTimeFrame = 24) {
            this.loadingText = this.$t('shift.template.loadShiftPlan');
            this.loading = true;

            this.optimizationMode = false;
            this.currentSelectedWeek = selectedDate.isoWeek();
            this.templateDaySelection = templateDaySelection ? templateDaySelection : 'W';

            this.currentYear = selectedDate.clone().startOf('isoWeek').isoWeekYear();
            if (displayTimeFrame <= 36 && this.templateDaySelection !== 'W') {
                this.hourlySelection = displayTimeFrame;
            }

            if (templateDaySelection === 'W') {
                this.planningType = 2;
            }

            //updateRosterFromTo
            let fixedDate = this.$helpers.getMomentTZ().year(selectedDate.year()).month(selectedDate.month()).date(1).startOf('day');

            if (fixedDate.year() != this.rosterFromTo.year || fixedDate != this.rosterFromTo.month) {
                let rosterFromTo = this.$helpers.CalculateRosterFromTo(fixedDate);

                this.$store.commit('setRosterFromTo', { rosterFromTo: rosterFromTo });
            }
        },

        activateEditMode() {
            this.editMode = true;
            // TODO: disable content switching only after changes were made
            this.$store.commit("setAllowContentSwitch", {allowContentSwitch: false});
        },

        befeoreUnload($event) {
            if (this.hasChanges && $event) $event.preventDefault();
        },

        templateCellResizeStart({ template, day }, { direction: side }) {
            this.pdLoading = true;
            if (!template.isNew) {
                if (template.updateMode) {
                    template.updateMode = Array.from(new Set([ ...template.updateMode, 'update-cow' ]));
                } else {
                    template.updateMode = [ 'update-cow' ];
                }
                this.$set(this.changedTemplateMap, template.id, Object.assign({}, template));
            } else {
                this.$set(this.addedTemplateMap, template.id, Object.assign({}, template));
            }
        },

        templateCellResizeMove({ template, day }, { direction: side, colFrom, colTo }) {
            requestAnimationFrame(() => {
                colFrom = Math.max(2 * X_OFFSET, colFrom);
                const minutesFrom = Math.max(colFrom - X_OFFSET, X_OFFSET) * ( this.templateDaySelection === 'W' ? WEEKMODE_MIN_INTERVAL : DAYMODE_MIN_INTERVAL );
                const minutesTo = Math.max(colTo - X_OFFSET, X_OFFSET) * ( this.templateDaySelection === 'W' ? WEEKMODE_MIN_INTERVAL : DAYMODE_MIN_INTERVAL );

                if (minutesFrom >= minutesTo) {
                    return;
                }

                const diff = minutesTo - minutesFrom;

                if (diff <= 0 || diff >= 24 * 60) {
                    return;
                }

                const fromTime = this.currentWeekStart.toDate();
                fromTime.setMinutes(fromTime.getMinutes() + minutesFrom);
                const toTime = this.currentWeekStart.toDate();
                toTime.setMinutes(toTime.getMinutes() + minutesTo);

                // console.log(fromTime.getDay());
                //
                // if (fromTime.getDay() !== day % 7) {
                //     return;
                // }

                const newTemplate = { ...template };
                if (side === 'left') {
                    newTemplate.from = dateToHHmm(fromTime);
                } else if (side === 'right') {
                    newTemplate.to = dateToHHmm(toTime);
                }

                if (!template.isNew) {
                    this.$set(this.changedTemplateMap, template.id, newTemplate);
                } else {
                    this.$set(this.addedTemplateMap, template.id, newTemplate);
                }
            });
        },

        async templateCellResizeEnd({ template, day }, { direction: side, colFrom, colTo }, directSave) {
            template = this.changedTemplateMap[template.id] || this.addedTemplateMap[template.id] || template;
            let newTemplate = { ...template };
            let { from, to, breakFrom, breakTo } = this.getTemplateFromToDates(template, day);

            if (!template.autoBreak && template.breakFrom) {
                // if (breakFrom.isBefore(from) || breakTo.isAfter(to)) {
                //     breakFrom = moment(from);
                // }
                if (breakFrom.getTime() < from.getTime() || breakTo.getTime() > to.getTime()) {
                    breakFrom = new Date(from);
                }
            }
            if (breakFrom) {
                newTemplate.breakFrom = dateToHHmm(breakFrom);
            }

            this.pdLoading = false;

            if (directSave) {
                this.loading = true;
                this.loadingText = this.$t('shift.template.savingShiftSchedule');
                try {
                    await this.updateShiftTemplateCow({
                        ...newTemplate,
                        from: this.$helpers.getMomentFromStringTZ(newTemplate.from, 'HH:mm'),
                        to: this.$helpers.getMomentFromStringTZ(newTemplate.to, 'HH:mm'),
                        autoBreak: newTemplate.breakFrom == null,
                        breakFrom: newTemplate.breakFrom == null ? null : this.$helpers.getMomentFromStringTZ(newTemplate.breakFrom, 'HH:mm'),
                    });
                    this.$delete(this.changedTemplateMap, template.id);
                } catch (e) {
                    this.$helpers.error(this.$t('global.notifications.error'), e.message);
                } finally {
                    this.loading = false;
                }
                return;
            }
            if (!template.isNew) {
                this.$set(this.changedTemplateMap, template.id, newTemplate);
            } else {
                this.$set(this.addedTemplateMap, template.id, newTemplate);
            }

        },

        async addWeekdayToTemplate(template, day, directSave = false) {
            template = { ...template, weekDays: [ ...template.weekDays, day ] };
            if (directSave) {
                this.loading = true;
                this.loadingText = this.$t('shift.template.savingShiftSchedule');
                try {
                    await this.updateShiftTemplateCow({
                        ...template,
                        from: this.$helpers.getMomentFromStringTZ(template.from, 'HH:mm'),
                        to: this.$helpers.getMomentFromStringTZ(template.to, 'HH:mm'),
                        autoBreak: template.breakFrom == null,
                        breakFrom: template.breakFrom == null ? null : this.$helpers.getMomentFromStringTZ(template.breakFrom, 'HH:mm'),
                    });
                } catch (e) {
                    this.$helpers.error(this.$t('global.notifications.error'), e.message);
                } finally {
                    this.loading = false;
                }
                return;
            }
            if (!template.isNew) {
                if (template.updateMode) {
                    template.updateMode = Array.from(new Set([ ...template.updateMode, 'update-cow' ]));
                } else {
                    template.updateMode = [ 'update-cow' ];
                }
                this.$set(this.changedTemplateMap, template.id, template);
            } else {
                this.$set(this.addedTemplateMap, template.id, template);
            }
        },

        async deleteWeekdayFromTemplate(template, day, directSave = false) {
            template = {
                ...template,
                weekDays: template.weekDays.filter(d => d !== day)
            };

            if (directSave) {
                if (template.weekDays.length === 0) {
                    this.loading = true;
                    this.loadingText = this.$t('shift.template.shiftScheduleDeleting');
                    try {
                        await this.deleteShiftTemplate(template);
                    } catch (e) {
                        this.$helpers.error(view.$t('global.notifications.error'), e.message);
                    } finally {
                        this.loading = false;
                    }
                    return;
                }
                this.loading = true;
                this.loadingText = this.$t('shift.template.savingShiftSchedule');
                try {
                    await this.updateShiftTemplateCow({
                        ...template,
                        from: this.$helpers.getMomentFromStringTZ(template.from, 'HH:mm'),
                        to: this.$helpers.getMomentFromStringTZ(template.to, 'HH:mm'),
                        autoBreak: template.breakFrom == null,
                        breakFrom: template.breakFrom == null ? null : this.$helpers.getMomentFromStringTZ(template.breakFrom, 'HH:mm'),
                    });
                } catch (e) {
                    this.$helpers.error(this.$t('global.notifications.error'), e.message);
                } finally {
                    this.loading = false;
                }
                return;
            }

            if (!template.isNew) {
                if (template.weekDays.length === 0) {
                    // TODO: Check if delete or change validTo (and show button on last weekday cell as well)
                    this.$set(this.removedTemplateMap, template.id, template);
                    this.$delete(this.changedTemplateMap, template.id);
                } else {
                    if (template.updateMode) {
                        template.updateMode = Array.from(new Set([ ...template.updateMode, 'update-cow' ]));
                    } else {
                        template.updateMode = [ 'update-cow' ];
                    }
                    this.$set(this.changedTemplateMap, template.id, template);
                }
            } else {
                if (template.weekDays.length === 0) {
                    this.$delete(this.addedTemplateMap, template.id);
                } else {
                    this.$set(this.addedTemplateMap, template.id, template);
                }
            }

            if (template.wasRecommended) {
                const origId = template.id.replace('-rec', '');
                const origTemplate = this.currentWeekShiftTemplateMap[origId];
                if (!origTemplate || !origTemplate.weekDays.includes(day)) return;
                const changedTemplate = this.changedTemplateMap[origId] || origTemplate;
                if (changedTemplate.updateMode) {
                    changedTemplate.updateMode = Array.from(new Set([ ...changedTemplate.updateMode, 'update-cow' ]));
                } else {
                    changedTemplate.updateMode = [ 'update-cow' ];
                }
                this.$set(this.changedTemplateMap, changedTemplate.id, {
                    ...changedTemplate,
                    weekDays: [ ...changedTemplate.weekDays, day ]
                });
            }
        },

        acceptRecommendation(templateToReplace, recommendation, day, directSave = false) {
            // TODO: Direct Save
            templateToReplace = {
                ...this.currentWeekShiftTemplateMap[templateToReplace],
                ...( this.changedTemplateMap[templateToReplace] || {} ),
            };
            recommendation = { ...( this.templateMap[recommendation.id] || {} ), ...recommendation };
            // console.log('templateToReplace:', templateToReplace);
            // console.log('recommendationOrig:', this.templateMap[recommendation.id]);
            // console.log('recommendation:', recommendation);

            const { from: recFrom, to: recTo } = this.getTemplateFromToDates(recommendation, day);
            const { from: tempFrom, to: tempTo } = this.getTemplateFromToDates(templateToReplace, day);

            if (templateToReplace.weekDays.includes(day)) {
                if (templateToReplace.weekDays.length === 1) {
                    // TODO: Create helper method to check if delete or change validTo
                    if (!directSave)
                        this.$set(this.removedTemplateMap, templateToReplace.id, templateToReplace);
                } else {
                    this.deleteWeekdayFromTemplate(templateToReplace, day, directSave);
                }
            } else if (dateToHHmm(recFrom) === dateToHHmm(tempFrom) && dateToHHmm(recTo) === dateToHHmm(tempTo)) {
                this.addWeekdayToTemplate(templateToReplace, day, directSave);
                return;
            }

            const newTemplate = {
                ...recommendation,

                from: dateToHHmm(recFrom),
                to: dateToHHmm(recTo),

                id: templateToReplace.id + '-rec',
                templateToReplace: templateToReplace.id,

                validFrom: templateToReplace.validFrom,
                validTo: templateToReplace.validTo,

                groupId: templateToReplace.groupId,
                spotId: templateToReplace.spotId,

                count: 1,

                weekDays: [ day ],
                isNew: true,

                wasRecommended: true,
            };

            if (this.addedTemplateMap[newTemplate.id]) {
                newTemplate.weekDays = [ ...this.addedTemplateMap[newTemplate.id].weekDays, day ];

                // TODO: Check: This code would allow merging with templates that are equal in length and breaks
            } else {
                const equalTemplate = this.findEqualTemplate(newTemplate, templateToReplace.groupId, templateToReplace.spotId);
                if (equalTemplate) {
                    this.addWeekdayToTemplate(equalTemplate, day);
                    return;
                }
            }

            this.$set(this.addedTemplateMap, newTemplate.id, newTemplate);
        },

        templateWillBeDeleted(template) {
            if (!this.editMode) return false;

            const templateId = typeof template === 'string' ? template : template.id;

            if (this.removedTemplateMap[templateId]) return true;

            if (!this.templateValidMomentMap[templateId]) return false;

            const templateTo = this.templateValidMomentMap[templateId].to;

            if (!templateTo) return false;

            const isValid = templateTo.isAfter(this.currentWeekStart);

            return !!this.changedTemplateMap[templateId] && !isValid;
        },

        restoreTemplate(template) {
            const templateId = typeof template === 'string' ? template : template.id;

            if (this.removedTemplateMap[templateId]) this.$delete(this.removedTemplateMap, templateId);

            if (this.changedTemplateMap[templateId]) this.$delete(this.changedTemplateMap, templateId);
        },

        discardChanges() {
            this.optimizationMode = false;
            this.optimizationGroup = null;
            this.editMode = false;
            this.$store.commit("setAllowContentSwitch", {allowContentSwitch: true});

            this.addedTemplateMap = {};
            this.changedTemplateMap = {};
            this.removedTemplateMap = {};

            this.addedEventMap = {};
            this.changedEventMap = {};
            this.removedEventMap = {};
        },

        async saveChanges() {
            /* console.log({
                added: this.addedTemplateMap,
                changed: this.changedTemplateMap,
                removed: this.removedTemplateMap,
            }); */

            // Templates
            const promiseQueue = [];

            const newTemplates = Object.values(this.addedTemplateMap).map((newTemplate) => ( {
                ...newTemplate,
                from: this.$helpers.getMomentFromStringTZ(newTemplate.from, 'HH:mm'),
                to: this.$helpers.getMomentFromStringTZ(newTemplate.to, 'HH:mm'),
            } ));
            if (newTemplates.length > 0) {
                promiseQueue.push(() => this.createShiftTemplateMulti(newTemplates));
            }
            // for (const newTemplate of newTemplates) {
            //     promiseQueue.push(() => this.createShiftTemplate(newTemplates));
            // }

            const changedTemplates = Object.values(this.changedTemplateMap).map((template) => ( {
                ...template,
                from: this.$helpers.getMomentFromStringTZ(template.from, 'HH:mm'),
                to: this.$helpers.getMomentFromStringTZ(template.to, 'HH:mm'),
                autoBreak: template.breakFrom == null,
                breakFrom: template.breakFrom == null ? null : this.$helpers.getMomentFromStringTZ(template.breakFrom, 'HH:mm'),
            } ));
            // TODO: Combine to single promise
            // TODO: Filter out removed templates


            // TODO: Create helper method for serial execution of promises
            for (const template of changedTemplates) {
                this.$set(this.currentWeekShiftTemplateMap, template.id, template);
                // Determine if only one or both update methods are needed // Array
                if (template.updateMode && template.updateMode.includes('update-cow')) {
                    promiseQueue.push(() => this.updateShiftTemplateCow(template, false));
                }
                if (template.updateMode && template.updateMode.includes('update')) {
                    promiseQueue.push(() => this.updateShiftTemplate(template, false));
                }
                // );
            }
            // await Promise.allSettled(promises);
            // let promises = [];

            this.shiftTemplates = Object.values(this.currentWeekShiftTemplateMap);

            const removedTemplates = Object.values(this.removedTemplateMap);
            for (const template of removedTemplates) {
                promiseQueue.push(() => this.deleteShiftTemplate(template.id));
            }
            // await Promise.allSettled(promises);
            // promises = [];

            // Events
            let eventsChanged = false;

            const newEvents = Object.values(this.addedEventMap).map((newEvent) => ( {
                ...newEvent,
                from: newEvent.from.format(),
                to: newEvent.to.format(),
            } ));
            if (newEvents.length > 0) eventsChanged = true;
            for (const newEvent of newEvents) {
                promiseQueue.push(() => this.createStoreEvent(newEvent));
            }
            // await Promise.allSettled(promises);
            // promises = [];

            const changedEvents = Object.values(this.changedEventMap).map((event) => ( {
                ...event,
                from: event.from,
                to: event.to,
            } ));
            if (changedEvents.length > 0) eventsChanged = true;
            for (const event of changedEvents) {
                promiseQueue.push(() => this.editStoreEvent(event));
            }
            // await Promise.allSettled(promises);
            // promises = [];

            const removedEvents = Object.values(this.removedEventMap);
            if (removedEvents.length > 0) eventsChanged = true;
            for (const event of removedEvents) {
                promiseQueue.push(() => this.deleteStoreEvent(event));
            }
            // await Promise.allSettled(promises);
            /*
                if (removedEvents.length > 0) {
                    for (const template of this.shiftTemplates) {
                        if (template.eventId && this.removedEventMap[template.eventId]) {
                            console.log(template);
                            this.$store.commit('deleteShiftTemplateMapEntry', template);
                            // TODO: Check why template is not removed from map
                        }
                    }
                }
                */
            let scrollPosition;
            try {
                
                this.loadingPercent = '0%';
                this.loadingPercent = 0;
                if(promiseQueue.length > 1){
                    this.loading = true;
                    scrollPosition = document.getElementById("template-grid").scrollTop;
                    this.refreshing = true;
                }
                for await (const result of this.$helpers.workPromiseQueue(promiseQueue)) {
                    if (result.error) {
                        const message = typeof result.error === 'string' ? result.error : result.error.message;
                        this.$helpers.error(this.$t('global.notifications.error'), message);
                        continue;
                    }
                    if (result.finished) {
                        this.loadingPercent = 99;
                        continue;
                    }
                    this.loadingPercent = Math.round(result.progress * 99);
                    this.loadingText = this.loadingPercent + '%';
                }
                // await Promise.allSettled(promises);
                if (eventsChanged) {
                    try {
                        const res = await this.loadStoreEvents();
                        this.updateStoreEvents(res.data);
                    } catch (e) {
                        this.$helpers.warning(this.$t('global.notifications.error'), 'Fehler beim Aktualisieren der Events');
                    }
                }

                this.loadingPercent = 100;
                this.loadingText = this.loadingPercent + '%';
                console.log("saveChanges")
                await this.RefreshShiftTemplates();
                // this.loadingPercent = null;
                // this.loadingText = 'Fertig!';

                this.optimizationMode = false;
                this.optimizationGroup = null;
                this.editMode = false;
                this.$store.commit("setAllowContentSwitch", {allowContentSwitch: true});

                this.addedTemplateMap = {};
                this.changedTemplateMap = {};
                this.removedTemplateMap = {};

                this.addedEventMap = {};
                this.changedEventMap = {};
                this.removedEventMap = {};
            } catch (e) {
                console.error(e);
            } finally {
                
                this.loading = false;
                this.refreshing = false;

                this.$nextTick(function () {
                    if (!this.refreshing) {
                        if (scrollPosition) {
                            document.getElementById("template-grid").scrollTop = scrollPosition;
                        }
                    }
                })
            }
        },

        toggleDirectSaveMode() {

            this.directSaveMode = !this.directSaveMode;
            localStorage.setItem('templateView.directSaveMode', this.directSaveMode);
        },

        toggleOptimizationMode() {
            if (this.optimizationMode) {
                this.optimizationMode = false;
                this.optimizationGroup = null;
                return;
            }
            const otherGroups = Object.entries(this.shiftTemplateGroupMap)
                .filter(([ key, value ]) => key !== this.selectedGroup.id)
                .map(([ key, value ]) => ( { label: value.name, value: key } ));

            // TODO: Display no groups available message if no other groups exist
            this.$helpers.OpenPopup({
                type: 'datafill',
                header: this.$t('shift.template.selectOptimizationGroup'),
                expanded: true,
                centered: true,
                componentState: {
                    entries: [
                        {
                            label: this.$t('shift.template.optimizationGroup'),
                            key: 'optimizationGroup',
                            hidden: false,
                            type: 'dropdownsearch',
                            value: null,
                            selectMultiple: false,
                            dropdownSearchState: {
                                toggled: false,
                                displayedItems: [],
                                input: '',
                                items: otherGroups,
                                selectMultiple: false,
                                selectFirst: true,
                                selectedItems: [],
                                styleLight: true,
                            },
                        },
                    ],
                    positiveButtonLabel: this.$t('global.ui.approve'),
                    description: this.$t('shift.template.overlapOptimization'),

                    selectCallback: (data) => {
                        this.loadShiftTemplatesPagedCompletely({ ids: [] }, data.optimizationGroup)
                            .then((response) => {
                                this.optimizationGroup = data.optimizationGroup;
                                this.optimizationMode = true;
                                this.$set(this, 'shiftTemplates', [ ...this.shiftTemplates, ...response ]);
                            })
                            .catch((e) => {
                                this.$helpers.error(this.$t('global.notifications.error'), this.$t('shift.template.errorLoadingOptimizationGroup'));
                                console.error(e);
                            });
                    }
                },
            });
        },

        RefreshDisplayedTimeframes() {
            if (this.templateDaySelection === 'W') {
                let weekTimeFrame = { label: 'W', length: 7 * 24 * 60 * 60 * 1000, divisions: 7 }
                this.displayedTimeframeSelected = weekTimeFrame;
            } else {
                let dayTimeFrame = { label: '24', length: 24 * 60 * 60 * 1000, divisions: 24 }
                this.displayedTimeframeSelected = dayTimeFrame;
            }

            this.$forceUpdate();
        },


        OpenBusinessUnitFilterDropdown($event) {
            let view = this;
            let target = $event.currentTarget;

            let list = [];
            this.spots.forEach(s => list.push({
                label: s.name, toggled: ( view.spotVisibilityList.find(p => s === p) ) ? true : false, spot: s,
                styles: s.isMain ? [] : [ { 'margin-left': '30px' } ]
            }));
            this.$helpers.OpenPopup({
                elRelative: target,
                type: 'list',
                componentState: {
                    entries: list,
                },
                header: this.$t('shift.template.toggleWorkspaces'),
                expanded: true,
                centered: true,
                iconsLeft: true,
                remainAfterSelect: true,
                selectCallback: function ($event, item, dropdown) {
                    item.toggled = !item.toggled;
                    if (item.toggled) {
                        if (!item.spot.isMain) {
                            dropdown.items.filter(i => !i.toggled && i.spot.unitId === item.spot.unitId && i.spot.isMain).forEach(i => {
                                i.toggled = true;
                                view.spotVisibilityList.push(i.spot);
                            });
                        }
                        view.spotVisibilityList.push(item.spot);
                    } else {
                        view.spotVisibilityList = view.spotVisibilityList.filter(s => s !== item.spot);
                        if (item.spot.isMain) {
                            dropdown.items.filter(i => i.spot.unitId === item.spot.unitId).forEach(i => i.toggled = false);
                            view.spotVisibilityList = view.spotVisibilityList.filter(s => s.unitId !== item.spot.unitId);
                        }
                    }
                },
            });
        },
        OpenBusinessUnitAndGroupFilterDropdown($event) {
            let view = this;
            let target = $event.currentTarget;

            let list = [];


            this.spots.forEach(s => list.push({
                label: s.name, toggled: ( view.spotVisibilityList.find(p => s === p) ) ? true : false, spot: s,
                styles: s.isMain ? [] : [ { 'margin-left': '30px' } ],
                selectCallback: function ($event, item, dropdown) {
                    item.toggled = !item.toggled;

                    if (item.toggled) {
                        if (!item.spot.isMain) {
                            dropdown.items.filter(i => !i.toggled && i.spot && i.spot.unitId === item.spot.unitId && i.spot.isMain).forEach(i => {
                                i.toggled = true;
                                view.spotVisibilityList.push(i.spot);
                            });
                        }
                        view.spotVisibilityList.push(item.spot);
                    } else {
                        view.spotVisibilityList = view.spotVisibilityList.filter(s => s !== item.spot);
                        if (item.spot.isMain) {
                            dropdown.items.filter(i => i.spot && i.spot.unitId === item.spot.unitId).forEach(i => i.toggled = false);
                            view.spotVisibilityList = view.spotVisibilityList.filter(s => s.unitId !== item.spot.unitId);
                        }
                    }
                },
            }));
            this.$helpers.OpenPopup({
                elRelative: target,
                type: 'list',
                componentState: {
                    entries: list,
                },
                header: this.$t('shift.template.toggleWorkspaces'),
                expanded: true,
                centered: false,
                iconsLeft: true,
                remainAfterSelect: true,
            });

        },
        GetTimeMarkWidthWeek(index) {

            return {
                left: ( ( $(this.$el).find('.time-marks').width() / parseInt(this.dayModeHours) ) * index ) + 'px',
                width: ( $(this.$el).find('.time-marks').width() / parseInt(this.dayModeHours) ) + 'px'
            };
        },


        GetTimeMarkWidth(index) {

            let timeFrame = this.displayedTimeframeSelected;

            return { width: ( $(this.$el).find('.time-marks').width() / parseInt(timeFrame.divisions) ) + 'px' };
        },


        GetTimeFrameStyles(timeFrame, isOverlay = false) {
            let fromDate = timeFrame.fromDate;
            let toDate = timeFrame.toDate;


            let background = timeFrame.background || '#e5e5e5';

            let timeFrameWidth = toDate._d.getTime() - fromDate._d.getTime();
            let width = ( timeFrameWidth / this.timeFrameLength ) * 100;
            let left = ( ( fromDate._d.getTime() - this.timeFrameStart._d.getTime() ) / this.timeFrameLength ) * 100;


            let styles = {
                width: width + '%',
                left: left + '%',
                background: background,
            };

            if (isOverlay) {
                let overlayStyles = {
                    background: '#2f61ac',
                    'margin-top': '5px',
                    height: '25px',
                };
                styles = { ...styles, ...overlayStyles };
            }

            return styles;
        },

        HasBreak(timeFrame) {
            return timeFrame && timeFrame.templateId && this.currentWeekShiftTemplateMap[timeFrame.templateId] && this.currentWeekShiftTemplateMap[timeFrame.templateId].breakFrom;
        },
        GetBreakOffset(timeFrame) {
            let styles = {};
            let timeFrameWidth = timeFrame.toDate._d.getTime() - timeFrame.fromDate._d.getTime();


            let breakFromMoment = timeFrame.breakFrom;

            let breakToMoment = timeFrame.breakTo;
            let templateFrameWidth = breakToMoment._d.getTime() - breakFromMoment._d.getTime();

            let frameWidth = ( templateFrameWidth / timeFrameWidth ) * 100;
            styles.width = frameWidth + '%';
            let frameLeft = ( ( breakFromMoment._d.getTime() - timeFrame.fromDate._d.getTime() ) / timeFrameWidth ) * 100;
            styles.left = frameLeft + '%';

            return styles;
        },
        TimeDiff(a, b) {
            return b._d.getTime() - a._d.getTime();
        },
        getProfilePictureUrl(employee) {
            if (!employee || !employee.employeePicture || !employee.employeePicture.server) return 'var(--contrast-1) no-repeat center/cover  url(../img/default.jpg)';
            let d = new Date();
            return 'var(--contrast-1) no-repeat center/cover  url(' + employee.employeePicture.server + '/api/file?id=' + employee.employeePicture.fileId + '&accessToken=' + employee.employeePicture.accessToken + '&thumb=small' + '&time=' + Date.now() + ')';
        },
        WeeksInMonth(moment) {
            let temp = moment.clone();

            let first = temp.startOf('month').week();
            let last = temp.endOf('month').week();

            if (first > last) {
                last = first + last;
            }
            return last - first + 1;
        },
        sortByName: function (a, b) {
            let x = a.firstName + ' ' + a.lastName;
            let y = b.firstName + ' ' + b.lastName;
            if (x.toUpperCase() < y.toUpperCase()) return -1;
            if (x.toUpperCase() > y.toUpperCase()) return 1;
            return 0;
        },


        loadPlanningIndicatorTypes: function () {

            let url = '/api/sec/planningindicatortype';

            url = this.$helpers.appendParam('storeId', this.$store.state.storeId, url, '/api/sec/planningindicatortype');


            return this.axios({
                method: 'get',
                headers: {
                    'AUTHORIZATION': 'bearer ' + this.$store.state.jwt
                },
                url: url
            })

        },


        UpdateBusinessUnitSort: function (data) {
            let view = this;
            let url = '/api/sec/userunitsortupdate';
            url = this.$helpers.appendParam('storeId', this.$store.state.storeId, url, '/api/sec/userunitsortupdate');
            let payload = {
                data: data
            }
            this.axios({
                method: 'put',
                headers: {
                    'AUTHORIZATION': 'bearer ' + this.$store.state.jwt
                },
                url: url,
                data: payload
            }).then(response => {
                view.$helpers.success(view.$t('global.notifications.saved'), view.$t('shift.template.newOrderSaved'));
                let map = view.userUnitSortMap;
                response.data.data.forEach(sort => {
                    map[sort.id] = sort;
                });

                view.$store.commit('setUserUnitSortMap', {
                    userUnitSortMap: map
                });

            }).catch(function (error) {
                view.$helpers.warning(view.$t('global.notifications.error'), view.$t('global.notifications.errorMessage'));
                view.error = true;
                console.log(error);
            })
        },
        loadShiftTemplateSkills: function (payload) {
            let view = this;
            if (this.$helpers.isBlank(payload.ids) || payload.ids.length === 0) {
                return new Promise((resolve, reject) => {
                    resolve([]);
                })
            } else {
                let url = '/api/sec/shifttemplateskillrequirement/any/template';
                return view.$helpers.RecursivePOSTAny(url, payload, []);

            }
        },
        updateShiftTemplateSkills: function (data) {

            let map = {};

            for (let i = 0; i < data.length; i++) {
                map[data[i].id] = data[i];
                if (this.currentWeekShiftTemplateMap[data[i].templateId] != undefined) {
                    if (this.currentWeekShiftTemplateMap[data[i].templateId].requiredSkills == undefined) {
                        this.currentWeekShiftTemplateMap[data[i].templateId].requiredSkills = []
                    }
                    this.currentWeekShiftTemplateMap[data[i].templateId].requiredSkills.push(data[i].id);
                }
            }
            this.shiftTemplateSkillMap = map;
        },
        loadShiftTemplatesPagedCompletely: function (payload, groupId = null, page = 1, data = []) {

            let url = '/api/sec/shifttemplate/any/event';
            let params = {
                storeId: this.$store.state.storeId,
                validFrom: this.currentWeekStart.format(),
                validTo: this.currentWeekStart.clone().add(7, 'days').format(),
                page: page,
                groupId,
            }
            if (this.selectedGroup != null && groupId === null) {
                params.groupId = this.selectedGroup.id;
            }

            return this.axios({
                method: 'post',
                params: params,
                headers: {
                    'AUTHORIZATION': 'bearer ' + this.$store.state.jwt
                },
                url: url,
                data: payload
            }).then(response => {

                if (response.data.length < 1) {
                    return data;
                } else {
                    data.push(...response.data);
                    return this.loadShiftTemplatesPagedCompletely(payload, groupId, page + 1, data);
                }

            });

        },


        updateShiftTemplates: function (data) {
            this.shiftTemplates = data;
            this.currentWeekShiftTemplateMap = {};
            this.shiftTemplates.forEach(s => this.currentWeekShiftTemplateMap[s.id] = s);
        },
        loadStoreEvents() {
            let url = '/api/sec/storeevent';

            let params = {
                storeId: this.$store.state.storeId,
                active: true,
                from: this.$helpers.getMomentTZ().year(this.currentYear).startOf('year').add(-1, 'year').format(),
                to: this.$helpers.getMomentTZ().year(this.currentYear).startOf('year').add(1, 'year').add(2, 'weeks').format()
            };

            return this.axios({
                method: 'get',
                params: params,
                headers: {
                    'AUTHORIZATION': 'bearer ' + this.$store.state.jwt
                },
                url: url
            });
        },
        updateStoreEvents: function (data) {
            let i;
            let tstoreevents = {}
            for (i = 0; i < data.length; i++) {
                let item = data[i];
                item.fromMoment = this.$helpers.getMomentFromStringTZ(item.from);
                item.toMoment = this.$helpers.getMomentFromStringTZ(item.to);

                tstoreevents[data[i].id] = item;

            }
            this.storeEventMap = tstoreevents;
        },
        togglePDSection() {
            this.$store.commit('setDisplayPdChart', { displayPdChart: !this.displayPdChart });
            this.selectedPDIndex = null;
            // Recalculate barWidth after toggle
            if (this.displayPdChart) {
                this.$nextTick(() => {
                    this.$refs.pdChart.refresh().then(() => {
                        this.$refs.pdChart.updateBarWidth();
                    });
                });
            }
        },
        onChartDayDblClick(day) {
            this.DateSelected(day, day.isoWeekday(), this.hourlySelection);
        },
        updatePlaningTable(shiftDesignId) {
            let view = this;

            if (view.showShiftDesignDetails && view.selectedShiftDesign.id === shiftDesignId) {
                view.showShiftDesignDetails = false;
                return;
            }

            view.shiftGroupState = {
                selectMultiple: false, selectFirst: true, styleLight: true,
                placeholder: this.$t('shift.template.selectShiftGroup'),
                items: Object.values(this.shiftTemplateGroupMap)
                    .map(s => ({label: s.name, value: s.id, selected: false})),
            }

            view.loadShiftDesign(shiftDesignId)
            .then(response => {
                    view.selectedShiftDesign = response.data;
                    let match = view.shiftDesigns.find(d => d.id === view.selectedShiftDesign.id);
                    if (match) {
                        match.state = view.selectedShiftDesign.state;
                    }
                    view.showShiftDesignDetails = true;
            }).catch(e => {
                view.$helpers.error(view.$t('global.notifications.error'), view.$t('global.notifications.dataLoadingError'));
            });


        },
        OpenLoadShiftDesignPopup() {
          let view = this;
          view.loadShiftDesignOverview()
              .then(response => {
                view.shiftDesigns = response.data;
                view.showShiftDesignModal = true;
              }).catch(function (error) {
            view.$helpers.error(view.$t('global.notifications.error'), view.$t('global.notifications.dataLoadingError'));

            console.log(error);
          })

        },
        OpenCreateShiftDesignPopup() {
            let view = this;
            let designTypeItems = [];
            if(this.storeSettings.enableMeo){
                designTypeItems.push({ label: this.$t('shift.main.meo'), value: 1, selected: designTypeItems.length === 0 });
            }
            if(this.storeSettings.enableVlh){
                designTypeItems.push({ label: this.$t('shift.main.vlh'), value: 2, selected: designTypeItems.length === 0 });
            }
            let spotItems = [];
            Object.values(this.spotMap).forEach(s => {
                spotItems.push({ label: s.name, value: s.id, selected: false });
            });

            let dataEntries = [];
            dataEntries.push({
                label: this.$t('shift.template.planningType'), key: 'designType', type: 'dropdownsearch', hidden: false,
                dropdownSearchState: {
                    selectMultiple: false, selectFirst: true, styleLight: true,
                    placeholder: this.$t('shift.template.selectPlanningType'),
                    items: designTypeItems,
                }

            });
            dataEntries.push({
                label: this.$tc('shift.main.position', 2), key: 'spotIds', type: 'dropdownsearch', hidden: false,
                dropdownSearchState: {
                    selectMultiple: true, styleLight: true,
                    placeholder: this.$tc('shift.main.position'),
                    items: spotItems,
                }

            });
            dataEntries.push({label: this.$tc('store.settings.weekdays', 2),
            key: 'weekDays', hidden: false, flexGroup: false, type: 'togglelist', toggleEntries: [
                { label: this.$t('store.openingHours.weekDay1Short'), value: 1, selected: true },
                { label: this.$t('store.openingHours.weekDay2Short'), value: 2, selected: true },
                { label: this.$t('store.openingHours.weekDay3Short'), value: 3, selected: true },
                { label: this.$t('store.openingHours.weekDay4Short'), value: 4, selected: true },
                { label: this.$t('store.openingHours.weekDay5Short'), value: 5, selected: true },
                { label: this.$t('store.openingHours.weekDay6Short'), value: 6, selected: true },
                { label: this.$t('store.openingHours.weekDay7Short'), value: 7, selected: true},
                
            ]
            });
            dataEntries.push(
                {
                    label: this.$t('shift.template.minShiftLength'),
                    key: 'minShiftLength',
                    hidden: false,
                    type: 'slider',
                    value: 4*60,
                    computed: (datafill, lastVal, entryBeforeUpdate) => {

                        let max = datafill.entries.find(i => i.key == 'maxShiftLength').value;
                        let slider = entryBeforeUpdate.slider;
                        slider.max = max;
                        slider.value = Math.max(0,Math.min(slider.value, max));
                        let newSlider = Object.assign({}, slider)
                        entryBeforeUpdate.slider = newSlider;
                        entryBeforeUpdate.value = newSlider.value;
                        return entryBeforeUpdate;
                    },
                    slider: {
                        min: 4*60,
                        max: 9*60,
                        value: 4*60,
                        step: 60,
                        DisplayLabel: (val) => {return val / 60},
                        labelled: true,
                        inputControl: true,
                        sliderInput: (x) => {return x * 60},
                    }
                },
                {
                    label: this.$t('shift.template.maxShiftLength'),
                    key: 'maxShiftLength',
                    hidden: false,

                    type: 'slider',
                    value: 9*60,
                    slider: {
                        min: 4*60,
                        max: 12*60,
                        value: 9*60,
                        step: 60,
                        DisplayLabel: (val) => {return val / 60},
                        labelled: true,
                        inputControl: true,
                        sliderInput: (x) => {return x * 60},
                    }
                },
                {
                    label: this.$t('shift.template.setMinShiftStart'),
                    key: 'enableMinShiftStart',
                    hidden: false,
                    type: 'bool',
                    value: false
                },
                {
                    label: this.$t('shift.template.minShiftStart'),
                    key: 'minShiftStart',
                    hidden: this.$helpers.CheckDatafillKeysForHidden('enableMinShiftStart'),
                    type: 'slider',
                    value: 0,
                    slider: {
                        min: 0,
                        max: 23,
                        value: 0,
                        step: 1,
                        DisplayLabel: (val) => {return val},
                        labelled: true,
                        inputControl: true,
                        sliderInput: (x) => {return x},
                    }
                },
                {
                    label: this.$t('shift.template.setMaxShiftStart'),
                    key: 'enableMaxShiftStart',
                    hidden: false,
                    type: 'bool',
                    value: false
                },
                {
                    label: this.$t('shift.template.maxShiftStart'),
                    key: 'maxShiftStart',
                    hidden: this.$helpers.CheckDatafillKeysForHidden('enableMaxShiftStart'),
                    type: 'slider',
                    value: 0,
                    slider: {
                        min: 0,
                        max: 23,
                        value: 0,
                        step: 1,
                        DisplayLabel: (val) => {return val},
                        labelled: true,
                        inputControl: true,
                        sliderInput: (x) => {return x},
                    }
                },
            )


            view.$helpers.OpenPopup({
                type: 'datafill',
                componentState: {
                    entries: dataEntries,
                    errorCheck: [
                        (entries) => {
                            if(entries['weekDays'] == null|| entries['weekDays'].length == 0){
                                return {key: 'weekDays', type: 'error', label: this.$tc('store.settings.weekdays', 2), text: this.$t('store.settings.weekDaysErrorCheck')}
                            } else {
                                return null
                            } 
                        },
                        (entries) => {
                            if(entries['spotIds'] == null|| entries['spotIds'].length == 0){
                                return {key: 'spotIds', type: 'error', label: this.$tc('shift.main.position', 2), text: this.$t('roster.main.selectBusinessUnitPlease')}
                            } else {
                                return null
                            } 
                        },
                        (entries) => {
                            if((entries['spotIds'].length * entries['weekDays'].length) > 60){
                                return {key: 'spotIds', type: 'error', label: this.$tc('shift.main.position', 2), text: this.$t('shift.template.shiftDesignTooManyRequests')}
                            } else {
                                return null
                            } 
                        },
                    ],
                    selectCallback: function (data) {
                        let p;
                        let from = view.currentWeekStart;
                        let to = view.currentWeekStart.clone().add(8, 'days');
                        if(data.designType == 1){
                            p = view.$helpers.GetPlanningEmployeeThresholdCurvePaged(from, to, view.displayIndicatorType, true, 'quarterhourly');
                        } else {
                            p = view.$helpers.GetVlhThresholdCurvePaged(from, to, view.displayIndicatorType, true, 'quarterhourly')
                        }

                        p.then(res => {
                            let minShiftStart = data.enableMinShiftStart ? view.$helpers.getMomentTZ().startOf('day').hours(data.minShiftStart).format('HH:mm') : null;
                            let maxShiftStart = data.enableMaxShiftStart ? view.$helpers.getMomentTZ().startOf('day').hours(data.maxShiftStart).format('HH:mm') : null;
                            view.buildShiftDesignRequest(from, to, data.weekDays, data.spotIds, data.minShiftLength, data.maxShiftLength, minShiftStart, maxShiftStart, res);
                        })
                        .catch(e => {
                            console.error(e);
                            view.$helpers.error(view.$t('global.notifications.error'), view.$t('global.notifications.dataLoadingError'));
                        });

                    }
                },
                header: this.$t('shift.template.autoShiftPlan'),
                remainAfterSelect: false,
                width: "700px",
                expanded: true,
                centered: true
            });
        },
        buildShiftDesignRequest(from, to, weekDays, spotIds, minShiftLength, maxShiftLength, minShiftStart, maxShiftStart, curveData){

            let requests = [];
            let curveMap = {};
            curveData.forEach(c => {
                if(!curveMap[c.spotId]){
                    curveMap[c.spotId] = {};
                }

                let date = this.$helpers.getMomentFromStringTZ(c.date).format('YYYY-MM-DD HH:mm');
                curveMap[c.spotId][date] = {date: date, required: c.workTime/15};
            });
            spotIds.forEach(spotId => {
                if(curveMap[spotId]){
                    let wStart = from.clone();
                    let wEnd = to.clone().add(-1,'days');
                    while(wStart.isBefore(wEnd)){
                        if(weekDays.includes(wStart.isoWeekday())){
                            let reqs = [];
                            let totalReq = 0;
                            let dayStart = wStart.clone().startOf('day').add(4, 'hours');
                            let dayEnd = wStart.clone().startOf('day').add(28, 'hours');
                            let dayEndEnd = dayEnd.clone().startOf('day').add(1, 'day');

                            reqs.push({spotId: 1, startDateTime: wStart.format('YYYY-MM-DDTHH:mm:ss'), endDateTime: dayStart.format('YYYY-MM-DDTHH:mm:ss'), required: 0});
                            reqs.push({spotId: 1, startDateTime: dayEnd.format('YYYY-MM-DDTHH:mm:ss'), endDateTime: dayEndEnd.format('YYYY-MM-DDTHH:mm:ss'), required: 0});

                            while(dayStart.isBefore(dayEnd)){
                                let date = dayStart.format('YYYY-MM-DD HH:mm');
                                let required = curveMap[spotId][date] ? curveMap[spotId][date].required : 0;
                                reqs.push({spotId: 1, startDateTime: dayStart.format('YYYY-MM-DDTHH:mm:ss'), endDateTime: dayStart.clone().add(15, 'minutes').format('YYYY-MM-DDTHH:mm:ss'), required: required});
                                totalReq += required*15;
                                dayStart.add(15, 'minutes');
                            }

                            if(totalReq > 0){
                                let avgShiftLength  = (minShiftLength + maxShiftLength) / 2;
                                let shiftCount = Math.ceil(Math.ceil(totalReq / avgShiftLength)*1.15);

                                let payload = {
                                    slotId: spotId,
                                    config: {
                                        maxRunTime: 5*60,
                                        unimprovedMaxTime: 20
                                    },
                                    parametrization: {
                                        maxEmployeeCount: 1,
                                        startTimeInterval: 15,
                                        minShiftStart: minShiftStart,
                                        maxShiftStart: maxShiftStart,
                                    },
                                    minShiftLength: minShiftLength,
                                    maxShiftLength: maxShiftLength,
                                    shiftCountLimits: [{spotId: 1, maxNrShifts: shiftCount}],
                                    requirements: reqs

                                }
                                requests.push(payload);

                            }
                        }
                        wStart.add(1, 'days');
                    }
                }

            })

            if(requests.length > 0){
                let designPayload =  {
                    storeId: this.$store.state.storeId,
                    fromDate: from.format('YYYY-MM-DDTHH:mm:ss'),
                    toDate: to.format('YYYY-MM-DDTHH:mm:ss'),
                    designRequests: requests
                }


                this.createShiftDesign(designPayload);

            }
        },
        createShiftDesign(payload){
            let view = this;
            let url = '/api/sec/shiftdesign';
            url = this.$helpers.appendParam('storeId', this.$store.state.storeId, url, '/api/sec/shiftdesign');
            return this.axios({
                method: 'post',
                headers: {
                    'AUTHORIZATION': 'bearer ' + this.$store.state.jwt
                },
                url: url,
                data: payload
            }).then(response => {
                view.$helpers.success(view.$t('global.notifications.saved'), view.$t('shift.template.shiftDesignCreated'));

            }).catch(function (error) {
                view.$helpers.error(view.$t('global.notifications.error'), view.$t('shift.template.createPlanningError'));

                console.log(error);
            })
        },
        deleteShiftDesign(id){
             let view = this;
            let url = '/api/sec/shiftdesign';
            url = this.$helpers.appendParam('storeId', this.$store.state.storeId, url, '/api/sec/shiftdesign');
            return this.axios({
                method: 'delete',
                headers: {
                    'AUTHORIZATION': 'bearer ' + this.$store.state.jwt
                },
                url: url,
                params: {id: id}
            }).then(response => {
                if(view.selectedShiftDesign && view.selectedShiftDesign.id == id){
                    view.showShiftDesignDetails = false;
                }
                let newDesigns = [];
                view.shiftDesigns.forEach(d => {
                    if(d.id != id){
                        newDesigns.push(d);
                    }
                })
                view.shiftDesigns = newDesigns;
                view.$helpers.success(view.$t('global.notifications.saved'), view.$t('shift.template.shiftDesignDeleted'));

            }).catch(function (error) {
                view.$helpers.error(view.$t('global.notifications.error'), view.$t('shift.template.deleteShiftDesignError'));

                console.log(error);
            })
        },
        loadShiftDesignOverview(){
            let view = this;
            let url = '/api/sec/shiftdesign';
            let from = view.currentWeekStart.format("YYYY-MM-DDTHH:mm:ss");
            let to = view.currentWeekStart.clone().add(6, 'days').format("YYYY-MM-DDTHH:mm:ss");
            let params = {
                storeId: this.$store.state.storeId,
                from: from,
                to: to
            }

            return this.axios({
                method: 'get',
                headers: {
                    'AUTHORIZATION': 'bearer ' + this.$store.state.jwt
                },
                url: url,
                params: params
            })
        },
        loadShiftDesign(id){
            let view = this;
            let url = '/api/sec/shiftdesign';

            let params = {
                storeId: this.$store.state.storeId,
                id: id
            }

            return this.axios({
                method: 'get',
                headers: {
                    'AUTHORIZATION': 'bearer ' + this.$store.state.jwt
                },
                url: url,
                params: params
            })
        },
        transformShiftDesignIntoChanges(data, group, currentWeekOnly = true){
            let view = this;


            let resultMap = {};
            //let validFrom = view.currentWeekStart.clone();
            let resultCount = 0;
            data.forEach(d => {
                if(d.state == 2){
                    let result = d.result.solution;
                    let spotId = d.slotId;
                    if(resultMap[spotId] == undefined){
                        resultMap[spotId] = {};
                    }
                    Object.values(result.shiftMap).forEach(s => {
                        let employeeCount = result.countMap[""+s.employeeCount].count;
                        if(employeeCount > 0){
                            let shiftStart = view.$helpers.getMomentFromStringTZ(result.startMap[""+s.start].startDateTime, 'YYYY-MM-DDTHH:mm:ss');
                            let duration = result.durationMap[""+s.duration].duration;
                            let shiftEnd = shiftStart.clone().add(duration, 'minutes');
                            let weekDay = shiftStart.isoWeekday();
                            let slotFound = false;
                            while(!slotFound){
                                let key = shiftStart.format('HH:mm')+ '-' + shiftEnd.format('HH:mm')+ '-' + employeeCount;
                                if(!resultMap[spotId][key]){
                                    resultMap[spotId][key] = {from: shiftStart.format('HH:mm'), to: shiftEnd.format('HH:mm'), weekDays:[weekDay], spotId: spotId};
                                    slotFound = true;
                                    resultCount++;
                                } else {
                                    let weekDays = resultMap[spotId][key].weekDays;
                                    if(!weekDays.includes(weekDay)){
                                        weekDays.push(weekDay);
                                        slotFound = true;
                                        resultCount++;
                                    } else {
                                        employeeCount++;
                                    }
                                }
                            }

                        }
                    })


                }
            })

            if(resultCount > 0){
                if (group != null) {
                    let view = this;
                    let p = null;
                    view.discardChanges();
                    this.loading = true;
                    this.loadingText = this.$t('shift.template.loadShiftPlan')
                    this.displayOnlyActiveTemplates = false;
                    if (view.selectedGroup == null || view.selectedGroup.id != group) {
                        this.selectedGroup = this.shiftTemplateGroupMap[group];
                        p = this.RefreshShiftTemplates(true);
                    } else {
                        p = new Promise((resolve, reject) => {
                            resolve();
                        });
                    }

                    p.then(() => {
                        if(view.directSaveMode){
                            view.toggleDirectSaveMode();
                        }
                        view.activateEditMode();

                        Object.keys(resultMap).forEach(spot => {
                            Object.values(resultMap[spot]).forEach(s => {
                                let shiftTemplate = {
                                    groupId: group,
                                    id: Math.random().toString(36).substring(2, 8),
                                    validFrom: this.weekStartDateItems.past[0].value,
                                    validTo: currentWeekOnly ? this.weekStartDateItems.future[0].value : null,
                                    spotId: s.spotId,
                                    from: s.from,
                                    to: s.to,
                                    weekDays: s.weekDays,
                                    autoBreak: true,
                                    workValue: -1,
                                    count: 1,
                                    isNew: true
                                }

                                view.$set(view.addedTemplateMap, shiftTemplate.id, shiftTemplate);
                            })

                        });
                        view.loading = false;
                        view.$helpers.success(view.$t('shift.template.loaded'), view.$t('shift.template.shiftPlanningLoaded'));

                        view.showShiftDesignModal = false;
                        view.showShiftDesignDetails = false;
                    })
                    .catch(error => {
                        view.loading = false;

                        view.$helpers.error(view.$t('global.notifications.error'), view.$t('global.notifications.dataLoadingError'));
                    })
                }
            }





        }

    },
}
</script>

<!-- Add "scoped" attribute to limit CSS to this component only -->
<style scoped>
.fa-custom-header {
    display: inline-block;
    margin: 0 10px;
    margin-top: 0px;
    cursor: pointer;
    -webkit-box-shadow: 0 0 0 1.4px var(--contrast-5);
    box-shadow: 0 0 0 1.4px var(--contrast-5);
    border-radius: 100px;
    height: 30px;
    width: 30px;
    -webkit-transition: .2s;
    transition: .2s;
    float: initial;
}

.main-container {
    position: absolute;
    height: 100%;
    left: 0;
    width: 100%;


}

.template-view {
    display: flex;
    flex-direction: column;
    justify-content: flex-start;
}

.template-view > .header {
    background: var(--contrast-1);
    position: relative;
    top: 0px;
    left: 0px;
    width: 100%;
    height: 60px;
    text-align: center;
    z-index: 5;


}

.template-view > .header > * {
    display: inline-flex;
    align-items: center;
}


.event-select {
    width: 15%;
    height: 21px;
    line-height: 1 !important;
    font-size: 12px !important;
    padding: .01rem 0.05rem .01rem 0.1rem !important;
    border: 1px solid var(--contrast-5);
    font-weight: 700;
    background-color: var(--contrast-1);
    transform: translateY(4px) !important;
    border-radius: 5px;
}

.vertical-center {
    display: inline-block;
    position: relative;
    top: 50%;
    transform: translateY(-50%);
}

.grid {
    padding-top: 40px;
    text-align: center;
    position: relative;
    height: calc(100% - 0px) !important;
    overflow-y: scroll;
    overflow-x: hidden;

}

.grid > .header {
    position: relative;
    top: 0;
    left: 0;
    width: 260px;
    height: 30px;
    z-index: 15;
    padding-left: 10px;
}

.grid > .header .bottom-line {
    position: absolute;
    left: 0;
    width: 100%;
}


.header .dropdown-search {
    position: absolute;
    left: 5px;
    top: 0px;
}

.group {
    margin-bottom: 10px;
}

.group > .header {
    width: 245px;
    height: 19px;
    background: #6d8d23;
    border-top-left-radius: 50px;
    border-top-right-radius: 0;
    border-bottom-right-radius: 0;
    border-bottom-left-radius: 50px;
    position: relative;
    display: block;
    z-index: 5;
    margin-left: 15px;
    margin-bottom: 4px;
}

.employeecount-info {
    width: calc(100% - 255px);
    display: block;
    position: relative;
    top: -20px;
    left: 260px;
    height: 20px;
    margin-bottom: -20px;
}

.employeecount-info .label {
    display: block;
    position: absolute;
    font-size: clamp(8px, 0.75vw, 12px);
}

.employeecount-info .label .date {

    pointer-events: none;
}


.group > .header .label {
    color: white;
    font-size: 13px;
    padding-left: 20px;
    float: none;
    width: 100%;
    text-align: left;
    position: relative;
    top: -3px;
}

.group > .header .fa-stack {
    position: absolute;
    right: -10px;
    transform: scale(0.55);
    top: -4px;
    margin: 0;
    cursor: pointer;
}


.group > .header .fa-stack > * {
    pointer-events: none;
}

.group > .header .fa-stack.add {
    right: 6px;
}

.group > .header .fa-stack *:not(.fa-circle) {
    color: #6d8d23;
}

.group > .header .fa-stack .fa-circle {
    color: #e5e5e5;
}

.group > .header .fa-stack:hover .fa-circle {
    color: #707070 !important;
}

.group > .header .fa-stack:hover *:not(.fa-circle):not(.fa-arrows-alt) {
    color: #e5e5e5 !important;
}

.group > .header .fa-stack.drag {
    right: initial;
    left: -10px;
}

.group > .header .fa-stack.drag .fa-arrows-alt {
    line-height: 130%;
    font-size: 20px;
    color: white;
}

.group > .header .fa-stack.drag .fa-circle {
    display: none;
}


.t-row {
    position: relative;
    width: calc(100% - 271px);
    padding: 5px;
    margin-left: 260px;
    height: 45px;
}

.time-marks {
    width: calc(100% - 288px) !important;
}

.templatedisplay {
    position: absolute;
    top: 0;
    left: -245px;
    height: 45px;
    margin-bottom: 5px;
    padding-left: 45px;
    width: 246px;
    box-shadow: inset 0px -2px 0 -1px var(--contrast-6);
    z-index: 5;
}

.grid .t-row:hover .templatedisplay {
    background: var(--contrast-2);
    border-radius: 0;
    border-top-left-radius: 100px;
    border-bottom-left-radius: 100px;
    box-shadow: none;
}

.templatedisplay .picture {
    background-size: 100%;
    position: absolute;
    height: 30px;
    width: 30px;
    left: 5px;
    top: 7px;
    border-radius: 33px;
}

.templatedisplay .name {
    font-size: 13px;
    color: var(--contrast-5);
    text-align: left;
    height: 15px;
    white-space: nowrap;
    font-weight: 800;
    margin-top: 5px;
    margin-left: 0px;
    font-family: DistrictProBold;
}

.templatedisplay .validLabel {
    font-size: 10px;
    color: var(--contrast-5);
    text-align: left;
    height: 5px;
    white-space: nowrap;
    font-weight: 800;
    margin-top: 5px;
    margin-left: 0px;
    font-family: DistrictProBold;
}

.templatedisplay .buttons {
    font-size: 13px;
}

.templatedisplay .buttons .first {
    position: absolute;
    top: 3px;
}

.templatedisplay .buttons .second {
    position: absolute;
    top: 6px;
}

.templatedisplay .fa-stack {

    position: absolute;
    right: 10px;
    top: 0;
    bottom: 0;
    margin: auto;
}

.templatedisplay:hover .fa-stack.detail {
    opacity: 1;
}

.templatedisplay .fa-stack.detail {
    opacity: 1;
    cursor: pointer;
    font-size: 25px;
    width: 25px;
    height: 25px;
    right: 5px;
}

.templatedisplay .fa-stack.detail:hover .fa-circle {
    color: #6d8d23 !important;
}

.templatedisplay .fa-stack.detail:hover .fa-map {
    color: #404040 !important;
}

.templatedisplay .fa-stack > .fas:not(.fa-circle) {
    transform: scale(.55);
}

.event-info, .timeframe-popup, .drag-info, .drag-group {
    position: fixed;
    margin: auto;
    display: inline-block;
    background: white;
    box-shadow: 4px 4px 5px 3px var(--contrast-6);;
    pointer-events: none;
    width: fit-content;
    padding: 5px;
    border-radius: 5px;
    opacity: 1;
    z-index: 100;
}

.drag-group {
    background: none;
    box-shadow: none;
    z-index: 100;
    pointer-events: none;
}

.event-info .header {
    position: relative;
    width: 100%;
    font-weight: 600;
    text-align: center;
}

.event-info .description {
    position: relative;
    width: 100%;
    text-align: center;
}

.timeframe-popup {
    pointer-events: initial;
    z-index: 3;
}

.timeframe-popup .button {
    position: relative;
    font-weight: 600;
    text-align: center;
    margin: 5px 15px;
    width: 120px;
    display: block;
    height: 18px;
    border-radius: 100px;
    line-height: 100%;
}

.timeframe-popup .background {
    position: fixed;
    height: 100vh;
    width: 100vw;
    left: 0;
    right: 0;
    z-index: -1;
    top: 0;
}


.time-marks {
    position: absolute;
    left: 260px;
    width: calc(100% - 258px);
    height: 25px;
    padding: 0;
    top: 50px;
    white-space: nowrap;

}

.time-marks .label {
    position: absolute;
    top: -20px;
    left: -15px;
    width: 30px;
    z-index: 11;
    text-align: center;
    cursor: pointer;
}

.time-marks .label .date {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    margin: auto;
    z-index: 10;
    text-align: center;

}

.time-marks .label .fa-plus {
    position: absolute;
    right: 0;
    left: 0;
    z-index: 10;
    text-align: center;
    margin: auto;
    opacity: 0;
    opacity: 1;
    transition: .1s;
    bottom: -25px;
    color: white;
    background: var(--contrast-1);
    height: 20px;
    width: 20px;
    line-height: 22px;
    font-size: 12px;
    border-radius: 100px;
    justify-content: center;
}

.time-marks .label:hover .fa-plus, .time-marks .label.selected .fa-plus {
    opacity: 1;
}

.time-marks .mark {
    text-align: center;
    position: relative;
    width: calc(100% / 24);
    height: 100vh;
    display: inline-block;
    box-shadow: inset 2px 0px 0 -1px var(--contrast-6);
    background: none;

}

.time-marks .mark.day-split:not(:first-child) {
    box-shadow: inset 3px 0px 0 -1px var(--contrast-4);
}

.time-marks .mark:last-child {
    box-shadow: inset 2px 0px 0 -1px var(--contrast-6), inset -2px 0px 0 -1px var(--contrast-6);
}

.timeframe {
    position: absolute;
    height: calc(100% - 10px);
    background: #cccccc;
    /*border-radius: 5px; */
    cursor: pointer;
    min-width: 5px;
    z-index: 3;
}

.timeframe.fake {
    position: absolute;
    height: calc(100% - 10px);
    background: #ffffff !important;
    border-style: dotted;
    /*border-radius: 5px; */
    cursor: pointer;
    min-width: 5px;
    z-index: 3;
    transition: .2s;
    opacity: 0;
}

.fake:hover {
    opacity: 1;
}

.timeframe.break {
    height: 100%;
    background: #e3c423;
    bottom: 0;
    z-index: -1;
}

.timeframe .handle {
    position: absolute;
    top: 0;
    bottom: 0;
    height: 100%;
    width: 15px;
    margin: auto;
    cursor: col-resize;
    color: #9f9f9f;
    text-align: center;
    transition: .2s;
    opacity: 0;
}

.timeframe .label {
    top: 0px;
    left: 5px;
    color: #404040;
    text-align: center;
    font-size: 10px;
    margin: 0 5px;
    float: left;
}

.timeframe .label.end {
    right: 5px;
    left: initial;
    float: right;
}


.timeframe .handle .inner {
    position: absolute;
    top: 0;
    bottom: 0;
    left: 0;
    right: 0;
    margin: auto;
    background: #9f9f9f;
    width: 2px;
    height: calc(100% - 20px);
    pointer-events: none;
}

.timeframe:hover .handle {
    opacity: 1;
}

.handle.start {
    left: -12px;

}

.handle.end {
    right: -12px;
}

.timeframe .handle.end {
}

.grid .backdrop {
    position: fixed;
    bottom: 0;
    left: 0;
    height: calc(100% - (var(--header-height) * 2.5));
    width: 260px;
    background: var(--bg);
    /*-webkit-box-shadow: 0 0 30px  var(--bg);
        box-shadow: 0 0 15px 10px  var(--bg); */
    z-index: 4;
    pointer-events: none;
}

.scrollbar-container {
    position: relative;
    width: 100%;
    overflow: hidden;
    top: 0;
    padding-top: 30px;
}

.loading {
    filter: none !important;
    position: absolute;
    box-sizing: content-box;
    top: 0;
    left: 0;
    width: 100%;
    height: calc(100vh);
    background: rgba(0, 0, 0, .5);
    z-index: 10;
}

.loading .fa-spinner {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    margin: auto;
    width: 50px;
    height: 50px;
    font-size: 50px;
    color: var(--contrast-3);
    animation-name: spin;
    animation-duration: 2000ms;
    animation-iteration-count: infinite;
    animation-timing-function: linear;
}

@keyframes spin {
    from {
        transform: rotate(0deg);
    }
    to {
        transform: rotate(360deg);
    }
}

.t-row .bottom-line {
    box-shadow: inset 0px 2px 0 -1px var(--contrast-6);;
    height: 1px;
    position: absolute;
    bottom: 0px;
    width: calc(100% + 245px);
    width: calc(100% + 300px);
    left: -245px;
}

.bottom-line {
    box-shadow: inset 0px 2px 0 -1px var(--contrast-6);;
    height: 1px;
    position: relative;
    bottom: 0px;
    width: calc(100% + 245px);
    left: -245px;
}

.group > .bottom-line {
    margin-top: -7px;

}

.refresh:hover {
    background: #6d8d23;
}

.fa-refresh {
    font-size: 18px;
    -webkit-transform: translate(0px, 5px);
    transform: translate(0px, 5px);
}

.fa-refresh {
    color: var(--contrast-5);
}

.fa-refresh:before {
    content: '\f021';
}

.group.show-drag-area:not(.group-dragged) {
    padding-top: 20px;
}

.group .drag-catch-area {
    display: none;
}

.group .drag-catch-area.last {
    display: none;
}

.group.show-drag-area:not(.group-dragged) .drag-catch-area {
    display: inline-block;
    width: 230px;
    height: 19px;
    left: 5px;
    margin-top: -45px;
    background: var(--contrast-2);
    border-radius: 50px;
    position: absolute;
    z-index: 5;
    color: transparent;
    text-align: left;
    padding-left: 25px;
    font-size: 13px;
}

.group .drag-catch-area.last {
    display: none !important;
}

.group.show-drag-area-last {
    padding-bottom: 20px;
}

.group.show-drag-area-last .drag-catch-area.last {
    display: inline-block !important;
}

.group.show-drag-area:not(.group-dragged) .drag-catch-area.last {
    margin-top: 4px;
}

.group.show-drag-area:not(.group-dragged) .drag-catch-area:hover {
    box-shadow: inset 0 0 0 1px var(--contrast-2);
    background: none;
    color: var(--contrast-4);
}

.group.sub-spot.show-drag-area:not(.group-dragged) .drag-catch-area {
    width: 210px;
    left: 25px;
}

.group.sub-spot.show-drag-area:not(.group-dragged) .drag-catch-area:hover {
    box-shadow: inset 0 0 0 1px var(--contrast-2);
    background: none;
    color: var(--contrast-4);
}


.group-dragged {
    opacity: .5;
}

.group.sub-spot > .header {
    width: 235px;
    margin-left: 25px;
}

.group.sub-spot .template {
    width: 181px;
    left: -225px;
}


.positions-filter, .group-filter, .year-day-filter {
    position: absolute;
    top: 4px;
    left: 30px;
    z-index: 3;
}

.group-filter {
    left: 80px;
}

.year-day-filter {
    left: 140px;
}

.timeframe .fa-calendar-alt {
    color: #e86e13;
    position: absolute;
    top: -5px;
    right: -5px;
    cursor: pointer;
}

.year-date-only .label {
    color: white;
    border-radius: 100px;
    background: #e86e13;
    padding: 0 5px;
    margin: 0 0px;
}

.grid > .header > .add {
    position: absolute;
    left: 190px;
    top: 8px;
    margin: 0;
    transform: scale(0.6);
    cursor: pointer;
}

.grid > .header > .add .fa-plus {
    transform: scale(0.7);
}

.grid > .header > .add .fa-circle {
    color: #6d8d23;
}


.templatedisplay .remove, .templatedisplay .duplicate {
    position: absolute;
    right: 5px;
    top: 13px;
    margin: 0;
    transform: scale(0.65);
    cursor: pointer;
}

.templatedisplay .edit1, .templatedisplay .edit2 {
    position: absolute;
    right: 5px;
    top: 1px;
    margin: 0;
    transform: scale(0.65);
    cursor: pointer;
}

.templatedisplay .edit1 {
    position: absolute;
    right: 80px;
}

.templatedisplay .edit2 {
    position: absolute;
    right: 60px;
}

.templatedisplay .duplicate {
    position: absolute;
    right: 25px;
}

.templatedisplay .remove .fa-minus {
    transform: scale(0.7);
}

.templatedisplay .edit1 .fa-edit {
    transform: scale(0.5);
}

.templatedisplay .edit2 .fa-pencil-alt {
    transform: scale(0.6);
}


.templatedisplay .duplicate .fa-copy {
    transform: scale(0.6);
}

.templatedisplay .remove .fa-circle {
    color: #e51c50;
}


.time-marks > .label {
    display: none;
}


.time-marks.weekly > .label {
    position: absolute;
    display: block;
    top: -19px;
    left: 0;
    right: initial;
    text-align: center;
    width: calc(100% / 7);
    bottom: initial;
    height: 34px;
    line-height: 35px;
    cursor: pointer;
}


.time-marks.weekly .mark .label {
    display: none;
}

.time-marks.weekly .mark.selected .label, .time-marks.weekly .label:hover {
    background: var(--contrast-1);
    color: #6d8d23;
}

.time-marks .label.today {
    color: white;
    background: #6d8d23;
}


.header > .fa-calendar {
    font-size: 25px;
    margin-top: -4px;
    color: var(--contrast-5);
}

.options.weeks .label {
    width: 24px;
}

.options.today .label {
    width: 40px;
}

.options.weeks .label.special {
    background: var(--contrast-3);
}

.options.weeks .label.special:hover, .options.weeks .label.special.selected {
    background: #6d8d23;
}

.options .label.arrow {
    width: 30px;
    background: var(--contrast-5);
}

.options .label.arrow .fas {
    position: absolute;
    top: 2px;
    left: 0;
    right: 0;
    bottom: 0;
    font-size: 17px;
}

.options .label.arrow .fa-angle-left, .options .label.arrow .fa-angle-right {
    color: var(--contrast-5);
}

.options .label.arrow .fa-circle {
    color: var(--contrast-3);
}

.options .label.arrow:hover {
    background: #6d8d23;
}

.options .label.arrow:hover .fas {
    color: #6d8d23;
}

.options .label.arrow:hover .fa-circle {
    color: white;
}


.time-marks.weekly .mark {
    margin-top: -15px;
}

.time-marks .backdrop {
    background: var(--bg);
    position: absolute;
    top: -50px;
    left: 0;
    height: 99px;
    width: 100%;
    z-index: 4;
    box-shadow: 0 1px var(--contrast-3);
}

.time-marks .lines {
    position: absolute;
    top: 0px;
    left: 1px;
    height: 69px;
    width: 100%;
    z-index: 5;
    box-shadow: -1px 0px var(--contrast-3);
}

.time-marks .label:nth-child(8) .lines {
    box-shadow: -1px 0px var(--contrast-3), 1px 0px var(--contrast-3);
}

.group#events .timeframe {
    height: 20px;
    background: var(--contrast-1) !important;
    border: 1px solid var(--contrast-4);
}


.group#events .timeframe .label {
    width: 100%;
    text-align: center;
    cursor: pointer;
    position: absolute;
    left: 0;
    right: 0;
    margin: auto;
}

.group#events .timeframe .color {
    position: absolute;
    left: 7px;
    top: 0;
    bottom: 0;
    margin: auto;
    height: 7px;
    width: 7px;
    border-radius: 100px;
}

.group#events .timeframe .delete {
    position: absolute;
    right: 5px;
    top: 0;
    bottom: 0;
    margin: auto;
    height: 10px;
    width: 10px;
    background: transparent;
    border-radius: 0px;
    cursor: pointer;
}

.group#events .timeframe .delete .fas {
    position: absolute;
    left: 0;
    right: 0;
    top: 0;
    bottom: 0;
    margin: auto;
    height: 10px;
    width: 10px;
    font-size: 10px;
    color: var(--contrast-4);
    border-radius: 0px;
    pointer-events: none;
}

.group#events .timeframe .delete:hover {
    background: var(--contrast-1);
    box-shadow: 0 0 0 1px var(--contrast-4);
}

.group#events .timeframe .delete:hover .fas {
    color: var(--contrast-4);
}

.group#events .bottom-line {
    display: none;
}

.group#events .templatedisplay {
    box-shadow: none;
}


.group#events .t-row:last-child .bottom-line {
    display: initial;
    box-shadow: inset 0px 3px 0 -1px var(--contrast-6);
    height: 2px;
}


.templatedisplay .template-color-indicator {
    display: inline-block;
    position: relative;
    top: 3px;
    left: 1px;
    margin-top: -2px;
    width: 14px;
    height: 14px;
    border: 2px solid var(--contrast-2);
    border-radius: 50%;
}

.templatedisplay .event-tag {
    position: absolute;
    top: 0;
    width: 10px;
    height: 100%;
    right: 0;

}

.group#events .templatedisplay, .group#events .t-row {
    height: 30px;
}

.group#events .templatedisplay .name {
    margin-top: 6px;
}

.sub-header {

    position: relative;
    display: flex;
    height: 25px;

    z-index: 6;
    justify-content: left;
    position: absolute;
    top: 5px;
    width: 100%;

}

.viewfilter-dropdown {
    padding-left: 5px;
    flex: 1;
}

.template-view .pretitle {
    position: relative;
    display: flex;
    align-self: auto;
    justify-content: center;
    padding-left: 10px;
}

.template-view .pretitle .label {
    font-size: 16px;
    font-weight: 1500;
}

.template-view .title {
    position: relative;
    display: flex;
    align-self: center;
    justify-content: center;
    padding: 0;
}

.template-view .title .label {
    font-size: 16px;
    font-weight: 500;
}

.template-view .title .label.year {
    color: #6d8d23;
    margin-left: 5px;
    font-weight: 800;
}

.template-view .title .label.title {

}


.template-view .title.second {
}

.template-view .title.second .label {
}

.template-view .title.second .label.year {
}


.indicator {
    position: relative;
    display: inline-block;
    width: 40px;
    border-radius: 100px;
    height: 8px;
    margin: 0 1px;
    background: var(--contrast-3);
    top: 8px;
    float: right;
    right: 4px;
    cursor: pointer;
}

.indicator .bar {
    position: absolute;
    left: 0;
    top: 0;
    height: 100%;
    width: 50%;
    background: #6d8d23;
    border-radius: 100px;
    pointer-events: none;
}

.quarter {
    position: relative;
    display: inline-block;
    width: 6px;
    border-radius: 100px;
    height: 6px;
    margin: 0 1px;
    z-index: 10;
    background: var(--contrast-3);
    right: 0px;
    white-space: nowrap;
    float: none;
    top: 10px;
}

.employeequarter {
    position: relative;
    display: inline-block;
    width: clamp(1px, 0.25vw, 6px);
    border-radius: 100px;
    height: clamp(1px, 0.25vw, 6px);
    margin: 0 1px;
    background: var(--contrast-3);
    right: 0px;
    white-space: nowrap;
    float: none;
}

.two-days .quarter {
    right: 1px;
    width: 6px;
    height: 6px;
}

.prod-bar {
    background: var(--contrast-3);
    display: flex;
    justify-content: space-around;
    align-items: center;
    width: 100%;
    height: 10px;
    margin-top: -27px;
    font-size: 10px;
    border-radius: 10px;
}

.quarter-wrapper {
    display: flex;
    justify-content: space-around;
    align-items: center;
    gap: 2px;
    height: 100%;
}

.quarter-wrapper .quarter-point {
    flex: 1;
    aspect-ratio: 1;
    max-width: 6px;
    background: var(--contrast-3);
    border-radius: 50%;
}

.total-unassigned-hours {
    position: relative;
    width: 65%;
    height: 17px;
    margin: 0 1px;
    top: 4px;
    float: left;
    left: 3px;
    cursor: pointer;
    font-size: 10px;
    display: flex;
    align-items: center;
    color: var(--contrast-4);
}

.total-unassigned-hours.actual {
    color: #bc6c2c;
}


/* optimization overlay */
.templatedisplay--overlay {
    display: flex;
    align-items: center;
    gap: 5px;
    padding-right: 10px;
    box-shadow: none;
}

.templatedisplay--overlay .name {
    margin-top: 2px;
    line-height: 13px;
}

.templatedisplay--overlay :first-child {
    flex: 1;
}

.templatedisplay-button {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 1.25rem;
    height: 1.25rem;
    font-size: 0.5rem;
    border: none;
    border-radius: 50%;
    background-color: var(--contrast-3);
    color: var(--contrast-4);
}

.templatedisplay-button--large {
    position: absolute;
    right: 10px;
    top: 10px;
    width: 25px;
    height: 25px;
    font-size: 0.75rem;
}

.optimization-button-container {
    position: absolute;
    height: 100%;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: space-evenly;
}

.optimization-button {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 0.75rem;
    height: 0.75rem;
    font-size: 0.35rem;
    border: none;
    border-radius: 50%;
    background-color: var(--contrast-3);
    color: var(--contrast-4);
}

.optimization-button:hover {
    cursor: pointer;
}

.optimization-button--accept {
    background-color: #2f61ac;
    color: #f2f2f2;
}

.header-cell--info {
    position: relative;
    overflow: visible;
}

.event-add-button-container {
    display: flex;
    flex-direction: row;
    align-items: center;
    justify-content: space-evenly;
}

.add-event-button {
    height: 100%;
    width: 100%;
    font-size: 1rem;
    border: none;
    outline: none;
    /* border-radius: 50%; */
    background-color: transparent;
    padding: 0.125rem;
    transition: background-color 125ms ease-in-out, color 125ms ease-in-out;
    display: flex;
    align-items: center;
    justify-content: center;
    color: var(--contrast-5);
    margin: 0 0 2px 1px;
}

.add-event-button:hover {
    background-color: var(--contrast-1);
    color: var(--contrast-4);
    cursor: pointer;
    /* border: 2px solid var(--md); */
}

</style>

<style scoped>

.template-grid {
    flex: 1;
    overflow: auto;
    margin-bottom: auto;
    align-content: start;
}

.header-cell {
    padding: 0.25rem 0.5rem;
    height: 2rem;
    z-index: 3;
    color: var(--contrast-4);
}

.header-cell--day-mode {
    margin-top: 2rem;
}

.header-cell--info {
    margin-top: 0;
}

.header-cell--first-col {
    background-color: var(--contrast-0);
    height: auto;
}

.group-header-cell {
    display: flex;
    flex-direction: row;
    justify-content: space-between;
    align-items: center;
    height: 1.5rem;
    margin-top: 0;
    text-align: left;
    padding: 0 0.25rem 0 0.5rem;
    color: #efefef;
}

.group-header-cell--subgroup {
    margin-left: 1rem;
}

.group-header-cell-label {
    font-family: "DistrictProBold", sans-serif;
    font-size: 0.75rem;
}

.action-button-container {
    display: flex;
    align-items: center;
    justify-content: flex-end;
    gap: 0.25rem;
}

.cell-button {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 2rem;
    height: 2rem;
    font-size: 1rem;
    border: none;
    border-radius: 50%;
    color: #efefef;
    background-color: rgba(40, 40, 40, 0.25);
    transition: transform 250ms ease-in-out;
}

.cell-button:focus {
    outline: 1px solid #efefef;
}

.cell-button--small {
    width: 1rem;
    height: 1rem;
    font-size: 0.65rem;
}

.group-button--collapsed {
    transform: rotate(180deg);
}

.group-row-cell {
    display: flex;
    align-items: center;
    justify-content: center;
    height: 1.5rem;
    font-size: 0.75rem;
    font-family: "DistrictProBold", sans-serif;
    color: var(--contrast-5);
    background-color: var(--bg);
    margin: 0 1px 1px;
}

.template-display-cell {
    display: grid;
    grid-template-columns: auto 1fr auto;
    grid-auto-rows: auto;
    grid-template-areas:
        "im l1 ac"
        "im l2 ac";
    row-gap: 0.125rem;
    column-gap: 0.5rem;
    height: 3rem;
    padding: 0.5rem;
    background-color: var(--bg);
    z-index: 1;
    border-bottom: 1px solid var(--contrast-3);
}

.template-display-cell--single-row {
    grid-template-areas: "im l1 ac";
    height: auto;
}

.display-cell-image {
    grid-area: im;
    width: 2rem;
    height: 2rem;
    border: none;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    color: var(--contrast-5);
    /* background: var(--contrast-1); */
    background: radial-gradient(circle, var(--contrast-3) 0%, var(--contrast-3) 35%, var(--contrast-2) 35%, var(--bg) 100%);
}

.display-cell-label {
    grid-area: l2;
    font-family: DistrictProBold, sans-serif;
    font-size: 0.6rem;
    line-height: 0.6rem;
    text-align: left;
    color: var(--contrast-5);
}

.template-display-cell--single-row > .display-cell-label {
    grid-area: l1;
    display: flex;
    flex-direction: row;
    justify-content: flex-start;
    align-items: center;
    color: var(--contrast-4);
}

.display-cell-label--large {
    grid-area: l1;
    font-size: 0.8rem;
    line-height: 1rem;
}

.display-cell-new-indicator {
    color: var(--md);
    position: relative;
    top: -0.05rem;
    left: 0.5rem;
    font-size: 0.5rem;
    cursor: default;
}

.display-cell-actions {
    grid-area: ac;
    display: flex;
    align-items: center;
    justify-content: flex-end;
    gap: 0.5rem;
}

.display-cell-action {
    border: none;
    outline: none;
    background: none;
    color: var(--contrast-6);
}

.display-cell-action:hover {
    color: var(--contrast-5);
}

.event-cell {
    display: flex;
    align-items: center;
    height: 1.5rem;
    margin: 0.5rem 0;
    padding: 0.75rem;
    background-color: var(--contrast-1);
    color: var(--contrast-4);
    font-size: 0.75rem;
    font-family: "DistrictProBold", sans-serif;
}

.event-cell-new-indicator {
    left: -0.5rem;
}

.event-cell--new {
    background-color: rgba(109, 141, 33, 0.5);
}

.event-cell--delete {
    background: repeating-linear-gradient(45deg, var(--contrast-1), var(--contrast-1) 10px, var(--contrast-2) 10px, var(--contrast-2) 20px);
}

.event-color-indicator {
    width: 0.5rem;
    height: 0.5rem;
    border-radius: 50%;
    cursor: default;
}

.event-label {
    flex: 1;
    text-align: center;
}

.event-delete-button {
    width: 0.5rem;
    height: 0.5rem;
    display: flex;
    justify-content: center;
    align-items: center;
    outline: none;
    box-shadow: none;
    border: none;
    background-color: transparent;
}

.template-break-cell {
    height: 2rem;
    background-color: #ffd600;
    margin-top: 0.75rem;
    mix-blend-mode: normal;
}

.nightmode .template-break-cell {
    mix-blend-mode: soft-light;
}

.template-cell {
    margin: 0.75rem 0 0.25rem;
    padding: 0.25rem 0.4rem;
    height: 2rem;
    background-color: var(--contrast-1);
    color: var(--contrast-4);
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    flex-wrap: wrap;
    gap: 0.125rem;
    position: relative;
    container: template-cell / inline-size;
    overflow: visible !important;
}

.recommendation-cell {
    background-color: #0066cc;
    color: #efefef;
    z-index: 2;
    align-items: center;
    container: recommendation-cell / inline-size;
    transition: background-color 250ms ease-in-out;
}

.recommendation-cell:hover {
    cursor: pointer;
    background-color: #0055aa;
}

.recommendation-cell:hover .cell-label {
    cursor: pointer;
}

.recommendation-check {
    position: absolute;
    font-size: 0.75rem;
    left: calc(50% - 0.375rem);
    cursor: pointer;
}

@container template-cell (max-width: 56px) {
    .cell-label {
        position: absolute;
        color: var(--contrast-4);
    }

    .cell-label.cell-label--left {
        left: -1.8rem;
    }

    .cell-label.cell-label--right {
        right: -1.8rem;
    }
}

@container recommendation-cell (max-width: 56px) {
    .cell-label {
        position: absolute;
        color: var(--contrast-4);
    }

    .cell-label.cell-label--left {
        left: -1.8rem;
    }

    .cell-label.cell-label--right {
        right: -1.8rem;
    }
}

.template-cell--planned {
    height: 0.5rem;
    margin-top: 0.25rem;
    background-color: #6d8d23;
    color: #f2f2f2;
    flex-wrap: nowrap;
}

.template-cell--actual {
    height: 0.5rem;
    margin-top: 0.25rem;
    background-color: #e76e13;
    color: #f2f2f2;
    flex-wrap: nowrap;
}

.template-cell--half {
    height: 1rem;
    margin-top: 1.75rem;
    flex-wrap: nowrap;
    align-items: center;
}

.recommendation-cell.template-cell--half {
    margin-top: 0.25rem;
}

.template-cell--add-weekday {
    border: 2px dashed var(--contrast-3);
    background-color: var(--contrast-1);
    cursor: pointer;
    opacity: 0;
    transition: opacity 250ms ease-in-out;
}

.template-cell--add-weekday:hover {
    opacity: 1;
}

.recommendation-cell--chosen {
    margin-top: 3.75rem;
}

.cell-label {
    font-size: 0.6rem;
    line-height: 0.6rem;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    z-index: 2;
}

.template-cell--planned > .cell-label, .template-cell--actual > .cell-label {
    font-size: 8px;
    margin: -0.25rem 0;
    color: white;
}

.template-cell--planned > .cell-label--right, .template-cell--actual > .cell-label--right {
    right: 2px;
}

.template-cell--planned > .cell-label--left, .template-cell--actual > .cell-label--left {
    left: 2px;
}

.cell-label--left {
    margin-right: auto;
    text-align: left;
}

.cell-label--right {
    margin-left: auto;
    text-align: right;
}

.delete-weekday-button {
    position: absolute;
    bottom: 0.25rem;
    left: calc(50% - 0.3rem);
    padding: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    border: none;
    border-radius: 50%;
    outline: none;
    width: 0.6rem;
    height: 0.6rem;
    font-size: 0.35rem;
    background: var(--contrast-5);
    color: var(--bg);
    z-index: 2;
    opacity: 0;
    transition: opacity 250ms ease-in-out;
}

.template-cell:hover .delete-weekday-button {
    opacity: 1;
}

</style>

<style scoped>
.template-view {
    height: calc(100% - 36px);
}

.pd-container {
    width: 100%;
    height: 100%;
    max-height: 20rem;
    transition: max-height 250ms ease-in-out;
    /*background-color: var(--bg);*/
    background: linear-gradient(to bottom, var(--contrast-1) 40px, var(--bg) 40px, var(--bg) 100%);
    z-index: 10;
}

.pd-container.pd-collapsed {
    max-height: 40px;
    /*background-color: var(--contrast-1);*/
}

.pd-container > .pd-detail {
    position: absolute;
    left: 0;
    width: 265px;
    height: 20rem;
    max-height: 20rem;
    transition: max-height 250ms ease-in-out;
    z-index: 2;
}

.pd-container.pd-collapsed > .pd-detail {
    max-height: 40px;
    overflow-y: hidden;
}

.pd-container > .pd-chart {
    position: absolute;
    left: 265px;
    right: 0;
    height: 20rem;
    max-width: 500%;
    max-height: 20rem;
    transition: max-width 175ms ease-in-out 75ms;
}

.pd-container.pd-collapsed > .pd-chart {
    max-width: 0;
    overflow: hidden;
}

.pd-section-toggle {
    cursor: pointer;
    padding: 0.25rem;
    margin-right: 0.25em;
    transform: rotate(0deg);
    transition: transform 750ms ease-in-out;
}

.pd-section-toggle.pd-toggle-up {
    transform: rotate(180deg);
}

.info-video-container {
    position: fixed;
    inset: 0;
    display: flex;
    justify-content: center;
    align-items: center;
    background-color: rgba(0,0,0,0.5);
    z-index: 1001;
}

.info-video-sheet {
    position: relative;
    display: flex;
    flex-direction: column;
    align-items: flex-end;
    background-color: var(--bg);
    box-shadow: 0 4px 8px 0 rgba(0, 0, 0, 0.5);
    padding: 0.5rem 1rem 1rem;
    width: min(95%, 720px);
    aspect-ratio: 16 / 9;
}

.close-video-button {
    /* position: absolute; */
    background-color: transparent;
    margin: 0.25rem;
    border: none;
    outline: none;
    box-shadow: none;
    color: var(--contrast-5);
    font-size: 1rem;
}
</style>
